import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import { Auth } from "aws-amplify";
import { ReactComponent as RefreshIcon } from "../../../assets/Refresh_icon.svg";
import { ReactComponent as GoogleDriveIcon } from "../../../assets/Google_Drive_icon.svg";
import { ReactComponent as AmazonS3Icon } from "../../../assets/amazon-s3-icon.svg";
import { ReactComponent as GoogleCloudIcon } from "../../../assets/google-cloud-icon.svg";
import { ReactComponent as AzureIcon } from "../../../assets/azure-icon.svg";
import FSAImage from "../../../assetsPipeline/FSA.png";
import FSAInfoImage from "../../../assetsPipeline/FSA1.png";
import ISDImage from "../../../assetsPipeline/ISD.png";
import ISDInfoImage from "../../../assetsPipeline/ISD1.png";
import PSDImage from "../../../assetsPipeline/PSD.png";
import PSDInfoImage from "../../../assetsPipeline/PSD1.png";
import SSDImage from "../../../assetsPipeline/SSD.png";
import SSDInfoImage from "../../../assetsPipeline/SSD1.png";
import HCAImage from "../../../assetsPipeline/HCA.png";
import HCAInfoImage from "../../../assetsPipeline/HCA1.png";
import GWAImage from "../../../assetsPipeline/GWA.png";
import GWAInfoImage from "../../../assetsPipeline/GWA1.png";
import rdvShadowImage from "../../../assets/rdvshadow.png";
import SGAImage from "../../../assetsPipeline/SGA.png";
import SGAInfoImage from "../../../assetsPipeline/SGA1.png";
import CLZImage from "../../../assetsPipeline/CLZ.png";
import CLZInfoImage from "../../../assetsPipeline/CLZ1.png";
import EXGImage from "../../../assetsPipeline/EXG.png";
import EXGInfoImage from "../../../assetsPipeline/EXG1.png";
import EMBImage from "../../../assetsPipeline/EMB.png";
import EMBInfoImage from "../../../assetsPipeline/EMB1.png";
import "./RSAAnalysis.css";
import PathSelectorMap, {
  getPathCoordinatesEveryFeet,
} from "./PathSelectorMap/PathSelectorMap";
import { useLaneEditHistory } from "./PathSelectorMap/useLaneEditHistory";
import {
  resolveCopiedLaneNodes,
  generatedLaneGeometryCoords,
} from "./InternalComponents/rsaPathUtils";
import { getPathLengthMiles } from "./PathsPanels/PathsPanelWaypoints";
import IntersectionPanelContent from "./PathsPanels/IntersectionPanelContent";
import "./PathsPanels/IntersectionPanelContent.css";
import MapsPanelContent from "./PathsPanels/MapsPanelContent";
import {
  ISD_CASES,
  ISD_CASE_LABELS,
  LANE_TYPE_LABELS,
} from "./PathsPanels/rsaPathTypes";
import {
  useRsaPaths,
  sanitizePathLabel,
  makeUniqueLabel,
} from "./PathsPanels/useRsaPaths";
import { RsaPathsContext } from "./PathsPanels/RsaPathsContext";
import { pathsNeedingReconnect } from "./PathsPanels/orthoJobView";
import PathsListPanel from "./PathsPanels/PathsListPanel";
import { API_BASE_URL, ENVIRONMENT, processApi } from "../../../API/api";
import PermissionGate from "../../PermissionGate/PermissionGate";
import { useUserPermissions } from "../../../hooks/useUserPermissions";
import AnalysisDashboard, {
  getRunListProgressStats,
  getDashboardRunActiveStepDisplay,
} from "../AnalysisDashboard/AnalysisDashboard";
import UploadModal from "../../Projects/ProjectView/FileExplorer/UploadModal";
import FileExplorer from "../../Projects/ProjectView/FileExplorer/FileExplorer";
import DeleteConfirmModal from "../../Projects/ProjectView/FileExplorer/DeleteConfirmModal";
import "../AnalysisDashboard/AnalysisDashboard.css";
import fileService from "../../../services/fileService";
import { useUserRuns } from "../../../hooks/useUserRuns";
import progressService from "../../../services/progressService";
import {
  DEFAULT_GLOBAL_BOUNDS,
  DEFAULT_LOCAL_CONFIG,
  localExtentToLatLngBounds,
} from "./mapOverlayCoordinateUtils";
import {
  latLngPointsToBounds,
  projectedBboxToLatLngQuadAsync,
  projectedBboxToLatLngBoundsAsync,
  latLngToProjectedXYAsync,
  projectedPointsToLatLngAsync,
} from "./wktTransformApi";
import { normalizeWktInput } from "./wktNormalize";
// Using relative URLs for production (CloudFront), API_BASE_URL for local development

// Add debugging to intercept all fetch/axios calls
(() => {
  const originalFetch = window.fetch;
  window.fetch = (...args) => {
    const url = args[0];
    if (typeof url === "string" && url.includes("null")) {
      console.trace("[SEARCH] NULL FETCH CALL DETECTED:", url);
      console.trace("[SEARCH] Stack trace:", new Error().stack);
    }
    return originalFetch(...args);
  };

  const originalAxiosGet = axios.get;
  axios.get = (...args) => {
    const url = args[0];
    if (typeof url === "string" && url.includes("null")) {
      console.trace("[SEARCH] NULL AXIOS GET CALL DETECTED:", url);
      console.trace("[SEARCH] Stack trace:", new Error().stack);
    }
    return originalAxiosGet(...args);
  };
})();

const TASK_OPTIONS = [
  "Stopping Sight Distance (SSD)",
  "Passing Sight Distance (PSD)",
  "Flat Spot Analysis (FSA)",
  "Intersection Sight Distance (ISD)",
  "Sunglare Analysis (GLR)",
  "Guardrails Warrant (GRW)",
  "Horizontal Curve Analysis (HCA)",
  "Existing Guardrails (EXG)",
  "Embankment Analysis (EMB)",
  "Clear Zone Analysis (CLZ)",
];

const DEFAULT_ANALYSIS_TEMPLATE_ID_MAPPING = {
  "Stopping Sight Distance (SSD)": "1rlzhq4UcaG89Z6-BgsvkzEoy07c-_FAh", // SSD
  "Passing Sight Distance (PSD)": "1WSPVWSMQm9ZNVZYi63glWnHtovalxiSw", // PSD
  "Flat Spot Analysis (FSA)": "1o5Tkz_1kZ3L9duI1m8iCUru3eg6kkj7o", // FSA
  "Intersection Sight Distance (ISD)": "14Wcpu_nadstIKGkMfydgdCn91JuTfN7a", // ISD
  "Sunglare Analysis (GLR)": "1OO7pFamfYbVi-kR7dHLy4zmi-dEUP1KD", // GLR (proper analysis template; old id 1SLUCIl… was a GIS-config stub)
  "Guardrails Warrant (GRW)": "1YrgG9gBrxU2qPP0OdeAF875RDvwyR5Ax", // Guardrail_Warrant
  "Horizontal Curve Analysis (HCA)": "1ui2FC_9umPQwJnVRxvGLFG3vheE2jzu8", // Horizontal
  "Existing Guardrails (EXG)": "1_UYE80G3n99vcEHPzOVFappGDXnEyThg", // Existing_Guardrails
  "Embankment Analysis (EMB)": "1wgbWg82DReXKI5SYbLVI4Ib6OGFbY7DY", // Embankment
  "Clear Zone Analysis (CLZ)": "1R7RxChS8YYc1v4zu_1moBj5pnVA-NKmQ", // ClearZone
};

const RSA_MODEL_ANALYSIS_TEMPLATE_ID_MAPPING = {
  "Intersection Sight Distance (ISD)": "1utdS8VLw2hoCf00xZbXhYtcTsWnWDdsm", // ISD-model.json
  "Passing Sight Distance (PSD)": "10bkKVtyerK5Qe20g7Rc15ncwDQn4daih", // PSD-model.json
  "Stopping Sight Distance (SSD)": "1rTfTDLA48ET--fAduVGsI0ad3LFssmLC", // SSD-model.json
  "Flat Spot Analysis (FSA)": "1UATrkaa_ASvlJejyfi0TekiM3nBfO9lC", // FSA-model.json
  "Sunglare Analysis (GLR)": "1irrUhf9npguUDAxHy29tILDiv7DTVO0t", // GLR-model.json
  "Horizontal Curve Analysis (HCA)": "1ElRBuXgNAHakGVVw11671QAn34IeQQKN", // Horizontal-model.json
};

const RSA_MODEL_TASK_OPTIONS = [
  "Stopping Sight Distance (SSD)",
  "Passing Sight Distance (PSD)",
  "Flat Spot Analysis (FSA)",
  "Intersection Sight Distance (ISD)",
  "Sunglare Analysis (GLR)",
  "Horizontal Curve Analysis (HCA)",
];

const normalizeFolderName = (value) =>
  String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "");

const TASK_FOLDER_CODE_TO_OPTION = {
  RSASSD: "Stopping Sight Distance (SSD)",
  RSAPSD: "Passing Sight Distance (PSD)",
  RSAFSA: "Flat Spot Analysis (FSA)",
  RSAISD: "Intersection Sight Distance (ISD)",
  RSAGRW: "Guardrails Warrant (GRW)",
  RSACLZ: "Clear Zone Analysis (CLZ)",
  RSAEMB: "Embankment Analysis (EMB)",
  RSAEXG: "Existing Guardrails (EXG)",
  RSAGLR: "Sunglare Analysis (GLR)",
  RSAHCA: "Horizontal Curve Analysis (HCA)",
};

const RSA_FOLDER_CODE_TO_SUFFIX = {
  RSASSD: "SSD",
  RSAPSD: "PSD",
  RSAFSA: "FSA",
  RSAISD: "ISD",
  RSAGLR: "GLR",
  RSAGRW: "GRW",
  RSAHCA: "HCA",
  RSAEXG: "EXG",
  RSAEMB: "EMB",
  RSACLZ: "CLZ",
};

const TASK_OPTION_TO_SERVICE = {
  "Stopping Sight Distance (SSD)": "stopping_sight_distance",
  "Passing Sight Distance (PSD)": "passing_sight_distance",
  "Flat Spot Analysis (FSA)": "flat_spot_analysis",
  "Intersection Sight Distance (ISD)": "intersection_sight_distance",
  "Sunglare Analysis (GLR)": "sunglare_analysis",
  "Guardrails Warrant (GRW)": "guardrails_warrant",
  "Horizontal Curve Analysis (HCA)": "horizontal",
  "Existing Guardrails (EXG)": "existing_guardrails",
  "Embankment Analysis (EMB)": "embankment",
  "Clear Zone Analysis (CLZ)": "clear_zone_analysis",
};

const TASK_OPTION_TO_FOLDER_CODE = Object.entries(
  TASK_FOLDER_CODE_TO_OPTION,
).reduce((acc, [folderCode, taskOption]) => {
  acc[taskOption] = folderCode;
  return acc;
}, {});

const SERVICE_TO_TASK_OPTION = Object.entries(TASK_OPTION_TO_SERVICE).reduce(
  (acc, [taskOption, service]) => {
    acc[service] = taskOption;
    return acc;
  },
  {},
);

const SERVICE_TO_RSA_FOLDER_CODE = Object.entries(
  SERVICE_TO_TASK_OPTION,
).reduce((acc, [service, taskOption]) => {
  const folderCode = TASK_OPTION_TO_FOLDER_CODE[taskOption];
  if (folderCode) {
    acc[service] = folderCode;
  }
  return acc;
}, {});

const getFileId = (file) => file?.id ?? file?.Id;
const getParentId = (file) => file?.parent_id ?? file?.parentId ?? null;
const getGoogleDriveId = (file) =>
  file?.google_drive_id ?? file?.googleDriveId ?? null;
const getFileName = (file) => file?.name ?? file?.Name ?? "";
const isFolderItem = (file) => file?.isfolder === 1 || file?.isFolder === true;
const normalizeDriveFolderId = (value) => {
  const id = String(value ?? "").trim();
  if (!id) return "";
  const lower = id.toLowerCase();
  if (
    lower === "0" ||
    lower === "null" ||
    lower === "none" ||
    lower === "undefined"
  ) {
    return "";
  }
  return id;
};
const isXmlFileName = (name) => /\.xml$/i.test(String(name ?? "").trim());
const isDxfFileName = (name) => /\.dxf$/i.test(String(name ?? "").trim());
const isLasLazFileName = (name) =>
  /\.(las|laz)$/i.test(String(name ?? "").trim());
const getDisplayFileName = (name) =>
  String(name ?? "")
    .trim()
    .replace(/\.xml$/i, "");
const normalizeFileNameKey = (name) =>
  String(name ?? "")
    .trim()
    .toLowerCase();
const DIRECTION_TAG_REGEX = /<DIRECTION>\s*([A-Za-z])\s*<\/DIRECTION>/i;
const STATE_TAG_REGEX = /<STATE>\s*([^<]+?)\s*<\/STATE>/i;
const BEGIN_MILE_TAG_REGEX =
  /<BEGIN_MILE>\s*([-+]?\d*\.?\d+(?:[eE][-+]?\d+)?)\s*<\/BEGIN_MILE>/gi;
const END_MILE_TAG_REGEX =
  /<END_MILE>\s*([-+]?\d*\.?\d+(?:[eE][-+]?\d+)?)\s*<\/END_MILE>/gi;
const INTERSECTION_POINTS_BLOCK_REGEX =
  /<INTERSECTION_POINTS\b[\s\S]*?<\/INTERSECTION_POINTS>/gi;
const INTERSECTION_TAG_REGEX = /<INTERSECTION(?=[\s>])/gi;

const computeDirectionalMilesFromValues = (beginValue, endValue, direction) => {
  if (!Number.isFinite(beginValue) || !Number.isFinite(endValue)) {
    return 0;
  }
  const dir = String(direction || "")
    .trim()
    .toUpperCase();
  if (dir === "N") {
    return beginValue - endValue;
  }
  if (dir === "P") {
    return endValue - beginValue;
  }
  return endValue - beginValue;
};

const sumDirectionalMilesFromXml = (xmlText) => {
  if (typeof xmlText !== "string" || !xmlText) {
    return 0;
  }

  const directionMatch = DIRECTION_TAG_REGEX.exec(xmlText);
  const direction = directionMatch?.[1] || "";
  let total = 0;
  let parsedWithDom = false;

  try {
    const doc = new DOMParser().parseFromString(xmlText, "application/xml");
    const parseError = doc.getElementsByTagName("parsererror");
    if (!parseError || parseError.length === 0) {
      const pathNodes = Array.from(doc.getElementsByTagName("PATH"));
      if (pathNodes.length > 0) {
        parsedWithDom = true;
        for (const pathNode of pathNodes) {
          const beginText =
            pathNode.getElementsByTagName("BEGIN_MILE")[0]?.textContent ?? "";
          const endText =
            pathNode.getElementsByTagName("END_MILE")[0]?.textContent ?? "";
          const beginValue = Number(beginText);
          const endValue = Number(endText);
          total += computeDirectionalMilesFromValues(
            beginValue,
            endValue,
            direction,
          );
        }
      }
    }
  } catch (e) {
    // Fallback to regex parser below.
  }

  if (!parsedWithDom) {
    const begins = [];
    const ends = [];
    let beginMatch = BEGIN_MILE_TAG_REGEX.exec(xmlText);
    while (beginMatch) {
      begins.push(Number(beginMatch[1]));
      beginMatch = BEGIN_MILE_TAG_REGEX.exec(xmlText);
    }
    BEGIN_MILE_TAG_REGEX.lastIndex = 0;

    let endMatch = END_MILE_TAG_REGEX.exec(xmlText);
    while (endMatch) {
      ends.push(Number(endMatch[1]));
      endMatch = END_MILE_TAG_REGEX.exec(xmlText);
    }
    END_MILE_TAG_REGEX.lastIndex = 0;

    const pairCount = Math.min(begins.length, ends.length);
    for (let i = 0; i < pairCount; i += 1) {
      total += computeDirectionalMilesFromValues(begins[i], ends[i], direction);
    }
  }

  return total;
};

const countIsdManeuversFromXml = (xmlText) => {
  if (typeof xmlText !== "string" || !xmlText) {
    return 0;
  }

  let total = 0;
  let parsedWithDom = false;

  try {
    const doc = new DOMParser().parseFromString(xmlText, "application/xml");
    const parseError = doc.getElementsByTagName("parsererror");
    if (!parseError || parseError.length === 0) {
      const pointsNodes = Array.from(
        doc.getElementsByTagName("INTERSECTION_POINTS"),
      );
      if (pointsNodes.length > 0) {
        parsedWithDom = true;
        for (const pointsNode of pointsNodes) {
          total += pointsNode.getElementsByTagName("INTERSECTION").length;
        }
      }
    }
  } catch (e) {
    // Fallback to regex parser below.
  }

  if (!parsedWithDom) {
    const blocks = xmlText.match(INTERSECTION_POINTS_BLOCK_REGEX) || [];
    for (const block of blocks) {
      const intersections = block.match(INTERSECTION_TAG_REGEX) || [];
      total += intersections.length;
    }
  }

  return Number.isFinite(total) ? total : 0;
};

const readStateFromXml = (xmlText) => {
  if (typeof xmlText !== "string" || !xmlText.trim()) {
    return "";
  }

  try {
    const doc = new DOMParser().parseFromString(xmlText, "application/xml");
    const parseError = doc.getElementsByTagName("parsererror");
    if (!parseError || parseError.length === 0) {
      const stateText = doc.getElementsByTagName("STATE")[0]?.textContent ?? "";
      const normalized = String(stateText).trim();
      if (normalized) {
        return normalized.toUpperCase();
      }
    }
  } catch (e) {
    // Fallback to regex parsing below.
  }

  const stateMatch = STATE_TAG_REGEX.exec(xmlText);
  return String(stateMatch?.[1] ?? "")
    .trim()
    .toUpperCase();
};

const mapFolderCodeToTaskOption = (folderName) => {
  const normalizedCode = normalizeFolderName(folderName);
  return TASK_FOLDER_CODE_TO_OPTION[normalizedCode] || null;
};

const isProjectRootLikeFolder = (
  item,
  projectId,
  knownFolderIds = new Set(),
) => {
  if (!item) return false;
  const itemId = String(getFileId(item) ?? "").trim();
  const projectIdKey = String(projectId ?? "").trim();
  const parentRaw = getParentId(item);
  const parentId = parentRaw == null ? "" : String(parentRaw).trim();

  if (!parentId) {
    return true;
  }
  if (projectIdKey && parentId === projectIdKey) {
    return true;
  }
  if (itemId && parentId === itemId) {
    return true;
  }
  return !knownFolderIds.has(parentId);
};

const findDeliverablesFolderInItems = (items = [], options = {}) => {
  const {
    projectId = null,
    rootDriveIds = [],
    knownFolderIds = new Set(),
  } = options || {};
  const folders = (Array.isArray(items) ? items : [])
    .filter(
      (item) =>
        isFolderItem(item) &&
        normalizeFolderName(getFileName(item)) === "DELIVERABLES",
    )
    .sort((a, b) => {
      const aParentDriveId = normalizeDriveFolderId(a?.parentDriveId);
      const bParentDriveId = normalizeDriveFolderId(b?.parentDriveId);
      const aRootScore = rootDriveIds.includes(aParentDriveId)
        ? 0
        : isProjectRootLikeFolder(a, projectId, knownFolderIds)
          ? 0
          : 1;
      const bRootScore = rootDriveIds.includes(bParentDriveId)
        ? 0
        : isProjectRootLikeFolder(b, projectId, knownFolderIds)
          ? 0
          : 1;
      if (aRootScore !== bRootScore) {
        return aRootScore - bRootScore;
      }

      const aHasDriveId = normalizeDriveFolderId(getGoogleDriveId(a)) ? 0 : 1;
      const bHasDriveId = normalizeDriveFolderId(getGoogleDriveId(b)) ? 0 : 1;
      if (aHasDriveId !== bHasDriveId) {
        return aHasDriveId - bHasDriveId;
      }

      return String(getFileId(b) ?? "").localeCompare(
        String(getFileId(a) ?? ""),
      );
    });

  return folders[0] || null;
};

const resolveDeliverablesFolderForProject = async (
  projectId,
  filesSnapshot = null,
) => {
  if (!projectId) {
    return { driveId: "", treeId: "", name: "Deliverables" };
  }

  const isProductionDomain =
    window.location.hostname.includes("123bimlive.com");
  const baseUrl =
    ENVIRONMENT === "production" || isProductionDomain ? "" : API_BASE_URL;
  const cacheBust = `_ts=${Date.now()}`;

  let files = Array.isArray(filesSnapshot) ? filesSnapshot : null;
  if (!files) {
    const filesUrl = baseUrl
      ? `${API_BASE_URL}/api/projects/${projectId}/files?${cacheBust}`
      : `/api/projects/${projectId}/files?${cacheBust}`;
    const filesResponse = await axios.get(filesUrl);
    files = filesResponse.data?.files || [];
  }

  const allFolders = (files || []).filter(isFolderItem);
  const knownFolderIds = new Set(
    allFolders
      .map((item) => String(getFileId(item) ?? "").trim())
      .filter(Boolean),
  );
  const dbTarget = findDeliverablesFolderInItems(allFolders, {
    projectId,
    knownFolderIds,
  });

  const fallbackInfo = dbTarget
    ? {
        driveId: normalizeDriveFolderId(getGoogleDriveId(dbTarget)),
        treeId: String(getFileId(dbTarget) ?? "").trim(),
        name:
          String(getFileName(dbTarget) || "Deliverables").trim() ||
          "Deliverables",
      }
    : { driveId: "", treeId: "", name: "Deliverables" };

  if (fallbackInfo.driveId) {
    return fallbackInfo;
  }

  const rootDriveIds = Array.from(
    new Set(
      allFolders
        .filter((item) =>
          isProjectRootLikeFolder(item, projectId, knownFolderIds),
        )
        .map((item) => normalizeDriveFolderId(getGoogleDriveId(item)))
        .filter(Boolean),
    ),
  );

  if (rootDriveIds.length > 0) {
    const prefetchUrl = baseUrl
      ? `${API_BASE_URL}/api/projects/${projectId}/google-drive-prefetch?root_ids=${encodeURIComponent(rootDriveIds.join(","))}&${cacheBust}`
      : `/api/projects/${projectId}/google-drive-prefetch?root_ids=${encodeURIComponent(rootDriveIds.join(","))}&${cacheBust}`;
    const prefetchResponse = await axios
      .get(prefetchUrl)
      .catch(() => ({ data: { folders: {} } }));
    const prefetchedFolders = prefetchResponse?.data?.folders || {};
    const driveTarget = findDeliverablesFolderInItems(
      Object.values(prefetchedFolders).flatMap((items) =>
        Array.isArray(items) ? items : [],
      ),
      { projectId, rootDriveIds, knownFolderIds },
    );

    if (driveTarget) {
      const driveId = normalizeDriveFolderId(getGoogleDriveId(driveTarget));
      const treeId = String(
        getFileId(driveTarget) ?? (driveId ? `gd_${driveId}` : ""),
      ).trim();
      return {
        driveId,
        treeId,
        name:
          String(
            getFileName(driveTarget) || fallbackInfo.name || "Deliverables",
          ).trim() || "Deliverables",
      };
    }
  }

  return fallbackInfo;
};

const createIdleWktValidationState = () => ({
  status: "idle",
  checkedWkt: "",
  valid: false,
  willWork: false,
  message: "",
  location: null,
  crs: null,
  inputType: "unknown",
});

const buildWktStatusText = (hasWkt, validation) => {
  if (!hasWkt) return "Not provided";
  const status = validation?.status || "idle";
  const valid = Boolean(validation?.valid);
  const willWork = Boolean(validation?.willWork);
  if (status === "checking") return "Checking...";
  if (status === "error") return "Validation error";
  if (valid)
    return willWork ? "Valid and compatible" : "Valid but not compatible";
  return "Invalid";
};

const buildPersistedWktSummary = (wktText, validation) => {
  const trimmedWkt = String(wktText ?? "").trim();
  const hasWkt = Boolean(trimmedWkt);
  const locationLabel =
    validation?.location?.label || validation?.crs?.name || "Unknown";
  return {
    crsDefined: hasWkt ? "Yes" : "No",
    validationStatus: buildWktStatusText(hasWkt, validation),
    detectedLocation: hasWkt ? locationLabel : "Unknown",
    country: hasWkt ? validation?.location?.country || "Unknown" : "Unknown",
    stateRegion: hasWkt ? validation?.location?.state || "Unknown" : "Unknown",
    valid: Boolean(validation?.valid),
    willWork: Boolean(validation?.willWork),
    status: validation?.status || (hasWkt ? "done" : "idle"),
    message: validation?.message || "",
    location: validation?.location || null,
    crs: validation?.crs || null,
    inputType: validation?.inputType || "unknown",
    checkedAt: new Date().toISOString(),
  };
};

const parseSetupWktValidation = (setup) => {
  const wkt = normalizeWktInput(String(setup?.wkt ?? "").trim());
  if (!wkt) return createIdleWktValidationState();

  const summary =
    setup?.wktSummary && typeof setup.wktSummary === "object"
      ? setup.wktSummary
      : setup?.wktValidation && typeof setup.wktValidation === "object"
        ? setup.wktValidation
        : null;

  if (!summary) {
    // Legacy setup without cached metadata: let runtime validator execute once.
    return createIdleWktValidationState();
  }

  const statusCandidate = String(summary.status || "")
    .trim()
    .toLowerCase();
  const statusText = String(summary.validationStatus || "").toLowerCase();
  let status = "done";
  if (
    statusCandidate === "checking" ||
    statusCandidate === "error" ||
    statusCandidate === "idle" ||
    statusCandidate === "done"
  ) {
    status = statusCandidate;
  } else {
    if (statusText.includes("checking")) status = "checking";
    else if (statusText.includes("error")) status = "error";
    else status = "done";
  }

  const inferredValid = statusText.includes("valid");
  const inferredWillWork = statusText.includes("valid and compatible");
  const valid =
    typeof summary.valid === "boolean" ? summary.valid : inferredValid;
  const willWork =
    typeof summary.willWork === "boolean" ? summary.willWork : inferredWillWork;

  const location =
    summary.location && typeof summary.location === "object"
      ? { ...summary.location }
      : {};
  if (!location.label && summary.detectedLocation)
    location.label = summary.detectedLocation;
  if (!location.country && summary.country) location.country = summary.country;
  if (!location.state && summary.stateRegion)
    location.state = summary.stateRegion;

  return {
    status,
    checkedWkt: wkt,
    valid: Boolean(valid),
    willWork: Boolean(willWork),
    message: summary.message || summary.validationStatus || "",
    location: Object.keys(location).length ? location : null,
    crs: summary.crs || null,
    inputType: summary.inputType || "unknown",
  };
};

const deriveTaskMetadataFromProjectFiles = (files = []) => {
  if (!Array.isArray(files) || files.length === 0) {
    return {
      hasSourceFolder: false,
      taskOptions: null,
      sourceGoogleDriveIds: [],
      sourceFolderIds: [],
      dbFoldersByCode: {},
    };
  }

  const folders = files.filter(isFolderItem);
  const sourceFolders = folders.filter(
    (item) => normalizeFolderName(item?.name) === "SOURCE",
  );
  const sourceFolderIds = sourceFolders
    .map((item) => String(getFileId(item)))
    .filter((id) => id && id !== "undefined" && id !== "null");

  if (sourceFolderIds.length === 0) {
    return {
      hasSourceFolder: false,
      taskOptions: null,
      sourceGoogleDriveIds: [],
      sourceFolderIds: [],
      dbFoldersByCode: {},
    };
  }

  const sourceIdSet = new Set(sourceFolderIds);
  const detectedTasks = new Set();
  const dbFoldersByCode = {};
  const sourceGoogleDriveIds = Array.from(
    new Set(
      sourceFolders
        .map((folder) => String(getGoogleDriveId(folder) ?? "").trim())
        .filter(Boolean),
    ),
  );

  for (const folder of folders) {
    const parentId = getParentId(folder);
    if (parentId == null || !sourceIdSet.has(String(parentId))) {
      continue;
    }
    const taskOption = mapFolderCodeToTaskOption(folder?.name);
    if (taskOption) {
      detectedTasks.add(taskOption);
      const normalizedCode = normalizeFolderName(folder?.name);
      if (!dbFoldersByCode[normalizedCode]) {
        dbFoldersByCode[normalizedCode] = {
          id: getFileId(folder),
          name: folder?.name || normalizedCode,
          googleDriveId: getGoogleDriveId(folder),
          source: "db",
        };
      }
    }
  }

  return {
    hasSourceFolder: true,
    taskOptions: TASK_OPTIONS.filter((task) => detectedTasks.has(task)),
    sourceGoogleDriveIds,
    sourceFolderIds,
    dbFoldersByCode,
  };
};

const buildRsaFoldersPayloadFromProject = async (projectId) => {
  if (!projectId) {
    return {
      model_folder_id: [],
      path_folders: [],
      output_folder_id: "",
      output_folder_tree_id: "",
    };
  }

  const isProductionDomain =
    window.location.hostname.includes("123bimlive.com");
  const baseUrl =
    ENVIRONMENT === "production" || isProductionDomain ? "" : API_BASE_URL;
  const cacheBust = `_ts=${Date.now()}`;
  const filesUrl = baseUrl
    ? `${API_BASE_URL}/api/projects/${projectId}/files?${cacheBust}`
    : `/api/projects/${projectId}/files?${cacheBust}`;
  const filesResponse = await axios.get(filesUrl);
  const files = filesResponse.data?.files || [];
  const { sourceGoogleDriveIds, sourceFolderIds, dbFoldersByCode } =
    deriveTaskMetadataFromProjectFiles(files);
  const foldersByCode = {};
  const modelFolderIdsSet = new Set();
  const deliverablesInfo = await resolveDeliverablesFolderForProject(
    projectId,
    files,
  );
  const outputFolderId = String(deliverablesInfo?.driveId ?? "").trim();
  const outputFolderTreeId = String(deliverablesInfo?.treeId ?? "").trim();

  // DB fallback: detect MODEL folder directly under SOURCE.
  const sourceIdSet = new Set((sourceFolderIds || []).map((id) => String(id)));
  const dbModelFolders = (files || []).filter((item) => {
    if (!isFolderItem(item)) {
      return false;
    }
    const parentId = getParentId(item);
    if (parentId == null || !sourceIdSet.has(String(parentId))) {
      return false;
    }
    return normalizeFolderName(getFileName(item)) === "MODEL";
  });
  for (const modelFolder of dbModelFolders) {
    const modelFolderId = String(
      getGoogleDriveId(modelFolder) ?? getFileId(modelFolder) ?? "",
    ).trim();
    if (modelFolderId) {
      modelFolderIdsSet.add(modelFolderId);
    }
  }

  for (const [folderCode, folderInfo] of Object.entries(
    dbFoldersByCode || {},
  )) {
    const suffix = RSA_FOLDER_CODE_TO_SUFFIX[folderCode];
    if (!suffix) {
      continue;
    }
    const folderGoogleDriveId = String(folderInfo?.googleDriveId ?? "").trim();
    if (!folderGoogleDriveId) {
      continue;
    }
    foldersByCode[folderCode] = {
      config_type: suffix,
      path_folder_id: folderGoogleDriveId,
    };
  }

  if (Array.isArray(sourceGoogleDriveIds) && sourceGoogleDriveIds.length > 0) {
    const sourceChildrenResponses = await Promise.all(
      sourceGoogleDriveIds.map((sourceFolderId) => {
        const childrenUrl = baseUrl
          ? `${API_BASE_URL}/api/projects/${projectId}/google-drive-contents?folder_id=${encodeURIComponent(sourceFolderId)}&${cacheBust}`
          : `/api/projects/${projectId}/google-drive-contents?folder_id=${encodeURIComponent(sourceFolderId)}&${cacheBust}`;
        return axios.get(childrenUrl).catch(() => ({ data: { items: [] } }));
      }),
    );

    for (const sourceChildrenResponse of sourceChildrenResponses) {
      const items = sourceChildrenResponse?.data?.items || [];
      for (const item of items) {
        if (!isFolderItem(item)) {
          continue;
        }
        const folderCode = normalizeFolderName(item?.name);
        if (folderCode === "MODEL") {
          const modelFolderId = String(
            getGoogleDriveId(item) ?? getFileId(item) ?? "",
          ).trim();
          if (modelFolderId) {
            modelFolderIdsSet.add(modelFolderId);
          }
          continue;
        }
        const suffix = RSA_FOLDER_CODE_TO_SUFFIX[folderCode];
        if (!suffix || foldersByCode[folderCode]) {
          continue;
        }

        const folderGoogleDriveId = String(
          getGoogleDriveId(item) ?? getFileId(item) ?? "",
        ).trim();
        if (!folderGoogleDriveId) {
          continue;
        }

        foldersByCode[folderCode] = {
          config_type: suffix,
          path_folder_id: folderGoogleDriveId,
        };
      }
    }
  }

  const pathFolders = Object.values(foldersByCode).sort((a, b) =>
    a.config_type.localeCompare(b.config_type),
  );
  const modelFolderIds = Array.from(modelFolderIdsSet);
  return {
    model_folder_id: modelFolderIds,
    path_folders: pathFolders,
    output_folder_id: outputFolderId,
    output_folder_tree_id: outputFolderTreeId,
  };
};

const TASK_IMAGE_CONFIG = {
  "Flat Spot Analysis (FSA)": { code: "FSA", image: FSAImage },
  "Intersection Sight Distance (ISD)": { code: "ISD", image: ISDImage },
  "Passing Sight Distance (PSD)": { code: "PSD", image: PSDImage },
  "Stopping Sight Distance (SSD)": { code: "SSD", image: SSDImage },
  "Horizontal Curve Analysis (HCA)": { code: "HCA", image: HCAImage },
  "Guardrails Warrant (GRW)": { code: "GRW", image: GWAImage },
  "Sunglare Analysis (GLR)": { code: "GLR", image: SGAImage },
  "Clear Zone Analysis (CLZ)": { code: "CLZ", image: CLZImage },
  "Existing Guardrails (EXG)": { code: "EXG", image: EXGImage },
  "Embankment Analysis (EMB)": { code: "EMB", image: EMBImage },
};

const TASK_INFO_IMAGE_CONFIG = {
  "Flat Spot Analysis (FSA)": FSAInfoImage,
  "Intersection Sight Distance (ISD)": ISDInfoImage,
  "Passing Sight Distance (PSD)": PSDInfoImage,
  "Stopping Sight Distance (SSD)": SSDInfoImage,
  "Horizontal Curve Analysis (HCA)": HCAInfoImage,
  "Guardrails Warrant (GRW)": GWAInfoImage,
  "Sunglare Analysis (GLR)": SGAInfoImage,
  "Clear Zone Analysis (CLZ)": CLZInfoImage,
  "Existing Guardrails (EXG)": EXGInfoImage,
  "Embankment Analysis (EMB)": EMBInfoImage,
};

// --- Recursive tree node component ---
function FolderTreeNode({
  folder,
  onSelect,
  selectedFolderId,
  parentPath = "",
  storageConnection,
  useSystemDrive,
}) {
  const [expanded, setExpanded] = useState(false);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggleExpand = async (e) => {
    // Prevent the click from triggering selection and form submission
    e.preventDefault();
    e.stopPropagation();
    if (!expanded) {
      setLoading(true);
      try {
        let url;
        if (useSystemDrive) {
          // Use system Google Drive
          url =
            ENVIRONMENT === "local"
              ? `${API_BASE_URL}/api/google-drive/folders?parent_id=${folder.id}`
              : `/api/google-drive/folders?parent_id=${folder.id}`;
        } else if (storageConnection) {
          // Use user's storage
          url =
            ENVIRONMENT === "local"
              ? `${API_BASE_URL}/api/storage/connections/${storageConnection.id}/folders?folder_id=${folder.id}`
              : `/api/storage/connections/${storageConnection.id}/folders?folder_id=${folder.id}`;
        }

        if (url) {
          console.log("🔧 RSAAnalysis - Making folder request to:", url);
          const response = await axios.get(url);
          setChildren(response.data.folders || []);
        } else {
          console.warn("No URL available for fetching folders");
          setChildren([]);
        }
      } catch (err) {
        console.error(
          "❌ RSAAnalysis - Error fetching children for folder",
          folder.id,
          err,
        );
        setChildren([]);
      } finally {
        setLoading(false);
      }
    }
    setExpanded(!expanded);
  };

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const fullPath = parentPath ? `${parentPath}/${folder.name}` : folder.name;
    onSelect({ ...folder, fullPath });
  };

  const handleDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleExpand(e);
  };

  const isSelected = folder.id === selectedFolderId;

  return (
    <li
      className={`rsa-tree-node ${isSelected ? "rsa-tree-node-selected" : ""}`}
    >
      <div
        className="rsa-tree-node-content"
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      >
        <button
          type="button"
          className={`rsa-tree-toggle ${expanded ? "rsa-tree-toggle-expanded" : ""}`}
          onClick={toggleExpand}
          disabled={loading}
          aria-label={expanded ? "Collapse folder" : "Expand folder"}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.5 3L7.5 6L4.5 9"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="rsa-tree-icon">
          {expanded ? (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>

        <span className="rsa-tree-label">{folder.name}</span>

        {loading && (
          <span className="rsa-tree-loading">
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="rsa-tree-loading-spinner"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="32"
                strokeDashoffset="32"
              >
                <animate
                  attributeName="stroke-dasharray"
                  dur="2s"
                  values="0 32;16 16;0 32;0 32"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="stroke-dashoffset"
                  dur="2s"
                  values="0;-16;-32;-32"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
          </span>
        )}
      </div>

      {expanded && children.length > 0 && (
        <ul className="rsa-tree-children">
          {children.map((child) => (
            <FolderTreeNode
              key={child.id}
              folder={child}
              onSelect={onSelect}
              selectedFolderId={selectedFolderId}
              parentPath={
                parentPath ? `${parentPath}/${folder.name}` : folder.name
              }
              storageConnection={storageConnection}
              useSystemDrive={useSystemDrive}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

// --- Storage Connection Modal ---
function StorageConnectionModal({ onClose, provider, onConnectionSuccess }) {
  const [s3Form, setS3Form] = useState({
    connection_name: "",
    access_key_id: "",
    secret_access_key: "",
    bucket_name: "",
    region: "us-east-1",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isOAuthProvider = ["google_drive", "azure", "gcp"].includes(provider);
  const isS3Provider = provider === "s3";

  const providerNames = {
    google_drive: "Google Drive",
    azure: "Microsoft Azure",
    gcp: "Google Cloud Storage",
    s3: "AWS S3",
  };

  // Helper function to get token from sessionStorage (handles JSON stringified tokens)
  const getAuthToken = async () => {
    try {
      // First, try to get token from AWS Amplify Auth (primary method)
      try {
        const session = await Auth.currentSession();
        if (session && session.getIdToken()) {
          const idToken = session.getIdToken().getJwtToken();
          console.log("[OK] [getAuthToken] Got token from Amplify Auth");
          return idToken;
        }
      } catch (amplifyError) {
        console.log(
          "â„¹ï¸ [getAuthToken] Amplify Auth not available, trying other methods:",
          amplifyError.message,
        );
      }

      // Fallback: Check multiple possible keys in storage (in order of likelihood)
      let token = sessionStorage.getItem("bimLiveAuthToken"); // Primary key used by this app

      if (!token) {
        token = sessionStorage.getItem("token");
      }
      if (!token) {
        token = sessionStorage.getItem("cognito_token");
      }
      if (!token) {
        token = sessionStorage.getItem("idToken");
      }
      if (!token) {
        // Check localStorage as fallback
        token =
          localStorage.getItem("bimLiveAuthToken") ||
          localStorage.getItem("token") ||
          localStorage.getItem("cognito_token") ||
          localStorage.getItem("idToken");
      }

      // Check cookies as last resort
      if (!token) {
        const cookies = document.cookie.split(";");
        for (let cookie of cookies) {
          const [name, value] = cookie.trim().split("=");
          if (name === "cognito_token" || name === "token") {
            token = value;
            break;
          }
        }
      }

      console.log(
        "🔍 [getAuthToken] Raw token from storage:",
        token ? `${token.substring(0, 20)}...` : "null",
      );

      if (!token) {
        console.warn(
          "[WARN] [getAuthToken] No token found in sessionStorage, localStorage, cookies, or Amplify",
        );
        console.log(
          "🔍 [getAuthToken] Available sessionStorage keys:",
          Object.keys(sessionStorage),
        );
        return null;
      }

      // Try to parse as JSON (token might be stored as JSON string)
      try {
        const parsed = JSON.parse(token);
        console.log(
          "[OK] [getAuthToken] Token is JSON, parsed structure:",
          typeof parsed,
          parsed ? Object.keys(parsed) : "null",
        );

        if (parsed && typeof parsed === "string") {
          // Token is a JSON stringified string
          console.log("[OK] [getAuthToken] Using parsed string token");
          return parsed;
        } else if (parsed && parsed.token) {
          // Token is an object with a token property
          console.log("[OK] [getAuthToken] Using parsed.token");
          return parsed.token;
        } else if (parsed && parsed.idToken) {
          // Token is an object with an idToken property (Cognito)
          console.log("[OK] [getAuthToken] Using parsed.idToken");
          return parsed.idToken;
        } else if (parsed && parsed.accessToken) {
          // Token is an object with an accessToken property
          console.log("[OK] [getAuthToken] Using parsed.accessToken");
          return parsed.accessToken;
        } else {
          // If parsed is an object but we don't know the structure, log it
          console.warn(
            "[WARN] [getAuthToken] Parsed token is object but no known property found:",
            parsed,
          );
          // Try to return the whole object as string (some APIs accept this)
          return JSON.stringify(parsed);
        }
      } catch (parseError) {
        // Not JSON, use as-is (raw JWT token)
        console.log(
          "[OK] [getAuthToken] Token is not JSON, using as-is (raw JWT)",
        );
        return token;
      }
    } catch (e) {
      console.error("❌ [getAuthToken] Error getting auth token:", e);
      return null;
    }
  };

  const handleOAuthConnect = async () => {
    try {
      setLoading(true);
      setError(""); // Clear any previous errors

      // Get token (tries Amplify Auth first, then storage)
      const token = await getAuthToken();
      if (!token) {
        setError(
          "You must be logged in to connect storage. Please log in and try again.",
        );
        setLoading(false);
        return;
      }

      console.log("🔐 Starting OAuth flow for provider:", provider);

      // Store current page URL to redirect back after OAuth
      const returnUrl = encodeURIComponent(
        window.location.pathname + window.location.search,
      );
      // Add popup=true parameter to indicate this is a popup request
      const url =
        ENVIRONMENT === "local"
          ? `${API_BASE_URL}/api/storage/oauth/${provider === "google_drive" ? "google" : provider}/authorize?return_url=${returnUrl}&popup=true`
          : `/api/storage/oauth/${provider === "google_drive" ? "google" : provider}/authorize?return_url=${returnUrl}&popup=true`;

      console.log("📡 Calling backend OAuth authorize endpoint:", url);

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("[OK] Backend response:", response.data);

      if (response.data && response.data.authorization_url) {
        const authUrl = response.data.authorization_url;
        console.log("🔗 Opening OAuth popup:", authUrl);

        // Open OAuth flow in a popup window
        const popupWidth = 600;
        const popupHeight = 700;
        const left = (window.screen.width - popupWidth) / 2;
        const top = (window.screen.height - popupHeight) / 2;

        const popup = window.open(
          authUrl,
          "oauth-popup",
          `width=${popupWidth},height=${popupHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`,
        );

        if (!popup) {
          setError(
            "Popup blocked. Please allow popups for this site and try again.",
          );
          setLoading(false);
          return;
        }

        // Listen for messages from the popup
        const handleMessage = (event) => {
          // Security: Only accept messages from our own origin
          if (event.origin !== window.location.origin) {
            console.warn(
              "[WARN] Ignoring message from different origin:",
              event.origin,
            );
            return;
          }

          if (event.data && event.data.type === "oauth-callback") {
            console.log("[OK] OAuth callback received:", event.data);

            if (event.data.success) {
              // Close popup
              popup.close();

              // Remove event listener
              window.removeEventListener("message", handleMessage);

              // Reload storage connections
              if (onConnectionSuccess) {
                onConnectionSuccess();
              }

              // Close modal and show success
              setLoading(false);
              onClose();

              // Show success message
              const providerNames = {
                google_drive: "Google Drive",
                azure: "Microsoft Azure",
                gcp: "Google Cloud Storage",
                s3: "AWS S3",
              };
              const providerName = providerNames[provider] || provider;
              alert(`${providerName} connected successfully!`);
            } else {
              // Error occurred
              popup.close();
              window.removeEventListener("message", handleMessage);
              setError(
                event.data.error ||
                  "Failed to connect storage. Please try again.",
              );
              setLoading(false);
            }
          }
        };

        window.addEventListener("message", handleMessage);

        // Check if popup was closed manually
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            window.removeEventListener("message", handleMessage);
            setLoading(false);
            // Don't show error if user just closed the popup
          }
        }, 1000);
      } else {
        console.error("❌ No authorization_url in response:", response.data);
        setError(
          "Failed to get authorization URL from server. Please try again.",
        );
        setLoading(false);
      }
    } catch (err) {
      console.error("❌ Error initiating OAuth:", err);
      console.error("❌ Error details:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });

      let errorMessage = "Failed to connect. Please try again.";

      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.status === 401) {
        errorMessage = "Authentication failed. Please log in and try again.";
      } else if (err.response?.status === 500) {
        errorMessage = "Server error. Please check if Google OAuth is Setupd.";
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleS3Connect = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Get token (tries Amplify Auth first, then storage)
      const token = await getAuthToken();
      if (!token) {
        setError(
          "You must be logged in to connect storage. Please log in and try again.",
        );
        setLoading(false);
        return;
      }

      const url =
        ENVIRONMENT === "local"
          ? `${API_BASE_URL}/api/storage/connect/s3`
          : "/api/storage/connect/s3";

      const response = await axios.post(url, s3Form, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 201) {
        // Success - reload connections and close modal
        if (onConnectionSuccess) {
          onConnectionSuccess();
        }
        onClose();
      }
    } catch (err) {
      console.error("Error connecting S3:", err);
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        "Failed to connect S3. Please check your credentials.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="TreeModalOverlay">
      <div className="TreeModalContainer">
        <div className="modal-header">
          <h2 className="modal-title">
            Connect {providerNames[provider] || provider}
          </h2>
        </div>

        <div className="modal-content">
          {isOAuthProvider && (
            <div className="modal-section">
              <p
                style={{
                  color: "#e2e8f0",
                  fontSize: "14px",
                  lineHeight: "1.6",
                  marginBottom: "20px",
                }}
              >
                You'll be redirected to {providerNames[provider]} to grant
                permission for our application to access your storage. This is a
                secure OAuth 2.0 flow - you'll just need to click "Allow" on the
                authorization page.
              </p>
              <div
                style={{
                  padding: "12px",
                  background: "rgba(59, 131, 246, 0.1)",
                  borderRadius: "8px",
                  border: "1px solid rgba(59, 131, 246, 0.3)",
                  marginBottom: "20px",
                }}
              >
                <p style={{ color: "#93c5fd", fontSize: "13px", margin: 0 }}>
                  <strong>What happens next:</strong>
                </p>
                <ul
                  style={{
                    color: "#cbd5e1",
                    fontSize: "13px",
                    margin: "8px 0 0 20px",
                    padding: 0,
                  }}
                >
                  <li>
                    You'll be redirected to {providerNames[provider]}'s login
                    page
                  </li>
                  <li>After logging in, you'll see a consent screen</li>
                  <li>Click "Allow" to grant access</li>
                  <li>You'll be redirected back automatically</li>
                </ul>
              </div>
            </div>
          )}

          {isS3Provider && (
            <form id="s3-connection-form" onSubmit={handleS3Connect}>
              <div className="modal-section">
                <label className="modal-label">
                  Connection Name (optional)
                </label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder="My S3 Bucket"
                  value={s3Form.connection_name}
                  onChange={(e) =>
                    setS3Form({ ...s3Form, connection_name: e.target.value })
                  }
                />
              </div>

              <div className="modal-section">
                <label className="modal-label">Access Key ID *</label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder="AKIAIOSFODNN7EXAMPLE"
                  value={s3Form.access_key_id}
                  onChange={(e) =>
                    setS3Form({ ...s3Form, access_key_id: e.target.value })
                  }
                  required
                />
              </div>

              <div className="modal-section">
                <label className="modal-label">Secret Access Key *</label>
                <input
                  type="password"
                  className="modal-input"
                  placeholder="Enter your secret key"
                  value={s3Form.secret_access_key}
                  onChange={(e) =>
                    setS3Form({ ...s3Form, secret_access_key: e.target.value })
                  }
                  required
                />
              </div>

              <div className="modal-section">
                <label className="modal-label">Bucket Name *</label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder="my-bucket-name"
                  value={s3Form.bucket_name}
                  onChange={(e) =>
                    setS3Form({ ...s3Form, bucket_name: e.target.value })
                  }
                  required
                />
              </div>

              <div className="modal-section">
                <label className="modal-label">Region *</label>
                <select
                  className="modal-select"
                  value={s3Form.region}
                  onChange={(e) =>
                    setS3Form({ ...s3Form, region: e.target.value })
                  }
                  required
                >
                  <option value="us-east-1">
                    US East (N. Virginia) - us-east-1
                  </option>
                  <option value="us-east-2">US East (Ohio) - us-east-2</option>
                  <option value="us-west-1">
                    US West (N. California) - us-west-1
                  </option>
                  <option value="us-west-2">
                    US West (Oregon) - us-west-2
                  </option>
                  <option value="eu-west-1">
                    Europe (Ireland) - eu-west-1
                  </option>
                  <option value="eu-central-1">
                    Europe (Frankfurt) - eu-central-1
                  </option>
                  <option value="ap-southeast-1">
                    Asia Pacific (Singapore) - ap-southeast-1
                  </option>
                  <option value="ap-southeast-2">
                    Asia Pacific (Sydney) - ap-southeast-2
                  </option>
                  <option value="ap-northeast-1">
                    Asia Pacific (Tokyo) - ap-northeast-1
                  </option>
                </select>
              </div>

              {error && (
                <div
                  style={{
                    padding: "12px",
                    background: "rgba(239, 68, 68, 0.1)",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    borderRadius: "8px",
                    color: "#fca5a5",
                    fontSize: "13px",
                    marginBottom: "20px",
                  }}
                >
                  {error}
                </div>
              )}
            </form>
          )}

          {error && isOAuthProvider && (
            <div
              style={{
                padding: "12px",
                background: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.3)",
                borderRadius: "8px",
                color: "#fca5a5",
                fontSize: "13px",
                marginBottom: "20px",
              }}
            >
              {error}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button
            type="button"
            className="modal-btn modal-btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          {isOAuthProvider && (
            <button
              type="button"
              className="modal-btn modal-btn-primary"
              onClick={handleOAuthConnect}
              disabled={loading}
            >
              {loading ? "Connecting..." : "Connect & Authorize"}
            </button>
          )}
          {isS3Provider && (
            <button
              type="submit"
              form="s3-connection-form"
              className="modal-btn modal-btn-primary"
              disabled={
                loading ||
                !s3Form.access_key_id ||
                !s3Form.secret_access_key ||
                !s3Form.bucket_name
              }
            >
              {loading ? "Connecting..." : "Connect S3"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Modal popup with the folder tree and shared drive selector ---
// projectId: when set, backend filters shared drives via GoogleDriveForProjects secret (* = all, else drive name)
function FolderTreePopup({
  onClose,
  onFolderSelect,
  onDriveSelect,
  storageConnection,
  useSystemDrive,
  projectId,
}) {
  // View: 'tree' = folder tree, 'selected_list' = list of selected folders
  const [view, setView] = useState("tree");
  // Accumulated selected folders (order of selection)
  const [selectedFoldersInModal, setSelectedFoldersInModal] = useState([]);
  // State for shared drives
  const [sharedDrives, setSharedDrives] = useState([]);
  const [noMatchingDrive, setNoMatchingDrive] = useState(false);
  const [selectedDrive, setSelectedDrive] = useState("");
  // Root folder id used to fetch folders (either shared drive id or manually entered)
  const [rootFolderId, setRootFolderId] = useState("");
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch available shared drives from backend (optionally scoped by project via GoogleDriveForProjects)
  const fetchSharedDrives = async () => {
    try {
      const base =
        ENVIRONMENT === "local"
          ? `${API_BASE_URL}/api/google-drive/shared-drives`
          : `/api/google-drive/shared-drives`;
      const url = projectId
        ? `${base}?project_id=${encodeURIComponent(projectId)}`
        : base;
      const response = await axios.get(url);
      const drives =
        response.data?.drives || response.data?.shared_drives || [];
      const drivesArray = Array.isArray(drives) ? drives : [];
      const noMatch = Boolean(response.data?.no_matching_drive);
      setNoMatchingDrive(noMatch);
      setSharedDrives(drivesArray);
      // Cache only when not project-filtered to avoid mixing lists
      if (!projectId) {
        sessionStorage.setItem("sharedDrives", JSON.stringify(drivesArray));
      }
      // If filtered to one drive, auto-select it
      if (drivesArray.length === 1 && drivesArray[0]?.id) {
        setSelectedDrive(drivesArray[0].id);
      }
      if (noMatch || (projectId && drivesArray.length === 0)) {
        setSelectedDrive("");
      }
    } catch (err) {
      console.error("Error fetching shared drives", err);
      setSharedDrives([]);
      setNoMatchingDrive(false);
    }
  };

  // Fetch subfolders for the current rootFolderId
  const fetchFolders = async (folderId) => {
    setLoading(true);
    try {
      if (useSystemDrive) {
        // Use system Google Drive
        console.log(
          "🔧 RSAAnalysis - Fetching folders from system Google Drive, parent_id:",
          folderId,
        );
        const url =
          ENVIRONMENT === "local"
            ? `${API_BASE_URL}/api/google-drive/folders?parent_id=${folderId}`
            : `/api/google-drive/folders?parent_id=${folderId}`;
        const response = await axios.get(url);
        console.log("🔧 RSAAnalysis - Folders response:", response.data);
        const folders = response.data?.folders || [];
        setFolders(Array.isArray(folders) ? folders : []);
        console.log(
          `[OK] RSAAnalysis - Set ${folders.length} folders from system Google Drive`,
        );
      } else if (storageConnection) {
        // Use user's storage connection
        console.log(
          "🔧 RSAAnalysis - Fetching folders from user storage:",
          storageConnection.connection_name,
        );
        const url =
          ENVIRONMENT === "local"
            ? `${API_BASE_URL}/api/storage/connections/${storageConnection.id}/folders?folder_id=${folderId}`
            : `/api/storage/connections/${storageConnection.id}/folders?folder_id=${folderId}`;
        const response = await axios.get(url);
        const folders = response.data?.folders || [];
        setFolders(Array.isArray(folders) ? folders : []);
        console.log(
          `[OK] RSAAnalysis - Set ${folders.length} folders from ${storageConnection.connection_name}`,
        );
      }
    } catch (err) {
      console.error(
        "❌ RSAAnalysis - Error fetching folders for",
        folderId,
        err,
      );
      setFolders([]);
    }
    setLoading(false);
  };

  // On mount (and when projectId changes), load shared drives - skip cache when project-scoped
  useEffect(() => {
    if (useSystemDrive) {
      if (!projectId) {
        const cachedDrives = sessionStorage.getItem("sharedDrives");
        if (cachedDrives) {
          try {
            const parsedDrives = JSON.parse(cachedDrives);
            setSharedDrives(Array.isArray(parsedDrives) ? parsedDrives : []);
            setNoMatchingDrive(false);
            console.log(
              "[OK] FolderTreePopup - Shared drives loaded from cache:",
              parsedDrives.length,
            );
          } catch (e) {
            console.error("Error parsing cached shared drives:", e);
          }
        }
      } else {
        setSharedDrives([]);
        setNoMatchingDrive(false);
        setSelectedDrive("");
      }
      fetchSharedDrives();
    } else if (storageConnection) {
      // Use user's storage - fetch root folders
      fetchFolders("root");
    }
  }, [useSystemDrive, storageConnection, projectId]);

  // When a shared drive is selected, update the rootFolderId and fetch its folders
  useEffect(() => {
    if (useSystemDrive && selectedDrive) {
      setRootFolderId(selectedDrive);
      fetchFolders(selectedDrive);
      // Get the drive name and pass it to parent
      const drive = sharedDrives.find((d) => d.id === selectedDrive);
      if (drive && onDriveSelect) {
        onDriveSelect(drive.name);
      }
    }
  }, [selectedDrive, sharedDrives, onDriveSelect, useSystemDrive]);

  const handleSelect = (folder) => {
    setSelectedFolder(folder);
  };

  // Add current tree selection to list and switch to selected-list view
  const handleAddSelected = () => {
    if (selectedFolder) {
      setSelectedFoldersInModal((prev) => [...prev, selectedFolder]);
      setSelectedFolder(null);
      setView("selected_list");
    } else {
      alert("Please select a folder from the tree.");
    }
  };

  const removeFolderAt = (index) => {
    setSelectedFoldersInModal((prev) => prev.filter((_, i) => i !== index));
  };

  // Confirm selection: close modal and pass array to parent
  const handleConfirmSelection = () => {
    if (selectedFoldersInModal.length > 0) {
      onFolderSelect(selectedFoldersInModal);
      onClose();
    } else {
      alert("Please select at least one folder.");
    }
  };

  const showTreeAgain = () => {
    setView("tree");
  };

  const clearAllFoldersInModal = () => {
    setSelectedFoldersInModal([]);
    setView("tree");
  };

  const handleRefresh = async () => {
    setLoading(true);
    if (useSystemDrive) {
      // Refresh shared drives for system Google Drive
      await fetchSharedDrives();
      // If a drive is selected, refresh its folders
      if (rootFolderId) {
        await fetchFolders(rootFolderId);
      }
    } else if (storageConnection) {
      // Refresh folders for user's storage
      await fetchFolders(rootFolderId || "root");
    }
    setLoading(false);
  };

  return (
    <div className="TreeModalOverlay">
      <div className="TreeModalContainer">
        <div className="modal-header">
          <h2 className="modal-title">
            {view === "tree" ? "Select LAS Folder" : "Selected Folders"}
          </h2>
        </div>

        <div className="modal-content">
          {view === "selected_list" ? (
            /* --- Selected folders list view --- */
            <>
              <div className="rsa-selected-folders-list">
                {selectedFoldersInModal.map((folder, index) => {
                  const path = folder.fullPath || folder.name;
                  const displayPath =
                    path.length > 60
                      ? path.substring(0, 30) +
                        "..." +
                        path.substring(path.length - 25)
                      : path;
                  return (
                    <div
                      key={`${folder.id}-${index}`}
                      className="rsa-selected-folder-item"
                    >
                      <span className="rsa-selected-folder-path" title={path}>
                        {displayPath}
                      </span>
                      <button
                        type="button"
                        className="rsa-selected-folder-remove"
                        onClick={() => removeFolderAt(index)}
                        aria-label="Remove folder"
                        title="Remove"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="rsa-selected-folders-actions">
                <button
                  type="button"
                  className="modal-btn modal-btn-secondary rsa-btn-select-another"
                  onClick={showTreeAgain}
                >
                  <svg
                    className="rsa-btn-select-another-icon"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Select another folder
                </button>
                <button
                  type="button"
                  className="modal-btn modal-btn-secondary rsa-btn-clear-folders"
                  onClick={clearAllFoldersInModal}
                >
                  Clear
                </button>
              </div>
            </>
          ) : (
            /* --- Tree view (unchanged layout) --- */
            <>
              {/* Show storage provider info if using user storage */}
              {!useSystemDrive && storageConnection && (
                <div className="modal-section">
                  <div className="modal-section-header">
                    <label className="modal-label">Storage Provider:</label>
                    <button
                      type="button"
                      onClick={handleRefresh}
                      className="rsa-modal-refresh-btn"
                      title="Refresh Storage"
                    >
                      <RefreshIcon className="rsa-modal-refresh-icon" />
                    </button>
                  </div>
                  <div
                    style={{
                      padding: "8px 12px",
                      background: "rgba(59, 131, 246, 0.1)",
                      borderRadius: "6px",
                      marginBottom: "15px",
                    }}
                  >
                    <span
                      style={{
                        color: "#60a5fa",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      {storageConnection.connection_name} (
                      {storageConnection.provider_type})
                    </span>
                  </div>
                </div>
              )}

              {/* Shared Drive Selector - Only show for system Google Drive */}
              {useSystemDrive && (
                <div className="modal-section">
                  <div className="modal-section-header">
                    <label className="modal-label">
                      Select a Shared Drive:
                    </label>
                    <button
                      type="button"
                      onClick={handleRefresh}
                      className="rsa-modal-refresh-btn"
                      title="Refresh Google Drive"
                    >
                      <RefreshIcon className="rsa-modal-refresh-icon" />
                    </button>
                  </div>
                  <select
                    className="modal-select"
                    value={selectedDrive}
                    onChange={(e) => setSelectedDrive(e.target.value)}
                    disabled={noMatchingDrive && sharedDrives.length === 0}
                  >
                    {noMatchingDrive && sharedDrives.length === 0 ? (
                      <option value="" className="modal-option" disabled>
                        No storage drive(s) were associated with this project.
                      </option>
                    ) : (
                      <>
                        <option value="" className="modal-option">
                          -- Select Shared Drive --
                        </option>
                        {Array.isArray(sharedDrives) &&
                        sharedDrives.length > 0 ? (
                          sharedDrives.map((drive) => (
                            <option
                              key={drive.id}
                              value={drive.id}
                              className="modal-option"
                            >
                              {drive.name}
                            </option>
                          ))
                        ) : (
                          <option value="" className="modal-option" disabled>
                            No shared drives available
                          </option>
                        )}
                      </>
                    )}
                  </select>
                </div>
              )}

              {loading ? (
                <div className="modal-loading">Loading folders...</div>
              ) : Array.isArray(folders) && folders.length > 0 ? (
                <ul className="tree">
                  {folders.map((folder) => (
                    <FolderTreeNode
                      key={folder.id}
                      folder={folder}
                      onSelect={handleSelect}
                      selectedFolderId={selectedFolder?.id}
                      parentPath=""
                      storageConnection={storageConnection}
                      useSystemDrive={useSystemDrive}
                    />
                  ))}
                </ul>
              ) : useSystemDrive && selectedDrive ? (
                <div className="modal-loading modal-loading-message">
                  No folders found in this shared drive. Please select a
                  different drive.
                </div>
              ) : useSystemDrive && !selectedDrive ? (
                <div className="modal-loading modal-loading-message">
                  Please select a shared drive to view folders.
                </div>
              ) : !useSystemDrive && storageConnection ? (
                <div className="modal-loading modal-loading-message">
                  No folders found. Please select a folder from the tree above.
                </div>
              ) : (
                <div className="modal-loading modal-loading-message">
                  Loading folders...
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-actions">
          <button className="modal-btn modal-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          {view === "tree" ? (
            <button
              className="modal-btn modal-btn-primary"
              onClick={handleAddSelected}
            >
              Select
            </button>
          ) : (
            <button
              className="modal-btn modal-btn-primary"
              onClick={handleConfirmSelection}
              disabled={selectedFoldersInModal.length === 0}
            >
              Select
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** For each waypoint, find the index in path coordinates that is closest along the routed path. */
function getWaypointCoordinateIndices(pathData, waypoints) {
  if (!pathData?.geometry?.coordinates?.length || !waypoints?.length) return [];
  const coords = pathData.geometry.coordinates;
  const indices = [];
  for (const wp of waypoints) {
    const lat = wp.lat;
    const lng = wp.lng;
    let bestIdx = 0;
    let bestD2 = Infinity;
    for (let i = 0; i < coords.length; i++) {
      const c = coords[i];
      const clat = Array.isArray(c) ? c[1] : c.lat;
      const clng = Array.isArray(c) ? c[0] : c.lng;
      const d2 = (clat - lat) ** 2 + (clng - lng) ** 2;
      if (d2 < bestD2) {
        bestD2 = d2;
        bestIdx = i;
      }
    }
    indices.push(bestIdx);
  }
  return indices;
}

/** Slice pathData to the segment between waypoint startIdx and endIdx (inclusive) */
function slicePathDataByWaypointRange(pathData, waypoints, startIdx, endIdx) {
  if (!pathData?.geometry?.coordinates?.length || !waypoints?.length)
    return null;
  const coords = pathData.geometry.coordinates;
  const wpIndices = getWaypointCoordinateIndices(pathData, waypoints);
  const i0 = Math.min(
    wpIndices[startIdx] ?? 0,
    wpIndices[endIdx] ?? coords.length - 1,
  );
  const i1 = Math.max(
    wpIndices[startIdx] ?? 0,
    wpIndices[endIdx] ?? coords.length - 1,
  );
  const sliced = coords.slice(i0, i1 + 1);
  if (sliced.length < 2) return null;
  return {
    type: "Feature",
    geometry: { type: "LineString", coordinates: sliced },
    properties: {},
  };
}

// Segment indicator colors (match map waypoint markers when segment is active)
const SEGMENT_COLORS = [
  "#DD4130" /* red */,
  "#2563eb" /* blue */,
  "#059669" /* emerald */,
  "#7c3aed" /* violet */,
  "#ea580c" /* orange */,
  "#0d9488" /* teal */,
  "#dc2626" /* red-6 */,
  "#ca8a04" /* yellow/amber */,
  "#db2777" /* pink */,
  "#0891b2" /* cyan */,
];

function parseLinearMetersPerUnitFromWkt(wkt) {
  if (!wkt || typeof wkt !== "string") return null;
  const re = /UNIT\["[^"]+"\s*,\s*([0-9.+\-eE]+)\s*\]/g;
  let match;
  let last = null;
  while ((match = re.exec(wkt)) !== null) {
    const v = Number(match[1]);
    if (Number.isFinite(v) && v > 0) last = v;
  }
  return last;
}

function parseUnitsTokenFromProj4(proj4String) {
  if (!proj4String || typeof proj4String !== "string") return "";
  const m = proj4String.match(/\+units=([^\s]+)/i);
  return (m?.[1] || "").toLowerCase();
}

function getProjectedUnitsPerFoot({ wkt, proj4String }) {
  const metersPerUnitFromWkt = parseLinearMetersPerUnitFromWkt(wkt);
  if (Number.isFinite(metersPerUnitFromWkt) && metersPerUnitFromWkt > 0) {
    return 0.3048 / metersPerUnitFromWkt;
  }
  const token = parseUnitsTokenFromProj4(proj4String);
  if (token === "us-ft" || token === "ft") return 1;
  if (token === "m") return 0.3048;
  return 0.3048;
}

function normalizeVector2(x, y) {
  const len = Math.hypot(x, y);
  if (!Number.isFinite(len) || len <= 1e-9) return [0, 0];
  return [x / len, y / len];
}

function offsetPolylineXY(polylineXY, signedOffsetUnits) {
  if (!Array.isArray(polylineXY) || polylineXY.length < 2) return [];
  const pts = polylineXY.map(([x, y]) => [Number(x), Number(y)]);
  if (pts.some(([x, y]) => !Number.isFinite(x) || !Number.isFinite(y)))
    return [];
  const n = pts.length;
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const p = pts[i];
    let tx = 0;
    let ty = 0;
    if (i === 0) {
      tx = pts[1][0] - p[0];
      ty = pts[1][1] - p[1];
    } else if (i === n - 1) {
      tx = p[0] - pts[n - 2][0];
      ty = p[1] - pts[n - 2][1];
    } else {
      const [p0, p1, p2] = [pts[i - 1], p, pts[i + 1]];
      const [aX, aY] = normalizeVector2(p1[0] - p0[0], p1[1] - p0[1]);
      const [bX, bY] = normalizeVector2(p2[0] - p1[0], p2[1] - p1[1]);
      tx = aX + bX;
      ty = aY + bY;
      if (Math.hypot(tx, ty) <= 1e-6) {
        tx = bX || aX;
        ty = bY || aY;
      }
    }
    const [nx, ny] = normalizeVector2(-ty, tx); // left-hand normal
    out.push([p[0] + signedOffsetUnits * nx, p[1] + signedOffsetUnits * ny]);
  }
  return out;
}

function clampNumber(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

function projectLatLngToLocalMeters(points) {
  if (!Array.isArray(points) || points.length === 0) return [];
  const origin = points[0];
  const lat0Rad = (origin.lat * Math.PI) / 180;
  const cosLat = Math.max(0.2, Math.cos(lat0Rad));
  return points.map((p) => ({
    x: (p.lng - origin.lng) * 111320 * cosLat,
    y: (p.lat - origin.lat) * 110540,
  }));
}

function getDistanceMetersXY(a, b) {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function computeCurvatureScores(projectedPoints) {
  const n = Array.isArray(projectedPoints) ? projectedPoints.length : 0;
  const scores = new Array(n).fill(0);
  if (n < 3) return scores;

  for (let i = 1; i < n - 1; i += 1) {
    const a = projectedPoints[i - 1];
    const b = projectedPoints[i];
    const c = projectedPoints[i + 1];
    const v1x = b.x - a.x;
    const v1y = b.y - a.y;
    const v2x = c.x - b.x;
    const v2y = c.y - b.y;
    const len1 = Math.hypot(v1x, v1y);
    const len2 = Math.hypot(v2x, v2y);
    if (len1 <= 1e-3 || len2 <= 1e-3) continue;
    const dot = clampNumber((v1x * v2x + v1y * v2y) / (len1 * len2), -1, 1);
    const angle = Math.acos(dot); // 0..PI
    const normalized = clampNumber(angle / Math.PI, 0, 1);
    scores[i] = normalized;
  }

  const smoothed = new Array(n).fill(0);
  for (let i = 0; i < n; i += 1) {
    const prev = scores[Math.max(0, i - 1)] ?? 0;
    const curr = scores[i] ?? 0;
    const next = scores[Math.min(n - 1, i + 1)] ?? 0;
    smoothed[i] = clampNumber(prev * 0.25 + curr * 0.5 + next * 0.25, 0, 1);
  }
  return smoothed;
}

function sampleLaneWaypoints(latLngPoints) {
  const points = (Array.isArray(latLngPoints) ? latLngPoints : [])
    .map((p) => ({ lat: Number(p?.lat), lng: Number(p?.lng) }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (points.length < 2) return points;
  const projected = projectLatLngToLocalMeters(points);
  if (projected.length < 2) return points;

  const minSpacingMeters = 10 * 0.3048; // 10 ft
  const maxSpacingMeters = 200 * 0.3048; // 200 ft
  const curvature = computeCurvatureScores(projected);
  const cumulativeMeters = new Array(projected.length).fill(0);
  for (let i = 1; i < projected.length; i += 1) {
    cumulativeMeters[i] =
      cumulativeMeters[i - 1] +
      getDistanceMetersXY(projected[i - 1], projected[i]);
  }

  const chosenIndices = [0];
  let lastChosen = 0;
  let localCurvePeak = 0;

  for (let i = 1; i < projected.length - 1; i += 1) {
    localCurvePeak = Math.max(localCurvePeak, curvature[i] ?? 0);
    const distanceFromLast = cumulativeMeters[i] - cumulativeMeters[lastChosen];

    const curveFactor = clampNumber(localCurvePeak * 1.35, 0, 1);
    const desiredSpacingMeters =
      maxSpacingMeters - (maxSpacingMeters - minSpacingMeters) * curveFactor;

    if (distanceFromLast >= desiredSpacingMeters) {
      chosenIndices.push(i);
      lastChosen = i;
      localCurvePeak = 0;
    }
  }

  if (chosenIndices[chosenIndices.length - 1] !== projected.length - 1) {
    chosenIndices.push(projected.length - 1);
  }

  const deduped = [];
  chosenIndices.forEach((idx) => {
    if (deduped.length === 0 || deduped[deduped.length - 1] !== idx)
      deduped.push(idx);
  });

  let sampled = deduped.map((idx) => ({
    lat: points[idx].lat,
    lng: points[idx].lng,
  }));
  const maxLaneWaypoints = 48;
  if (sampled.length > maxLaneWaypoints) {
    const n = sampled.length;
    const capped = [];
    for (let k = 0; k < maxLaneWaypoints; k += 1) {
      const i = Math.round((k / (maxLaneWaypoints - 1)) * (n - 1));
      capped.push(sampled[i]);
    }
    sampled = capped.filter(
      (p, j, arr) =>
        j === 0 || p.lat !== arr[j - 1].lat || p.lng !== arr[j - 1].lng,
    );
  }
  return sampled;
}

const EMPTY_WAYPOINTS = [];
const normalizeLaneXmlName = (value) =>
  String(value ?? "").replace(/[\s_-]/g, "");

// Compute the RSAPATH XML filenames that saveLasSeparationPathToDrive will
// produce, without doing the heavy projection work. Used by the pre-save
// conflict check. Mirrors the filename-building rules inside
// saveLasSeparationPathToDrive (lane.label / segment counter / position / dir
// / STATE) — keep the two in sync if either changes.
function planRsaXmlFilenames(xmlLanes, allPaths, xmlState) {
  const stateUpper = String(xmlState || "")
    .trim()
    .toUpperCase();
  if (!stateUpper) return [];
  const counter = {};
  const pathSegmentMap = {};
  for (const p of allPaths || []) {
    const name = String(p.label ?? "").trim();
    counter[name] = (counter[name] || 0) + 1;
    pathSegmentMap[p.id] = counter[name];
  }
  const out = [];
  for (const lane of xmlLanes || []) {
    const routeNumber = String(lane.label ?? "").trim();
    if (!routeNumber) continue;
    const segment = pathSegmentMap[lane.pathId] ?? 1;
    const dir = lane.direction ?? "P";
    const pos = lane.lanePosition ?? "LL";
    out.push({
      laneId: lane.id,
      filename: `${stateUpper}_${routeNumber}_${segment}_${pos}_${dir}.xml`,
    });
  }
  return out;
}

// --- PathsList connected component (reads from RsaPathsContext) ---
function RsaPathsListPanelConnected({
  onCreateOffsetLane,
  onStartManualLane,
  onStopManualLane,
  onResumeManualLane,
  manualLaneCreationActive,
  activeManualLanePointCount,
  wktAvailable,
  onGoToPathData, // (pathData: GeoJSON.Feature) => void
  maps2000ByPath = {},
  onCreateOrthoMaps,
  creatingOrthoMapsByPath = {},
  orthoProgressByPath = {},
  onStartAutoTrace,
  onContinueAutoTrace,
  laneTraceProgress = {},
  laneTraceStop = null,
  bridge = null,
  onResumeAuto,
  onEndBridge,
  onEnterBridgeManually,
  onDeletePath: onDeletePathProp,
  onRenamePath: onRenamePathProp,
  onRefreshOrthoMaps,
  loadingOrthoTiles = false,
  hasFoldersSelected = false,
  deletingPathIds,
}) {
  const ctx = React.useContext(RsaPathsContext);
  if (!ctx) return null;
  const {
    paths,
    lanes,
    activePathId,
    activeLaneId,
    laneEditMode,
    speedEditMode,
    selectedLaneWaypointIndices,
    actions,
    SEGMENT_COLORS: colors,
  } = ctx;

  return (
    <PathsListPanel
      paths={paths}
      lanes={lanes}
      activePathId={activePathId}
      activeLaneId={activeLaneId}
      laneEditMode={laneEditMode}
      speedEditMode={speedEditMode}
      selectedLaneWaypointIndices={selectedLaneWaypointIndices}
      segmentColors={colors ?? SEGMENT_COLORS}
      wktAvailable={wktAvailable}
      manualLaneCreationActive={manualLaneCreationActive}
      manualLanePointCount={activeManualLanePointCount}
      onCreateOffsetLane={onCreateOffsetLane}
      onStartManualLane={onStartManualLane}
      onStopManualLane={onStopManualLane}
      onResumeManualLane={onResumeManualLane}
      onSelectPath={actions.setActivePath}
      onDeletePath={onDeletePathProp || actions.deletePath}
      onTogglePathVisible={(id) => {
        const p = paths.find((x) => x.id === id);
        if (p) actions.updatePath(id, { visible: !p.visible });
      }}
      onGoToPath={(id) => {
        const p = paths.find((x) => x.id === id);
        if (p?.pathData?.geometry?.coordinates?.length)
          onGoToPathData?.(p.pathData);
      }}
      onRenamePath={
        onRenamePathProp ||
        ((id, label) => {
          const unique = makeUniqueLabel(label, paths, id);
          actions.updatePath(id, { label: unique });
        })
      }
      onSelectLane={actions.setActiveLane}
      onDeleteLane={actions.deleteLane}
      onToggleLaneVisible={(id) => {
        const l = lanes.find((x) => x.id === id);
        if (l) actions.updateLane(id, { visible: !l.visible });
      }}
      onRenameLane={(id, label) =>
        actions.updateLane(id, { label: normalizeLaneXmlName(label) })
      }
      onChangeLaneColor={(id, colorIndex) =>
        actions.updateLane(id, { colorIndex })
      }
      onChangeLanePosition={(id, lanePosition) =>
        actions.updateLane(id, { lanePosition })
      }
      onChangeLaneDirection={(id, direction) =>
        actions.updateLane(id, { direction })
      }
      onAddPath={actions.addPath}
      onAddLane={actions.setActivePath}
      onEditLanePoints={(laneId) => {
        actions.setActiveLane(laneId);
        actions.enterLaneEditMode();
      }}
      onExitEditLanePoints={actions.exitLaneEditMode}
      onClearLaneSelection={() => actions.setSelectedLaneWaypoints([])}
      onEnterSpeedMode={(laneId) => {
        actions.setActiveLane(laneId);
        actions.enterSpeedEditMode();
        // Auto-create the first breakpoint at mile 0 if none exist
        const lane = lanes.find((l) => l.id === laneId);
        if (!lane?.speedSegments?.length) {
          actions.addSpeedBreakpoint(laneId, 0, 0);
        }
      }}
      onExitSpeedMode={actions.exitSpeedEditMode}
      onUpdateSpeedAtBreakpoint={(laneId, idx, mph) =>
        actions.updateSpeedAtBreakpoint(laneId, idx, mph)
      }
      onUpdateSpeedBreakpointMile={(laneId, idx, mile) =>
        actions.updateSpeedBreakpointMile(laneId, idx, mile)
      }
      onAddSpeedBreakpoint={(laneId, mileMark, mph) =>
        actions.addSpeedBreakpoint(laneId, mileMark, mph)
      }
      onRemoveSpeedBreakpoint={(laneId, idx) =>
        actions.removeSpeedBreakpoint(laneId, idx)
      }
      onMarkReviewed={(laneId, reviewed) =>
        actions.updateLane(laneId, { reviewed })
      }
      maps2000ByPath={maps2000ByPath}
      onCreateOrthoMaps={onCreateOrthoMaps}
      creatingOrthoMapsByPath={creatingOrthoMapsByPath}
      orthoProgressByPath={orthoProgressByPath}
      onStartAutoTrace={onStartAutoTrace}
      laneTraceProgress={laneTraceProgress}
      bridge={bridge}
      onResumeAuto={onResumeAuto}
      onEndBridge={onEndBridge}
      onEnterBridgeManually={onEnterBridgeManually}
      onRefreshOrthoMaps={onRefreshOrthoMaps}
      loadingOrthoTiles={loadingOrthoTiles}
      hasFoldersSelected={hasFoldersSelected}
      deletingPathIds={deletingPathIds}
    />
  );
}

function RsaMapsPanelContentConnected({ onGoToPathData }) {
  const ctx = React.useContext(RsaPathsContext);
  if (!ctx) return null;

  const {
    paths,
    lanes,
    activePathId,
    activeLaneId,
    activeEntityType,
    actions,
    SEGMENT_COLORS: colors,
  } = ctx;

  const rows = [
    ...paths.map((path, idx) => ({
      ...path,
      entityType: "path",
      label: path.label || `Route${idx + 1}`,
    })),
    ...lanes.map((lane, idx) => ({
      ...lane,
      entityType: "lane",
      label: lane.label || `Lane ${idx + 1}`,
    })),
  ];

  const activeRowIndex = rows.findIndex((row) =>
    activeEntityType === "lane"
      ? row.entityType === "lane" && row.id === activeLaneId
      : row.entityType === "path" && row.id === activePathId,
  );

  return (
    <MapsPanelContent
      segments={rows}
      activeSegmentIndex={activeRowIndex >= 0 ? activeRowIndex : 0}
      segmentColors={colors ?? SEGMENT_COLORS}
      segmentVisibility={rows.map((row) => row.visible !== false)}
      onToggleSegmentVisibility={(idx) => {
        const row = rows[idx];
        if (!row) return;
        if (row.entityType === "lane") {
          actions.updateLane(row.id, { visible: !row.visible });
          return;
        }
        actions.updatePath(row.id, { visible: !row.visible });
      }}
      onSelectSegment={(idx) => {
        const row = rows[idx];
        if (!row) return;
        if (row.entityType === "lane") {
          actions.setActiveLane(row.id);
          return;
        }
        actions.setActivePath(row.id);
      }}
      onGoToSegment={(idx) => {
        const row = rows[idx];
        if (row?.pathData?.geometry?.coordinates?.length) {
          onGoToPathData?.(row.pathData);
        }
      }}
    />
  );
}

// --- Paths Panel Component ---
function PathsPanel({
  mode = "path-creator",
  pendingIsdDraft = null,
  onSetPendingIsdDraft,
  oppositePointPickActive = false,
  onStartOppositePointPick,
  onCancelOppositePointPick,
  oppositeBlockDraft = null,
  onUpdateOppositeBlockDraft,
  selectedRsaTargetId = null,
  onSetSelectedRsaTargetId,
  lasBoundingBoxes = [],
  orthoTiles = [],
  lasVisible = true,
  onToggleLasVisibility,
  onClearLas,
  orthoVisible = true,
  onToggleOrthoVisibility,
  onClearOrtho,
  onGoToLasBounds,
  onSelectedLasExtentIndexChange,
  loadingLasBounds = false,
  loadingOrthoTiles = false,
  orthoImagesLoading = false,
  orthoLoadedCount = 0,
  orthoTotal = 0,
  maps2000ByPath = {},
  loadingMaps2000Check = false,
  onRescanMaps2000,
  onLoadLasToMap,
  hasFoldersSelected = false,
  lasFoldersLoading = false,
  onLoadOrthoMaps,
  onCreateOffsetLane,
  onStartManualLane,
  onStopManualLane,
  onResumeManualLane,
  manualLaneCreationActive = false,
  activeManualLanePointCount = 0,
  overlayOpacity = 0.9,
  onOverlayOpacityChange,
  overlayBrightness = 1,
  onOverlayBrightnessChange,
  overlayContrast = 1,
  onOverlayContrastChange,
  intersectionDrawingMode = null,
  onIntersectionDrawingModeChange,
  intersectionOverlays = [],
  onIntersectionOverlayUpdate,
  onClearIntersectionOverlays,
  onIntersectionOverlayRemove,
  curvePoints = [],
  measurePoints = [],
  onResetMeasureDistance,
  curvedLineEditPointCount = 5,
  onCurvedLineEditPointCountChange,
  onSavePath,
  onGoToSegment,
  isSavingPath = false,
  wktAvailable = false,
  onCreateOrthoMaps,
  creatingOrthoMapsByPath = {},
  orthoProgressByPath = {},
  onStartAutoTrace,
  onContinueAutoTrace,
  laneTraceProgress = {},
  laneTraceStop = null,
  bridge = null,
  onResumeAuto,
  onEndBridge,
  onEnterBridgeManually,
  onDeletePath,
  onRenamePath,
  deletingPathIds,
}) {
  const ctx = React.useContext(RsaPathsContext);
  const [showClearPathModal, setShowClearPathModal] = useState(false);
  const [saveReviewOpen, setSaveReviewOpen] = useState(false);
  const [selectedLasExtentIndex, setSelectedLasExtentIndex] = useState(0);
  const [selectedOrthoExtentIndex, setSelectedOrthoExtentIndex] = useState(0);

  const activePath =
    ctx?.paths?.find((path) => path.id === ctx.activePathId) ??
    ctx?.paths?.[0] ??
    null;
  const activeLane =
    ctx?.lanes?.find((lane) => lane.id === ctx.activeLaneId) ?? null;
  const activeEntityType =
    ctx?.activeEntityType === "lane" && activeLane ? "lane" : "path";
  const activeEntity = activeEntityType === "lane" ? activeLane : activePath;
  const activeEntityLabel = activeEntity?.label || activeEntityType;
  const canSave = Boolean(
    ctx?.paths?.some(
      (path) =>
        Array.isArray(path?.pathData?.geometry?.coordinates) &&
        path.pathData.geometry.coordinates.length >= 2,
    ),
  );
  const canClear = Boolean(activeEntity?.waypoints?.length);
  const activeIntersection = (ctx?.intersections ?? []).find(
    (intersection) => intersection.id === ctx?.activeIntersectionId,
  );
  const activeIntersectionLabel =
    activeIntersection?.intersectionName ||
    (activeIntersection?.maneuverIndex
      ? `Intersection ${activeIntersection.maneuverIndex}`
      : "ISD point");
  const saveReviewItems = useMemo(() => {
    const paths = ctx?.paths ?? [];
    const lanes = ctx?.lanes ?? [];
    const intersections = ctx?.intersections ?? [];
    let lanesToSave = lanes.filter(
      (lane) =>
        lane.waypoints?.length >= 2 ||
        lane.pathData?.geometry?.coordinates?.length >= 2,
    );

    if (lanesToSave.length === 0) {
      lanesToSave = paths
        .filter(
          (path) =>
            path.waypoints?.length >= 2 ||
            path.pathData?.geometry?.coordinates?.length >= 2,
        )
        .map((path) => ({
          ...path,
          pathId: path.id,
          lanePosition: "LL",
          direction: "P",
          speedSegments: [],
          __promotedPath: true,
        }));
    }

    return lanesToSave.map((lane) => {
      const parentPath = paths.find((path) => path.id === lane.pathId);
      const coords = lane.pathData?.geometry?.coordinates;
      const totalMiles =
        (coords?.length >= 2
          ? getPathLengthMiles(coords)
          : getPathLengthMiles(lane.waypoints)) || 0;
      const speedSegments = Array.isArray(lane.speedSegments)
        ? [...lane.speedSegments]
            .filter((segment) => Number.isFinite(Number(segment?.mileMark)))
            .sort((a, b) => Number(a.mileMark) - Number(b.mileMark))
        : [];
      const laneIsds = intersections
        .filter((isd) => isd.rsaPathId === lane.id)
        .sort(
          (a, b) =>
            (Number(a.maneuverIndex) || 0) - (Number(b.maneuverIndex) || 0),
        );

      return {
        id: lane.id,
        label: lane.label || "Lane",
        pathLabel:
          parentPath?.label || (lane.__promotedPath ? lane.label : "Route"),
        laneTypeLabel: LANE_TYPE_LABELS[lane.laneType] || "Lane",
        lanePosition: lane.lanePosition || "LL",
        direction: lane.direction || "P",
        totalMiles,
        speedSegments,
        isdPoints: laneIsds,
      };
    });
  }, [ctx?.paths, ctx?.lanes, ctx?.intersections]);
  const modeTitle =
    mode === "path-creator"
      ? "Path Creator"
      : mode === "intersection"
        ? "Intersections"
        : "Map Layers";

  const handleClearPathClick = () => {
    if (!activeEntity?.id) return;
    setShowClearPathModal(true);
  };

  const handleClearPathConfirm = () => {
    if (activeEntityType === "lane" && activeLane?.id) {
      ctx?.actions?.updateLane(activeLane.id, {
        pathData: null,
        waypoints: [],
      });
    } else if (activePath?.id) {
      ctx?.actions?.updatePath(activePath.id, {
        pathData: null,
        waypoints: [],
      });
    }
    setShowClearPathModal(false);
  };

  const handleSavePathClick = () => {
    if (!canSave || isSavingPath) return;
    setSaveReviewOpen(true);
  };

  const handleApproveSavePath = async () => {
    if (!canSave || isSavingPath) return;
    const result = await onSavePath?.();
    // Only close the save-review panel on success. If the user cancelled the
    // XML overwrite modal (or the save failed), leave the review open so they
    // can rename lanes / retry without redoing all their inputs.
    if (result?.status === "completed") {
      setSaveReviewOpen(false);
    }
  };

  const handleKeepEditingSavePath = () => {
    if (isSavingPath) return;
    setSaveReviewOpen(false);
  };

  useEffect(() => {
    setSelectedLasExtentIndex(0);
  }, [lasBoundingBoxes]);

  useEffect(() => {
    const maxIndex = Math.max(0, lasBoundingBoxes.length - 1);
    const clampedIndex = Math.max(
      0,
      Math.min(selectedLasExtentIndex, maxIndex),
    );
    if (clampedIndex !== selectedLasExtentIndex) {
      setSelectedLasExtentIndex(clampedIndex);
      return;
    }
    onSelectedLasExtentIndexChange?.(clampedIndex);
  }, [
    selectedLasExtentIndex,
    lasBoundingBoxes.length,
    onSelectedLasExtentIndexChange,
  ]);

  return (
    <div className="tabs w35 paths-panel">
      <div className="summary-header">
        <h3>{modeTitle}</h3>
      </div>
      <div
        className={`summary-content paths-panel-content paths-panel-content-${mode}`}
      >
        <div className="paths-panel-shared-map-controls">
          <div className="paths-panel-overlay-opacity paths-panel-overlay-opacity--compact">
            <label
              className="paths-panel-overlay-opacity-label"
              htmlFor="paths-panel-overlay-opacity-slider"
            >
              <span>Ortho opacity</span>
              <button
                type="button"
                className="paths-panel-shared-map-controls-reset-btn"
                onClick={(e) => {
                  e.preventDefault();
                  onOverlayOpacityChange?.(0.9);
                  onOverlayBrightnessChange?.(1);
                  onOverlayContrastChange?.(1);
                }}
                title="Reset opacity, brightness, and contrast to defaults"
                aria-label="Reset map controls to defaults"
              >
                <i className="fas fa-undo" aria-hidden />
              </button>
            </label>
            <input
              id="paths-panel-overlay-opacity-slider"
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={overlayOpacity}
              onChange={(e) => onOverlayOpacityChange?.(Number(e.target.value))}
              className="paths-panel-overlay-opacity-slider"
              title="Ortho map PNG opacity on the map"
              style={{
                "--paths-panel-overlay-fill-pct": `${Math.round(Math.min(1, Math.max(0, overlayOpacity)) * 100)}%`,
              }}
            />
            <span className="paths-panel-overlay-opacity-value">
              {Math.round(overlayOpacity * 100)}%
            </span>
          </div>

          <details className="paths-panel-shared-map-controls-advanced">
            <summary className="paths-panel-shared-map-controls-advanced-summary">
              <i
                className="fas fa-chevron-right paths-panel-shared-map-controls-advanced-chevron"
                aria-hidden
              />
              <span>Advanced options</span>
            </summary>

            <div className="paths-panel-overlay-opacity paths-panel-overlay-opacity--compact">
              <label
                className="paths-panel-overlay-opacity-label"
                htmlFor="paths-panel-overlay-brightness-slider"
              >
                Ortho brightness
              </label>
              <input
                id="paths-panel-overlay-brightness-slider"
                type="range"
                min={0.5}
                max={2}
                step={0.05}
                value={overlayBrightness}
                onChange={(e) =>
                  onOverlayBrightnessChange?.(Number(e.target.value))
                }
                onDoubleClick={() => onOverlayBrightnessChange?.(1)}
                className="paths-panel-overlay-opacity-slider"
                title="Ortho map brightness (double-click to reset)"
                style={{
                  "--paths-panel-overlay-fill-pct": `${Math.round(((Math.min(2, Math.max(0.5, overlayBrightness)) - 0.5) / 1.5) * 100)}%`,
                }}
              />
              <span className="paths-panel-overlay-opacity-value">
                {Math.round(overlayBrightness * 100)}%
              </span>
            </div>

            <div className="paths-panel-overlay-opacity paths-panel-overlay-opacity--compact">
              <label
                className="paths-panel-overlay-opacity-label"
                htmlFor="paths-panel-overlay-contrast-slider"
              >
                Ortho contrast
              </label>
              <input
                id="paths-panel-overlay-contrast-slider"
                type="range"
                min={0.5}
                max={2}
                step={0.05}
                value={overlayContrast}
                onChange={(e) =>
                  onOverlayContrastChange?.(Number(e.target.value))
                }
                onDoubleClick={() => onOverlayContrastChange?.(1)}
                className="paths-panel-overlay-opacity-slider"
                title="Ortho map contrast (double-click to reset)"
                style={{
                  "--paths-panel-overlay-fill-pct": `${Math.round(((Math.min(2, Math.max(0.5, overlayContrast)) - 0.5) / 1.5) * 100)}%`,
                }}
              />
              <span className="paths-panel-overlay-opacity-value">
                {Math.round(overlayContrast * 100)}%
              </span>
            </div>
          </details>
        </div>

        {mode === "path-creator" && (
          <RsaPathsListPanelConnected
            onCreateOffsetLane={onCreateOffsetLane}
            onStartManualLane={onStartManualLane}
            onStopManualLane={onStopManualLane}
            onResumeManualLane={onResumeManualLane}
            manualLaneCreationActive={manualLaneCreationActive}
            activeManualLanePointCount={activeManualLanePointCount}
            wktAvailable={wktAvailable}
            onGoToPathData={(pathData) => onGoToSegment?.(pathData)}
            maps2000ByPath={maps2000ByPath}
            onCreateOrthoMaps={onCreateOrthoMaps}
            creatingOrthoMapsByPath={creatingOrthoMapsByPath}
            orthoProgressByPath={orthoProgressByPath}
            onStartAutoTrace={onStartAutoTrace}
            laneTraceProgress={laneTraceProgress}
            bridge={bridge}
            onResumeAuto={onResumeAuto}
            onEndBridge={onEndBridge}
            onEnterBridgeManually={onEnterBridgeManually}
            onDeletePath={onDeletePath}
            onRenamePath={onRenamePath}
            onRefreshOrthoMaps={onLoadOrthoMaps}
            loadingOrthoTiles={loadingOrthoTiles}
            hasFoldersSelected={hasFoldersSelected}
            deletingPathIds={deletingPathIds}
          />
        )}

        {mode === "intersection" && (
          <IntersectionPanelContent
            drawingMode={intersectionDrawingMode}
            onDrawingModeChange={onIntersectionDrawingModeChange}
            drawnOverlays={intersectionOverlays}
            onOverlayUpdate={onIntersectionOverlayUpdate}
            onClearOverlays={onClearIntersectionOverlays}
            onOverlayRemove={onIntersectionOverlayRemove}
            intersections={ctx?.intersections ?? []}
            activeIntersectionId={ctx?.activeIntersectionId ?? null}
            onUpdateIntersection={ctx?.actions?.updateIntersection}
            onDeleteIntersection={ctx?.actions?.deleteIntersection}
            onSetActiveIntersection={ctx?.actions?.setActiveIntersection}
            paths={ctx?.paths ?? []}
            lanes={ctx?.lanes ?? []}
            onTogglePathVisible={(id) => {
              const p = (ctx?.paths ?? []).find((x) => x.id === id);
              if (p) ctx?.actions?.updatePath?.(id, { visible: !p.visible });
            }}
            onToggleLaneVisible={(id) => {
              const l = (ctx?.lanes ?? []).find((x) => x.id === id);
              if (l) ctx?.actions?.updateLane?.(id, { visible: !l.visible });
            }}
            pendingIsdDraft={pendingIsdDraft}
            onSetPendingIsdDraft={onSetPendingIsdDraft}
            oppositePointPickActive={oppositePointPickActive}
            onStartOppositePointPick={onStartOppositePointPick}
            onCancelOppositePointPick={onCancelOppositePointPick}
            oppositeBlockDraft={oppositeBlockDraft}
            onUpdateOppositeBlockDraft={onUpdateOppositeBlockDraft}
            onRefreshPathsAndLanes={ctx?.actions?.reload}
            selectedTargetId={selectedRsaTargetId}
            onSetSelectedTargetId={onSetSelectedRsaTargetId}
          />
        )}

        {mode === "maps" && (
          <RsaMapsPanelContentConnected onGoToPathData={onGoToSegment} />
        )}

        {saveReviewOpen && (
          <div
            className="paths-save-review-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Save path review"
          >
            <div className="paths-save-review-card">
              <div className="paths-save-review-header">
                <div>
                  <p className="paths-save-review-eyebrow">Save Review</p>
                  <h4>Path Summary</h4>
                </div>
                <span className="paths-save-review-count">
                  {saveReviewItems.length} lane
                  {saveReviewItems.length === 1 ? "" : "s"}
                </span>
              </div>

              {saveReviewItems.length === 0 ? (
                <p className="paths-save-review-empty">
                  No lanes with drawable geometry are ready to save.
                </p>
              ) : (
                <div className="paths-save-review-list">
                  {saveReviewItems.map((lane) => (
                    <section className="paths-save-review-lane" key={lane.id}>
                      <div className="paths-save-review-lane-header">
                        <div>
                          <h5>{lane.label}</h5>
                          <p>
                            {lane.pathLabel} · {lane.laneTypeLabel} ·{" "}
                            {lane.lanePosition} · {lane.direction}
                          </p>
                        </div>
                        <span>
                          {lane.totalMiles > 0
                            ? `${lane.totalMiles.toFixed(2)} mi`
                            : "No length"}
                        </span>
                      </div>

                      <div className="paths-save-review-section">
                        <div className="paths-save-review-section-title">
                          Speed Data
                        </div>
                        {lane.speedSegments.length > 0 ? (
                          <div className="paths-save-review-speed-list">
                            {lane.speedSegments.map((segment, idx) => {
                              const startMile = Number(segment.mileMark);
                              const endMile =
                                idx < lane.speedSegments.length - 1
                                  ? Number(lane.speedSegments[idx + 1].mileMark)
                                  : lane.totalMiles;
                              const speedMph = Number.isFinite(
                                Number(segment.speedMph),
                              )
                                ? Number(segment.speedMph)
                                : 0;

                              return (
                                <div
                                  className="paths-save-review-speed-row"
                                  key={`${lane.id}-speed-${idx}`}
                                >
                                  <span>
                                    {startMile.toFixed(2)}-{endMile.toFixed(2)}{" "}
                                    mi
                                  </span>
                                  <strong>{speedMph} mph</strong>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="paths-save-review-muted">
                            No speed segments. Save flow will use the default
                            speed fallback.
                          </p>
                        )}
                      </div>

                      <div className="paths-save-review-section">
                        <div className="paths-save-review-section-title">
                          ISD Points
                        </div>
                        {lane.isdPoints.length > 0 ? (
                          <div className="paths-save-review-isd-list">
                            {lane.isdPoints.map((isd) => (
                              <div
                                className="paths-save-review-isd-row"
                                key={isd.id}
                              >
                                <span>
                                  {isd.intersectionName ||
                                    `Intersection ${isd.maneuverIndex}`}
                                </span>
                                <strong>
                                  {ISD_CASE_LABELS[isd.isdCase] ||
                                    isd.isdCase ||
                                    "Case"}
                                </strong>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="paths-save-review-muted">
                            No ISD points attached to this lane.
                          </p>
                        )}
                      </div>
                    </section>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div
        className={`paths-panel-las-bounds-bottom ${lasBoundingBoxes.length === 0 && !loadingLasBounds ? "paths-panel-las-bounds-bottom--action" : ""} ${loadingLasBounds ? "paths-panel-las-bounds-bottom--loading" : ""}`}
      >
        <span className="paths-panel-las-bounds-bottom__label">Optional</span>
        {lasBoundingBoxes.length > 0 ? (
          <>
            <select
              className="paths-panel-las-bounds-bottom__select"
              value={Math.min(
                selectedLasExtentIndex,
                lasBoundingBoxes.length - 1,
              )}
              onChange={(e) =>
                setSelectedLasExtentIndex(Number(e.target.value))
              }
              aria-label="Select LAS extent"
            >
              {lasBoundingBoxes.map((b, i) => (
                <option key={`${b?.name || "las"}-${i}`} value={i}>
                  {b?.name || `LAS ${i + 1}`}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="paths-panel-las-bounds-bottom__icon-btn"
              onClick={() => {
                const idx = Math.min(
                  selectedLasExtentIndex,
                  lasBoundingBoxes.length - 1,
                );
                const b = lasBoundingBoxes[idx];
                if (b) onGoToLasBounds?.(b);
              }}
              title="Fit map to selected extent"
            >
              <i className="fas fa-map-marker-alt" aria-hidden />
            </button>
            <button
              type="button"
              className="paths-panel-las-bounds-bottom__icon-btn"
              onClick={() => onToggleLasVisibility?.()}
              title={
                lasVisible
                  ? "Hide LAS bounding boxes"
                  : "Show LAS bounding boxes"
              }
            >
              <i
                className={lasVisible ? "fas fa-eye" : "fas fa-eye-slash"}
                aria-hidden
              />
            </button>
            <button
              type="button"
              className="paths-panel-las-bounds-bottom__icon-btn paths-panel-las-bounds-bottom__icon-btn--delete"
              onClick={() => onClearLas?.()}
              title="Remove LAS bounding boxes from map"
            >
              <i className="fas fa-trash-alt" aria-hidden />
            </button>
          </>
        ) : loadingLasBounds ? (
          <div
            className="paths-panel-las-bounds-bottom__progress-wrap"
            aria-busy="true"
          >
            <div className="paths-panel-las-bounds-bottom__progress-track">
              <div className="paths-panel-las-bounds-bottom__progress-fill" />
            </div>
            <span className="paths-panel-las-bounds-bottom__progress-label">
              Loading LAS bounds
            </span>
          </div>
        ) : lasFoldersLoading ? (
          <button
            type="button"
            className="paths-panel-las-bounds-bottom__action-btn paths-panel-las-bounds-bottom__action-btn--loading"
            disabled
            aria-busy="true"
            title="Detecting LAS folder from project setup…"
          >
            <i className="fa-solid fa-spinner fa-spin" aria-hidden />
            <span>Loading…</span>
          </button>
        ) : (
          <button
            type="button"
            className="paths-panel-las-bounds-bottom__action-btn"
            onClick={() => onLoadLasToMap?.()}
            disabled={!hasFoldersSelected}
            title={
              hasFoldersSelected
                ? "Load LAS/LAZ file bounding boxes from selected folder(s) and show them on the map"
                : "Select a LAS folder first"
            }
          >
            <i className="fas fa-cube" aria-hidden />
            <span>Load LAS Bounds</span>
          </button>
        )}
      </div>

      {(mode === "path-creator" || mode === "intersection") && (
        <div className="paths-panel-footer">
          <button
            type="button"
            className={`paths-panel-save-btn${saveReviewOpen ? " paths-panel-save-btn--approve" : ""}`}
            disabled={!canSave || isSavingPath}
            onClick={
              saveReviewOpen ? handleApproveSavePath : handleSavePathClick
            }
            title={
              saveReviewOpen
                ? "Approve this summary and continue saving"
                : canSave
                  ? isSavingPath
                    ? "Saving"
                    : "Review path data before saving"
                  : "Add at least 2 waypoints to save"
            }
          >
            <i
              className={saveReviewOpen ? "fas fa-check" : "fas fa-save"}
              aria-hidden
            />
            {saveReviewOpen
              ? isSavingPath
                ? "Saving"
                : "Approve"
              : isSavingPath
                ? "Saving"
                : "Save path"}
          </button>
          <button
            type="button"
            className="paths-panel-clear-footer-btn"
            disabled={
              saveReviewOpen
                ? isSavingPath
                : mode === "intersection"
                  ? !activeIntersection
                  : !canClear
            }
            onClick={() => {
              if (saveReviewOpen) {
                handleKeepEditingSavePath();
                return;
              }
              if (mode === "intersection") {
                if (!activeIntersection?.id) return;
                ctx?.actions?.deleteIntersection?.(activeIntersection.id);
                return;
              }
              handleClearPathClick();
            }}
            title={
              saveReviewOpen
                ? "Return to editing without saving"
                : mode === "intersection"
                  ? activeIntersection
                    ? `Clear ${activeIntersectionLabel}`
                    : "Select an ISD point to clear"
                  : canClear
                    ? `Clear active ${activeEntityType}`
                    : "No path to clear"
            }
          >
            <i className="fas fa-times" aria-hidden />
            {saveReviewOpen
              ? "Keep Editing"
              : `Clear ${mode === "intersection" ? "ISD" : activeEntityType}`}
          </button>
        </div>
      )}

      {showClearPathModal && (
        <div
          className="TreeModalOverlay paths-clear-path-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="paths-clear-path-modal-title"
        >
          <div className="TreeModalContainer paths-clear-path-modal">
            <div className="modal-header">
              <h2 className="modal-title" id="paths-clear-path-modal-title">
                <i
                  className="fa-solid fa-triangle-exclamation paths-clear-path-modal-icon"
                  aria-hidden
                />
                Clear {activeEntityType}
              </h2>
            </div>
            <div className="modal-content">
              <p className="clear-popup-message paths-clear-path-modal-message">
                {`Are you sure you want to clear ${activeEntityLabel}? Its waypoints will be removed.`}
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="modal-btn modal-btn-secondary"
                onClick={() => setShowClearPathModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="modal-btn modal-btn-primary"
                onClick={handleClearPathConfirm}
              >
                Clear {activeEntityType}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// --- Dashboard Projects Panel Component ---
function DashboardProjectsPanel({
  runs,
  selectedRun,
  onSelectRun,
  onRequestDeleteRun,
  onCancelDeleteRun,
  onConfirmDeleteRun,
  onDeleteAllRuns,
  confirmingRunId,
  deletingRunId,
  deletingAllRuns,
  runProgressMap = {},
}) {
  const [hasScroll, setHasScroll] = useState(false);
  const [confirmingDeleteAll, setConfirmingDeleteAll] = useState(false);
  const projectsListRef = useRef(null);

  // Drop the delete-all confirmation once the list empties out.
  useEffect(() => {
    if (!runs.length) setConfirmingDeleteAll(false);
  }, [runs.length]);

  useEffect(() => {
    const checkScroll = () => {
      if (projectsListRef.current) {
        const hasOverflow =
          projectsListRef.current.scrollHeight >
          projectsListRef.current.clientHeight;
        setHasScroll(hasOverflow);
      }
    };

    checkScroll();
    // Check on resize and when projects change
    window.addEventListener("resize", checkScroll);
    const timeoutId = setTimeout(checkScroll, 100); // Small delay to ensure DOM is updated

    return () => {
      window.removeEventListener("resize", checkScroll);
      clearTimeout(timeoutId);
    };
  }, [runs]);

  const getStatusClass = (status) => {
    switch (status) {
      case "completed":
        return "status-completed";
      case "processing":
      case "running":
        return "status-running";
      case "failed":
        return "status-failed";
      case "pending":
        return "status-pending";
      default:
        return "status-pending";
    }
  };

  const formatDate = (date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="tabs w35 dashboard-projects-panel">
      <div className="summary-header">
        <h3>Analysis Runs</h3>
        <div className="summary-header-actions">
          <span className="project-count">{runs.length} runs</span>
          {onDeleteAllRuns && runs.length > 0 && (
            <button
              type="button"
              className="dashboard-delete-all-btn dashboard-delete-all-btn-trigger"
              title="Remove all runs"
              aria-label="Remove all runs"
              disabled={deletingAllRuns || confirmingDeleteAll}
              onClick={() => setConfirmingDeleteAll(true)}
            >
              <i
                className={`fa-solid ${deletingAllRuns ? "fa-spinner fa-spin" : "fa-trash-can"}`}
                aria-hidden
              />
              <span>Delete all</span>
            </button>
          )}
        </div>
      </div>
      <div className="summary-content dashboard-projects-content">
        <div
          className={`projects-list ${hasScroll ? "has-scroll" : ""}`}
          ref={projectsListRef}
        >
          {runs.map((run) => {
            const isSelected = selectedRun && selectedRun.run_id === run.run_id;
            const isDeleting =
              deletingRunId && String(deletingRunId) === String(run.run_id);
            const isConfirming =
              confirmingRunId && String(confirmingRunId) === String(run.run_id);
            const { progressPct, stepsCount } = getRunListProgressStats(
              run,
              runProgressMap[run.run_id],
            );
            const progress = progressPct;
            const runStatus =
              run.status === "processing" ? "running" : run.status || "pending";
            const updatedAt = run.last_updated || run.created_at || new Date();
            const runDisplayId = run.run_display_id || run.run_id;
            const runProjectLabel = `Project ${run.project_id ?? "N/A"}`;
            const runRunLabel = `Run ${runDisplayId}`;
            const activeStepLabel = getDashboardRunActiveStepDisplay(
              run,
              runProgressMap[run.run_id],
            );

            return (
              <div
                key={run.run_id}
                role="button"
                tabIndex={0}
                className={`dashboard-tile-bg dashboard-run-item ${isSelected ? "selected" : ""}`}
                onClick={() => {
                  if (isDeleting || isConfirming) return;
                  onSelectRun && onSelectRun(run);
                }}
                onKeyDown={(e) => {
                  if (isDeleting || isConfirming) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelectRun && onSelectRun(run);
                  }
                }}
              >
                <div className="dashboard-project-item-header-row">
                  <div className="analysis-dashboard-project-header">
                    <div className="dashboard-run-item-project-row">
                      <div className="dashboard-run-item-title-with-step">
                        <h4
                          className="dashboard-run-item-project"
                          title={runProjectLabel}
                        >
                          {runRunLabel}
                        </h4>
                        <span
                          className={`project-status ${getStatusClass(runStatus)}`}
                        >
                          {run._isQueuing ? "Queuing..." : runStatus}
                        </span>
                      </div>
                      {onRequestDeleteRun && (
                        <button
                          type="button"
                          className="dashboard-project-item-delete"
                          title={isDeleting ? "Removing run" : "Remove run"}
                          aria-label={
                            isDeleting ? "Removing run" : "Remove run"
                          }
                          disabled={isDeleting || isConfirming}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRequestDeleteRun(run);
                          }}
                        >
                          <i
                            className={`fa-solid ${isDeleting ? "fa-spinner fa-spin" : "fa-trash-can"}`}
                            aria-hidden
                          />
                        </button>
                      )}
                    </div>
                    {activeStepLabel && (
                      <span className="step-info" title={activeStepLabel}>
                        {activeStepLabel}
                      </span>
                    )}
                  </div>
                </div>
                <div className="project-stats">
                  <div className="analysis-dashboard-stat-item">
                    <span className="analysis-dashboard-stat-label">
                      Progress
                    </span>
                    <span className="analysis-dashboard-stat-value">
                      {progress}%
                    </span>
                  </div>
                  <div className="analysis-dashboard-stat-item">
                    <span className="analysis-dashboard-stat-label">
                      Project
                    </span>
                    <span className="analysis-dashboard-stat-value">
                      {run.project_id ?? "N/A"}
                    </span>
                  </div>
                  <div className="analysis-dashboard-stat-item">
                    <span className="analysis-dashboard-stat-label">
                      Services
                    </span>
                    <span className="analysis-dashboard-stat-value running">
                      {stepsCount}
                    </span>
                  </div>
                </div>
                {isConfirming && (
                  <div
                    className="dashboard-run-delete-overlay"
                    onClick={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Confirm run removal"
                  >
                    <div className="dashboard-run-delete-overlay-content">
                      <p className="dashboard-run-delete-overlay-message">
                        Are you sure you want to remove this item?
                      </p>
                      <p className="dashboard-run-delete-overlay-submessage">
                        This is an irreverbsible action
                      </p>
                      <div className="dashboard-run-delete-overlay-actions">
                        <button
                          type="button"
                          className="dashboard-run-delete-overlay-btn dashboard-run-delete-overlay-btn-cancel"
                          onClick={(e) => {
                            e.stopPropagation();
                            onCancelDeleteRun && onCancelDeleteRun();
                          }}
                          disabled={isDeleting}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="dashboard-run-delete-overlay-btn dashboard-run-delete-overlay-btn-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            onConfirmDeleteRun && onConfirmDeleteRun(run);
                          }}
                          disabled={isDeleting}
                        >
                          {isDeleting ? "Deleting" : "Delete"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {confirmingDeleteAll && (
          <div
            className="dashboard-run-delete-overlay dashboard-delete-all-overlay"
            onClick={() => {
              if (!deletingAllRuns) setConfirmingDeleteAll(false);
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Confirm removal of all runs"
          >
            <div
              className="dashboard-run-delete-overlay-content"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="dashboard-run-delete-overlay-message">
                Are you sure you want to remove all {runs.length} runs?
              </p>
              <p className="dashboard-run-delete-overlay-submessage">
                This is an irreverbsible action
              </p>
              <div className="dashboard-run-delete-overlay-actions">
                <button
                  type="button"
                  className="dashboard-run-delete-overlay-btn dashboard-run-delete-overlay-btn-cancel"
                  onClick={() => setConfirmingDeleteAll(false)}
                  disabled={deletingAllRuns}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="dashboard-run-delete-overlay-btn dashboard-run-delete-overlay-btn-delete"
                  onClick={async () => {
                    await onDeleteAllRuns(runs);
                    setConfirmingDeleteAll(false);
                  }}
                  disabled={deletingAllRuns}
                >
                  {deletingAllRuns ? "Deleting" : "Delete all"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Projected CRS example WKT (NAD83 Pennsylvania South, Lambert Conformal Conic). */
const WKT_EXAMPLE_PROJECTED = `PROJCS["PA83-S",
  GEOGCS["LL83",
    DATUM["NAD83",
      SPHEROID["GRS1980",6378137,298.25722210]
    ],
    PRIMEM["Greenwich",0],
    UNIT["Degree",0.017453292519943295]
  ],
  PROJECTION["Lambert_Conformal_Conic_2SP"],
  PARAMETER["false_easting",600000],
  PARAMETER["false_northing",0],
  PARAMETER["central_meridian",-77.75],
  PARAMETER["latitude_of_origin",39.33333333333333],
  PARAMETER["standard_parallel_1",40.96666666666667],
  PARAMETER["standard_parallel_2",39.93333333333333],
  UNIT["Meter",1]
]`;

/** Geographic CRS example WKT (NAD83 unprojected, latitude/longitude). */
const WKT_EXAMPLE_GEOGRAPHIC = `GEOGCS["GCS_NAD83",
  DATUM["NAD83",
    SPHEROID["GRS1980",6378137,298.25722210]
  ],
  PRIMEM["Greenwich",0],
  UNIT["Degree",0.017453292519943295]
]`;

/** Shared WKT format / requirements dialog (Setup panel info + summary error hints). */
function WktRequirementsHelpModal({ open, onClose }) {
  const [exampleTab, setExampleTab] = useState("projected");
  if (!open) return null;
  return (
    <div
      className="TreeModalOverlay setup-wkt-requirements-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="setup-wkt-requirements-title"
      onClick={onClose}
    >
      <div
        className="TreeModalContainer setup-wkt-requirements-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header setup-wkt-requirements-modal-header">
          <h2
            className="modal-title setup-wkt-requirements-modal-title"
            id="setup-wkt-requirements-title"
          >
            <i
              className="fa-solid fa-circle-info setup-wkt-requirements-modal-title-icon"
              aria-hidden
            />
            Coordinate System Format (WKT)
          </h2>
        </div>
        <div className="modal-content setup-wkt-requirements-modal-body">
          <p className="setup-wkt-requirements-intro">
            <strong>WKT (Well-Known Text)</strong> is the standard text format
            for describing a coordinate reference system (CRS). It defines the
            datum, projection, units, and other parameters needed to correctly
            interpret spatial data. Any valid WKT is accepted — NAD83, WGS84,
            state plane, UTM, custom datums, and more.
          </p>
          <p className="setup-wkt-requirements-subhead">
            How to obtain your WKT
          </p>
          <ul className="setup-wkt-requirements-obtain-list">
            <li>
              <strong>.prj file</strong> — open the <code>.prj</code> sidecar
              file that accompanies your shapefile or LAS/LAZ data. Its entire
              contents are valid WKT; paste them as-is.
            </li>
            <li>
              <strong>EPSG.io</strong> — search your CRS name or EPSG code, then
              copy the <em>WKT1</em> export.
            </li>
            <li>
              <strong>QGIS</strong> — Layer Properties → Information → copy the
              WKT shown under "Coordinate Reference System".
            </li>
            <li>
              <strong>ArcGIS / ArcMap</strong> — Data Frame Properties →
              Coordinate System → export as WKT.
            </li>
          </ul>
          <p className="setup-wkt-requirements-subhead">Examples</p>
          <div className="setup-wkt-requirements-example-wrap">
            <div
              className="setup-wkt-requirements-example-tablist"
              role="tablist"
              aria-label="WKT example by CRS type"
            >
              <button
                type="button"
                role="tab"
                id="setup-wkt-tab-projected"
                aria-selected={exampleTab === "projected"}
                aria-controls="setup-wkt-example-panel"
                className={`setup-wkt-requirements-example-tab${exampleTab === "projected" ? " setup-wkt-requirements-example-tab--active" : ""}`}
                onClick={() => setExampleTab("projected")}
              >
                Projected CRS (PROJCS)
              </button>
              <button
                type="button"
                role="tab"
                id="setup-wkt-tab-geographic"
                aria-selected={exampleTab === "geographic"}
                aria-controls="setup-wkt-example-panel"
                className={`setup-wkt-requirements-example-tab${exampleTab === "geographic" ? " setup-wkt-requirements-example-tab--active" : ""}`}
                onClick={() => setExampleTab("geographic")}
              >
                Geographic CRS (GEOGCS)
              </button>
            </div>
            <pre
              id="setup-wkt-example-panel"
              role="tabpanel"
              aria-labelledby={
                exampleTab === "projected"
                  ? "setup-wkt-tab-projected"
                  : "setup-wkt-tab-geographic"
              }
              className="setup-wkt-requirements-example"
              tabIndex={0}
            >
              {exampleTab === "projected"
                ? WKT_EXAMPLE_PROJECTED
                : WKT_EXAMPLE_GEOGRAPHIC}
            </pre>
          </div>
          <p className="setup-wkt-requirements-tip">
            Paste your WKT exactly as exported from your source — do not edit
            values manually.
          </p>
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="modal-btn modal-btn-primary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const formatUploadTimestamp = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "Unknown";
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "Unknown";
  return parsed.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// --- Summary Component ---
function RSASummary({
  activeMainTab,
  mapOverlayLocalConfig,
  wktValidation,
  mapOverlaySelectedFolders = [],
  setupFolderAvailable = false,
  lasBoundingBoxes = [],
  orthoTiles = [],
  existingProjectName,
  selectedProjectId,
  rsaGroupsMileages = {},
  rsaProjectsPerGroup = {},
  rsaGroupsIsdManeuvers = {},
  services,
  selectedFolders,
  selectedDriveName,
  roadDetectionCategory,
  horizontalData,
  EmbankmentData,
  selectedStorageProvider,
  onSubmit,
  onClear,
  onSaveSetup,
  savingSetup = false,
  wktInputLocked = false,
  onOpenWktRequirementsHelp,
  isRsaModelProject = false,
  rsaModelRouteInfoRef = null,
  rsaRunMetricsRef = null,
  rsaUsageLedgerState = null,
  rsaUsageLedgerLoading = false,
  routesSourceFiles = [],
  routesXmlFiles = [],
  modelFiles = [],
  modelFolderId = null,
  existingSourceFolderId = "",
  existingSourceFiles = [],
  routesMatrixSelection = {},
  onToggleRouteFile,
  routesMatrixSaving = false,
}) {
  const isUploadTab = activeMainTab === "upload";
  const isSetupTab = activeMainTab === "setup";
  const isRoutesTab =
    activeMainTab === "routes" || activeMainTab === "analysis";
  const config = mapOverlayLocalConfig || {};
  const hasWkt = !!(config.wkt || "").trim();
  const validationStatus = wktValidation?.status || "idle";

  const [isRunInProgress, setIsRunInProgress] = useState(false);
  const [mongoOverwriteWarning, setMongoOverwriteWarning] = useState(null);
  const [showRouteSelectionValidation, setShowRouteSelectionValidation] =
    useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const hasAnySelectedRoute = useMemo(() => {
    if (routesXmlFiles.length === 0) return true;
    return routesXmlFiles.some((routeFile) => {
      const fileKey = normalizeDriveFolderId(routeFile.googleDriveId);
      if (!fileKey) return false;
      return services.some((service) => {
        const taskOption = SERVICE_TO_TASK_OPTION[service];
        return (
          taskOption && Boolean(routesMatrixSelection?.[fileKey]?.[taskOption])
        );
      });
    });
  }, [routesXmlFiles, services, routesMatrixSelection]);

  useEffect(() => {
    if (hasAnySelectedRoute && showRouteSelectionValidation) {
      setShowRouteSelectionValidation(false);
    }
  }, [hasAnySelectedRoute, showRouteSelectionValidation]);

  const handleRunButtonClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isRunInProgress || routesMatrixSaving || !onSubmit) {
      return;
    }

    if (!hasAnySelectedRoute) {
      setShowRouteSelectionValidation(true);
      return;
    }

    // Build per-file check list from only the checked routes in the matrix.
    // Each entry is {file_drive_id, analysis_code} — one per (checked file × service) pair.
    const filesToCheck = [];
    for (const row of checkedRouteMatrixRows) {
      const routeFile = routesXmlFiles.find(
        (f) => normalizeDriveFolderId(f.googleDriveId) === row.key,
      );
      if (!routeFile?.googleDriveId) continue;
      for (const assignment of row.assignments) {
        if (!assignment.checked) continue;
        const folderCode = SERVICE_TO_RSA_FOLDER_CODE[assignment.service];
        const analysisCode = folderCode
          ? RSA_FOLDER_CODE_TO_SUFFIX[folderCode]
          : null;
        if (analysisCode) {
          filesToCheck.push({
            file_drive_id: routeFile.googleDriveId,
            analysis_code: analysisCode,
          });
        }
      }
    }

    if (filesToCheck.length > 0) {
      try {
        const isProductionDomain =
          window.location.hostname.includes("123bimlive.com");
        const baseUrl =
          ENVIRONMENT === "production" || isProductionDomain
            ? ""
            : API_BASE_URL;
        const checkUrl = baseUrl
          ? `${API_BASE_URL}/api/projects/${selectedProjectId}/check-mongo-overwrite`
          : `/api/projects/${selectedProjectId}/check-mongo-overwrite`;
        const checkRes = await axios.post(checkUrl, { files: filesToCheck });
        const existing = checkRes?.data?.existing_collections || [];
        if (existing.length > 0) {
          setMongoOverwriteWarning({ collections: existing, pendingEvent: e });
          return;
        }
      } catch (checkErr) {
        // Non-fatal: if check fails, proceed with the run
        console.warn(
          "[mongo_check] pre-run check failed, proceeding:",
          checkErr,
        );
      }
    }
    // (no else needed — if filesToCheck is empty, no conflicts possible)

    setIsRunInProgress(true);
    try {
      setShowRouteSelectionValidation(false);
      await onSubmit(e);
    } finally {
      if (isMountedRef.current) {
        setIsRunInProgress(false);
      }
    }
  };

  const handleMongoOverwriteConfirm = async () => {
    const pendingEvent = mongoOverwriteWarning?.pendingEvent;
    setMongoOverwriteWarning(null);
    setIsRunInProgress(true);
    try {
      setShowRouteSelectionValidation(false);
      await onSubmit(pendingEvent);
    } finally {
      if (isMountedRef.current) {
        setIsRunInProgress(false);
      }
    }
  };
  const isWktValidated = Boolean(wktValidation?.valid);
  const isWktCompatible = Boolean(wktValidation?.willWork);
  const wktValid = hasWkt && isWktValidated && isWktCompatible;
  const setupSaveEnabled = wktValid;
  const wktStatusText = !hasWkt
    ? "Not provided"
    : validationStatus === "checking"
      ? "Checking..."
      : validationStatus === "error"
        ? "Validation error"
        : isWktValidated
          ? isWktCompatible
            ? "Valid and compatible"
            : "Valid but not compatible"
          : "Invalid";
  const wktStatusClass = !hasWkt
    ? ""
    : validationStatus === "checking"
      ? "setup-summary-status-pending"
      : isWktValidated && isWktCompatible
        ? "setup-summary-status-ok"
        : "setup-summary-status-error";
  const showWillWorkRow =
    hasWkt &&
    validationStatus !== "idle" &&
    validationStatus !== "checking" &&
    !isWktCompatible;
  const wktLocationLabel =
    wktValidation?.location?.label || wktValidation?.crs?.name || "Unknown";
  const wktCountryLabel = wktValidation?.location?.country || "Unknown";
  const wktStateLabel = wktValidation?.location?.state || "Unknown";
  const serviceMapping = {
    tiling: "Tiling",
    csf: "CSF",
    road_detection: "Road Detection",
    car_removal: "Car Removal",
    file_transfer: "File Transfer",
    create_rsa: "Analysis",
    psd_classification: "PSD Classification",
    horizontal: "Horizontal Curve Analysis (HCA)",
    embankment: "Embankment Analysis (EMB)",
    automatic_reports: "Automatic Reports",
    // New service value to display name mappings (with abbreviations)
    stopping_sight_distance: "Stopping Sight Distance (SSD)",
    passing_sight_distance: "Passing Sight Distance (PSD)",
    flat_spot_analysis: "Flat Spot Analysis (FSA)",
    intersection_sight_distance: "Intersection Sight Distance (ISD)",
    sunglare_analysis: "Sunglare Analysis (GLR)",
    guardrails_warrant: "Guardrails Warrant (GRW)",
    existing_guardrails: "Existing Guardrails (EXG)",
    clear_zone_analysis: "Clear Zone Analysis (CLZ)",
  };

  const analysisMapping = {
    SSD: "SSD",
    PSD: "PSD",
    FSA: "FSA",
    ISD: "ISD",
    GLR: "GLR",
  };

  const storageProviderMapping = {
    system_google_drive: {
      name: "System Drive",
      icon: GoogleDriveIcon,
      iconSize: 16,
    },
    google_drive: { name: "Google Drive", icon: GoogleDriveIcon, iconSize: 16 },
    s3: { name: "AWS S3", icon: AmazonS3Icon, iconSize: 16 },
    azure: { name: "Microsoft Azure", icon: AzureIcon, iconSize: 16 },
    gcp: { name: "Google Cloud Storage", icon: GoogleCloudIcon, iconSize: 16 },
  };

  const selectedAnalysisCount = Array.isArray(services) ? services.length : 0;
  const selectedRouteCount = routesXmlFiles.filter((routeFile) => {
    const fileKey = normalizeDriveFolderId(routeFile.googleDriveId);
    if (!fileKey) return false;
    return services.some((service) => {
      const taskOption = SERVICE_TO_TASK_OPTION[service];
      return (
        taskOption && Boolean(routesMatrixSelection?.[fileKey]?.[taskOption])
      );
    });
  }).length;
  const routeDxfCount = routesSourceFiles.filter((file) =>
    isDxfFileName(file?.name),
  ).length;
  const latestRoutesUploadTime = (() => {
    const latest = routesSourceFiles.reduce((best, item) => {
      const stamp = item?.modified || item?.created || "";
      if (!stamp) return best;
      if (!best) return stamp;
      return new Date(stamp).getTime() > new Date(best).getTime()
        ? stamp
        : best;
    }, "");
    return latest ? formatUploadTimestamp(latest) : "No uploads yet";
  })();
  const latestModelUploadTime = (() => {
    const latest = modelFiles.reduce((best, item) => {
      const stamp = item?.modified || item?.created || "";
      if (!stamp) return best;
      if (!best) return stamp;
      return new Date(stamp).getTime() > new Date(best).getTime()
        ? stamp
        : best;
    }, "");
    return latest ? formatUploadTimestamp(latest) : "No uploads yet";
  })();
  const latestExistingSourceUploadTime = (() => {
    const latest = existingSourceFiles.reduce((best, item) => {
      const stamp = item?.modified || item?.created || "";
      if (!stamp) return best;
      if (!best) return stamp;
      return new Date(stamp).getTime() > new Date(best).getTime()
        ? stamp
        : best;
    }, "");
    return latest ? formatUploadTimestamp(latest) : "No uploads yet";
  })();
  const summaryTitle = isUploadTab
    ? "Upload Summary"
    : isSetupTab
      ? "Setup Summary"
      : isRoutesTab
        ? "Tasks & Routes Summary"
        : "Analysis Selection Summary";

  const renderStorageProviderButton = () => {
    if (!selectedStorageProvider) return null;

    const provider = storageProviderMapping[selectedStorageProvider];
    if (!provider) return null;

    const IconComponent = provider.icon;

    return (
      <button
        className="toggle-btn-rsa task-button storage-provider-btn storage-provider-btn-summary selected"
        type="button"
        disabled
        style={{ cursor: "default" }}
      >
        <IconComponent
          className="storage-provider-icon"
          style={{
            width: `${provider.iconSize}px`,
            height: `${provider.iconSize}px`,
          }}
        />
        <span>{provider.name}</span>
      </button>
    );
  };

  const extent = config.extent || {};
  const originXY = config.originXY || {};
  const originLatLng = config.originLatLng || {};

  const [rsaFoldersByCode, setRsaFoldersByCode] = useState({});
  const [projectFilesSnapshot, setProjectFilesSnapshot] = useState([]);
  const [expandedServiceTags, setExpandedServiceTags] = useState({});
  const [xmlFilesByService, setXmlFilesByService] = useState({});
  const [xmlEndMileTotalsByService, setXmlEndMileTotalsByService] = useState(
    {},
  );
  const [xmlIsdManeuverTotalsByService, setXmlIsdManeuverTotalsByService] =
    useState({});
  const [xmlPerFileMilesByService, setXmlPerFileMilesByService] = useState({}); // { service: { fileName: miles } }
  const [xmlPerFileManeuversByService, setXmlPerFileManeuversByService] =
    useState({}); // { service: { fileName: count } }
  const [xmlLoadingByService, setXmlLoadingByService] = useState({});
  const [refreshingAllXml, setRefreshingAllXml] = useState(false);
  const [routesPerFileMileage, setRoutesPerFileMileage] = useState({}); // { fileName: miles } for RSA model route files
  const [routesPerFileManeuvers, setRoutesPerFileManeuvers] = useState({}); // { fileName: maneuvers } for RSA model route files
  const [routesPerFileDataLoading, setRoutesPerFileDataLoading] =
    useState(false);
  // Bump to force the per-file mileage/maneuvers fetch effect to re-run when
  // the cache has been resynced server-side (rsa-existing auto-resync + DB button).
  const [routesPerFileNonce, setRoutesPerFileNonce] = useState(0);
  const analysisServices = useMemo(
    () =>
      Array.isArray(services)
        ? services.filter((service) =>
            Boolean(SERVICE_TO_RSA_FOLDER_CODE[service]),
          )
        : [],
    [services],
  );
  const checkedRouteMatrixColumns = useMemo(
    () =>
      analysisServices
        .map((service) => ({
          service,
          taskOption: SERVICE_TO_TASK_OPTION[service] || null,
          label: serviceMapping[service] || service,
          shortLabel:
            (serviceMapping[service] || service).match(/\(([^)]+)\)/)?.[1] ||
            serviceMapping[service] ||
            service,
        }))
        .filter((column) => column.taskOption),
    [analysisServices],
  );
  const checkedRouteMatrixRows = useMemo(() => {
    if (checkedRouteMatrixColumns.length === 0 || routesXmlFiles.length === 0) {
      return [];
    }

    return routesXmlFiles
      .map((routeFile) => {
        const fileKey = normalizeDriveFolderId(routeFile.googleDriveId);
        if (!fileKey) return null;

        const assignments = checkedRouteMatrixColumns.map((column) => ({
          ...column,
          checked: Boolean(
            routesMatrixSelection?.[fileKey]?.[column.taskOption],
          ),
        }));

        if (!assignments.some((assignment) => assignment.checked)) {
          return null;
        }

        return {
          key: fileKey,
          name: routeFile.name,
          assignments,
        };
      })
      .filter(Boolean);
  }, [routesXmlFiles, checkedRouteMatrixColumns, routesMatrixSelection]);

  useEffect(() => {
    if (!rsaModelRouteInfoRef) {
      return;
    }
    const next = {};
    for (const service of analysisServices) {
      if (!SERVICE_TO_RSA_FOLDER_CODE[service]) {
        continue;
      }
      const hasLoaded = Object.prototype.hasOwnProperty.call(
        xmlFilesByService,
        service,
      );
      next[service] = {
        loading: !!xmlLoadingByService[service],
        count: (xmlFilesByService[service] || []).length,
        hasLoaded,
      };
    }
    rsaModelRouteInfoRef.current = next;
  }, [
    rsaModelRouteInfoRef,
    analysisServices,
    xmlFilesByService,
    xmlLoadingByService,
  ]);

  useEffect(() => {
    setExpandedServiceTags({});
    setXmlFilesByService({});
    setXmlEndMileTotalsByService({});
    setXmlIsdManeuverTotalsByService({});
    setXmlPerFileMilesByService({});
    setXmlPerFileManeuversByService({});
    setXmlLoadingByService({});
    setRsaFoldersByCode({});
    setProjectFilesSnapshot([]);
    setRoutesPerFileMileage({});
    setRoutesPerFileManeuvers({});
    setRoutesPerFileDataLoading(false);
  }, [selectedProjectId]);

  const invalidateXmlSummaryCaches = useCallback(() => {
    setXmlFilesByService({});
    setXmlEndMileTotalsByService({});
    setXmlIsdManeuverTotalsByService({});
    setXmlPerFileMilesByService({});
    setXmlPerFileManeuversByService({});
    setXmlLoadingByService({});
    // Force the routes per-file fetch to re-run too — the resync may have
    // populated Source/Routes cache rows that weren't there before.
    setRoutesPerFileNonce((n) => n + 1);
  }, []);

  // After developer resync (ProjectView DB button), MySQL totals update but this panel keeps stale state.
  useEffect(() => {
    const onCachesSynced = (ev) => {
      const pid = ev?.detail?.projectId;
      if (pid == null || String(pid).trim() === "") {
        return;
      }
      if (String(selectedProjectId ?? "") !== String(pid)) {
        return;
      }
      invalidateXmlSummaryCaches();
    };
    window.addEventListener("rsa-mileage-caches-synced", onCachesSynced);
    return () =>
      window.removeEventListener("rsa-mileage-caches-synced", onCachesSynced);
  }, [selectedProjectId, invalidateXmlSummaryCaches]);

  // Same-tab: user resynced on Files then opened Analysis (RSA remounts); event may have fired while unmounted.
  useEffect(() => {
    if (!selectedProjectId) {
      return;
    }
    let ts = null;
    try {
      ts = sessionStorage.getItem(
        `rsaMileageCachesSynced:${selectedProjectId}`,
      );
    } catch (e) {
      return;
    }
    if (!ts) {
      return;
    }
    try {
      sessionStorage.removeItem(`rsaMileageCachesSynced:${selectedProjectId}`);
    } catch (e) {
      /* ignore */
    }
    invalidateXmlSummaryCaches();
  }, [selectedProjectId, invalidateXmlSummaryCaches]);

  useEffect(() => {
    let cancelled = false;

    const loadRsaFoldersForSummary = async () => {
      if (!selectedProjectId) {
        setRsaFoldersByCode({});
        setProjectFilesSnapshot([]);
        return;
      }

      try {
        const isProductionDomain =
          window.location.hostname.includes("123bimlive.com");
        const baseUrl =
          ENVIRONMENT === "production" || isProductionDomain
            ? ""
            : API_BASE_URL;
        const cacheBust = `_ts=${Date.now()}`;
        const filesUrl = baseUrl
          ? `${API_BASE_URL}/api/projects/${selectedProjectId}/files?${cacheBust}`
          : `/api/projects/${selectedProjectId}/files?${cacheBust}`;
        const filesResponse = await axios.get(filesUrl);
        const files = filesResponse.data?.files || [];
        const metadata = deriveTaskMetadataFromProjectFiles(files);
        const mergedFoldersByCode = { ...(metadata.dbFoldersByCode || {}) };

        if (
          Array.isArray(metadata.sourceGoogleDriveIds) &&
          metadata.sourceGoogleDriveIds.length > 0
        ) {
          const sourceChildrenRequests = metadata.sourceGoogleDriveIds.map(
            (sourceFolderId) => {
              const childrenUrl = baseUrl
                ? `${API_BASE_URL}/api/projects/${selectedProjectId}/google-drive-contents?folder_id=${encodeURIComponent(sourceFolderId)}&${cacheBust}`
                : `/api/projects/${selectedProjectId}/google-drive-contents?folder_id=${encodeURIComponent(sourceFolderId)}&${cacheBust}`;
              return axios
                .get(childrenUrl)
                .catch(() => ({ data: { items: [] } }));
            },
          );
          const sourceChildrenResponses = await Promise.all(
            sourceChildrenRequests,
          );

          for (const sourceChildrenResponse of sourceChildrenResponses) {
            const items = sourceChildrenResponse?.data?.items || [];
            for (const item of items) {
              if (!isFolderItem(item)) {
                continue;
              }
              const normalizedCode = normalizeFolderName(item?.name);
              const taskOption = mapFolderCodeToTaskOption(item?.name);
              if (!taskOption) {
                continue;
              }
              mergedFoldersByCode[normalizedCode] = {
                id: getFileId(item),
                name: item?.name || normalizedCode,
                googleDriveId: getGoogleDriveId(item),
                source: "drive",
              };
            }
          }
        }

        if (cancelled) {
          return;
        }
        setProjectFilesSnapshot(Array.isArray(files) ? files : []);
        setRsaFoldersByCode(mergedFoldersByCode);
      } catch (error) {
        if (!cancelled) {
          console.error(
            "Failed to load RSA Source folders for summary tags:",
            error,
          );
          setProjectFilesSnapshot([]);
          setRsaFoldersByCode({});
        }
      }
    };

    loadRsaFoldersForSummary();
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId]);

  // Fetch per-file mileage/maneuvers directly from the Routes folder
  // so the token bar can sum up only the checked files rather than relying on RSA_SSD_* folders.
  useEffect(() => {
    if (!selectedProjectId || routesXmlFiles.length === 0) {
      setRoutesPerFileMileage({});
      setRoutesPerFileManeuvers({});
      return;
    }
    const routesFolderDriveId = routesXmlFiles.find(
      (f) => f.parentDriveId,
    )?.parentDriveId;
    if (!routesFolderDriveId) return;

    let cancelled = false;
    setRoutesPerFileDataLoading(true);
    const isProductionDomain =
      window.location.hostname.includes("123bimlive.com");
    const baseUrl =
      ENVIRONMENT === "production" || isProductionDomain ? "" : API_BASE_URL;
    const perFileUrl = baseUrl
      ? `${API_BASE_URL}/api/projects/${selectedProjectId}/rsa-mileage-per-file?folder_ids=${encodeURIComponent(routesFolderDriveId)}`
      : `/api/projects/${selectedProjectId}/rsa-mileage-per-file?folder_ids=${encodeURIComponent(routesFolderDriveId)}`;
    axios
      .get(perFileUrl)
      .then((res) => {
        if (cancelled) return;
        const files = res?.data?.files || [];
        const milesMap = {};
        const maneuversMap = {};
        // Key by both raw file_name and normalized (trim+lower-case) name so
        // breakdown lookups survive whitespace/case drift between the cached
        // row and the Drive listing.
        files.forEach((f) => {
          const rawName = f.file_name;
          const normKey = normalizeFileNameKey(rawName);
          if (f.computed_miles != null) {
            if (rawName) milesMap[rawName] = f.computed_miles;
            if (normKey) milesMap[normKey] = f.computed_miles;
          }
          if (f.computed_maneuvers != null) {
            if (rawName) maneuversMap[rawName] = f.computed_maneuvers;
            if (normKey) maneuversMap[normKey] = f.computed_maneuvers;
          }
        });
        setRoutesPerFileMileage(milesMap);
        setRoutesPerFileManeuvers(maneuversMap);
      })
      .catch(() => {
        if (!cancelled) {
          setRoutesPerFileMileage({});
          setRoutesPerFileManeuvers({});
        }
      })
      .finally(() => {
        if (!cancelled) setRoutesPerFileDataLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId, routesXmlFiles, routesPerFileNonce]);

  const loadXmlFilesForService = useCallback(
    async (service, forceReload = false) => {
      if (!service || !selectedProjectId) {
        return;
      }

      setXmlLoadingByService((prev) => ({ ...prev, [service]: true }));
      try {
        const folderCode = SERVICE_TO_RSA_FOLDER_CODE[service];
        const folderInfo = folderCode ? rsaFoldersByCode[folderCode] : null;
        const isProductionDomain =
          window.location.hostname.includes("123bimlive.com");
        const baseUrl =
          ENVIRONMENT === "production" || isProductionDomain
            ? ""
            : API_BASE_URL;
        const xmlFiles = [];

        if (folderInfo?.googleDriveId) {
          const cacheBust = `_ts=${Date.now()}`;
          const childrenUrl = baseUrl
            ? `${API_BASE_URL}/api/projects/${selectedProjectId}/google-drive-contents?folder_id=${encodeURIComponent(folderInfo.googleDriveId)}&${cacheBust}`
            : `/api/projects/${selectedProjectId}/google-drive-contents?folder_id=${encodeURIComponent(folderInfo.googleDriveId)}&${cacheBust}`;
          const childrenResponse = await axios.get(childrenUrl);
          const items = childrenResponse?.data?.items || [];
          xmlFiles.push(
            ...items
              .filter(
                (item) => !isFolderItem(item) && isXmlFileName(item?.name),
              )
              .map((item) => ({
                name: String(item?.name ?? "").trim(),
                fileId: getFileId(item),
                googleDriveId: getGoogleDriveId(item),
              }))
              .filter((item) => item.name),
          );
        } else if (folderInfo?.id != null) {
          xmlFiles.push(
            ...(Array.isArray(projectFilesSnapshot) ? projectFilesSnapshot : [])
              .filter(
                (item) =>
                  String(getParentId(item) ?? "") === String(folderInfo.id) &&
                  !isFolderItem(item) &&
                  isXmlFileName(item?.name),
              )
              .map((item) => ({
                name: String(item?.name ?? "").trim(),
                fileId: getFileId(item),
                googleDriveId: getGoogleDriveId(item),
              }))
              .filter((item) => item.name),
          );
        }

        xmlFiles.sort((a, b) => a.name.localeCompare(b.name));
        const xmlFileNames = xmlFiles.map((item) => item.name);
        const isIsdService = service === "intersection_sight_distance";
        let totalEndMileForService = 0;
        let totalManeuversForService = 0;

        if (folderInfo?.googleDriveId) {
          const folderDriveKey = String(folderInfo.googleDriveId).trim();
          const pickTotalFromByFolder = (totalsByFolder, key) => {
            if (!totalsByFolder || !key) {
              return null;
            }
            if (Object.prototype.hasOwnProperty.call(totalsByFolder, key)) {
              return Number(totalsByFolder[key]);
            }
            const lower = key.toLowerCase();
            const matchKey = Object.keys(totalsByFolder).find(
              (k) => String(k).toLowerCase() === lower,
            );
            return matchKey != null ? Number(totalsByFolder[matchKey]) : null;
          };
          const cacheBustTotals = `_ts=${Date.now()}`;
          if (isIsdService) {
            try {
              const maneuversUrl = baseUrl
                ? `${API_BASE_URL}/api/projects/${selectedProjectId}/rsa-isd-maneuver-totals?folder_ids=${encodeURIComponent(folderDriveKey)}&${cacheBustTotals}`
                : `/api/projects/${selectedProjectId}/rsa-isd-maneuver-totals?folder_ids=${encodeURIComponent(folderDriveKey)}&${cacheBustTotals}`;
              const maneuversResponse = await axios.get(maneuversUrl);
              const totalsByFolder =
                maneuversResponse?.data?.totals_by_folder || {};
              const cachedTotal = pickTotalFromByFolder(
                totalsByFolder,
                folderDriveKey,
              );
              if (cachedTotal != null && Number.isFinite(cachedTotal)) {
                totalManeuversForService = cachedTotal;
              } else {
                const fallbackTotal = Number(
                  maneuversResponse?.data?.grand_total,
                );
                totalManeuversForService = Number.isFinite(fallbackTotal)
                  ? fallbackTotal
                  : 0;
              }
            } catch (maneuverError) {
              console.warn(
                `Failed to fetch cached ISD maneuver total for folder ${folderDriveKey}:`,
                maneuverError,
              );
              totalManeuversForService = 0;
            }
          } else {
            try {
              const mileageUrl = baseUrl
                ? `${API_BASE_URL}/api/projects/${selectedProjectId}/rsa-mileage-totals?folder_ids=${encodeURIComponent(folderDriveKey)}&${cacheBustTotals}`
                : `/api/projects/${selectedProjectId}/rsa-mileage-totals?folder_ids=${encodeURIComponent(folderDriveKey)}&${cacheBustTotals}`;
              const mileageResponse = await axios.get(mileageUrl);
              try {
                if (
                  typeof window !== "undefined" &&
                  window.localStorage?.getItem("rsaMileageDebug") === "1"
                ) {
                  // eslint-disable-next-line no-console
                  console.info(
                    "[rsaMileageDebug]",
                    mileageUrl,
                    mileageResponse?.data,
                  );
                }
              } catch (e) {
                /* ignore */
              }
              const totalsByFolder =
                mileageResponse?.data?.totals_by_folder || {};
              const cachedTotal = pickTotalFromByFolder(
                totalsByFolder,
                folderDriveKey,
              );
              if (cachedTotal != null && Number.isFinite(cachedTotal)) {
                totalEndMileForService = cachedTotal;
              } else {
                const fallbackTotal = Number(
                  mileageResponse?.data?.grand_total,
                );
                totalEndMileForService = Number.isFinite(fallbackTotal)
                  ? fallbackTotal
                  : 0;
              }
            } catch (mileageError) {
              console.warn(
                `Failed to fetch cached mileage total for folder ${folderDriveKey}:`,
                mileageError,
              );
              totalEndMileForService = 0;
            }
          }
        } else {
          const xmlValues = await Promise.all(
            xmlFiles.map(async (xmlFile) => {
              try {
                const driveId = String(xmlFile.googleDriveId ?? "").trim();
                let xmlText = "";
                if (driveId) {
                  const mediaUrl = baseUrl
                    ? `${API_BASE_URL}/api/google-drive/file/${encodeURIComponent(driveId)}/media`
                    : `/api/google-drive/file/${encodeURIComponent(driveId)}/media`;
                  const mediaResponse = await axios.get(mediaUrl, {
                    responseType: "text",
                  });
                  xmlText =
                    typeof mediaResponse?.data === "string"
                      ? mediaResponse.data
                      : "";
                } else {
                  const fileId = String(xmlFile.fileId ?? "").trim();
                  if (/^\d+$/.test(fileId)) {
                    const downloadUrl = baseUrl
                      ? `${API_BASE_URL}/api/files/${encodeURIComponent(fileId)}/download?project_id=${encodeURIComponent(selectedProjectId)}`
                      : `/api/files/${encodeURIComponent(fileId)}/download?project_id=${encodeURIComponent(selectedProjectId)}`;
                    const downloadResponse = await axios.get(downloadUrl, {
                      responseType: "text",
                    });
                    xmlText =
                      typeof downloadResponse?.data === "string"
                        ? downloadResponse.data
                        : "";
                  }
                }
                if (isIsdService) {
                  return countIsdManeuversFromXml(xmlText);
                }
                return sumDirectionalMilesFromXml(xmlText);
              } catch (fileError) {
                console.warn(
                  `Failed to read XML content for ${xmlFile.name}:`,
                  fileError,
                );
                return 0;
              }
            }),
          );
          if (isIsdService) {
            totalManeuversForService = xmlValues.reduce(
              (sum, value) => sum + value,
              0,
            );
          } else {
            totalEndMileForService = xmlValues.reduce(
              (sum, value) => sum + value,
              0,
            );
          }
        }

        setXmlFilesByService((prev) => ({ ...prev, [service]: xmlFileNames }));
        setXmlEndMileTotalsByService((prev) => ({
          ...prev,
          [service]: totalEndMileForService,
        }));
        setXmlIsdManeuverTotalsByService((prev) => ({
          ...prev,
          [service]: totalManeuversForService,
        }));

        // Fetch per-file breakdown so we can show miles/maneuvers next to each filename.
        // Fire-and-forget — doesn't block the totals from rendering.
        const perFileFolderCode = SERVICE_TO_RSA_FOLDER_CODE[service];
        const perFileFolderInfo = perFileFolderCode
          ? rsaFoldersByCode[perFileFolderCode]
          : null;
        if (perFileFolderInfo?.googleDriveId) {
          const isProductionDomain =
            window.location.hostname.includes("123bimlive.com");
          const baseUrl =
            ENVIRONMENT === "production" || isProductionDomain
              ? ""
              : API_BASE_URL;
          const perFileUrl = baseUrl
            ? `${API_BASE_URL}/api/projects/${selectedProjectId}/rsa-mileage-per-file?folder_ids=${encodeURIComponent(perFileFolderInfo.googleDriveId)}`
            : `/api/projects/${selectedProjectId}/rsa-mileage-per-file?folder_ids=${encodeURIComponent(perFileFolderInfo.googleDriveId)}`;
          axios
            .get(perFileUrl)
            .then((res) => {
              const files = res?.data?.files || [];
              const milesMap = {};
              const maneuversMap = {};
              files.forEach((f) => {
                if (f.computed_miles != null)
                  milesMap[f.file_name] = f.computed_miles;
                if (f.computed_maneuvers != null)
                  maneuversMap[f.file_name] = f.computed_maneuvers;
              });
              setXmlPerFileMilesByService((prev) => ({
                ...prev,
                [service]: milesMap,
              }));
              setXmlPerFileManeuversByService((prev) => ({
                ...prev,
                [service]: maneuversMap,
              }));
            })
            .catch(() => {
              // Non-fatal — file list still shows, just without inline miles
            });
        }
      } catch (error) {
        console.error(
          `Failed to load XML files for service ${service}:`,
          error,
        );
        setXmlFilesByService((prev) => ({ ...prev, [service]: [] }));
        setXmlEndMileTotalsByService((prev) => ({ ...prev, [service]: 0 }));
        setXmlIsdManeuverTotalsByService((prev) => ({ ...prev, [service]: 0 }));
      } finally {
        setXmlLoadingByService((prev) => ({ ...prev, [service]: false }));
      }
    },
    [projectFilesSnapshot, rsaFoldersByCode, selectedProjectId],
  );

  const refreshAllServiceXmlFiles = useCallback(async () => {
    if (!selectedProjectId || analysisServices.length === 0) {
      return;
    }
    setRefreshingAllXml(true);
    try {
      await Promise.all(
        analysisServices.map((service) =>
          loadXmlFilesForService(service, true),
        ),
      );
    } finally {
      setRefreshingAllXml(false);
    }
  }, [analysisServices, loadXmlFilesForService, selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) {
      return;
    }
    const servicesWithDetectedFolders = Object.entries(
      SERVICE_TO_RSA_FOLDER_CODE,
    )
      .filter(([, folderCode]) => Boolean(rsaFoldersByCode[folderCode]))
      .map(([service]) => service);
    if (servicesWithDetectedFolders.length === 0) {
      return;
    }

    const servicesToPrefetch = servicesWithDetectedFolders.filter(
      (service) =>
        !Object.prototype.hasOwnProperty.call(xmlFilesByService, service) &&
        !xmlLoadingByService[service],
    );
    if (servicesToPrefetch.length === 0) {
      return;
    }

    Promise.all(
      servicesToPrefetch.map((service) => loadXmlFilesForService(service)),
    ).catch((error) => {
      console.warn(
        "XML prefetch for summary tags completed with errors:",
        error,
      );
    });
  }, [
    selectedProjectId,
    rsaFoldersByCode,
    xmlFilesByService,
    xmlLoadingByService,
    loadXmlFilesForService,
  ]);

  // Auto-resync for rsa-existing: when Source/Routes has XML files but the
  // rsa_xml_file_cache has no rows for them (typical on first visit), every
  // per-service row shows 0.00 mi / 0 mnv. Fire the resync endpoint once per
  // session to populate the cache; the synced-event handler will re-fetch.
  useEffect(() => {
    if (!selectedProjectId) return;
    if (isRsaModelProject) return;
    if (routesXmlFiles.length === 0) return;
    if (routesPerFileDataLoading) return;
    const milesKeys = Object.keys(routesPerFileMileage || {}).length;
    const maneuversKeys = Object.keys(routesPerFileManeuvers || {}).length;
    if (milesKeys > 0 || maneuversKeys > 0) return;

    // Key suffix bumped (v2) so prior sessions that attempted under the old
    // resync (which didn't scan Source/Routes) get a fresh try with the fix.
    const attemptedKey = `rsaAutoResyncAttemptedV2:${selectedProjectId}`;
    try {
      if (sessionStorage.getItem(attemptedKey)) return;
      sessionStorage.setItem(attemptedKey, String(Date.now()));
    } catch (e) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const isProductionDomain =
          window.location.hostname.includes("123bimlive.com");
        const baseUrl =
          ENVIRONMENT === "production" || isProductionDomain
            ? ""
            : API_BASE_URL;
        const url = baseUrl
          ? `${API_BASE_URL}/api/projects/${selectedProjectId}/rsa-resync-source-caches`
          : `/api/projects/${selectedProjectId}/rsa-resync-source-caches`;
        const response = await axios.post(url);
        if (cancelled) return;
        const processed = Number(response?.data?.xml_files_processed ?? 0);
        if (!processed) return;
        try {
          sessionStorage.setItem(
            `rsaMileageCachesSynced:${selectedProjectId}`,
            String(Date.now()),
          );
        } catch (e) {
          /* ignore */
        }
        window.dispatchEvent(
          new CustomEvent("rsa-mileage-caches-synced", {
            detail: { projectId: selectedProjectId },
          }),
        );
      } catch (err) {
        console.warn(
          "RSA cache auto-resync failed (no retry this session):",
          err,
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    selectedProjectId,
    isRsaModelProject,
    routesXmlFiles,
    routesPerFileMileage,
    routesPerFileManeuvers,
    routesPerFileDataLoading,
  ]);

  const toggleServiceTag = useCallback(
    (service) => {
      if (!service) {
        return;
      }
      const isExpanded = !!expandedServiceTags[service];
      if (!isExpanded) {
        const hasLoadedXml = Object.prototype.hasOwnProperty.call(
          xmlFilesByService,
          service,
        );
        if (!hasLoadedXml) {
          loadXmlFilesForService(service);
        }
      }
      setExpandedServiceTags((prev) => ({ ...prev, [service]: !isExpanded }));
    },
    [expandedServiceTags, loadXmlFilesForService, xmlFilesByService],
  );

  const hasSelectedProject =
    selectedProjectId !== null &&
    selectedProjectId !== undefined &&
    String(selectedProjectId) !== "";
  const selectedProjectKey = hasSelectedProject
    ? String(selectedProjectId)
    : "";
  const normalizeProjectId = (value) => String(value ?? "").trim();
  const normalizedSelectedProjectId = normalizeProjectId(selectedProjectKey);
  let associatedGroup = null;
  if (
    normalizedSelectedProjectId &&
    rsaProjectsPerGroup &&
    typeof rsaProjectsPerGroup === "object"
  ) {
    const direct =
      rsaProjectsPerGroup[normalizedSelectedProjectId] ??
      rsaProjectsPerGroup[`project_${normalizedSelectedProjectId}`];
    if (typeof direct === "string" || typeof direct === "number") {
      associatedGroup = String(direct);
    }

    if (!associatedGroup) {
      const entries = Object.entries(rsaProjectsPerGroup);
      for (const [groupName, assignment] of entries) {
        if (Array.isArray(assignment)) {
          if (
            assignment.some(
              (item) =>
                normalizeProjectId(item) === normalizedSelectedProjectId,
            )
          ) {
            associatedGroup = groupName;
            break;
          }
          continue;
        }
        if (typeof assignment === "string") {
          const tokens = assignment
            .split(/[,\s;|]+/)
            .map((t) => t.trim())
            .filter(Boolean);
          if (
            tokens.some(
              (token) =>
                normalizeProjectId(token) === normalizedSelectedProjectId,
            )
          ) {
            associatedGroup = groupName;
            break;
          }
          continue;
        }
        if (typeof assignment === "number") {
          if (normalizeProjectId(assignment) === normalizedSelectedProjectId) {
            associatedGroup = groupName;
            break;
          }
          continue;
        }
        if (assignment && typeof assignment === "object") {
          const projectKeys = Object.keys(assignment);
          if (
            projectKeys.some(
              (projectKey) =>
                normalizeProjectId(projectKey) === normalizedSelectedProjectId,
            )
          ) {
            associatedGroup = groupName;
            break;
          }
        }
      }
    }
  }

  let rawGroupMileage = null;
  if (
    associatedGroup &&
    rsaGroupsMileages &&
    typeof rsaGroupsMileages === "object"
  ) {
    rawGroupMileage = rsaGroupsMileages[associatedGroup];
    if (rawGroupMileage === undefined) {
      const matchKey = Object.keys(rsaGroupsMileages).find(
        (k) =>
          String(k).toLowerCase() === String(associatedGroup).toLowerCase(),
      );
      if (matchKey) {
        rawGroupMileage = rsaGroupsMileages[matchKey];
      }
    }
    if (rawGroupMileage && typeof rawGroupMileage === "object") {
      rawGroupMileage =
        rawGroupMileage.mileage ??
        rawGroupMileage.mileages ??
        rawGroupMileage.total_mileage ??
        rawGroupMileage.total ??
        rawGroupMileage.value ??
        null;
    }
  }
  const parsedGroupMileage = Number(rawGroupMileage);
  const totalGroupMileageDisplay =
    rawGroupMileage === null ||
    rawGroupMileage === undefined ||
    rawGroupMileage === ""
      ? "-"
      : Number.isFinite(parsedGroupMileage)
        ? String(Math.round(parsedGroupMileage))
        : String(rawGroupMileage);
  const isIsdSelected =
    Array.isArray(services) && services.includes("intersection_sight_distance");
  let rawGroupIsdManeuvers = null;
  if (
    associatedGroup &&
    rsaGroupsIsdManeuvers &&
    typeof rsaGroupsIsdManeuvers === "object"
  ) {
    rawGroupIsdManeuvers = rsaGroupsIsdManeuvers[associatedGroup];
    if (rawGroupIsdManeuvers === undefined) {
      const matchKey = Object.keys(rsaGroupsIsdManeuvers).find(
        (k) =>
          String(k).toLowerCase() === String(associatedGroup).toLowerCase(),
      );
      if (matchKey) {
        rawGroupIsdManeuvers = rsaGroupsIsdManeuvers[matchKey];
      }
    }
    if (rawGroupIsdManeuvers && typeof rawGroupIsdManeuvers === "object") {
      rawGroupIsdManeuvers =
        rawGroupIsdManeuvers.maneuvers ??
        rawGroupIsdManeuvers.isd_maneuvers ??
        rawGroupIsdManeuvers.total_maneuvers ??
        rawGroupIsdManeuvers.total ??
        rawGroupIsdManeuvers.value ??
        null;
    }
  }
  const parsedGroupIsdManeuvers = Number(rawGroupIsdManeuvers);
  const totalGroupIsdManeuversDisplay =
    rawGroupIsdManeuvers === null ||
    rawGroupIsdManeuvers === undefined ||
    rawGroupIsdManeuvers === ""
      ? "-"
      : Number.isFinite(parsedGroupIsdManeuvers)
        ? String(Math.round(parsedGroupIsdManeuvers))
        : String(rawGroupIsdManeuvers);
  const isdServices = analysisServices.filter(
    (service) => service === "intersection_sight_distance",
  );
  const mileageServices = analysisServices.filter(
    (service) => service !== "intersection_sight_distance",
  );

  const taskManeuversTotal = isdServices.reduce((sum, service) => {
    const parsed = Number(xmlIsdManeuverTotalsByService[service]);
    return Number.isFinite(parsed) ? sum + parsed : sum;
  }, 0);
  const taskManeuversLoading =
    hasSelectedProject &&
    isdServices.some((service) => {
      const folderCode = SERVICE_TO_RSA_FOLDER_CODE[service];
      const hasFolder = Boolean(folderCode && rsaFoldersByCode[folderCode]);
      if (!hasFolder) {
        return false;
      }
      const hasComputedValue = Object.prototype.hasOwnProperty.call(
        xmlIsdManeuverTotalsByService,
        service,
      );
      return !hasComputedValue && !!xmlLoadingByService[service];
    });

  const taskMileageTotalMiles = mileageServices.reduce((sum, service) => {
    const parsed = Number(xmlEndMileTotalsByService[service]);
    return Number.isFinite(parsed) ? sum + parsed : sum;
  }, 0);
  const taskMileageLoading =
    hasSelectedProject &&
    mileageServices.some((service) => {
      const folderCode = SERVICE_TO_RSA_FOLDER_CODE[service];
      const hasFolder = Boolean(folderCode && rsaFoldersByCode[folderCode]);
      if (!hasFolder) {
        return false;
      }
      const hasComputedValue = Object.prototype.hasOwnProperty.call(
        xmlEndMileTotalsByService,
        service,
      );
      return !hasComputedValue && !!xmlLoadingByService[service];
    });

  const toSafeInt = (value, fallback = 0) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return fallback;
    }
    return Math.trunc(parsed);
  };

  const usageMileageUsed = toSafeInt(rsaUsageLedgerState?.mileage?.used, 0);
  const usageManeuverUsed = toSafeInt(rsaUsageLedgerState?.maneuver?.used, 0);

  // For RSA model projects: sum mileage/maneuvers from checked route files, accumulating
  // per service — the same file assigned to N services contributes N times (mirrors how
  // the pipeline copies the file into each RSA_SSD_* / RSA_HCA_* etc. folder).
  let pendingMileageTokens;
  let pendingManeuverTokens;
  if (routesXmlFiles.length > 0) {
    let routesMileageTotal = 0;
    for (const service of mileageServices) {
      const taskOption = SERVICE_TO_TASK_OPTION[service];
      if (!taskOption) continue;
      for (const routeFile of routesXmlFiles) {
        const fileKey = normalizeDriveFolderId(routeFile.googleDriveId);
        if (!fileKey || !routesMatrixSelection?.[fileKey]?.[taskOption])
          continue;
        const nameKey = normalizeFileNameKey(routeFile.name);
        const miles = Number(
          routesPerFileMileage[nameKey] ?? routesPerFileMileage[routeFile.name],
        );
        if (Number.isFinite(miles)) routesMileageTotal += miles;
      }
    }
    let routesManeuversTotal = 0;
    for (const service of isdServices) {
      const taskOption = SERVICE_TO_TASK_OPTION[service];
      if (!taskOption) continue;
      for (const routeFile of routesXmlFiles) {
        const fileKey = normalizeDriveFolderId(routeFile.googleDriveId);
        if (!fileKey || !routesMatrixSelection?.[fileKey]?.[taskOption])
          continue;
        const nameKey = normalizeFileNameKey(routeFile.name);
        const maneuvers = Number(
          routesPerFileManeuvers[nameKey] ??
            routesPerFileManeuvers[routeFile.name],
        );
        if (Number.isFinite(maneuvers)) routesManeuversTotal += maneuvers;
      }
    }
    pendingMileageTokens =
      hasSelectedProject && !routesPerFileDataLoading
        ? Math.max(0, Math.ceil(routesMileageTotal))
        : null;
    pendingManeuverTokens =
      hasSelectedProject && !routesPerFileDataLoading
        ? Math.max(0, Math.ceil(routesManeuversTotal))
        : null;
  } else {
    pendingMileageTokens =
      hasSelectedProject && !taskMileageLoading
        ? Math.max(0, Math.ceil(taskMileageTotalMiles))
        : null;
    pendingManeuverTokens =
      hasSelectedProject && !taskManeuversLoading
        ? Math.max(0, Math.ceil(taskManeuversTotal))
        : null;
  }

  const hasMileageLimit = Number.isFinite(parsedGroupMileage);
  const hasManeuverLimit = Number.isFinite(parsedGroupIsdManeuvers);

  const mileageBaseLimit = hasMileageLimit
    ? Math.round(parsedGroupMileage)
    : null;
  const maneuverBaseLimit = hasManeuverLimit
    ? Math.round(parsedGroupIsdManeuvers)
    : null;

  const buildTokenBarModel = (
    baseLimit,
    usedTokens,
    pendingTokens,
    totalTokens = null,
  ) => {
    const resolvedBase = Number.isFinite(baseLimit)
      ? Math.max(0, toSafeInt(baseLimit, 0))
      : null;
    const used = Math.max(0, toSafeInt(usedTokens, 0));
    const pending = Number.isFinite(pendingTokens)
      ? Math.max(0, toSafeInt(pendingTokens, 0))
      : 0;
    const consumedTotal = used + pending;
    const requestedTotal = Number.isFinite(totalTokens)
      ? Math.max(0, toSafeInt(totalTokens, consumedTotal))
      : consumedTotal;
    const totalNeeded = Math.max(consumedTotal, requestedTotal);
    const scaleTotal = Math.max(1, resolvedBase ?? 0, totalNeeded);
    const overage =
      resolvedBase === null ? 0 : Math.max(0, totalNeeded - resolvedBase);
    const pendingSafe =
      resolvedBase === null
        ? pending
        : Math.max(0, Math.min(pending, Math.max(0, resolvedBase - used)));
    const pendingOverage = Math.max(0, pending - pendingSafe);
    const remaining = resolvedBase === null ? null : resolvedBase - totalNeeded;

    const usedWidthPct = Math.max(0, Math.min(100, (used / scaleTotal) * 100));
    const pendingSafeWidthPct = Math.max(
      0,
      Math.min(100, (pendingSafe / scaleTotal) * 100),
    );
    const pendingOverageWidthPct = Math.max(
      0,
      Math.min(100, (pendingOverage / scaleTotal) * 100),
    );
    const limitMarkerPct =
      resolvedBase === null
        ? null
        : Math.max(0, Math.min(100, (resolvedBase / scaleTotal) * 100));

    return {
      baseLimit: resolvedBase,
      used,
      pending,
      totalNeeded,
      scaleTotal,
      overage,
      remaining,
      pendingSafe,
      pendingOverage,
      usedWidthPct,
      pendingSafeWidthPct,
      pendingOverageWidthPct,
      limitMarkerPct,
      isOverage: overage > 0,
    };
  };

  const mileageModel = buildTokenBarModel(
    mileageBaseLimit,
    usageMileageUsed,
    pendingMileageTokens,
  );
  const maneuverModel = buildTokenBarModel(
    maneuverBaseLimit,
    usageManeuverUsed,
    pendingManeuverTokens,
  );

  const renderSummaryTokenBar = (model, metricLabel) => {
    const showLimitMarker = model.isOverage && model.limitMarkerPct !== null;
    return (
      <div
        className="summary-token-widget"
        aria-label={`${metricLabel} usage summary`}
      >
        <div className="summary-token-widget-header">
          <span className="summary-label">{metricLabel}:</span>
          <span
            className={`summary-token-status${model.isOverage ? " is-overage" : ""}`}
          >
            {model.isOverage
              ? `Burst +${model.overage}`
              : `Remaining: ${model.remaining === null ? "--" : model.remaining}`}
          </span>
        </div>

        <div className="summary-token-bar-shell">
          <div
            className={`summary-token-bar-track${model.isOverage ? " is-overage" : ""}`}
          >
            <div className="summary-token-bar-segments">
              {model.usedWidthPct > 0 && (
                <span
                  className="summary-token-segment summary-token-segment-used"
                  style={{ width: `${model.usedWidthPct}%` }}
                />
              )}
              {model.pendingSafeWidthPct > 0 && (
                <span
                  className={`summary-token-segment summary-token-segment-pending-safe${model.used > 1 ? " summary-token-segment-pending-safe-has-used" : ""}`}
                  style={{ width: `${model.pendingSafeWidthPct}%` }}
                />
              )}
              {model.pendingOverageWidthPct > 0 && (
                <span
                  className="summary-token-segment summary-token-segment-pending-overage"
                  style={{ width: `${model.pendingOverageWidthPct}%` }}
                />
              )}
            </div>
            {showLimitMarker && (
              <span
                className="summary-token-limit-marker"
                style={{ left: `${model.limitMarkerPct}%` }}
                aria-hidden
              />
            )}
          </div>
          <div className="summary-token-meta-row">
            <span className="summary-token-meta-item">
              <span
                className="summary-token-meta-dot summary-token-meta-dot-used"
                aria-hidden
              />
              Used {model.used}
            </span>
            <span className="summary-token-meta-item">
              <span
                className="summary-token-meta-dot summary-token-meta-dot-safe"
                aria-hidden
              />
              Required {model.pendingSafe}
            </span>
            {model.pendingOverage > 0 && (
              <span className="summary-token-meta-item summary-token-meta-item-overage">
                <span
                  className="summary-token-meta-dot summary-token-meta-dot-overage"
                  aria-hidden
                />
                Burst {model.pendingOverage}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!rsaRunMetricsRef) {
      return;
    }

    const mileageByService = {};
    const maneuversByService = {};
    const routeCountByService = {};

    for (const service of analysisServices) {
      const mileage = Number(xmlEndMileTotalsByService[service]);
      const maneuvers = Number(xmlIsdManeuverTotalsByService[service]);
      const routes = (xmlFilesByService[service] || []).length;

      mileageByService[service] = Number.isFinite(mileage) ? mileage : 0;
      maneuversByService[service] = Number.isFinite(maneuvers) ? maneuvers : 0;
      routeCountByService[service] = Number.isFinite(routes) ? routes : 0;
    }

    rsaRunMetricsRef.current = {
      selected_project_id: selectedProjectId ?? null,
      selected_services: [...analysisServices],
      mileage_by_service: mileageByService,
      maneuvers_by_service: maneuversByService,
      route_count_by_service: routeCountByService,
      mileage_total_miles: Number.isFinite(taskMileageTotalMiles)
        ? taskMileageTotalMiles
        : 0,
      maneuvers_total: Number.isFinite(taskManeuversTotal)
        ? taskManeuversTotal
        : 0,
      route_total: Object.values(routeCountByService).reduce(
        (sum, value) => sum + (Number(value) || 0),
        0,
      ),
      metrics_generated_at: new Date().toISOString(),
    };
  }, [
    rsaRunMetricsRef,
    selectedProjectId,
    analysisServices,
    xmlEndMileTotalsByService,
    xmlIsdManeuverTotalsByService,
    xmlFilesByService,
    taskMileageTotalMiles,
    taskManeuversTotal,
  ]);

  return (
    <div className={`tabs w35 ${isSetupTab ? "setup-panel" : ""}`}>
      <div className="summary-header">
        <h3>{summaryTitle}</h3>
      </div>
      <div className="summary-content">
        {isUploadTab ? (
          <>
            <div className="summary-section">
              <h4>Source / Routes</h4>
              <div className="summary-item">
                <span className="summary-label">Route files:</span>
                <span className="summary-value">{routesXmlFiles.length}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">DXF files:</span>
                <span className="summary-value">{routeDxfCount}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Latest upload:</span>
                <span className="summary-value">{latestRoutesUploadTime}</span>
              </div>
            </div>

            {isRsaModelProject && (
              <div className="summary-section">
                <h4>Source / Model</h4>
                <div className="summary-item">
                  <span className="summary-label">Folder status:</span>
                  <span className="summary-value">
                    {modelFolderId ? "Found" : "Missing"}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Model files:</span>
                  <span className="summary-value">{modelFiles.length}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Latest upload:</span>
                  <span className="summary-value">{latestModelUploadTime}</span>
                </div>
              </div>
            )}

            {!isRsaModelProject && (
              <div className="summary-section">
                <h4>Source</h4>
                <div className="summary-item">
                  <span className="summary-label">Folder status:</span>
                  <span className="summary-value">
                    {existingSourceFolderId ? "Found" : "Missing"}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">LAS/LAZ files:</span>
                  <span className="summary-value">
                    {existingSourceFiles.length}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Latest upload:</span>
                  <span className="summary-value">
                    {latestExistingSourceUploadTime}
                  </span>
                </div>
              </div>
            )}

            <div className="summary-section">
              <h4>Wizard Status</h4>
              <div className="summary-item">
                <span className="summary-label">Ready for setup:</span>
                <span className="summary-value">
                  {routesXmlFiles.length > 0 || existingSourceFiles.length > 0
                    ? "Yes"
                    : "No"}
                </span>
              </div>
            </div>
          </>
        ) : isSetupTab ? (
          /* Setup tab: unique content for map overlay & coordinate system */
          <>
            <div className="summary-section setup-summary-section">
              <div className="setup-summary-section-heading">
                <h4>Coordinate system (WKT)</h4>
                {wktValid && (
                  <i
                    className="fas fa-check setup-summary-section-check"
                    aria-hidden
                    title="Required data set"
                  />
                )}
              </div>
              <div className="summary-item">
                <span className="summary-label">CRS defined:</span>
                <span className="summary-value">
                  {(config.wkt || "").trim() ? "Yes" : "No"}
                </span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Validation status:</span>
                {wktStatusClass === "setup-summary-status-error" ? (
                  <span className="setup-summary-value-with-wkt-hint">
                    <span className={`summary-value ${wktStatusClass}`}>
                      {wktStatusText}
                    </span>
                    <button
                      type="button"
                      className="setup-wkt-requirements-info-btn"
                      onClick={() => onOpenWktRequirementsHelp?.()}
                      aria-label="Open supported coordinate system format (WKT) requirements"
                      title="Supported coordinate system format (WKT)"
                    >
                      <i
                        className="fa-solid fa-triangle-exclamation"
                        aria-hidden
                      />
                    </button>
                  </span>
                ) : (
                  <span className={`summary-value ${wktStatusClass}`}>
                    {wktStatusText}
                  </span>
                )}
              </div>
              {showWillWorkRow && (
                <div className="summary-item">
                  <span className="summary-label">Will work in map:</span>
                  <span className="setup-summary-value-with-wkt-hint">
                    <span className="summary-value setup-summary-status-error">
                      No
                    </span>
                    <button
                      type="button"
                      className="setup-wkt-requirements-info-btn"
                      onClick={() => onOpenWktRequirementsHelp?.()}
                      aria-label="Open supported coordinate system format (WKT) requirements"
                      title="Supported coordinate system format (WKT)"
                    >
                      <i
                        className="fa-solid fa-triangle-exclamation"
                        aria-hidden
                      />
                    </button>
                  </span>
                </div>
              )}
              {hasWkt && (
                <>
                  <div className="summary-item">
                    <span className="summary-label">Detected location:</span>
                    <span className="summary-value" title={wktLocationLabel}>
                      {wktLocationLabel}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Country:</span>
                    <span className="summary-value">{wktCountryLabel}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">State/Region:</span>
                    <span className="summary-value">{wktStateLabel}</span>
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          /* Analysis / Settings: existing Analysis Selection Summary */
          <>
            <div className="summary-section">
              <div className="summary-section-header">
                <h4>Selected Services:</h4>
                {analysisServices.length > 0 && (
                  <button
                    type="button"
                    className="summary-services-refresh-btn"
                    onClick={refreshAllServiceXmlFiles}
                    disabled={refreshingAllXml}
                    title="Refresh XML list for selected services"
                    aria-label="Refresh XML list for selected services"
                  >
                    <i
                      className={`fas fa-rotate-right ${refreshingAllXml ? "fa-spin" : ""}`}
                      aria-hidden
                    />
                  </button>
                )}
              </div>
              {services.length > 0 ? (
                <>
                  <div className="summary-list">
                    {services.map((service) => {
                      const serviceLabel = serviceMapping[service] || service;
                      const isAnalysisService = Boolean(
                        SERVICE_TO_RSA_FOLDER_CODE[service],
                      );
                      const isExpanded = !!expandedServiceTags[service];
                      const isLoadingXml = !!xmlLoadingByService[service];
                      const xmlFileNames = xmlFilesByService[service] || [];
                      const isIsdSvc =
                        service === "intersection_sight_distance";
                      const perFileMiles =
                        xmlPerFileMilesByService[service] || {};
                      const perFileManeuvers =
                        xmlPerFileManeuversByService[service] || {};
                      const xmlCountLabel = isLoadingXml
                        ? "Loading..."
                        : `${xmlFileNames.length} routes`;
                      const hasXmlLoadedForService =
                        Object.prototype.hasOwnProperty.call(
                          xmlFilesByService,
                          service,
                        );
                      const hasRoutes = routesXmlFiles.length > 0;
                      const showZeroRoutesPayloadWarning = Boolean(
                        hasRoutes &&
                        hasXmlLoadedForService &&
                        !isLoadingXml &&
                        xmlFileNames.length === 0,
                      );

                      if (!isAnalysisService) {
                        return (
                          <div key={service} className="summary-tag">
                            {serviceLabel}
                          </div>
                        );
                      }

                      const taskOption =
                        SERVICE_TO_TASK_OPTION[service] || null;
                      const assignedCount =
                        hasRoutes && taskOption
                          ? routesXmlFiles.filter((f) =>
                              Boolean(
                                routesMatrixSelection?.[
                                  normalizeDriveFolderId(f.googleDriveId)
                                ]?.[taskOption],
                              ),
                            ).length
                          : null;
                      const metaLabel =
                        hasRoutes && taskOption
                          ? routesXmlFiles.length > 0
                            ? `${assignedCount} / ${routesXmlFiles.length} routes`
                            : "No route files"
                          : xmlCountLabel;
                      const zeroWarning =
                        hasRoutes && taskOption
                          ? routesXmlFiles.length > 0 && assignedCount === 0
                          : showZeroRoutesPayloadWarning;

                      return (
                        <div
                          key={service}
                          className={`summary-tag summary-tag-expandable ${isExpanded ? "expanded" : ""}`}
                        >
                          <button
                            type="button"
                            className={`summary-tag-toggle${showRouteSelectionValidation && hasRoutes && taskOption ? " summary-tag-toggle-route-error" : ""}`}
                            onClick={() => toggleServiceTag(service)}
                            aria-expanded={isExpanded}
                          >
                            <i
                              className={`fas fa-chevron-right summary-tag-chevron ${isExpanded ? "expanded" : ""}`}
                              aria-hidden
                            />
                            <span className="summary-tag-title">
                              {serviceLabel}
                            </span>
                            <span
                              className={`summary-tag-meta${zeroWarning ? " summary-tag-meta-zero-routes" : ""}`}
                              title={
                                zeroWarning
                                  ? "No route files selected — this task will not run"
                                  : undefined
                              }
                            >
                              {metaLabel}
                            </span>
                          </button>
                          {isExpanded && (
                            <div className="summary-tag-files">
                              {hasRoutes && taskOption ? (
                                /* Interactive route file checkboxes */
                                routesXmlFiles.length === 0 ? (
                                  <div className="summary-tag-file summary-tag-file-empty">
                                    No route files in Source / Routes
                                  </div>
                                ) : (
                                  <>
                                    <label className="rsa-task-card-file-row rsa-task-card-file-row-bulk">
                                      <input
                                        type="checkbox"
                                        className="rsa-task-card-file-checkbox"
                                        checked={routesXmlFiles.every((f) => {
                                          const key = normalizeDriveFolderId(
                                            f.googleDriveId,
                                          );
                                          return (
                                            key &&
                                            routesMatrixSelection?.[key]?.[
                                              taskOption
                                            ]
                                          );
                                        })}
                                        onChange={(event) => {
                                          if (
                                            typeof onToggleRouteFile !==
                                            "function"
                                          )
                                            return;
                                          const shouldSelectAll =
                                            event.target.checked;
                                          routesXmlFiles.forEach((f) => {
                                            const key = normalizeDriveFolderId(
                                              f.googleDriveId,
                                            );
                                            const isSelected = Boolean(
                                              key &&
                                              routesMatrixSelection?.[key]?.[
                                                taskOption
                                              ],
                                            );
                                            if (
                                              key &&
                                              isSelected !== shouldSelectAll
                                            ) {
                                              onToggleRouteFile(
                                                key,
                                                taskOption,
                                              );
                                            }
                                          });
                                        }}
                                        disabled={routesMatrixSaving}
                                      />
                                      <span className="rsa-task-card-file-name rsa-task-card-file-name-bulk">
                                        Select all
                                      </span>
                                    </label>
                                    {routesXmlFiles.map((routeFile) => {
                                      const fileKey = normalizeDriveFolderId(
                                        routeFile.googleDriveId,
                                      );
                                      const isChecked = Boolean(
                                        routesMatrixSelection?.[fileKey]?.[
                                          taskOption
                                        ],
                                      );
                                      const routeNameKey = normalizeFileNameKey(
                                        routeFile.name,
                                      );
                                      const fileMiles = isIsdSvc
                                        ? null
                                        : (routesPerFileMileage[routeNameKey] ??
                                          routesPerFileMileage[routeFile.name]);
                                      const fileManeuvers = isIsdSvc
                                        ? (routesPerFileManeuvers[
                                            routeNameKey
                                          ] ??
                                          routesPerFileManeuvers[
                                            routeFile.name
                                          ])
                                        : null;
                                      const valueLabel = isIsdSvc
                                        ? fileManeuvers != null
                                          ? `${fileManeuvers} mnv`
                                          : null
                                        : fileMiles != null
                                          ? `${Number(fileMiles).toFixed(2)} mi`
                                          : null;
                                      return (
                                        <label
                                          key={`${service}-route-${routeFile.googleDriveId}`}
                                          className={`rsa-task-card-file-row${isChecked ? " checked" : ""}`}
                                          title={
                                            valueLabel
                                              ? `${routeFile.name} — ${valueLabel}`
                                              : routeFile.name
                                          }
                                        >
                                          <input
                                            type="checkbox"
                                            className="rsa-task-card-file-checkbox"
                                            checked={isChecked}
                                            onChange={() =>
                                              typeof onToggleRouteFile ===
                                                "function" &&
                                              onToggleRouteFile(
                                                fileKey,
                                                taskOption,
                                              )
                                            }
                                            disabled={routesMatrixSaving}
                                          />
                                          <span className="rsa-task-card-file-name">
                                            {routeFile.name}
                                          </span>
                                          {valueLabel && (
                                            <span className="summary-tag-file-miles">
                                              {valueLabel}
                                            </span>
                                          )}
                                        </label>
                                      );
                                    })}
                                  </>
                                )
                              ) : /* Non-RSA-model: original static file listing */
                              isLoadingXml ? (
                                <div className="summary-tag-file summary-tag-file-empty">
                                  Loading XML files...
                                </div>
                              ) : xmlFileNames.length > 0 ? (
                                xmlFileNames.map((xmlName) => {
                                  const fileMiles = isIsdSvc
                                    ? null
                                    : perFileMiles[xmlName];
                                  const fileManeuvers = isIsdSvc
                                    ? perFileManeuvers[xmlName]
                                    : null;
                                  const hasValue =
                                    fileMiles != null || fileManeuvers != null;
                                  const valueLabel = isIsdSvc
                                    ? fileManeuvers != null
                                      ? `${fileManeuvers} mnv`
                                      : null
                                    : fileMiles != null
                                      ? `${Number(fileMiles).toFixed(2)} mi`
                                      : null;
                                  return (
                                    <div
                                      key={`${service}-${xmlName}`}
                                      className="summary-tag-file"
                                      title={
                                        hasValue
                                          ? `${xmlName} — ${valueLabel}`
                                          : xmlName
                                      }
                                    >
                                      <span className="summary-tag-file-name">
                                        {xmlName}
                                      </span>
                                      {valueLabel && (
                                        <span className="summary-tag-file-miles">
                                          {valueLabel}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="summary-tag-file summary-tag-file-empty">
                                  No XML files found
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {showRouteSelectionValidation && !hasAnySelectedRoute && (
                    <div
                      className="summary-route-selection-warning"
                      role="alert"
                    >
                      Please select at least one route.
                    </div>
                  )}
                </>
              ) : (
                <span className="summary-empty">No services selected</span>
              )}
            </div>

            {/* <div className="summary-section">
              <h4>Cloud Storage Service</h4>
              {selectedStorageProvider ? (
                <div className="summary-storage-provider">
                  {renderStorageProviderButton()}
                </div>
              ) : (
                <span className="summary-empty">No storage provider selected</span>
              )}
            </div> */}

            {/* <div className="summary-section">
              <h4>Folder Selection</h4>
              {selectedDriveName && (
                <div className="summary-item">
                  <span className="summary-label">Shared Drive:</span>
                  <span className="summary-value">{selectedDriveName}</span>
                </div>
              )}
              {selectedFolders && selectedFolders.length > 0 ? (
                <div className="summary-folders-list">
                  <span className="summary-label">Folder{selectedFolders.length > 1 ? 's' : ''}:</span>
                  {selectedFolders.map((folder, index) => {
                    const path = folder.fullPath || folder.name;
                    const displayPath = path.length > 50 ? path.substring(0, 25) + '...' + path.substring(path.length - 20) : path;
                    return (
                      <div key={folder.id + (folder.name || '') + index} className="summary-item summary-folder-item">
                        <span className="summary-value" title={path}>{displayPath}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <span className="summary-empty">No folder selected</span>
              )}
            </div> */}

            {routesXmlFiles.length > 0 &&
              checkedRouteMatrixColumns.length > 0 && (
                <div className="summary-section summary-route-matrix-section">
                  <h4>Checked Routes Summary Table:</h4>
                  {checkedRouteMatrixRows.length > 0 ? (
                    <div className="summary-route-matrix-wrap">
                      <table className="summary-route-matrix-table">
                        <thead>
                          <tr>
                            <th className="summary-route-matrix-file-col">
                              Route File
                            </th>
                            {checkedRouteMatrixColumns.map((column) => (
                              <th
                                key={column.service}
                                className="summary-route-matrix-analysis-col"
                                title={column.label}
                              >
                                {column.shortLabel}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {checkedRouteMatrixRows.map((row) => (
                            <tr key={row.key}>
                              <td
                                className="summary-route-matrix-file-name"
                                title={row.name}
                              >
                                {getDisplayFileName(row.name)}
                              </td>
                              {row.assignments.map((assignment) => (
                                <td
                                  key={`${row.key}-${assignment.service}`}
                                  className={`summary-route-matrix-cell${assignment.checked ? " checked" : ""}`}
                                >
                                  {assignment.checked ? (
                                    <i
                                      className="fa-solid fa-check"
                                      aria-hidden
                                    />
                                  ) : (
                                    "—"
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <span className="summary-empty">
                      No checked route assignments yet
                    </span>
                  )}
                </div>
              )}

            <div className="summary-section summary-mileage-section">
              {/* Per-service accumulated breakdown */}
              {analysisServices.length > 0 && (
                <div className="summary-per-service-breakdown">
                  {routesXmlFiles.length > 0 ? (
                    /* Sum per-service from checked route files */
                    <>
                      {mileageServices.map((service) => {
                        const taskOption = SERVICE_TO_TASK_OPTION[service];
                        const serviceTotal = taskOption
                          ? routesXmlFiles.reduce((sum, f) => {
                              const fKey = normalizeDriveFolderId(
                                f.googleDriveId,
                              );
                              if (
                                !fKey ||
                                !routesMatrixSelection?.[fKey]?.[taskOption]
                              )
                                return sum;
                              const nameKey = normalizeFileNameKey(f.name);
                              const miles = Number(
                                routesPerFileMileage[nameKey] ??
                                  routesPerFileMileage[f.name],
                              );
                              return Number.isFinite(miles) ? sum + miles : sum;
                            }, 0)
                          : 0;
                        const label = serviceMapping[service] || service;
                        return (
                          <div
                            key={service}
                            className="summary-per-service-row"
                          >
                            <span
                              className="summary-per-service-label"
                              title={label}
                            >
                              {label}
                            </span>
                            <span className="summary-per-service-value">
                              {routesPerFileDataLoading ? (
                                <i
                                  className="fas fa-spinner fa-spin"
                                  aria-hidden
                                />
                              ) : (
                                `${serviceTotal.toFixed(2)} mi`
                              )}
                            </span>
                          </div>
                        );
                      })}
                      {isdServices.map((service) => {
                        const taskOption = SERVICE_TO_TASK_OPTION[service];
                        const serviceTotal = taskOption
                          ? routesXmlFiles.reduce((sum, f) => {
                              const fKey = normalizeDriveFolderId(
                                f.googleDriveId,
                              );
                              if (
                                !fKey ||
                                !routesMatrixSelection?.[fKey]?.[taskOption]
                              )
                                return sum;
                              const nameKey = normalizeFileNameKey(f.name);
                              const maneuvers = Number(
                                routesPerFileManeuvers[nameKey] ??
                                  routesPerFileManeuvers[f.name],
                              );
                              return Number.isFinite(maneuvers)
                                ? sum + maneuvers
                                : sum;
                            }, 0)
                          : 0;
                        const label = serviceMapping[service] || service;
                        return (
                          <div
                            key={service}
                            className="summary-per-service-row"
                          >
                            <span
                              className="summary-per-service-label"
                              title={label}
                            >
                              {label}
                            </span>
                            <span className="summary-per-service-value">
                              {routesPerFileDataLoading ? (
                                <i
                                  className="fas fa-spinner fa-spin"
                                  aria-hidden
                                />
                              ) : (
                                `${Number.isFinite(serviceTotal) ? serviceTotal : 0} mnv`
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    /* Standard project: totals from RSA_SSD_* etc. folders */
                    <>
                      {mileageServices.map((service) => {
                        const miles = Number(
                          xmlEndMileTotalsByService[service],
                        );
                        const isLoading = !!xmlLoadingByService[service];
                        const hasLoaded = Object.prototype.hasOwnProperty.call(
                          xmlEndMileTotalsByService,
                          service,
                        );
                        if (!hasLoaded && !isLoading) return null;
                        const label = serviceMapping[service] || service;
                        return (
                          <div
                            key={service}
                            className="summary-per-service-row"
                          >
                            <span
                              className="summary-per-service-label"
                              title={label}
                            >
                              {label}
                            </span>
                            <span className="summary-per-service-value">
                              {isLoading ? (
                                <i
                                  className="fas fa-spinner fa-spin"
                                  aria-hidden
                                />
                              ) : (
                                `${Number.isFinite(miles) ? miles.toFixed(2) : "0.00"} mi`
                              )}
                            </span>
                          </div>
                        );
                      })}
                      {isdServices.map((service) => {
                        const maneuvers = Number(
                          xmlIsdManeuverTotalsByService[service],
                        );
                        const isLoading = !!xmlLoadingByService[service];
                        const hasLoaded = Object.prototype.hasOwnProperty.call(
                          xmlIsdManeuverTotalsByService,
                          service,
                        );
                        if (!hasLoaded && !isLoading) return null;
                        const label = serviceMapping[service] || service;
                        return (
                          <div
                            key={service}
                            className="summary-per-service-row"
                          >
                            <span
                              className="summary-per-service-label"
                              title={label}
                            >
                              {label}
                            </span>
                            <span className="summary-per-service-value">
                              {isLoading ? (
                                <i
                                  className="fas fa-spinner fa-spin"
                                  aria-hidden
                                />
                              ) : (
                                `${Number.isFinite(maneuvers) ? maneuvers : 0} mnv`
                              )}
                            </span>
                          </div>
                        );
                      })}
                      {(mileageServices.some((s) =>
                        Object.prototype.hasOwnProperty.call(
                          xmlEndMileTotalsByService,
                          s,
                        ),
                      ) ||
                        isdServices.some((s) =>
                          Object.prototype.hasOwnProperty.call(
                            xmlIsdManeuverTotalsByService,
                            s,
                          ),
                        )) && (
                        <div className="summary-per-service-row summary-per-service-total">
                          <span className="summary-per-service-label">
                            Total
                          </span>
                          <span className="summary-per-service-value">
                            {taskMileageLoading ? (
                              <i
                                className="fas fa-spinner fa-spin"
                                aria-hidden
                              />
                            ) : (
                              `${Number.isFinite(taskMileageTotalMiles) ? taskMileageTotalMiles.toFixed(2) : "0.00"} mi`
                            )}
                            {isIsdSelected && !taskManeuversLoading && (
                              <span className="summary-per-service-maneuvers-total">
                                {` · ${Number.isFinite(taskManeuversTotal) ? taskManeuversTotal : 0} mnv`}
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="summary-item summary-maneuvers-item">
                <div
                  className={`summary-value summary-mileage-ratio ${maneuverModel.isOverage ? "summary-ratio-overage" : ""}`}
                >
                  {(routesXmlFiles.length > 0
                    ? routesPerFileDataLoading
                    : taskManeuversLoading) || rsaUsageLedgerLoading ? (
                    <>
                      <i
                        className="fas fa-spinner fa-spin summary-ratio-loading-spinner"
                        aria-hidden
                      />{" "}
                      Computing...
                    </>
                  ) : (
                    renderSummaryTokenBar(maneuverModel, "Maneuvers")
                  )}
                </div>
              </div>
              <div className="summary-item summary-mileage-item">
                <div
                  className={`summary-value summary-mileage-ratio ${mileageModel.isOverage ? "summary-ratio-overage" : ""}`}
                >
                  {(routesXmlFiles.length > 0
                    ? routesPerFileDataLoading
                    : taskMileageLoading) || rsaUsageLedgerLoading ? (
                    <>
                      <i
                        className="fas fa-spinner fa-spin summary-ratio-loading-spinner"
                        aria-hidden
                      />{" "}
                      Computing...
                    </>
                  ) : (
                    renderSummaryTokenBar(mileageModel, "Mileage")
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {isSetupTab && onSaveSetup && (
        <div className="summary-button-container">
          <button
            type="button"
            className="setup-wkt-btn setup-save-setup-btn"
            onClick={onSaveSetup}
            disabled={savingSetup || !setupSaveEnabled}
          >
            {savingSetup ? (
              <>
                <i
                  className="fas fa-spinner fa-spin setup-save-setup-spinner"
                  aria-hidden
                />
                Saving Setup...
              </>
            ) : (
              <>
                <i className="fas fa-floppy-disk" aria-hidden />
                Save Setup
              </>
            )}
          </button>
        </div>
      )}
      {!isSetupTab && !isUploadTab && (
        <div className="summary-button-container">
          <div className="summary-buttons-row">
            <button
              className={`toggle-btn-rsa-run${isRunInProgress ? " toggle-btn-rsa-run--loading" : ""}`}
              type="button"
              onClick={handleRunButtonClick}
              disabled={isRunInProgress || routesMatrixSaving}
            >
              {isRunInProgress ? (
                <>
                  <i className="fas fa-spinner fa-spin" aria-hidden />{" "}
                  Running...
                </>
              ) : (
                "Run Analysis"
              )}
            </button>
            <button
              className="toggle-btn-rsa-clear"
              type="button"
              onClick={onClear}
            >
              Clear
            </button>
          </div>
        </div>
      )}
      {mongoOverwriteWarning && (
        <div className="mongo-overwrite-modal-overlay">
          <div className="mongo-overwrite-modal">
            <h3 className="mongo-overwrite-modal-title">
              <i
                className="fa-solid fa-triangle-exclamation mongo-overwrite-modal-title-icon"
                aria-hidden="true"
              />
              Existing Data Will Be Overwritten
            </h3>
            <p className="mongo-overwrite-modal-body">
              The following MongoDB collections already contain data and will be
              overwritten by this run:
            </p>
            <ul className="mongo-overwrite-modal-list">
              {mongoOverwriteWarning.collections.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <div className="mongo-overwrite-modal-actions">
              <button
                className="mongo-overwrite-btn-cancel"
                onClick={() => setMongoOverwriteWarning(null)}
              >
                Cancel
              </button>
              <button
                className="mongo-overwrite-btn-confirm"
                onClick={handleMongoOverwriteConfirm}
              >
                Overwrite &amp; Run
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const RSA_ORTHO_TILES_STORAGE_KEY = "rsa_ortho_tiles";

// ─── IndexedDB cache helpers for ortho tile metadata ─────────────────────────
const ORTHO_IDB_NAME = "rsa_ortho_tiles_cache";
const ORTHO_IDB_STORE = "tiles";
const ORTHO_IDB_VERSION = 1;

function openOrthoCacheDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(ORTHO_IDB_NAME, ORTHO_IDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(ORTHO_IDB_STORE)) {
        db.createObjectStore(ORTHO_IDB_STORE, { keyPath: "projectId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function saveOrthoTilesToCache(projectId, tiles) {
  try {
    const db = await openOrthoCacheDb();
    const tx = db.transaction(ORTHO_IDB_STORE, "readwrite");
    tx.objectStore(ORTHO_IDB_STORE).put({ projectId, tiles, _ts: Date.now() });
    db.close();
  } catch (_) {
    /* IndexedDB unavailable — skip silently */
  }
}

async function loadOrthoTilesFromCache(projectId) {
  try {
    const db = await openOrthoCacheDb();
    return new Promise((resolve) => {
      const tx = db.transaction(ORTHO_IDB_STORE, "readonly");
      const req = tx.objectStore(ORTHO_IDB_STORE).get(projectId);
      req.onsuccess = () => {
        db.close();
        const data = req.result;
        if (!data || !Array.isArray(data.tiles)) resolve(null);
        else resolve(data.tiles);
      };
      req.onerror = () => {
        db.close();
        resolve(null);
      };
    });
  } catch (_) {
    return null;
  }
}
// ─────────────────────────────────────────────────────────────────────────────

const RSA_MODEL_ALLOWED_MAIN_TABS = [
  "upload",
  "setup",
  "routes",
  "dashboard",
  "deliverables",
];
const RSA_MAIN_TABS = [
  "upload",
  "setup",
  "routes",
  "paths",
  "analysis",
  "dashboard",
  "deliverables",
  "settings",
];
const normalizeRsaMainTab = (tab) => {
  const normalized = String(tab || "")
    .trim()
    .toLowerCase();
  return RSA_MAIN_TABS.includes(normalized) ? normalized : "upload";
};

const getRoutesXmlCacheKey = (projectId) => `rsaRoutesXmlFiles:${projectId}`;
const getRoutesUploadStatusCacheKey = (projectId) =>
  `rsaUploadStepStatus:${projectId}`;

const readRoutesXmlCache = (projectId) => {
  if (typeof window === "undefined" || !projectId) return [];
  try {
    const sessionRaw = window.sessionStorage.getItem(
      getRoutesUploadStatusCacheKey(projectId),
    );
    if (sessionRaw) {
      const sessionParsed = JSON.parse(sessionRaw);
      if (Array.isArray(sessionParsed?.routeFiles)) {
        return sessionParsed.routeFiles
          .map((item) => ({
            id: item?.id ?? "",
            googleDriveId: normalizeDriveFolderId(item?.googleDriveId),
            name: String(item?.name || "").trim(),
            parentDriveId: normalizeDriveFolderId(item?.parentDriveId),
          }))
          .filter((item) => item.googleDriveId && item.name);
      }
    }

    const raw = window.localStorage.getItem(getRoutesXmlCacheKey(projectId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => ({
        id: item?.id ?? "",
        googleDriveId: normalizeDriveFolderId(item?.googleDriveId),
        name: String(item?.name || "").trim(),
        parentDriveId: normalizeDriveFolderId(item?.parentDriveId),
      }))
      .filter((item) => item.googleDriveId && item.name);
  } catch (error) {
    return [];
  }
};

const writeRoutesXmlCache = (projectId, routeFiles) => {
  if (typeof window === "undefined" || !projectId) return;
  try {
    const safeRows = (Array.isArray(routeFiles) ? routeFiles : [])
      .map((item) => ({
        id: item?.id ?? "",
        googleDriveId: normalizeDriveFolderId(item?.googleDriveId),
        name: String(item?.name || "").trim(),
        parentDriveId: normalizeDriveFolderId(item?.parentDriveId),
      }))
      .filter((item) => item.googleDriveId && item.name);
    window.sessionStorage.setItem(
      getRoutesUploadStatusCacheKey(projectId),
      JSON.stringify({
        checkedAt: Date.now(),
        hasFiles: safeRows.length > 0,
        routeFiles: safeRows,
      }),
    );
    window.localStorage.setItem(
      getRoutesXmlCacheKey(projectId),
      JSON.stringify(safeRows),
    );
  } catch (error) {
    // ignore storage errors
  }
};

const hasRoutesUploadStatusCache = (projectId) => {
  if (typeof window === "undefined" || !projectId) return false;
  try {
    return Boolean(
      window.sessionStorage.getItem(getRoutesUploadStatusCacheKey(projectId)),
    );
  } catch (error) {
    return false;
  }
};

function PayloadForm({
  projectId,
  projectName = "",
  preloadedRsaSetup,
  preloadedRsaSetupFolderId,
  preloadedRsaSetupFolderName,
  preloadRsaSetupLoading = false,
  projectType,
  initialActiveMainTab = "upload",
  onActiveMainTabChange,
  onGoToFiles,
}) {
  // Cached project list and dropdown options
  const [allProjects, setAllProjects] = useState([]);
  const [searchResults, setSearchResults] = useState([]);

  // State for selected project from dropdown
  const [selectedProjectId, setSelectedProjectId] = useState(projectId ?? null);
  // Store an array of selected folder objects (order of selection)
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [selectedDriveName, setSelectedDriveName] = useState("");
  const [roadDetectionCategory, setRoadDetectionCategory] = useState("");
  const [lidarType, setLidarType] = useState("");
  const [horizontalData, setHorizontalData] = useState(null);
  const [EmbankmentData, setEmbankmentData] = useState(null); // State for Embankment data
  const [manualLaneCreationActive, setManualLaneCreationActive] =
    useState(false);
  const [activeManualLaneId, setActiveManualLaneId] = useState(null);
  const [fitToPathData, setFitToPathData] = useState(null); // when set, map fits view to this path then clears
  const [fitToBounds, setFitToBounds] = useState(null); // when set, map fits view to this bounds (e.g. LAS extent) then clears
  const [pathsPanelMode, setPathsPanelMode] = useState("path-creator"); // 'path-creator' | 'intersection' | 'maps' â€“ controls right-side paths panel content
  const [overlayOpacity, setOverlayOpacity] = useState(0.9); // PNG overlay opacity 0â€“1
  const [overlayBrightness, setOverlayBrightness] = useState(1); // CSS filter brightness, 0.5â€“2
  const [overlayContrast, setOverlayContrast] = useState(1); // CSS filter contrast, 0.5â€“2
  // Map overlay coordinate system: 'global' (lat/lon) or 'local' (x,y + WKT for production)
  const [mapOverlayCoordinateMode, setMapOverlayCoordinateMode] =
    useState("local");
  const [mapOverlayBoundsGlobal, setMapOverlayBoundsGlobal] = useState(() => ({
    ...DEFAULT_GLOBAL_BOUNDS,
  }));
  const [mapOverlayLocalConfig, setMapOverlayLocalConfig] = useState(() => ({
    extent: { ...DEFAULT_LOCAL_CONFIG.extent },
    originXY: { ...DEFAULT_LOCAL_CONFIG.originXY },
    originLatLng: { ...DEFAULT_LOCAL_CONFIG.originLatLng },
    scaleLatPerUnit: DEFAULT_LOCAL_CONFIG.scaleLatPerUnit,
    scaleLngPerUnit: DEFAULT_LOCAL_CONFIG.scaleLngPerUnit,
    wkt: DEFAULT_LOCAL_CONFIG.wkt,
    state: DEFAULT_LOCAL_CONFIG.state,
    routeNumber: DEFAULT_LOCAL_CONFIG.routeNumber,
    pathNameSegment: DEFAULT_LOCAL_CONFIG.pathNameSegment,
  }));
  const [wktInputLocked, setWktInputLocked] = useState(false); // lock WKT textarea to prevent accidental changes
  const [wktValidation, setWktValidation] = useState(
    createIdleWktValidationState,
  );
  const [wktRequirementsHelpModalOpen, setWktRequirementsHelpModalOpen] =
    useState(false);
  const wktValidationRequestRef = useRef(0);
  const [intersectionDrawingMode, setIntersectionDrawingMode] = useState(null); // null | 'marker' | 'polyline' | 'polyline-curved' | 'measure-distance' | 'polygon' | 'circle' | 'rectangle'
  const [intersectionOverlays, setIntersectionOverlays] = useState([]); // { id, type, coordinates }[]
  // Pending ISD creation draft — set by the form, consumed by the placement
  // click-drag, cleared when the block is committed or the user cancels.
  const [pendingIsdDraft, setPendingIsdDraft] = useState(null); // { intersectionName, maneuverIndex, isdCase, rsaPathId, secondaryRoute } | null
  // Opposite-block picker — when active, the next mousedown→mouseup drag
  // produces a full ISD block (cyan preview) and resolves the stored
  // callback with `{ insertionPoint, azimuth, arrowLengthFeet }`. Used by
  // F-case ISDs.
  const [oppositePointPickActive, setOppositePointPickActive] = useState(false);
  const oppositePointPickRef = useRef(null);
  // Form draft for the opposite block — lifted here so the map can render
  // the cyan block while the user is still filling the creation form.
  // Cleared when the form OK merges it into the intersection or on cancel.
  const [oppositeBlockDraft, setOppositeBlockDraft] = useState(null); // { insertionPoint, azimuth, arrowLengthFeet } | null
  const startOppositePointPick = useCallback((onPick) => {
    oppositePointPickRef.current = typeof onPick === "function" ? onPick : null;
    setOppositePointPickActive(true);
  }, []);
  const cancelOppositePointPick = useCallback(() => {
    oppositePointPickRef.current = null;
    setOppositePointPickActive(false);
  }, []);
  const handleOppositeBlockPicked = useCallback((block) => {
    if (oppositePointPickRef.current && block) {
      oppositePointPickRef.current(block);
    }
    oppositePointPickRef.current = null;
    setOppositePointPickActive(false);
  }, []);
  // Lifted lane/path picker selection — shared between the Intersection panel
  // (picker UI) and PathSelectorMap (so the selected target can be drawn
  // as a highlight overlay on top of the map).
  const [selectedRsaTargetId, setSelectedRsaTargetId] = useState(null);
  const [curvePoints, setCurvePoints] = useState([]); // points for curved line in progress
  const [measurePoints, setMeasurePoints] = useState([]); // points for distance measurement in progress
  const handleIntersectionDrawingModeChange = useCallback((nextMode) => {
    setIntersectionDrawingMode(nextMode);
    if (nextMode !== "polyline-curved") setCurvePoints([]);
    if (nextMode !== "measure-distance") setMeasurePoints([]);
  }, []);
  const [curvedLineEditPointCount, setCurvedLineEditPointCount] = useState(5); // 3â€“10 edit points along curve
  // Storage provider state
  const [selectedStorageProvider, setSelectedStorageProvider] = useState(
    "system_google_drive",
  );
  const [storageConnections, setStorageConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [useSystemGoogleDrive, setUseSystemGoogleDrive] = useState(true);

  // Only existing projects are supported
  const [existingProjectName, setExistingProjectName] = useState("");
  const [rsaGroupsMileages, setRsaGroupsMileages] = useState({});
  const [rsaProjectsPerGroup, setRsaProjectsPerGroup] = useState({});
  const [rsaGroupsIsdManeuvers, setRsaGroupsIsdManeuvers] = useState({});
  const [rsaUsageLedgerState, setRsaUsageLedgerState] = useState(null);
  const [rsaUsageLedgerLoading, setRsaUsageLedgerLoading] = useState(false);

  // State for the analysis types and services (toggle buttons)
  const [analysisTypes, setAnalysisTypes] = useState([]);
  const [services, setServices] = useState([]);
  // State to track selected task buttons and their analysis_template_ids
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [hasSubmittedRsaRun, setHasSubmittedRsaRun] = useState(false);
  const [visibleTaskOptions, setVisibleTaskOptions] = useState([]);
  const [isLoadingVisibleTaskOptions, setIsLoadingVisibleTaskOptions] =
    useState(false);
  // Instead of a global loading state, we now track each payload request.
  const [requests, setRequests] = useState([]);
  // Additional state for our new custom radio button group (e.g. Time selection)
  const [timeOption, setTimeOption] = useState("");

  // --- State for Folder Tree Popup ---
  const [showFolderTreePopup, setShowFolderTreePopup] = useState(false);
  const [mapOverlaySelectedFolders, setMapOverlaySelectedFolders] = useState(
    [],
  );
  const [projectSetupFolderId, setProjectSetupFolderId] = useState(null);
  const effectiveSetupFolderId = useMemo(() => {
    const folders = mapOverlaySelectedFolders || [];
    const selectedFolderId =
      folders[0]?.id ?? folders[0]?.folder_id ?? folders[0]?.folderId;
    return (
      selectedFolderId ??
      preloadedRsaSetupFolderId ??
      projectSetupFolderId ??
      null
    );
  }, [
    mapOverlaySelectedFolders,
    preloadedRsaSetupFolderId,
    projectSetupFolderId,
  ]);
  const [savingLasSeparationPath, setSavingLasSeparationPath] = useState(false);
  const [xmlOverwriteWarning, setXmlOverwriteWarning] = useState(null);
  // { filenames: string[], resolve: ('overwrite'|'cancel') => void }
  const [creatingOrthoMapsByPath, setCreatingOrthoMapsByPath] = useState({}); // { [pathId]: true|false }
  const [orthoProgressByPath, setOrthoProgressByPath] = useState({}); // { [pathId]: { progress: 0-100, message: '' } }
  // jobIds currently being polled in THIS tab — prevents the reconnect
  // effect (a later task) from double-starting a poll loop already running here.
  const trackedOrthoJobIdsRef = useRef(new Set());
  const [lasBoundingBoxes, setLasBoundingBoxes] = useState([]); // [{ name, south, north, west, east }] in lat/lon for map overlay
  const [selectedLasBoundingBoxIndex, setSelectedLasBoundingBoxIndex] =
    useState(0);
  const [loadingLasBounds, setLoadingLasBounds] = useState(false);
  const [orthoTiles, setOrthoTiles] = useState([]); // [{ name, pngFileId, bounds: { south, north, west, east }, quad: [{lat,lng} x4] }] for map overlay
  const [loadingOrthoTiles, setLoadingOrthoTiles] = useState(false);
  const [maps2000ByPath, setMaps2000ByPath] = useState({}); // { [pathId]: true|false|null }
  const [loadingMaps2000Check, setLoadingMaps2000Check] = useState(false);
  const [imageOverlaysLoadedCount, setImageOverlaysLoadedCount] = useState(0);
  const [imageOverlaysTotal, setImageOverlaysTotal] = useState(0);
  // Holds an "Ortho maps created" notification queued by createOrthoMaps until
  // the rendering layer has actually placed the tiles on the map. Without this
  // gate the toast appears the instant the backend reports completion, which
  // can be several seconds before the images visibly finish loading. Shape:
  // { message, expectedTileCount, startTime } | null.
  const [pendingOrthoSuccessMessage, setPendingOrthoSuccessMessage] =
    useState(null);
  const [lasBoundingBoxesVisible, setLasBoundingBoxesVisible] = useState(true);
  const [orthoTilesVisible, setOrthoTilesVisible] = useState(true);
  // Auto-trace lane: when active, the next two map clicks are the trace start
  // point and a direction point. firstPoint holds the start after the 1st click.
  // appendLaneId continues an existing lane (re-seed).
  const [traceSeed, setTraceSeed] = useState({
    active: false,
    pathId: null,
    appendLaneId: null,
    firstPoint: null,
  });
  const [laneTraceProgress, setLaneTraceProgress] = useState({}); // { [pathId]: { progress, message } }
  // Bridge mode lets the user manually anchor waypoints through a gap/intersection
  // where the auto-trace lost the lane marking, then resume auto-tracing past it.
  const [bridge, setBridge] = useState({
    active: false,
    laneId: null,
    pathId: null,
    entryMode: null, // 'lost_line' | 'lost_line_post_ai' | 'manual'
  });
  const trackedLaneTraceJobIdsRef = useRef(new Set());
  const [groupSize, setGroupSize] = useState(25);

  // --- State for custom dropdown ---
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const dropdownRef = useRef(null);

  const [projectFilesSnapshot, setProjectFilesSnapshot] = useState([]);

  // --- State for clear confirmation popup ---
  const [showClearPopup, setShowClearPopup] = useState(false);
  const [taskInfoModal, setTaskInfoModal] = useState({
    isOpen: false,
    title: "",
    image: null,
  });
  const effectiveProjectId = selectedProjectId ?? projectId ?? null;

  // ─── Source-storage flag (drive | s3) — gates the upload strategy ──────────
  // S3-source projects skip the drive-direct web-worker path; all large uploads
  // go through piplineuploadtemp -> project bucket via the backend's
  // complete-large S3-source branch.
  const [sourceStorage, setSourceStorage] = useState("drive");
  useEffect(() => {
    if (!effectiveProjectId) return;
    let cancelled = false;
    const url = API_BASE_URL
      ? `${API_BASE_URL}/api/projects/${effectiveProjectId}/source/s3-list`
      : `/api/projects/${effectiveProjectId}/source/s3-list`;
    fetch(url, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const v = (data.sourceStorage || "drive").toLowerCase();
        setSourceStorage(v === "s3" ? "s3" : "drive");
      })
      .catch(() => {
        /* default stays 'drive' */
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveProjectId]);

  // ─── Multi-path / multi-lane system ─────────────────────────────────────────
  // pauseDestructiveReloads: while the user is placing manual-lane points
  // OR editing speeds OR editing lane waypoints, a 409 from a save race must
  // NOT trigger a RESET+HYDRATE reload — that wipes their in-progress work
  // and silently flips them out of the active edit mode (e.g. kicking the
  // speed page to the next step). The hook will just adopt the server's new
  // version and let the next save retry. Function form so the closure can
  // read rsaPathsHook's own state without TDZ on `rsaPathsHook`.
  const rsaPathsHook = useRsaPaths(effectiveProjectId, {
    pauseDestructiveReloads: () =>
      manualLaneCreationActive ||
      Boolean(rsaPathsHook?.speedEditMode) ||
      Boolean(rsaPathsHook?.laneEditMode),
  });

  // Switching to intersection mode auto-exits any path-creator edit mode
  // (lane edit / speed edit) — treats the in-progress edit as "done" so
  // the intersection's drawing-tool overlay can appear at the top of the
  // map without the lane-edit-hint conflict.
  useEffect(() => {
    if (pathsPanelMode !== "intersection") return;
    if (rsaPathsHook?.laneEditMode) rsaPathsHook.actions?.exitLaneEditMode?.();
    if (rsaPathsHook?.speedEditMode)
      rsaPathsHook.actions?.exitSpeedEditMode?.();
  }, [
    pathsPanelMode,
    rsaPathsHook?.laneEditMode,
    rsaPathsHook?.speedEditMode,
    rsaPathsHook?.actions,
  ]);

  const laneEditHistory = useLaneEditHistory(rsaPathsHook.laneEditMode);
  // Initialize undo/redo history when entering lane edit mode
  const laneEditModeRef = useRef(false);
  useEffect(() => {
    if (rsaPathsHook.laneEditMode && !laneEditModeRef.current) {
      const lane = rsaPathsHook.lanes.find(
        (l) => l.id === rsaPathsHook.activeLaneId,
      );
      if (lane?.waypoints) laneEditHistory.initialize(lane.waypoints);
    }
    laneEditModeRef.current = rsaPathsHook.laneEditMode;
  }, [
    rsaPathsHook.laneEditMode,
    rsaPathsHook.lanes,
    rsaPathsHook.activeLaneId,
    laneEditHistory,
  ]);
  const activeRsaPath = useMemo(
    () =>
      rsaPathsHook.paths.find((p) => p.id === rsaPathsHook.activePathId) ??
      rsaPathsHook.paths[0] ??
      null,
    [rsaPathsHook.paths, rsaPathsHook.activePathId],
  );
  const activeRsaLane = useMemo(
    () =>
      rsaPathsHook.lanes.find((l) => l.id === rsaPathsHook.activeLaneId) ??
      null,
    [rsaPathsHook.lanes, rsaPathsHook.activeLaneId],
  );
  const activeRsaEntityType =
    rsaPathsHook.activeEntityType === "lane" && activeRsaLane ? "lane" : "path";
  const activeRsaEntity =
    activeRsaEntityType === "lane" ? activeRsaLane : activeRsaPath;
  const activeRsaEntityId = activeRsaEntity?.id ?? null;
  const activeRsaWaypoints = Array.isArray(activeRsaEntity?.waypoints)
    ? activeRsaEntity.waypoints
    : EMPTY_WAYPOINTS;
  const activeRsaPathData = activeRsaEntity?.pathData ?? null;

  // Refs that always hold the latest active path/lane state.
  // Used by pathSetWaypoints so stale closures (e.g. geocoding callbacks)
  // always read current data instead of an outdated capture.
  const activeRsaPathRef = useRef(activeRsaPath);
  const activeRsaLaneRef = useRef(activeRsaLane);
  const activeRsaEntityTypeRef = useRef(activeRsaEntityType);
  useEffect(() => {
    activeRsaPathRef.current = activeRsaPath;
  }, [activeRsaPath]);
  useEffect(() => {
    activeRsaLaneRef.current = activeRsaLane;
  }, [activeRsaLane]);
  useEffect(() => {
    activeRsaEntityTypeRef.current = activeRsaEntityType;
  }, [activeRsaEntityType]);
  const activeRsaColorIndex = activeRsaEntity?.colorIndex ?? 0;
  const activeRsaColor =
    SEGMENT_COLORS[activeRsaColorIndex % SEGMENT_COLORS.length];
  const rsaLegacySegments = useMemo(() => {
    const paths = rsaPathsHook.paths.map((path, idx) => ({
      ...path,
      type: "path",
      entityType: "path",
      entityId: path.id,
      label: path.label || `Route${idx + 1}`,
    }));
    const lanes = rsaPathsHook.lanes.map((lane, idx) => ({
      ...lane,
      type: "lane",
      entityType: "lane",
      entityId: lane.id,
      manualLane: lane.creationMode === "manual",
      label: lane.label || `Lane ${idx + 1}`,
    }));
    return [...paths, ...lanes];
  }, [rsaPathsHook.paths, rsaPathsHook.lanes]);
  const activeRsaLegacyIndex = useMemo(() => {
    if (!activeRsaEntityId) return 0;
    const idx = rsaLegacySegments.findIndex(
      (seg) => seg.entityId === activeRsaEntityId,
    );
    return idx >= 0 ? idx : 0;
  }, [rsaLegacySegments, activeRsaEntityId]);
  const rsaSegmentVisibility = useMemo(
    () => rsaLegacySegments.map((seg) => seg.visible !== false),
    [rsaLegacySegments],
  );

  const normalizedProjectType = String(projectType || "")
    .trim()
    .toLowerCase();
  const isRsaModelProject = normalizedProjectType === "rsa-model";
  const rsaModelRouteInfoRef = useRef({});
  const rsaRunMetricsRef = useRef({});
  const allowedTaskOptions = isRsaModelProject
    ? RSA_MODEL_TASK_OPTIONS
    : TASK_OPTIONS;
  const analysisTemplateIdMapping = isRsaModelProject
    ? RSA_MODEL_ANALYSIS_TEMPLATE_ID_MAPPING
    : DEFAULT_ANALYSIS_TEMPLATE_ID_MAPPING;

  useEffect(() => {
    if (
      projectId !== null &&
      projectId !== undefined &&
      String(projectId) !== ""
    ) {
      setSelectedProjectId(projectId);
    }
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;

    const loadVisibleTasksFromProjectFiles = async () => {
      if (!effectiveProjectId) {
        setVisibleTaskOptions([]);
        setIsLoadingVisibleTaskOptions(false);
        setProjectFilesSnapshot([]);
        return;
      }

      setIsLoadingVisibleTaskOptions(true);
      setVisibleTaskOptions([]);
      // Recheck once project loading completes to avoid stale folder snapshots.
      if (preloadRsaSetupLoading) {
        return;
      }

      try {
        const isProductionDomain =
          window.location.hostname.includes("123bimlive.com");
        const baseUrl =
          ENVIRONMENT === "production" || isProductionDomain
            ? ""
            : API_BASE_URL;
        const cacheBust = `_ts=${Date.now()}`;
        const url = baseUrl
          ? `${API_BASE_URL}/api/projects/${effectiveProjectId}/files?${cacheBust}`
          : `/api/projects/${effectiveProjectId}/files?${cacheBust}`;
        const response = await axios.get(url);
        const files = response.data?.files || [];
        if (!cancelled) {
          setProjectFilesSnapshot(Array.isArray(files) ? files : []);
        }
        const {
          hasSourceFolder,
          taskOptions: dbTaskOptions,
          sourceGoogleDriveIds,
        } = deriveTaskMetadataFromProjectFiles(files);

        if (!hasSourceFolder) {
          if (!cancelled) {
            setVisibleTaskOptions(allowedTaskOptions);
            setIsLoadingVisibleTaskOptions(false);
          }
          return;
        }

        const detectedTasks = new Set(
          Array.isArray(dbTaskOptions) ? dbTaskOptions : [],
        );

        if (
          Array.isArray(sourceGoogleDriveIds) &&
          sourceGoogleDriveIds.length > 0
        ) {
          const driveChildrenRequests = sourceGoogleDriveIds.map(
            (sourceFolderId) => {
              const childrenUrl = baseUrl
                ? `${API_BASE_URL}/api/projects/${effectiveProjectId}/google-drive-contents?folder_id=${encodeURIComponent(sourceFolderId)}&${cacheBust}`
                : `/api/projects/${effectiveProjectId}/google-drive-contents?folder_id=${encodeURIComponent(sourceFolderId)}&${cacheBust}`;
              return axios.get(childrenUrl).catch((err) => {
                console.warn(
                  "Failed to fetch Source children from Google Drive:",
                  sourceFolderId,
                  err,
                );
                return { data: { items: [] } };
              });
            },
          );

          const driveChildrenResponses = await Promise.all(
            driveChildrenRequests,
          );
          for (const driveResponse of driveChildrenResponses) {
            const items = driveResponse?.data?.items || [];
            for (const item of items) {
              if (!isFolderItem(item)) {
                continue;
              }
              const taskOption = mapFolderCodeToTaskOption(item?.name);
              if (taskOption) {
                detectedTasks.add(taskOption);
              }
            }
          }
        }

        const derivedTaskOptions = allowedTaskOptions.filter((task) =>
          detectedTasks.has(task),
        );
        if (cancelled) {
          return;
        }
        setVisibleTaskOptions(derivedTaskOptions);
        setIsLoadingVisibleTaskOptions(false);
      } catch (error) {
        if (!cancelled) {
          console.error(
            "Failed to derive RSA tasks from project files:",
            error,
          );
          setVisibleTaskOptions(allowedTaskOptions);
          setIsLoadingVisibleTaskOptions(false);
        }
      }
    };

    loadVisibleTasksFromProjectFiles();
    return () => {
      cancelled = true;
    };
  }, [
    effectiveProjectId,
    preloadRsaSetupLoading,
    allowedTaskOptions,
    isRsaModelProject,
  ]);

  useEffect(() => {
    const visibleTasksSet = new Set(visibleTaskOptions);
    const allTaskServiceValues = new Set(Object.values(TASK_OPTION_TO_SERVICE));
    const allowedTaskServiceValues = new Set(
      visibleTaskOptions
        .map((task) => TASK_OPTION_TO_SERVICE[task])
        .filter(Boolean),
    );

    setSelectedTasks((prev) =>
      prev.filter((task) => visibleTasksSet.has(task)),
    );
    setServices((prev) =>
      prev.filter(
        (service) =>
          !allTaskServiceValues.has(service) ||
          allowedTaskServiceValues.has(service),
      ),
    );
  }, [visibleTaskOptions]);

  const openTaskInfoModal = useCallback((taskType) => {
    const infoImage = TASK_INFO_IMAGE_CONFIG[taskType];
    if (!infoImage) return;
    setTaskInfoModal({ isOpen: true, title: taskType, image: infoImage });
  }, []);

  const closeTaskInfoModal = useCallback(() => {
    setTaskInfoModal({ isOpen: false, title: "", image: null });
  }, []);

  // --- State for Save Setup overwrite confirmation modal ---
  const [showSaveSetupOverwriteModal, setShowSaveSetupOverwriteModal] =
    useState(false);
  const [savingSetup, setSavingSetup] = useState(false);

  // --- Slide-down notification (replaces alert) ---
  const [notification, setNotification] = useState({
    message: null,
    leaving: false,
  });
  const notificationTimeoutRef = useRef(null);
  const showNotification = useCallback((message, durationMs = 4000) => {
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
    setNotification({ message, leaving: false });
    const displayDuration = Number.isFinite(Number(durationMs))
      ? Math.max(0, Number(durationMs))
      : 4000;
    notificationTimeoutRef.current = setTimeout(() => {
      setNotification((prev) => ({ ...prev, leaving: true }));
      notificationTimeoutRef.current = setTimeout(() => {
        setNotification({ message: null, leaving: false });
        notificationTimeoutRef.current = null;
      }, 300);
    }, displayDuration);
  }, []);
  useEffect(
    () => () => {
      if (notificationTimeoutRef.current)
        clearTimeout(notificationTimeoutRef.current);
    },
    [],
  );

  const validateWktInput = useCallback(
    async (rawWkt, { silent = true } = {}) => {
      const trimmedRaw = String(rawWkt ?? "").trim();
      const wkt = normalizeWktInput(trimmedRaw);
      if (!wkt) {
        wktValidationRequestRef.current += 1;
        setWktValidation(createIdleWktValidationState());
        return null;
      }

      const requestId = wktValidationRequestRef.current + 1;
      wktValidationRequestRef.current = requestId;
      setWktValidation((prev) => ({
        ...prev,
        status: "checking",
        checkedWkt: wkt,
        message: "",
      }));

      try {
        const isProductionDomain =
          window.location.hostname.includes("123bimlive.com");
        const baseUrl =
          ENVIRONMENT === "production" || isProductionDomain
            ? ""
            : API_BASE_URL;
        const url = baseUrl
          ? `${API_BASE_URL}/api/wkt/validate`
          : "/api/wkt/validate";
        const response = await axios.post(url, { wkt }, { timeout: 15000 });
        if (requestId !== wktValidationRequestRef.current) {
          return null;
        }

        const payload = response?.data || {};
        const nextValidation = {
          status: "done",
          checkedWkt: wkt,
          valid: Boolean(payload.valid),
          willWork: Boolean(payload.willWork),
          message: payload.message || "",
          location: payload.location || null,
          crs: payload.crs || null,
          inputType: payload.inputType || "unknown",
        };
        setWktValidation(nextValidation);

        // Replace JSON-escaped paste (e.g. PROJCS[\"a\"]) with valid WKT in the textarea after successful parse
        if (nextValidation.valid && wkt !== trimmedRaw) {
          setMapOverlayLocalConfig((c) =>
            String(c.wkt ?? "").trim() === trimmedRaw ? { ...c, wkt } : c,
          );
        }

        if (!silent && !nextValidation.valid) {
          showNotification(nextValidation.message || "WKT is invalid.");
        } else if (
          !silent &&
          nextValidation.valid &&
          !nextValidation.willWork
        ) {
          showNotification(
            nextValidation.message ||
              "WKT is valid but not compatible with map conversion.",
          );
        }

        return nextValidation;
      } catch (err) {
        if (requestId !== wktValidationRequestRef.current) {
          return null;
        }
        const msg =
          err.response?.data?.error || err.message || "Failed to validate WKT.";
        const failedValidation = {
          status: "error",
          checkedWkt: wkt,
          valid: false,
          willWork: false,
          message: msg,
          location: null,
          crs: null,
          inputType: "unknown",
        };
        setWktValidation(failedValidation);
        if (!silent) {
          showNotification(`WKT validation failed: ${msg}`);
        }
        return failedValidation;
      }
    },
    [showNotification, setMapOverlayLocalConfig],
  );

  useEffect(() => {
    const wktRaw = (mapOverlayLocalConfig?.wkt ?? "").trim();
    const wkt = normalizeWktInput(wktRaw);
    if (!wkt) {
      wktValidationRequestRef.current += 1;
      setWktValidation(createIdleWktValidationState());
      return;
    }

    if (
      wkt === wktValidation.checkedWkt &&
      (wktValidation.status === "checking" || wktValidation.status === "done")
    ) {
      return;
    }

    const timeoutId = setTimeout(() => {
      validateWktInput(mapOverlayLocalConfig?.wkt ?? "", { silent: true });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    mapOverlayLocalConfig?.wkt,
    wktValidation.checkedWkt,
    wktValidation.status,
    validateWktInput,
  ]);

  // Load LAS/LAZ bounding boxes from selected map-overlay folder(s) and show on map (triggered by "Load Las to Map" button)
  const loadLasToMap = useCallback(() => {
    const folders = mapOverlaySelectedFolders || [];
    const wkt = normalizeWktInput(
      String(mapOverlayLocalConfig?.wkt ?? "").trim(),
    );
    if (folders.length === 0) {
      showNotification("Select at least one LAS folder first.");
      return;
    }
    if (!useSystemGoogleDrive) {
      showNotification(
        "Load Las to Map is only available when using System Drive.",
      );
      return;
    }
    if (!wkt) {
      showNotification(
        "Enter WKT CRS in Local (x, y + WKT) to load LAS bounds.",
      );
      return;
    }
    setLoadingLasBounds(true);
    const isProductionDomain =
      window.location.hostname.includes("123bimlive.com");
    const baseUrl =
      ENVIRONMENT === "production" || isProductionDomain ? "" : API_BASE_URL;
    const fetchOne = (folderId) =>
      axios
        .get(`${baseUrl}/api/google-drive/las-laz-bounds`, {
          params: {
            folder_id: folderId,
            // project_id lets the server route to S3 for flagged projects
            // (SSM <name>_source_storage=s3). Drive-only projects ignore it.
            ...(effectiveProjectId != null
              ? { project_id: effectiveProjectId }
              : {}),
          },
        })
        .then((res) => res.data?.bounds || [])
        .catch(() => []);
    const proj4FromValidate = wktValidation?.crs?.proj4;
    Promise.all(
      folders
        .map((f) => f.id ?? f.folder_id ?? f.folderId)
        .filter(Boolean)
        .map(fetchOne),
    )
      .then(async (arrays) => {
        const all = arrays.flat();
        if (all.length === 0) {
          setLasBoundingBoxes([]);
          setSelectedLasBoundingBoxIndex(0);
          showNotification("No LAS/LAZ bounds found in selected folder(s).");
          return;
        }
        // Batch all bbox corners into a single projection call for speed
        const allCorners = [];
        const validItems = [];
        for (const item of all) {
          const bb = item.bbox;
          if (
            !bb ||
            typeof bb.min_x !== "number" ||
            typeof bb.max_x !== "number" ||
            typeof bb.min_y !== "number" ||
            typeof bb.max_y !== "number"
          )
            continue;
          allCorners.push(
            [bb.min_x, bb.max_y],
            [bb.max_x, bb.max_y], // TL, TR
            [bb.max_x, bb.min_y],
            [bb.min_x, bb.min_y], // BR, BL
          );
          validItems.push(item);
        }
        const projected = await projectedPointsToLatLngAsync(
          allCorners,
          wkt,
          proj4FromValidate,
        );
        const bounds = [];
        if (projected && projected.length === validItems.length * 4) {
          for (let i = 0; i < validItems.length; i++) {
            const quad = projected.slice(i * 4, i * 4 + 4);
            const b = latLngPointsToBounds(quad);
            if (b) bounds.push({ ...b, name: validItems[i]?.name || "" });
          }
        } else {
          // Fallback: transform one-by-one if batch failed
          for (const item of all) {
            const b = await projectedBboxToLatLngBoundsAsync(
              item.bbox,
              wkt,
              proj4FromValidate,
            );
            if (b) bounds.push({ ...b, name: item?.name || "" });
          }
        }
        setLasBoundingBoxes(bounds);
        setSelectedLasBoundingBoxIndex(0);
      })
      .catch(() => {
        setLasBoundingBoxes([]);
        setSelectedLasBoundingBoxIndex(0);
        showNotification("Failed to load LAS bounds.");
      })
      .finally(() => setLoadingLasBounds(false));
  }, [
    mapOverlaySelectedFolders,
    mapOverlayLocalConfig?.wkt,
    wktValidation?.crs?.proj4,
    useSystemGoogleDrive,
    showNotification,
  ]);

  // Load ortho map tiles (PNG) from maps_2000 in selected folder(s) and show on map
  const loadOrthoMaps = useCallback(() => {
    const folders = mapOverlaySelectedFolders || [];
    const wkt = normalizeWktInput(
      String(mapOverlayLocalConfig?.wkt ?? "").trim(),
    );
    if (folders.length === 0) {
      showNotification("Select at least one LAS folder first.");
      return;
    }
    if (!useSystemGoogleDrive) {
      showNotification(
        "Load ortho maps is only available when using System Drive.",
      );
      return;
    }
    if (!wkt) {
      showNotification(
        "Enter WKT CRS in Local (x, y + WKT) to load ortho maps.",
      );
      return;
    }
    setLoadingOrthoTiles(true);
    const isProductionDomain =
      window.location.hostname.includes("123bimlive.com");
    const baseUrl =
      ENVIRONMENT === "production" || isProductionDomain ? "" : API_BASE_URL;
    const orthoUrl = baseUrl
      ? `${API_BASE_URL}/api/google-drive/ortho-tiles`
      : "/api/google-drive/ortho-tiles";
    // Fetch tiles from per-path folders (by stored folder ID or name) + legacy bare maps_2000
    const currentPaths = rsaPathsHook.paths;
    const fetchOne = (folderId, pathName, mapsFolderId) =>
      axios
        .get(orthoUrl, {
          params: {
            ...(folderId ? { folder_id: folderId } : {}),
            ...(pathName ? { path_name: pathName } : {}),
            ...(mapsFolderId ? { maps_folder_id: mapsFolderId } : {}),
          },
        })
        .then((res) => ({
          tiles: res.data?.tiles || [],
          mapsFolderId: res.data?.maps_folder_id || null,
        }))
        .catch(() => ({ tiles: [], mapsFolderId: null }));
    const proj4FromValidate = wktValidation?.crs?.proj4;
    const folderIds = folders
      .map((f) => f.id ?? f.folder_id ?? f.folderId)
      .filter(Boolean);
    const fetchPromises = [];
    // Self-heal map: pathId -> rediscovered maps2000FolderId. We only update
    // path state once after Promise.all settles to avoid mid-loop renders
    // that would invalidate currentPaths references.
    const rediscoveredFolderIds = new Map();
    // E: skip the legacy bare maps_2000 fetch when the project has migrated
    // to the per-path layout (any path has maps2000FolderId or
    // orthoMapsName). Modern multi-path projects don't have a bare
    // maps_2000 folder, so the call returns nothing and the deduper drops
    // it — it's just wasted latency. Fully-legacy projects (no per-path
    // fields anywhere) still need it, so keep it as the fallback path.
    const projectUsesPerPathLayout = currentPaths.some(
      (p) => p.maps2000FolderId || p.orthoMapsName,
    );
    for (const folderId of folderIds) {
      // Fetch per-path folders — prefer stored folder ID, fall back to name
      // lookup (using orthoMapsName, which survives a path rename — falls
      // back to sanitized label for legacy paths created before that field
      // existed). When fallback rediscovers tiles, queue the new id for
      // self-heal so the next reload uses the fast direct path.
      for (const p of currentPaths) {
        const pId = p.id;
        const nameForLookup =
          p.orthoMapsName || (p.label ? sanitizePathLabel(p.label) : null);
        if (p.maps2000FolderId) {
          fetchPromises.push(
            fetchOne(folderId, null, p.maps2000FolderId).then(
              async (primary) => {
                if (primary.tiles.length > 0 || !nameForLookup) {
                  return { pathId: pId, tiles: primary.tiles };
                }
                const fallback = await fetchOne(folderId, nameForLookup, null);
                if (
                  fallback.tiles.length > 0 &&
                  fallback.mapsFolderId &&
                  fallback.mapsFolderId !== p.maps2000FolderId
                ) {
                  rediscoveredFolderIds.set(pId, fallback.mapsFolderId);
                }
                return { pathId: pId, tiles: fallback.tiles };
              },
            ),
          );
        } else if (nameForLookup) {
          fetchPromises.push(
            fetchOne(folderId, nameForLookup, null).then((r) => {
              // First-time discovery for paths that never had a stored id —
              // also self-heal so subsequent reloads can use the fast path.
              if (r.tiles.length > 0 && r.mapsFolderId) {
                rediscoveredFolderIds.set(pId, r.mapsFolderId);
              }
              return { pathId: pId, tiles: r.tiles };
            }),
          );
        }
      }
      // Also fetch bare maps_2000 for backward compat (no pathId) — only
      // when the project hasn't migrated to the per-path layout (see
      // projectUsesPerPathLayout above). Skipping this on modern projects
      // removes one redundant call per LAS folder.
      if (!projectUsesPerPathLayout) {
        fetchPromises.push(
          fetchOne(folderId, null, null).then((r) => ({
            pathId: null,
            tiles: r.tiles,
          })),
        );
      }
    }
    Promise.all(fetchPromises)
      .then(async (results) => {
        // Deduplicate tiles by png_file_id, tag each with its source pathId
        const seen = new Set();
        const all = [];
        for (const { pathId: srcPathId, tiles } of results) {
          for (const t of tiles) {
            if (!t?.png_file_id || seen.has(t.png_file_id)) continue;
            seen.add(t.png_file_id);
            all.push({ ...t, _pathId: srcPathId });
          }
        }
        // D: batch all tiles' 4 corners into a single
        // projectedPointsToLatLngAsync call. Previously each tile awaited
        // its own conversion sequentially — for local proj4 that's N
        // microtask hops, but for CRSes that fall back to
        // /api/wkt/transform it was N sequential HTTP POSTs. Either way,
        // batching collapses the conversion phase to one call. Mirrors
        // the LAS-bounds code (~line 7083) for consistency.
        //
        // Source corners per tile: projected_corners when valid, else
        // 4 corners derived from the axis-aligned bbox (TL, TR, BR, BL —
        // the same order projectedBboxToLatLngQuadAsync uses internally).
        const allCorners = [];
        const tileForRow = []; // index i -> tile owning rows 4i .. 4i+3
        for (const tile of all) {
          let xy = null;
          const pc = Array.isArray(tile?.projected_corners)
            ? tile.projected_corners
            : [];
          if (pc.length === 4) {
            const candidate = pc.map((p) => [Number(p?.x), Number(p?.y)]);
            if (
              candidate.every(
                ([x, y]) => Number.isFinite(x) && Number.isFinite(y),
              )
            ) {
              xy = candidate;
            }
          }
          if (!xy) {
            const bb = tile?.bounds;
            const minX = Number(bb?.min_x);
            const maxX = Number(bb?.max_x);
            const minY = Number(bb?.min_y);
            const maxY = Number(bb?.max_y);
            if ([minX, maxX, minY, maxY].every((v) => Number.isFinite(v))) {
              xy = [
                [minX, maxY], // TL
                [maxX, maxY], // TR
                [maxX, minY], // BR
                [minX, minY], // BL
              ];
            }
          }
          if (!xy) continue; // no usable georeference — drop tile
          tileForRow.push(tile);
          allCorners.push(...xy);
        }
        const projected = allCorners.length
          ? await projectedPointsToLatLngAsync(
              allCorners,
              wkt,
              proj4FromValidate,
            )
          : [];
        const withLatLng = [];
        const batchOk =
          Array.isArray(projected) && projected.length === allCorners.length;
        if (batchOk) {
          for (let i = 0; i < tileForRow.length; i++) {
            const tile = tileForRow[i];
            const quad = projected.slice(i * 4, i * 4 + 4);
            const b = latLngPointsToBounds(quad);
            if (b && tile.png_file_id) {
              withLatLng.push({
                name: tile.name,
                pngFileId: tile.png_file_id,
                pathId: tile._pathId || null,
                bounds: b,
                quad,
              });
            }
          }
        } else {
          // Fallback: batched conversion returned a wrong-length or null
          // result (worker hiccup, partial /api/wkt/transform failure).
          // Drop back to the prior per-tile path with its multi-stage
          // fallbacks so behaviour is preserved. Slow but rare.
          for (const tile of all) {
            let quad = null;
            const projectedCorners = Array.isArray(tile?.projected_corners)
              ? tile.projected_corners
              : [];
            if (projectedCorners.length === 4) {
              const xy = projectedCorners.map((p) => [
                Number(p?.x),
                Number(p?.y),
              ]);
              if (
                xy.every(([x, y]) => Number.isFinite(x) && Number.isFinite(y))
              ) {
                quad = await projectedPointsToLatLngAsync(
                  xy,
                  wkt,
                  proj4FromValidate,
                );
              }
            }
            if (!quad) {
              quad = await projectedBboxToLatLngQuadAsync(
                tile.bounds,
                wkt,
                proj4FromValidate,
              );
            }
            const b =
              latLngPointsToBounds(quad) ||
              (await projectedBboxToLatLngBoundsAsync(
                tile.bounds,
                wkt,
                proj4FromValidate,
              ));
            if (b && tile.png_file_id) {
              withLatLng.push({
                name: tile.name,
                pngFileId: tile.png_file_id,
                pathId: tile._pathId || null,
                bounds: b,
                quad,
              });
            }
          }
        }
        setOrthoTiles(withLatLng);
        // Note: do NOT mutate maps2000ByPath here. "Does this path have a
        // maps_2000 folder?" is answered by p.maps2000FolderId on the
        // persisted path (see checkMaps2000ForPaths) — it is independent of
        // whether this particular fetch produced renderable tiles. A folder
        // can exist with no PNG content yet, WKT/proj4 conversion can fail
        // silently, or fetchOne can swallow per-folder errors and return [].
        // Clobbering the per-path flag here used to flip "Add Lane" back to
        // "Create Ortho Maps" even though the Drive folder still exists.
        // Self-heal: when the name-based fallback rediscovered a folder ID
        // that differs from what's stored on the path, persist the new id so
        // the next reload uses the fast direct lookup. Done after Promise.all
        // so all per-path fallbacks settle before we touch state.
        if (rediscoveredFolderIds.size > 0) {
          for (const [pathId, newFolderId] of rediscoveredFolderIds) {
            rsaPathsHook.actions.updatePath(pathId, {
              maps2000FolderId: newFolderId,
            });
          }
        }
        if (projectId && withLatLng.length > 0) {
          saveOrthoTilesToCache(projectId, withLatLng);
        }
      })
      .catch(() => {
        setOrthoTiles([]);
        showNotification("Failed to load ortho maps.");
      })
      .finally(() => setLoadingOrthoTiles(false));
  }, [
    mapOverlaySelectedFolders,
    mapOverlayLocalConfig?.wkt,
    wktValidation?.crs?.proj4,
    useSystemGoogleDrive,
    projectId,
    showNotification,
    rsaPathsHook.paths,
    rsaPathsHook.actions,
  ]);

  // Restore ortho tiles from IndexedDB when projectId is set (e.g. on load or project switch)
  const orthoRestoredFromCacheRef = useRef(false);
  useEffect(() => {
    if (!projectId) return;
    orthoRestoredFromCacheRef.current = false;
    let cancelled = false;
    loadOrthoTilesFromCache(projectId).then((cached) => {
      if (cancelled) return;
      if (Array.isArray(cached) && cached.length > 0) {
        setOrthoTiles(cached);
        orthoRestoredFromCacheRef.current = true;
      } else {
        setOrthoTiles([]);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Tracks which paths have an in-flight delete so the path-block UI can
  // overlay a spinner while the backend cleanup runs (4-7s for Drive ops).
  // Atomicity is preserved: if the user closes the tab mid-cleanup the POST
  // is canceled and local state stays in sync with MySQL/Drive.
  const [deletingPathIds, setDeletingPathIds] = useState(() => new Set());

  // Delete path: aggressive cleanup. Calls the backend to hard-delete the
  // maps_2000 folder + LasSeparationPath JSON + any _backup_* siblings on
  // Drive and atomically upsert the trimmed paths_json. Then prunes local
  // state (reducer cascades lanes + intersections), maps2000ByPath, and
  // the IndexedDB ortho-tile cache (filter keeps only tiles whose pathId
  // is still in the active paths list, killing both this path's tiles
  // and any pathId:null orphans from legacy bare-maps_2000 fetches).
  //
  // Best-effort: backend warnings or a network failure don't block the
  // local delete — we always succeed locally and surface a toast on
  // partial failure.
  const handleDeletePathWithCleanup = useCallback(
    async (pathId) => {
      setDeletingPathIds((prev) => {
        const next = new Set(prev);
        next.add(pathId);
        return next;
      });
      try {
        const isProductionDomain =
          typeof window !== "undefined" &&
          window.location.hostname.includes("123bimlive.com");
        const baseUrl =
          ENVIRONMENT === "production" || isProductionDomain
            ? ""
            : API_BASE_URL;
        const deleteUrl = baseUrl
          ? `${API_BASE_URL}/api/projects/${projectId}/rsa-paths/${pathId}/delete`
          : `/api/projects/${projectId}/rsa-paths/${pathId}/delete`;

        let warnings = [];
        let conflictHit = false;
        if (projectId) {
          try {
            const res = await axios.post(
              deleteUrl,
              {
                // Optimistic-concurrency check: backend rejects with 409 if
                // another tab/user has already modified paths_json.
                expected_version: rsaPathsHook.pathsVersionRef?.current ?? 0,
              },
              {
                headers: { "Content-Type": "application/json" },
                timeout: 30000,
              },
            );
            warnings = res.data?.warnings || [];
            // Adopt the new server-side version so the next save targets it.
            if (
              rsaPathsHook.pathsVersionRef &&
              typeof res.data?.version === "number"
            ) {
              rsaPathsHook.pathsVersionRef.current = res.data.version;
            }
          } catch (e) {
            const status = e.response?.status;
            const respData = e.response?.data;
            if (status === 409) {
              conflictHit = true;
              if (
                rsaPathsHook.pathsVersionRef &&
                typeof respData?.version === "number"
              ) {
                rsaPathsHook.pathsVersionRef.current = respData.version;
              }
              warnings = [
                ...(respData?.warnings || []),
                "Another tab/user modified this project's paths since you loaded; reloading to get the latest state. Retry the delete after the reload.",
              ];
              console.warn(
                "[handleDeletePathWithCleanup] 409 conflict; will trigger reload via rsaPathsHook.actions.reload",
              );
            } else {
              const msg = respData?.error || e.message || String(e);
              warnings.push(`Backend cleanup failed: ${msg}`);
              console.error(
                "[handleDeletePathWithCleanup] backend call failed:",
                e,
              );
            }
          }
        } else {
          warnings.push("No projectId — backend cleanup skipped");
        }

        // On 409 conflict the backend did NOT trim paths_json, so we must
        // not also dispatch the local delete (that would make local state
        // diverge from the server). Reload from the server and bail out
        // of the rest of the local cleanup.
        if (conflictHit) {
          if (warnings.length > 0) {
            console.warn(`Path delete deferred: ${warnings.join("; ")}`);
          }
          rsaPathsHook.actions.reload?.();
          return;
        }

        // Compute remaining path IDs *before* dispatching the delete, so we can
        // filter the IndexedDB tile cache against the post-delete path set.
        const remainingPathIds = new Set(
          rsaPathsHook.paths.filter((p) => p.id !== pathId).map((p) => p.id),
        );

        rsaPathsHook.actions.deletePath(pathId);

        setMaps2000ByPath((prev) => {
          const next = { ...prev };
          delete next[pathId];
          return next;
        });

        // Filter tiles: keep only those whose pathId is in the remaining set.
        // This drops the deleted path's tiles AND prunes orphans
        // (pathId === null from legacy bare-maps_2000 fetches, or pathId
        // pointing at a path that no longer exists).
        setOrthoTiles((prev) => {
          const next = prev.filter(
            (t) => t.pathId && remainingPathIds.has(t.pathId),
          );
          if (projectId) saveOrthoTilesToCache(projectId, next);
          return next;
        });

        if (warnings.length > 0) {
          console.warn(
            `Path deleted. Cleanup warnings: ${warnings.join("; ")}`,
          );
        }
      } finally {
        setDeletingPathIds((prev) => {
          if (!prev.has(pathId)) return prev;
          const next = new Set(prev);
          next.delete(pathId);
          return next;
        });
      }
    },
    [rsaPathsHook.actions, rsaPathsHook.paths, projectId, showNotification],
  );

  // Rename path — no Drive calls needed since maps_2000 link is by folder ID, not name
  const handleRenamePath = useCallback(
    (id, label) => {
      const unique = makeUniqueLabel(label, rsaPathsHook.paths, id);
      rsaPathsHook.actions.updatePath(id, { label: unique });
    },
    [rsaPathsHook.paths, rsaPathsHook.actions],
  );

  // Check which paths have maps_2000 folders using stored folder IDs (no API call needed)
  const checkMaps2000ForPaths = useCallback(() => {
    const currentPaths = rsaPathsHook.paths;
    const byPath = {};
    currentPaths.forEach((p) => {
      byPath[p.id] = Boolean(p.maps2000FolderId);
    });
    setMaps2000ByPath(byPath);
  }, [rsaPathsHook.paths]);

  useEffect(() => {
    checkMaps2000ForPaths();
  }, [checkMaps2000ForPaths]);

  // Auto-load ortho tiles from Drive when maps exist but no cached tiles were restored
  useEffect(() => {
    if (orthoRestoredFromCacheRef.current) return;
    if (orthoTiles.length > 0) return;
    if (loadingOrthoTiles || loadingMaps2000Check) return;
    const hasAnyMaps = Object.values(maps2000ByPath).some(Boolean);
    if (!hasAnyMaps) return;
    const folders = mapOverlaySelectedFolders || [];
    if (folders.length === 0 || !useSystemGoogleDrive) return;
    const wkt = normalizeWktInput(
      String(mapOverlayLocalConfig?.wkt ?? "").trim(),
    );
    if (!wkt) return;
    loadOrthoMaps();
  }, [
    maps2000ByPath,
    orthoTiles.length,
    loadingOrthoTiles,
    loadingMaps2000Check,
    mapOverlaySelectedFolders,
    useSystemGoogleDrive,
    mapOverlayLocalConfig?.wkt,
    loadOrthoMaps,
  ]);

  // Reset image-overlay load progress when ortho tile list changes (e.g. after new Load Ortho Maps)
  useEffect(() => {
    setImageOverlaysLoadedCount(0);
    setImageOverlaysTotal(0);
  }, [orthoTiles]);

  // Show the queued "Ortho maps created" toast once the rendering layer has
  // caught up — i.e. all expected tiles are visibly on the map. Includes a
  // 30s timeout fallback so the user always sees the message; if the timeout
  // fires we log a warning since it usually means the render didn't catch up.
  useEffect(() => {
    if (!pendingOrthoSuccessMessage) return undefined;
    const { message, expectedTileCount, startTime } =
      pendingOrthoSuccessMessage;

    // No tiles to render — release the message immediately.
    if (expectedTileCount === 0) {
      showNotification(message);
      setPendingOrthoSuccessMessage(null);
      return undefined;
    }

    // Rendering caught up — release now.
    const renderingDone =
      imageOverlaysTotal > 0 && imageOverlaysLoadedCount >= imageOverlaysTotal;
    if (renderingDone) {
      showNotification(message);
      setPendingOrthoSuccessMessage(null);
      return undefined;
    }

    // Still loading — set a fallback timer.
    const remaining = Math.max(0, 30000 - (Date.now() - startTime));
    const timer = setTimeout(() => {
      console.warn(
        `[createOrthoMaps] success toast fallback fired — render did not catch up. loaded=${imageOverlaysLoadedCount}, total=${imageOverlaysTotal}, expected=${expectedTileCount}`,
      );
      showNotification(message);
      setPendingOrthoSuccessMessage(null);
    }, remaining);
    return () => clearTimeout(timer);
  }, [
    pendingOrthoSuccessMessage,
    imageOverlaysLoadedCount,
    imageOverlaysTotal,
    showNotification,
  ]);

  const onImageOverlaysLoadProgress = useCallback((loaded, total) => {
    setImageOverlaysLoadedCount(loaded);
    setImageOverlaysTotal(total);
  }, []);

  // Memoize so progress callback updates don't change reference and restart ImageOverlays effect (avoids 0/34 1/34 flicker).
  // Each item carries `name` (the tile's stable id) so the ImageOverlays
  // renderer can do diff-based reconciliation: items with the same name
  // across renders are NOT re-loaded, eliminating the blinking-on-every-
  // streamed-tile effect and preventing in-flight image loads from being
  // canceled mid-load while a fresh ortho-maps run streams tiles in.
  const imageOverlaysForMap = useMemo(() => {
    const isProd = window.location.hostname.includes("123bimlive.com");
    const base = ENVIRONMENT === "production" || isProd ? "" : API_BASE_URL;
    const mediaBase = base
      ? `${API_BASE_URL}/api/google-drive/file`
      : "/api/google-drive/file";
    const orthoOverlays =
      (orthoTiles || []).length > 0
        ? (orthoTiles || []).map((t) => ({
            name: t.name,
            url: t.localUrl || `${mediaBase}/${t.pngFileId}/media`,
            bounds: t.bounds,
            quad: t.quad,
          }))
        : [];
    return orthoOverlays;
  }, [orthoTiles]);

  // Build setup payload for .rsa/setup.json (same shape as mapOverlayLocalConfig)
  const buildSetupPayload = useCallback(
    (validationOverride = null) => {
      const c = mapOverlayLocalConfig || {};
      const activeValidation = validationOverride || wktValidation;
      const wktForSetup = normalizeWktInput(String(c.wkt ?? "").trim());
      const wktSummary = buildPersistedWktSummary(
        wktForSetup,
        activeValidation,
      );
      return {
        wkt: wktForSetup,
        wktSummary,
        wktValidation: {
          status: wktSummary.status,
          valid: wktSummary.valid,
          willWork: wktSummary.willWork,
          message: wktSummary.message,
          location: wktSummary.location,
          crs: wktSummary.crs,
          inputType: wktSummary.inputType,
        },
        extent: c.extent ? { ...c.extent } : undefined,
        originXY: c.originXY ? { ...c.originXY } : undefined,
        originLatLng: c.originLatLng ? { ...c.originLatLng } : undefined,
        scaleLatPerUnit: c.scaleLatPerUnit,
        scaleLngPerUnit: c.scaleLngPerUnit,
        state: String(c.state ?? "").trim(),
        routeNumber: String(c.routeNumber ?? "").trim(),
        pathNameSegment: String(c.pathNameSegment ?? "").trim(),
      };
    },
    [mapOverlayLocalConfig, wktValidation],
  );

  const fetchProjectSetupFolderId = useCallback(async (targetProjectId) => {
    if (!targetProjectId) return null;
    const isProd = window.location.hostname.includes("123bimlive.com");
    const baseUrl = ENVIRONMENT === "production" || isProd ? "" : API_BASE_URL;
    const cacheBust = `_ts=${Date.now()}`;
    const filesUrl = baseUrl
      ? `${API_BASE_URL}/api/projects/${targetProjectId}/files?${cacheBust}`
      : `/api/projects/${targetProjectId}/files?${cacheBust}`;

    try {
      const filesResponse = await axios.get(filesUrl);
      const files = filesResponse.data?.files || [];
      const { sourceGoogleDriveIds } =
        deriveTaskMetadataFromProjectFiles(files);
      if (
        Array.isArray(sourceGoogleDriveIds) &&
        sourceGoogleDriveIds.length > 0
      ) {
        return String(sourceGoogleDriveIds[0]).trim();
      }

      const fallbackFolder = (files || []).find((item) => {
        if (!isFolderItem(item)) return false;
        const driveId = String(getGoogleDriveId(item) ?? "").trim();
        return Boolean(driveId);
      });
      const fallbackFolderId = String(
        getGoogleDriveId(fallbackFolder) ?? "",
      ).trim();
      return fallbackFolderId || null;
    } catch (err) {
      console.warn("Failed to resolve setup folder from project files:", err);
      return null;
    }
  }, []);

  useEffect(() => {
    if (!projectId) {
      setProjectSetupFolderId(null);
      return;
    }
    if (preloadedRsaSetupFolderId) {
      setProjectSetupFolderId(String(preloadedRsaSetupFolderId));
      return;
    }
    let cancelled = false;
    fetchProjectSetupFolderId(projectId).then((folderId) => {
      if (!cancelled && folderId) {
        setProjectSetupFolderId(folderId);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, preloadedRsaSetupFolderId, fetchProjectSetupFolderId]);

  // Save Setup to central registry (Shared Drive): validate (project, folder, WKT), check if registry already has setup for overwrite modal, then POST
  const performSaveSetup = useCallback(
    async (overwrite = false) => {
      try {
        if (!projectId) {
          showNotification(
            "Open a project to save setup (setup is stored per project).",
          );
          return;
        }
        let folderId = effectiveSetupFolderId;
        const wkt = normalizeWktInput(
          String(mapOverlayLocalConfig?.wkt ?? "").trim(),
        );
        if (!folderId) {
          folderId = await fetchProjectSetupFolderId(projectId);
          if (folderId) {
            setProjectSetupFolderId(folderId);
          }
        }
        if (!folderId) {
          showNotification(
            "Failed to save setup: project folder ID not found.",
          );
          return;
        }
        if (!wkt) {
          showNotification(
            "Enter a valid WKT in Map overlay & coordinate system first.",
          );
          return;
        }
        const validationResult = await validateWktInput(wkt, { silent: false });
        if (!validationResult?.valid) {
          setWktInputLocked(false);
          return;
        }
        if (!validationResult?.willWork) {
          setWktInputLocked(false);
          showNotification(
            "WKT is valid, but not compatible with current map conversion support.",
          );
          return;
        }
        if (!useSystemGoogleDrive) {
          showNotification(
            "Save Setup is only available when using System Drive.",
          );
          return;
        }
        const isProd = window.location.hostname.includes("123bimlive.com");
        const baseUrl =
          ENVIRONMENT === "production" || isProd ? "" : API_BASE_URL;
        const apiBase = baseUrl
          ? `${API_BASE_URL}/api/google-drive/rsa`
          : "/api/google-drive/rsa";
        const existsRes = await axios
          .get(`${apiBase}/registry/setup`, {
            params: { project_id: projectId },
          })
          .catch(() => ({ status: 404 }));
        const exists = existsRes.status === 200 && existsRes.data?.folderId;
        if (exists && !overwrite) {
          setShowSaveSetupOverwriteModal(true);
          return;
        }
        const setup = buildSetupPayload(validationResult);
        await axios.post(`${apiBase}/registry/setup`, {
          project_id: projectId,
          folder_id: folderId,
          setup,
        });
        setWktInputLocked(true);
        setShowSaveSetupOverwriteModal(false);
      } catch (err) {
        const msg =
          err.response?.data?.error || err.message || "Failed to save setup.";
        showNotification(msg);
      } finally {
        setSavingSetup(false);
      }
    },
    [
      projectId,
      effectiveSetupFolderId,
      mapOverlayLocalConfig,
      useSystemGoogleDrive,
      buildSetupPayload,
      showNotification,
      validateWktInput,
      fetchProjectSetupFolderId,
    ],
  );

  const handleSaveWktClick = useCallback(async () => {
    const wkt = (mapOverlayLocalConfig?.wkt ?? "").trim();
    if (!wkt) {
      showNotification("Enter a WKT value before saving.");
      setWktInputLocked(false);
      return;
    }
    const validationResult = await validateWktInput(wkt, { silent: false });
    if (!validationResult?.valid || !validationResult?.willWork) {
      setWktInputLocked(false);
      return;
    }
    setWktInputLocked(true);
  }, [mapOverlayLocalConfig?.wkt, showNotification, validateWktInput]);

  const handleSaveSetupClick = useCallback(() => {
    setSavingSetup(true);
    performSaveSetup(false);
  }, [performSaveSetup]);

  const handleSaveSetupOverwriteConfirm = useCallback(() => {
    setShowSaveSetupOverwriteModal(false);
    setSavingSetup(true);
    performSaveSetup(true);
  }, [performSaveSetup]);

  const handleSaveSetupOverwriteCancel = useCallback(() => {
    setShowSaveSetupOverwriteModal(false);
  }, []);

  const lastLoadedSetupKeyRef = useRef(null);
  const [loadingSetupFromFolder, setLoadingSetupFromFolder] = useState(false);
  // Apply preloaded folder/setup when opening Analysis (fetched by ProjectView on project load)
  const appliedPreloadFolderKeyRef = useRef(null);
  const appliedPreloadSetupKeyRef = useRef(null);
  useEffect(() => {
    if (!projectId || !preloadedRsaSetupFolderId) return;
    const folderKey = `${preloadedRsaSetupFolderId}:${projectId}`;
    if (appliedPreloadFolderKeyRef.current !== folderKey) {
      appliedPreloadFolderKeyRef.current = folderKey;
      lastLoadedSetupKeyRef.current = folderKey;
      setMapOverlaySelectedFolders([
        {
          id: preloadedRsaSetupFolderId,
          name: preloadedRsaSetupFolderName || undefined,
        },
      ]);
    }

    if (!preloadedRsaSetup || appliedPreloadSetupKeyRef.current === folderKey)
      return;
    appliedPreloadSetupKeyRef.current = folderKey;
    const setup = preloadedRsaSetup;
    setMapOverlayLocalConfig((prev) => ({
      ...prev,
      wkt: setup.wkt ?? prev?.wkt ?? "",
      extent: setup.extent ? { ...setup.extent } : prev?.extent,
      originXY: setup.originXY ? { ...setup.originXY } : prev?.originXY,
      originLatLng: setup.originLatLng
        ? { ...setup.originLatLng }
        : prev?.originLatLng,
      scaleLatPerUnit: setup.scaleLatPerUnit ?? prev?.scaleLatPerUnit,
      scaleLngPerUnit: setup.scaleLngPerUnit ?? prev?.scaleLngPerUnit,
      state: setup.state ?? prev?.state ?? "",
      routeNumber: setup.routeNumber ?? prev?.routeNumber ?? "",
      pathNameSegment: setup.pathNameSegment ?? prev?.pathNameSegment ?? "",
    }));
    setWktValidation(parseSetupWktValidation(setup));
    if (String(setup.wkt ?? "").trim()) {
      setWktInputLocked(true);
    }
  }, [
    projectId,
    preloadedRsaSetup,
    preloadedRsaSetupFolderId,
    preloadedRsaSetupFolderName,
  ]);

  // (Previously also read setupConfig from Mongo pipeline_project_lanes
  // here. Removed alongside the Mongo paths/lanes layer — `.rsa/setup.json`
  // on Drive is the source of truth for setup config; the Mongo copy was a
  // stale shadow.)

  // Load setup from .rsa/setup.json for this project when user has selected exactly one map-overlay folder (System Drive only)
  useEffect(() => {
    if (!useSystemGoogleDrive || !projectId) return;
    const folders = mapOverlaySelectedFolders || [];
    if (folders.length === 0) {
      lastLoadedSetupKeyRef.current = null;
      return;
    }
    const folderId =
      folders.length === 1
        ? (folders[0]?.id ?? folders[0]?.folder_id ?? folders[0]?.folderId)
        : null;
    const loadKey = folderId && projectId ? `${folderId}:${projectId}` : null;
    if (!loadKey || loadKey === lastLoadedSetupKeyRef.current) return;
    const isProd = window.location.hostname.includes("123bimlive.com");
    const baseUrl = ENVIRONMENT === "production" || isProd ? "" : API_BASE_URL;
    const apiBase = baseUrl
      ? `${API_BASE_URL}/api/google-drive/rsa`
      : "/api/google-drive/rsa";
    setLoadingSetupFromFolder(true);
    axios
      .get(`${apiBase}/setup`, {
        params: { folder_id: folderId, project_id: projectId },
      })
      .then((res) => {
        const setup = res.data?.setup;
        if (setup && typeof setup === "object") {
          lastLoadedSetupKeyRef.current = loadKey;
          setMapOverlayLocalConfig((prev) => ({
            ...prev,
            wkt: setup.wkt ?? prev?.wkt ?? "",
            extent: setup.extent ? { ...setup.extent } : prev?.extent,
            originXY: setup.originXY ? { ...setup.originXY } : prev?.originXY,
            originLatLng: setup.originLatLng
              ? { ...setup.originLatLng }
              : prev?.originLatLng,
            scaleLatPerUnit: setup.scaleLatPerUnit ?? prev?.scaleLatPerUnit,
            scaleLngPerUnit: setup.scaleLngPerUnit ?? prev?.scaleLngPerUnit,
            state: setup.state ?? prev?.state ?? "",
            routeNumber: setup.routeNumber ?? prev?.routeNumber ?? "",
            pathNameSegment:
              setup.pathNameSegment ?? prev?.pathNameSegment ?? "",
          }));
          setWktValidation(parseSetupWktValidation(setup));
          if (String(setup.wkt ?? "").trim()) {
            setWktInputLocked(true);
          }
        }
      })
      .catch(() => {
        /* no setup for this project or error: keep current config */
      })
      .finally(() => setLoadingSetupFromFolder(false));
  }, [projectId, mapOverlaySelectedFolders, useSystemGoogleDrive]);

  // --- State for storage connection modal ---
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [connectionProvider, setConnectionProvider] = useState(null);

  // --- State for main content tabs ---
  const [activeMainTab, setActiveMainTab] = useState(() =>
    normalizeRsaMainTab(initialActiveMainTab),
  );
  const [hasVisitedSetupTab, setHasVisitedSetupTab] = useState(
    () => normalizeRsaMainTab(initialActiveMainTab) === "setup",
  );
  const [routesSourceFolderId, setRoutesSourceFolderId] = useState("");
  const [routesFolderId, setRoutesFolderId] = useState("");
  const [modelFolderId, setModelFolderId] = useState("");
  const [routesTaskFoldersByCode, setRoutesTaskFoldersByCode] = useState({});
  const [routesXmlFiles, setRoutesXmlFiles] = useState(() =>
    readRoutesXmlCache(effectiveProjectId),
  );
  const [routesSourceFiles, setRoutesSourceFiles] = useState([]);
  const [routesStateByDriveId, setRoutesStateByDriveId] = useState({});
  const [modelFiles, setModelFiles] = useState([]);
  const [routesExistingNamesByTask, setRoutesExistingNamesByTask] = useState(
    {},
  );
  const [routesExistingXmlFilesByTask, setRoutesExistingXmlFilesByTask] =
    useState({});
  const [routesMatrixSelection, setRoutesMatrixSelection] = useState({});
  const [routesMatrixLoading, setRoutesMatrixLoading] = useState(false);
  // Set to true when /api/projects/<id>/files returns 409 with
  // {"error":"no_storage_configured"} — the project is missing per-project
  // storage config (CommonStorage[<id>_name] + <name>_Storage SSM entry). Each
  // upload-step status-row renders "no storage found for this project" instead
  // of the generic empty/loading state.
  const [noStorageConfigured, setNoStorageConfigured] = useState(false);
  const [routesMatrixSaving, setRoutesMatrixSaving] = useState(false);
  const [showRoutesUploadModal, setShowRoutesUploadModal] = useState(false);
  const [showModelUploadModal, setShowModelUploadModal] = useState(false);
  const [existingSourceFolderId, setExistingSourceFolderId] = useState("");
  const [existingSourceFiles, setExistingSourceFiles] = useState([]);
  const [existingSourceLoading, setExistingSourceLoading] = useState(false);
  const [showExistingSourceUploadModal, setShowExistingSourceUploadModal] =
    useState(false);
  const [routesMatrixError, setRoutesMatrixError] = useState("");
  const [uploadTableSelection, setUploadTableSelection] = useState({
    routes: [],
    model: [],
    source: [],
  });
  const [uploadFileDeleteCandidate, setUploadFileDeleteCandidate] =
    useState(null);
  const [uploadFileDeletePendingIds, setUploadFileDeletePendingIds] = useState(
    [],
  );
  const [deliverablesError, setDeliverablesError] = useState("");
  const [deliverablesFolderInfo, setDeliverablesFolderInfo] = useState({
    driveId: "",
    treeId: "",
    name: "Deliverables",
  });
  const [deliverablesFolderResolving, setDeliverablesFolderResolving] =
    useState(false);
  const [deliverablesSelectedFolder, setDeliverablesSelectedFolder] =
    useState(null);
  const [deliverablesCurrentPath, setDeliverablesCurrentPath] = useState([]);
  const [deliverablesFilterText, setDeliverablesFilterText] = useState("");
  const [showDeliverablesUploadModal, setShowDeliverablesUploadModal] =
    useState(false);
  const [deliverablesSelectionState, setDeliverablesSelectionState] = useState({
    selectedFiles: [],
    selectedCount: 0,
    hasSelectedFiles: false,
    canDeleteSelection: false,
  });
  const deliverablesExplorerRef = useRef(null);
  const routesMatrixHasLocalChangesRef = useRef(false);
  const { permissions: userPermissions, loading: permissionsLoading } =
    useUserPermissions();
  const DEVELOPER_TABS = ["setup", "paths", "dashboard"];

  useEffect(() => {
    routesMatrixHasLocalChangesRef.current = false;
    setRoutesSourceFolderId("");
    setRoutesFolderId("");
    setModelFolderId("");
    setRoutesTaskFoldersByCode({});
    setRoutesExistingNamesByTask({});
    setRoutesExistingXmlFilesByTask({});
    setRoutesMatrixSelection({});
    setRoutesMatrixError("");
    setRoutesXmlFiles(readRoutesXmlCache(effectiveProjectId));
    setRoutesSourceFiles([]);
    setModelFiles([]);
    setUploadTableSelection({ routes: [], model: [] });
    setUploadFileDeleteCandidate(null);
    setUploadFileDeletePendingIds([]);
    setDeliverablesError("");
    setDeliverablesFolderInfo({
      driveId: "",
      treeId: "",
      name: "Deliverables",
    });
    setDeliverablesFolderResolving(false);
    setDeliverablesSelectedFolder(null);
    setDeliverablesCurrentPath([]);
    setDeliverablesFilterText("");
    setShowDeliverablesUploadModal(false);
    setDeliverablesSelectionState({
      selectedFiles: [],
      selectedCount: 0,
      hasSelectedFiles: false,
      canDeleteSelection: false,
    });
  }, [effectiveProjectId]);

  useEffect(() => {
    const xmlRouteFiles = (
      Array.isArray(routesSourceFiles) ? routesSourceFiles : []
    ).filter(
      (file) =>
        isXmlFileName(file?.name) &&
        normalizeDriveFolderId(file?.googleDriveId),
    );

    if (xmlRouteFiles.length === 0) {
      setRoutesStateByDriveId({});
      return;
    }

    let cancelled = false;
    const isProductionDomain =
      window.location.hostname.includes("123bimlive.com");
    const baseUrl =
      ENVIRONMENT === "production" || isProductionDomain ? "" : API_BASE_URL;

    Promise.all(
      xmlRouteFiles.map(async (file) => {
        const driveId = normalizeDriveFolderId(file?.googleDriveId);
        if (!driveId) {
          return null;
        }

        try {
          const mediaUrl = baseUrl
            ? `${API_BASE_URL}/api/google-drive/file/${encodeURIComponent(driveId)}/media`
            : `/api/google-drive/file/${encodeURIComponent(driveId)}/media`;
          const response = await axios.get(mediaUrl, { responseType: "text" });
          const xmlText =
            typeof response?.data === "string" ? response.data : "";
          return [driveId, readStateFromXml(xmlText)];
        } catch (error) {
          console.warn(
            `Failed to read XML state for ${file?.name || driveId}:`,
            error,
          );
          return [driveId, ""];
        }
      }),
    ).then((entries) => {
      if (cancelled) {
        return;
      }

      const nextStateByDriveId = {};
      entries.forEach((entry) => {
        if (!Array.isArray(entry) || entry.length < 2) {
          return;
        }
        const [driveId, stateValue] = entry;
        if (driveId) {
          nextStateByDriveId[driveId] = stateValue || "";
        }
      });
      setRoutesStateByDriveId(nextStateByDriveId);
    });

    return () => {
      cancelled = true;
    };
  }, [routesSourceFiles]);

  useEffect(() => {
    if (activeMainTab !== "deliverables" || !effectiveProjectId) {
      return undefined;
    }

    let cancelled = false;
    setDeliverablesFolderResolving(true);
    setDeliverablesError("");
    resolveDeliverablesFolderForProject(
      effectiveProjectId,
      Array.isArray(projectFilesSnapshot) && projectFilesSnapshot.length > 0
        ? projectFilesSnapshot
        : null,
    )
      .then((resolvedInfo) => {
        if (cancelled) return;
        setDeliverablesFolderInfo({
          driveId: String(resolvedInfo?.driveId ?? "").trim(),
          treeId: String(resolvedInfo?.treeId ?? "").trim(),
          name:
            String(resolvedInfo?.name ?? "Deliverables").trim() ||
            "Deliverables",
        });
        if (!resolvedInfo?.driveId && !resolvedInfo?.treeId) {
          setDeliverablesError(
            "Deliverables folder could not be resolved for this project.",
          );
        }
      })
      .catch(() => {
        if (cancelled) return;
        setDeliverablesFolderInfo({
          driveId: "",
          treeId: "",
          name: "Deliverables",
        });
        setDeliverablesError(
          "Failed to resolve Deliverables folder for this project.",
        );
      })
      .finally(() => {
        if (!cancelled) {
          setDeliverablesFolderResolving(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeMainTab, effectiveProjectId, projectFilesSnapshot]);

  const effectiveDeliverablesFolderDriveId = String(
    deliverablesFolderInfo.driveId ?? "",
  ).trim();
  const effectiveDeliverablesFolderTreeId = String(
    deliverablesFolderInfo.treeId ||
      (effectiveDeliverablesFolderDriveId
        ? `gd_${effectiveDeliverablesFolderDriveId}`
        : ""),
  ).trim();

  const deliverablesScopedRootFolder = useMemo(() => {
    if (
      !effectiveDeliverablesFolderTreeId &&
      !effectiveDeliverablesFolderDriveId
    ) {
      return null;
    }
    return {
      id:
        effectiveDeliverablesFolderTreeId ||
        `gd_${effectiveDeliverablesFolderDriveId}`,
      googleDriveId: effectiveDeliverablesFolderDriveId || null,
      name: deliverablesFolderInfo.name || "Deliverables",
    };
  }, [
    deliverablesFolderInfo.name,
    effectiveDeliverablesFolderDriveId,
    effectiveDeliverablesFolderTreeId,
  ]);

  useEffect(() => {
    if (activeMainTab !== "deliverables" || !deliverablesScopedRootFolder) {
      return;
    }
    setDeliverablesSelectedFolder({
      id: deliverablesScopedRootFolder.id,
      name: deliverablesScopedRootFolder.name,
    });
    setDeliverablesCurrentPath([
      {
        id: deliverablesScopedRootFolder.id,
        name: deliverablesScopedRootFolder.name,
      },
    ]);
  }, [activeMainTab, deliverablesScopedRootFolder]);

  useEffect(() => {
    const normalizedInitialTab = normalizeRsaMainTab(initialActiveMainTab);
    setActiveMainTab((currentTab) =>
      currentTab === normalizedInitialTab ? currentTab : normalizedInitialTab,
    );
  }, [initialActiveMainTab]);

  useEffect(() => {
    if (activeMainTab === "setup") {
      setHasVisitedSetupTab(true);
    }
  }, [activeMainTab]);

  useEffect(() => {
    if (typeof onActiveMainTabChange === "function") {
      onActiveMainTabChange(activeMainTab);
    }
  }, [activeMainTab, onActiveMainTabChange]);
  const hasDevelopersGroup =
    Array.isArray(userPermissions?.groups) &&
    userPermissions.groups.some(
      (g) => String(g).trim().toLowerCase() === "developers",
    );
  // Non-developers must not land on Setup/Paths/Dashboard on rsa-existing projects - switch to Analysis tab.
  // RSA model projects use a wizard where all tabs are user-facing, so this restriction does not apply there.
  useEffect(() => {
    if (permissionsLoading || !userPermissions) return;
    if (isRsaModelProject) return;
    if (!hasDevelopersGroup && DEVELOPER_TABS.includes(activeMainTab)) {
      setActiveMainTab("analysis");
    }
  }, [
    permissionsLoading,
    userPermissions,
    hasDevelopersGroup,
    activeMainTab,
    isRsaModelProject,
  ]);

  // RSA model projects expose Setup + Routes + Analysis + Dashboard tabs.
  useEffect(() => {
    if (!isRsaModelProject) return;
    if (!RSA_MODEL_ALLOWED_MAIN_TABS.includes(activeMainTab)) {
      setActiveMainTab("upload");
    }
  }, [isRsaModelProject, activeMainTab]);

  const routesTabTaskOptions = useMemo(
    () =>
      Array.isArray(visibleTaskOptions)
        ? visibleTaskOptions.filter((taskOption) =>
            Boolean(TASK_OPTION_TO_FOLDER_CODE[taskOption]),
          )
        : [],
    [visibleTaskOptions],
  );

  const toggleRoutesMatrixCell = useCallback((routeFileDriveId, taskOption) => {
    if (!routeFileDriveId || !taskOption) return;
    routesMatrixHasLocalChangesRef.current = true;
    setRoutesMatrixSelection((prev) => {
      const row = prev[routeFileDriveId] || {};
      return {
        ...prev,
        [routeFileDriveId]: {
          ...row,
          [taskOption]: !Boolean(row[taskOption]),
        },
      };
    });
  }, []);

  const loadRoutesTabData = useCallback(
    async ({
      preserveLocalSelection = true,
      manageLoadingState = true,
    } = {}) => {
      if (!effectiveProjectId) {
        setRoutesSourceFolderId("");
        setRoutesFolderId("");
        setModelFolderId("");
        setRoutesTaskFoldersByCode({});
        setRoutesXmlFiles([]);
        setRoutesSourceFiles([]);
        setModelFiles([]);
        writeRoutesXmlCache(effectiveProjectId, []);
        setRoutesExistingNamesByTask({});
        setRoutesExistingXmlFilesByTask({});
        setRoutesMatrixSelection({});
        setRoutesMatrixError("");
        return;
      }

      if (manageLoadingState) {
        setRoutesMatrixLoading(true);
      }
      setRoutesMatrixError("");
      setNoStorageConfigured(false);
      try {
        const isProductionDomain =
          window.location.hostname.includes("123bimlive.com");
        const baseUrl =
          ENVIRONMENT === "production" || isProductionDomain
            ? ""
            : API_BASE_URL;
        const cacheBust = `_ts=${Date.now()}`;
        const filesUrl = baseUrl
          ? `${API_BASE_URL}/api/projects/${effectiveProjectId}/files?${cacheBust}`
          : `/api/projects/${effectiveProjectId}/files?${cacheBust}`;

        let filesResponse;
        try {
          filesResponse = await axios.get(filesUrl);
        } catch (err) {
          if (
            err?.response?.status === 409 &&
            err?.response?.data?.error === "no_storage_configured"
          ) {
            setNoStorageConfigured(true);
            setRoutesSourceFolderId("");
            setRoutesFolderId("");
            setModelFolderId("");
            setRoutesTaskFoldersByCode({});
            setRoutesXmlFiles([]);
            setRoutesSourceFiles([]);
            setModelFiles([]);
            writeRoutesXmlCache(effectiveProjectId, []);
            setRoutesExistingNamesByTask({});
            setRoutesExistingXmlFilesByTask({});
            setRoutesMatrixSelection({});
            setRoutesMatrixError("");
            return;
          }
          throw err;
        }
        const files = filesResponse?.data?.files || [];
        const { hasSourceFolder, sourceGoogleDriveIds } =
          deriveTaskMetadataFromProjectFiles(files);

        if (
          !hasSourceFolder ||
          !Array.isArray(sourceGoogleDriveIds) ||
          sourceGoogleDriveIds.length === 0
        ) {
          setRoutesSourceFolderId("");
          setRoutesFolderId("");
          setModelFolderId("");
          setRoutesTaskFoldersByCode({});
          setRoutesXmlFiles([]);
          setRoutesSourceFiles([]);
          setModelFiles([]);
          writeRoutesXmlCache(effectiveProjectId, []);
          setRoutesExistingNamesByTask({});
          setRoutesExistingXmlFilesByTask({});
          setRoutesMatrixSelection({});
          setRoutesMatrixError(
            "Source folder is missing or not connected to Google Drive.",
          );
          return;
        }

        const sourceChildrenResponses = await Promise.all(
          sourceGoogleDriveIds.map((sourceFolderId) => {
            const childrenUrl = baseUrl
              ? `${API_BASE_URL}/api/projects/${effectiveProjectId}/google-drive-contents?folder_id=${encodeURIComponent(sourceFolderId)}&${cacheBust}`
              : `/api/projects/${effectiveProjectId}/google-drive-contents?folder_id=${encodeURIComponent(sourceFolderId)}&${cacheBust}`;
            return axios
              .get(childrenUrl)
              .catch(() => ({ data: { items: [] } }));
          }),
        );

        const sourceContexts = sourceGoogleDriveIds.map(
          (sourceFolderId, idx) => {
            const items = sourceChildrenResponses[idx]?.data?.items || [];
            const childFolders = items.filter((item) => isFolderItem(item));
            const routesFolder =
              childFolders.find(
                (folder) =>
                  normalizeFolderName(getFileName(folder)) === "ROUTES",
              ) || null;
            const modelFolder =
              childFolders.find(
                (folder) =>
                  normalizeFolderName(getFileName(folder)) === "MODEL",
              ) || null;
            const taskFoldersByCode = {};

            for (const folder of childFolders) {
              const folderCode = normalizeFolderName(getFileName(folder));
              if (!TASK_FOLDER_CODE_TO_OPTION[folderCode]) {
                continue;
              }
              taskFoldersByCode[folderCode] = {
                id: getFileId(folder),
                name: getFileName(folder) || folderCode,
                googleDriveId: normalizeDriveFolderId(getGoogleDriveId(folder)),
              };
            }

            return {
              sourceFolderId: sourceFolderId,
              routesFolder,
              modelFolder,
              taskFoldersByCode,
            };
          },
        );

        const selectedSourceContext =
          sourceContexts.find((ctx) => Boolean(ctx?.routesFolder)) ||
          sourceContexts[0] ||
          null;
        if (!selectedSourceContext) {
          setRoutesSourceFolderId("");
          setRoutesFolderId("");
          setModelFolderId("");
          setRoutesTaskFoldersByCode({});
          setRoutesXmlFiles([]);
          setRoutesSourceFiles([]);
          setModelFiles([]);
          writeRoutesXmlCache(effectiveProjectId, []);
          setRoutesExistingNamesByTask({});
          setRoutesExistingXmlFilesByTask({});
          setRoutesMatrixSelection({});
          setRoutesMatrixError("Could not resolve Source folder contents.");
          return;
        }

        const selectedSourceFolderId = normalizeDriveFolderId(
          selectedSourceContext.sourceFolderId,
        );
        const selectedRoutesFolderId = normalizeDriveFolderId(
          getGoogleDriveId(selectedSourceContext.routesFolder),
        );
        const selectedModelFolderId = normalizeDriveFolderId(
          getGoogleDriveId(selectedSourceContext.modelFolder),
        );

        setRoutesSourceFolderId(selectedSourceFolderId);
        setModelFolderId(selectedModelFolderId);
        setRoutesTaskFoldersByCode(
          selectedSourceContext.taskFoldersByCode || {},
        );

        if (!selectedRoutesFolderId) {
          setRoutesFolderId("");
          setRoutesXmlFiles([]);
          setRoutesSourceFiles([]);
          setModelFiles([]);
          writeRoutesXmlCache(effectiveProjectId, []);
          setRoutesExistingNamesByTask({});
          setRoutesExistingXmlFilesByTask({});
          setRoutesMatrixSelection({});
          setRoutesMatrixError("Routes folder was not found under Source.");
          return;
        }

        setRoutesFolderId(selectedRoutesFolderId);

        // --- Prefetch all sub-folders in one server-side parallel call ---
        const allFolderIds = [selectedRoutesFolderId];
        if (selectedModelFolderId) allFolderIds.push(selectedModelFolderId);
        for (const taskOption of routesTabTaskOptions) {
          const folderCode = TASK_OPTION_TO_FOLDER_CODE[taskOption];
          const taskFolderId = normalizeDriveFolderId(
            selectedSourceContext?.taskFoldersByCode?.[folderCode]
              ?.googleDriveId,
          );
          if (taskFolderId) allFolderIds.push(taskFolderId);
        }
        const uniqueFolderIds = [...new Set(allFolderIds)];
        const prefetchUrl = baseUrl
          ? `${API_BASE_URL}/api/projects/${effectiveProjectId}/google-drive-prefetch?root_ids=${encodeURIComponent(uniqueFolderIds.join(","))}&${cacheBust}`
          : `/api/projects/${effectiveProjectId}/google-drive-prefetch?root_ids=${encodeURIComponent(uniqueFolderIds.join(","))}&${cacheBust}`;
        const prefetchResponse = await axios
          .get(prefetchUrl)
          .catch(() => ({ data: { folders: {} } }));
        const prefetchedFolders = prefetchResponse?.data?.folders || {};

        // --- Extract Routes contents from prefetch ---
        const routesItems = prefetchedFolders[selectedRoutesFolderId] || [];
        const routeXmlRows = routesItems
          .filter(
            (item) => !isFolderItem(item) && isXmlFileName(getFileName(item)),
          )
          .map((item) => ({
            id: getFileId(item),
            googleDriveId: normalizeDriveFolderId(getGoogleDriveId(item)),
            name: String(getFileName(item) || "").trim(),
            parentDriveId:
              normalizeDriveFolderId(item?.parentDriveId) ||
              selectedRoutesFolderId,
            modified: String(item?.modified || "").trim(),
            created: String(item?.created || "").trim(),
          }))
          .filter((item) => item.googleDriveId && item.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        const sourceRouteFiles = routesItems
          .filter((item) => !isFolderItem(item))
          .map((item) => ({
            id: getFileId(item),
            googleDriveId: normalizeDriveFolderId(getGoogleDriveId(item)),
            name: String(getFileName(item) || "").trim(),
            parentDriveId:
              normalizeDriveFolderId(item?.parentDriveId) ||
              selectedRoutesFolderId,
            modified: String(item?.modified || "").trim(),
            created: String(item?.created || "").trim(),
          }))
          .filter(
            (item) =>
              item.googleDriveId &&
              item.name &&
              (isXmlFileName(item.name) || isDxfFileName(item.name)),
          )
          .sort((a, b) => {
            const aTime = new Date(a.modified || a.created || 0).getTime();
            const bTime = new Date(b.modified || b.created || 0).getTime();
            if (aTime !== bTime) return bTime - aTime;
            return a.name.localeCompare(b.name);
          });

        // --- Extract Model contents from prefetch ---
        let nextModelFiles = [];
        if (selectedModelFolderId) {
          const modelItems = prefetchedFolders[selectedModelFolderId] || [];
          nextModelFiles = modelItems
            .filter((item) => !isFolderItem(item))
            .map((item) => ({
              id: getFileId(item),
              googleDriveId: normalizeDriveFolderId(getGoogleDriveId(item)),
              name: String(getFileName(item) || "").trim(),
              parentDriveId:
                normalizeDriveFolderId(item?.parentDriveId) ||
                selectedModelFolderId,
              modified: String(item?.modified || "").trim(),
              created: String(item?.created || "").trim(),
            }))
            .filter((item) => item.googleDriveId && item.name)
            .sort((a, b) => {
              const aTime = new Date(a.modified || a.created || 0).getTime();
              const bTime = new Date(b.modified || b.created || 0).getTime();
              if (aTime !== bTime) return bTime - aTime;
              return a.name.localeCompare(b.name);
            });
        }

        // --- Extract task folder contents from prefetch ---
        const existingNamesByTask = {};
        const existingXmlFilesByTask = {};
        for (const taskOption of routesTabTaskOptions) {
          const folderCode = TASK_OPTION_TO_FOLDER_CODE[taskOption];
          const taskFolderId = normalizeDriveFolderId(
            selectedSourceContext?.taskFoldersByCode?.[folderCode]
              ?.googleDriveId,
          );
          if (!taskFolderId) {
            existingNamesByTask[taskOption] = new Set();
            existingXmlFilesByTask[taskOption] = [];
            continue;
          }

          const taskItems = prefetchedFolders[taskFolderId] || [];
          const existingXmlItems = taskItems
            .filter(
              (item) => !isFolderItem(item) && isXmlFileName(getFileName(item)),
            )
            .map((item) => ({
              id: getFileId(item),
              googleDriveId: normalizeDriveFolderId(getGoogleDriveId(item)),
              name: String(getFileName(item) || "").trim(),
              parentDriveId:
                normalizeDriveFolderId(item?.parentDriveId) || taskFolderId,
            }))
            .filter((item) => item.googleDriveId && item.name);
          const namesSet = new Set(
            existingXmlItems
              .map((item) => normalizeFileNameKey(item.name))
              .filter(Boolean),
          );
          existingXmlFilesByTask[taskOption] = existingXmlItems;
          existingNamesByTask[taskOption] = namesSet;
        }

        // Default matrix selection:
        //   rsa-model:    derive from "already copied to RSA_<task>_*" (mirrors prior run).
        //   rsa-existing: every Route XML applies to every selected service. The pipeline
        //                 copies the relevant files into each RSA_<task>_* folder at run
        //                 time, so before the first run the RSA_<task>_* folders are empty
        //                 and the derived-from-presence default would leave every row
        //                 unchecked, surfacing as 0.00 mi / 0 mnv in the breakdown. User
        //                 can still uncheck specific (file, service) pairs in the matrix UI.
        const nextSelection = {};
        for (const row of routeXmlRows) {
          const rowSelection = {};
          const fileNameKey = normalizeFileNameKey(row.name);
          for (const taskOption of routesTabTaskOptions) {
            rowSelection[taskOption] = isRsaModelProject
              ? Boolean(existingNamesByTask[taskOption]?.has(fileNameKey))
              : true;
          }
          nextSelection[row.googleDriveId] = rowSelection;
        }

        setRoutesXmlFiles(routeXmlRows);
        setRoutesSourceFiles(sourceRouteFiles);
        setModelFiles(nextModelFiles);
        writeRoutesXmlCache(effectiveProjectId, routeXmlRows);
        setRoutesExistingNamesByTask(existingNamesByTask);
        setRoutesExistingXmlFilesByTask(existingXmlFilesByTask);
        setRoutesMatrixSelection((prev) => {
          if (
            !preserveLocalSelection ||
            !routesMatrixHasLocalChangesRef.current
          ) {
            return nextSelection;
          }

          const mergedSelection = { ...nextSelection };
          for (const [routeDriveId, previousRow] of Object.entries(
            prev || {},
          )) {
            if (
              !mergedSelection[routeDriveId] ||
              !previousRow ||
              typeof previousRow !== "object"
            ) {
              continue;
            }
            mergedSelection[routeDriveId] = {
              ...mergedSelection[routeDriveId],
              ...previousRow,
            };
          }
          return mergedSelection;
        });
        if (routeXmlRows.length === 0) {
          setRoutesMatrixError("No XML files were found in Source/Routes.");
        }
      } catch (error) {
        console.error("Failed to load Routes matrix data:", error);
        setRoutesSourceFolderId("");
        setRoutesFolderId("");
        setModelFolderId("");
        setRoutesTaskFoldersByCode({});
        setRoutesXmlFiles([]);
        setRoutesSourceFiles([]);
        setModelFiles([]);
        writeRoutesXmlCache(effectiveProjectId, []);
        setRoutesExistingNamesByTask({});
        setRoutesExistingXmlFilesByTask({});
        setRoutesMatrixSelection({});
        setRoutesMatrixError(
          error?.response?.data?.error || "Failed to load Routes table.",
        );
      } finally {
        if (manageLoadingState) {
          setRoutesMatrixLoading(false);
        }
      }
    },
    [effectiveProjectId, routesTabTaskOptions, isRsaModelProject],
  );

  // Invocation-id guard: every setState in loadExistingSourceData checks isCurrent()
  // before writing. Prevents the late Drive response from clobbering a fresh S3
  // result when sourceStorage flips mid-flight (or any other supersession).
  const existingSourceLoadIdRef = useRef(0);
  const loadExistingSourceData = useCallback(
    async ({ manageLoadingState = true } = {}) => {
      const myLoadId = ++existingSourceLoadIdRef.current;
      const isCurrent = () => myLoadId === existingSourceLoadIdRef.current;

      if (isRsaModelProject || !effectiveProjectId) {
        if (isCurrent()) {
          setExistingSourceFolderId("");
          setExistingSourceFiles([]);
        }
        return;
      }

      if (manageLoadingState) {
        setExistingSourceLoading(true);
      }

      try {
        const isProductionDomain =
          window.location.hostname.includes("123bimlive.com");
        const baseUrl =
          ENVIRONMENT === "production" || isProductionDomain
            ? ""
            : API_BASE_URL;
        const cacheBust = `_ts=${Date.now()}`;

        // ─── S3 branch (pilot projects only) ────────────────────────────────
        // Single ListObjectsV2 call; skips the Drive DB-lookup + Drive-API hops.
        // Gated by sourceStorage, which is populated on mount from
        // /api/projects/:id/source/s3-list and reflects SSM <name>_source_storage.
        if (sourceStorage === "s3") {
          const s3ListUrl = baseUrl
            ? `${API_BASE_URL}/api/projects/${effectiveProjectId}/source/s3-list?${cacheBust}`
            : `/api/projects/${effectiveProjectId}/source/s3-list?${cacheBust}`;
          const resp = await axios.get(s3ListUrl);
          if (!isCurrent()) return;
          const items = (resp?.data?.items || [])
            .map((it) => ({
              id: `s3:${it.key}`,
              googleDriveId: `s3:${it.key}`, // synthetic id — selection keys reuse googleDriveId
              name: String(it.name || "").trim(),
              s3Key: String(it.key || ""),
              storage: "s3",
              size: Number(it.size || 0),
              modified: String(it.modified || "").trim(),
              created: String(it.modified || "").trim(),
            }))
            .filter(
              (item) => item.name && item.s3Key && isLasLazFileName(item.name),
            )
            .sort((a, b) => {
              const aTime = new Date(a.modified || a.created || 0).getTime();
              const bTime = new Date(b.modified || b.created || 0).getTime();
              if (aTime !== bTime) return bTime - aTime;
              return a.name.localeCompare(b.name);
            });

          setExistingSourceFolderId("s3:source"); // sentinel truthy value
          setExistingSourceFiles(items);
          return;
        }

        // ─── Drive branch (existing behavior, untouched) ────────────────────
        const filesUrl = baseUrl
          ? `${API_BASE_URL}/api/projects/${effectiveProjectId}/files?${cacheBust}`
          : `/api/projects/${effectiveProjectId}/files?${cacheBust}`;
        const filesResponse = await axios.get(filesUrl);
        if (!isCurrent()) return;
        const files = filesResponse?.data?.files || [];
        const { sourceGoogleDriveIds } =
          deriveTaskMetadataFromProjectFiles(files);
        const sourceFolderDriveId = normalizeDriveFolderId(
          sourceGoogleDriveIds?.[0],
        );

        if (!sourceFolderDriveId) {
          if (isCurrent()) {
            setExistingSourceFolderId("");
            setExistingSourceFiles([]);
          }
          return;
        }

        const sourceContentsUrl = baseUrl
          ? `${API_BASE_URL}/api/projects/${effectiveProjectId}/google-drive-contents?folder_id=${encodeURIComponent(sourceFolderDriveId)}&${cacheBust}`
          : `/api/projects/${effectiveProjectId}/google-drive-contents?folder_id=${encodeURIComponent(sourceFolderDriveId)}&${cacheBust}`;
        const sourceContentsResponse = await axios.get(sourceContentsUrl);
        if (!isCurrent()) return;
        const nextSourceFiles = (sourceContentsResponse?.data?.items || [])
          .filter((item) => !isFolderItem(item))
          .map((item) => ({
            id: getFileId(item),
            googleDriveId: normalizeDriveFolderId(getGoogleDriveId(item)),
            name: String(getFileName(item) || "").trim(),
            parentDriveId:
              normalizeDriveFolderId(item?.parentDriveId) ||
              sourceFolderDriveId,
            modified: String(item?.modified || "").trim(),
            created: String(item?.created || "").trim(),
          }))
          .filter(
            (item) =>
              item.googleDriveId && item.name && isLasLazFileName(item.name),
          )
          .sort((a, b) => {
            const aTime = new Date(a.modified || a.created || 0).getTime();
            const bTime = new Date(b.modified || b.created || 0).getTime();
            if (aTime !== bTime) return bTime - aTime;
            return a.name.localeCompare(b.name);
          });

        setExistingSourceFolderId(sourceFolderDriveId);
        setExistingSourceFiles(nextSourceFiles);
      } catch (error) {
        if (
          error?.response?.status === 409 &&
          error?.response?.data?.error === "no_storage_configured"
        ) {
          if (isCurrent()) {
            setNoStorageConfigured(true);
            setExistingSourceFolderId("");
            setExistingSourceFiles([]);
          }
        } else {
          console.error("Failed to load existing-project Source files:", error);
          if (isCurrent()) {
            setExistingSourceFolderId("");
            setExistingSourceFiles([]);
          }
        }
      } finally {
        if (manageLoadingState && isCurrent()) {
          setExistingSourceLoading(false);
        }
      }
    },
    [effectiveProjectId, isRsaModelProject, sourceStorage],
  );

  useEffect(() => {
    if (!effectiveProjectId || isRsaModelProject) {
      setExistingSourceFolderId("");
      setExistingSourceFiles([]);
      setExistingSourceLoading(false);
      setShowExistingSourceUploadModal(false);
      setUploadTableSelection((prev) => ({ ...prev, source: [] }));
    }
  }, [effectiveProjectId, isRsaModelProject]);

  const saveRoutesMatrixSelection = useCallback(async () => {
    if (!effectiveProjectId) {
      showNotification("Select a project first.");
      return { ok: false, reason: "missing_project" };
    }
    if (!routesFolderId) {
      showNotification("Routes folder was not found under Source.");
      return { ok: false, reason: "missing_routes_folder" };
    }
    if (!Array.isArray(routesXmlFiles) || routesXmlFiles.length === 0) {
      showNotification("No route XML files found in Source/Routes.");
      return { ok: false, reason: "missing_route_files" };
    }
    if (
      !Array.isArray(routesTabTaskOptions) ||
      routesTabTaskOptions.length === 0
    ) {
      showNotification("No analysis options are available for mapping.");
      return { ok: false, reason: "missing_task_options" };
    }

    const groupedByDestination = {};
    const missingTaskFolders = new Set();
    const selectedNameKeysByTask = {};
    let skippedAlreadyExisting = 0;
    for (const taskOption of routesTabTaskOptions) {
      selectedNameKeysByTask[taskOption] = new Set();
    }

    for (const routeFile of routesXmlFiles) {
      const sourceDriveId = normalizeDriveFolderId(routeFile?.googleDriveId);
      if (!sourceDriveId) {
        continue;
      }
      const fileName = String(routeFile?.name || "").trim();
      if (!fileName) {
        continue;
      }
      const fileNameKey = normalizeFileNameKey(fileName);
      const rowSelection = routesMatrixSelection[sourceDriveId] || {};

      for (const taskOption of routesTabTaskOptions) {
        if (!rowSelection[taskOption]) {
          continue;
        }
        selectedNameKeysByTask[taskOption]?.add(fileNameKey);
        const folderCode = TASK_OPTION_TO_FOLDER_CODE[taskOption];
        const destinationFolderId = normalizeDriveFolderId(
          routesTaskFoldersByCode?.[folderCode]?.googleDriveId,
        );
        if (!destinationFolderId) {
          missingTaskFolders.add(taskOption);
          continue;
        }

        const existingNames = routesExistingNamesByTask[taskOption];
        if (existingNames instanceof Set && existingNames.has(fileNameKey)) {
          skippedAlreadyExisting += 1;
          continue;
        }

        if (!groupedByDestination[destinationFolderId]) {
          groupedByDestination[destinationFolderId] = {
            taskOptions: new Set(),
            itemsBySource: new Map(),
          };
        }
        groupedByDestination[destinationFolderId].taskOptions.add(taskOption);
        groupedByDestination[destinationFolderId].itemsBySource.set(
          sourceDriveId,
          {
            id: `gd_${sourceDriveId}`,
            googleDriveId: sourceDriveId,
            name: fileName,
            isGoogleDriveItem: true,
            isFolder: false,
            parentDriveId: routesFolderId,
          },
        );
      }
    }

    const destinationEntries = Object.entries(groupedByDestination)
      .map(([destinationFolderId, group]) => ({
        destinationFolderId,
        taskOptions: Array.from(group.taskOptions),
        items: Array.from(group.itemsBySource.values()),
      }))
      .filter((entry) => entry.items.length > 0);

    const deleteEntries = routesTabTaskOptions
      .map((taskOption) => {
        const folderCode = TASK_OPTION_TO_FOLDER_CODE[taskOption];
        const destinationFolderId = normalizeDriveFolderId(
          routesTaskFoldersByCode?.[folderCode]?.googleDriveId,
        );
        if (!destinationFolderId) {
          return null;
        }

        const selectedNames = selectedNameKeysByTask[taskOption] || new Set();
        const existingXmlItems = Array.isArray(
          routesExistingXmlFilesByTask[taskOption],
        )
          ? routesExistingXmlFilesByTask[taskOption]
          : [];
        const itemsToDelete = existingXmlItems
          .filter((item) => {
            const itemNameKey = normalizeFileNameKey(item?.name);
            if (!itemNameKey) return false;
            return !selectedNames.has(itemNameKey);
          })
          .map((item) => ({
            id: item.id || `gd_${item.googleDriveId}`,
            googleDriveId: item.googleDriveId,
            name: item.name,
            isFolder: false,
            folder_drive_id: destinationFolderId,
            parentDriveId: destinationFolderId,
          }));

        if (itemsToDelete.length === 0) {
          return null;
        }

        return {
          taskOption,
          destinationFolderId,
          items: itemsToDelete,
        };
      })
      .filter(Boolean);

    if (destinationEntries.length === 0 && deleteEntries.length === 0) {
      return {
        ok: missingTaskFolders.size === 0,
        changed: false,
        skippedAlreadyExisting,
        missingTaskFolders: Array.from(missingTaskFolders),
      };
    }

    setRoutesMatrixSaving(true);
    try {
      const isProductionDomain =
        window.location.hostname.includes("123bimlive.com");
      const baseUrl =
        ENVIRONMENT === "production" || isProductionDomain ? "" : API_BASE_URL;
      const pasteUrl = baseUrl
        ? `${API_BASE_URL}/api/projects/${effectiveProjectId}/files/paste`
        : `/api/projects/${effectiveProjectId}/files/paste`;
      const deleteUrl = baseUrl
        ? `${API_BASE_URL}/api/projects/${effectiveProjectId}/google-drive-files/delete`
        : `/api/projects/${effectiveProjectId}/google-drive-files/delete`;

      const copyResults = await Promise.all(
        destinationEntries.map(async (entry) => {
          try {
            const response = await axios.post(pasteUrl, {
              operation: "copy",
              destination_drive_id: entry.destinationFolderId,
              items: entry.items,
            });
            return {
              destinationFolderId: entry.destinationFolderId,
              taskOptions: entry.taskOptions,
              status: response.status,
              processedCount: Number(response?.data?.processed_count) || 0,
              errors: Array.isArray(response?.data?.errors)
                ? response.data.errors
                : [],
            };
          } catch (error) {
            return {
              destinationFolderId: entry.destinationFolderId,
              taskOptions: entry.taskOptions,
              status: error?.response?.status || 500,
              processedCount:
                Number(error?.response?.data?.processed_count) || 0,
              errors: Array.isArray(error?.response?.data?.errors)
                ? error.response.data.errors
                : [
                    {
                      error:
                        error?.response?.data?.error ||
                        error.message ||
                        "Copy failed",
                    },
                  ],
            };
          }
        }),
      );
      const deleteResults = await Promise.all(
        deleteEntries.map(async (entry) => {
          try {
            const response = await axios.delete(deleteUrl, {
              data: {
                folder_drive_id: entry.destinationFolderId,
                items: entry.items,
              },
              headers: {
                "Content-Type": "application/json",
              },
            });
            return {
              destinationFolderId: entry.destinationFolderId,
              taskOption: entry.taskOption,
              status: response.status,
              deletedCount: Number(response?.data?.deleted_count) || 0,
              errors: Array.isArray(response?.data?.errors)
                ? response.data.errors
                : [],
            };
          } catch (error) {
            return {
              destinationFolderId: entry.destinationFolderId,
              taskOption: entry.taskOption,
              status: error?.response?.status || 500,
              deletedCount: Number(error?.response?.data?.deleted_count) || 0,
              errors: Array.isArray(error?.response?.data?.errors)
                ? error.response.data.errors
                : [
                    {
                      error:
                        error?.response?.data?.error ||
                        error.message ||
                        "Delete failed",
                    },
                  ],
            };
          }
        }),
      );

      const totalCopied = copyResults.reduce(
        (sum, result) => sum + (Number(result.processedCount) || 0),
        0,
      );
      const totalDeleted = deleteResults.reduce(
        (sum, result) => sum + (Number(result.deletedCount) || 0),
        0,
      );
      const copyErrors = copyResults.reduce((sum, result) => {
        const count = Array.isArray(result.errors) ? result.errors.length : 0;
        return sum + count;
      }, 0);
      const deleteErrors = deleteResults.reduce((sum, result) => {
        const count = Array.isArray(result.errors) ? result.errors.length : 0;
        return sum + count;
      }, 0);
      const totalErrors = copyErrors + deleteErrors;

      const syncedAt = Date.now();
      try {
        sessionStorage.setItem(
          `rsaMileageCachesSynced:${effectiveProjectId}`,
          String(syncedAt),
        );
      } catch (e) {
        // ignore storage errors
      }
      window.dispatchEvent(
        new CustomEvent("rsa-mileage-caches-synced", {
          detail: { projectId: effectiveProjectId, syncedAt },
        }),
      );

      routesMatrixHasLocalChangesRef.current = false;
      await loadRoutesTabData({ preserveLocalSelection: false });

      const missingHint =
        missingTaskFolders.size > 0
          ? ` Missing RSA folder(s): ${Array.from(missingTaskFolders).join(", ")}.`
          : "";
      if (totalErrors > 0) {
        showNotification(
          `Saved with warnings: copied ${totalCopied} file(s), deleted ${totalDeleted} file(s), ${totalErrors} error(s).${missingHint}`,
        );
        return {
          ok: false,
          changed: true,
          totalCopied,
          totalDeleted,
          totalErrors,
          missingTaskFolders: Array.from(missingTaskFolders),
        };
      } else {
        const skippedHint =
          skippedAlreadyExisting > 0
            ? ` ${skippedAlreadyExisting} already existed and were skipped.`
            : "";
        // Keep the user in the current routes tab instead of navigating away automatically.
        // setActiveMainTab('analysis'); -- removed to prevent unexpected tab switching.
        return {
          ok: missingTaskFolders.size === 0,
          changed: true,
          totalCopied,
          totalDeleted,
          totalErrors,
          skippedAlreadyExisting,
          missingTaskFolders: Array.from(missingTaskFolders),
        };
      }
    } catch (error) {
      console.error("Failed to save route assignments before run:", error);
      showNotification(
        `Failed to save route assignments: ${error?.response?.data?.error || error.message || "Unknown error"}`,
      );
      return {
        ok: false,
        changed: false,
        reason: "unexpected_error",
        error,
      };
    } finally {
      setRoutesMatrixSaving(false);
    }
  }, [
    effectiveProjectId,
    routesFolderId,
    routesXmlFiles,
    routesTabTaskOptions,
    routesMatrixSelection,
    routesTaskFoldersByCode,
    routesExistingNamesByTask,
    routesExistingXmlFilesByTask,
    showNotification,
    loadRoutesTabData,
  ]);

  useEffect(() => {
    if (isRsaModelProject && activeMainTab !== "routes") return;
    if (!isRsaModelProject && activeMainTab !== "analysis") return;
    loadRoutesTabData();
  }, [isRsaModelProject, activeMainTab, loadRoutesTabData]);

  useEffect(() => {
    if (activeMainTab !== "upload" || !effectiveProjectId) {
      return;
    }
    if (hasRoutesUploadStatusCache(effectiveProjectId)) {
      const cachedRouteFiles = readRoutesXmlCache(effectiveProjectId);
      setRoutesXmlFiles(cachedRouteFiles);
    }
    loadRoutesTabData();
  }, [activeMainTab, effectiveProjectId, loadRoutesTabData]);

  useEffect(() => {
    if (
      isRsaModelProject ||
      activeMainTab !== "upload" ||
      !effectiveProjectId
    ) {
      return;
    }
    setExistingSourceLoading(true);
    loadExistingSourceData();
  }, [
    isRsaModelProject,
    activeMainTab,
    effectiveProjectId,
    loadExistingSourceData,
  ]);

  const handleOpenRoutesUploadModal = useCallback(async () => {
    if (!routesFolderId) {
      await loadRoutesTabData();
    }
    setShowRoutesUploadModal(true);
  }, [routesFolderId, loadRoutesTabData]);

  const handleOpenModelUploadModal = useCallback(async () => {
    if (!modelFolderId) {
      await loadRoutesTabData();
    }
    setShowModelUploadModal(true);
  }, [modelFolderId, loadRoutesTabData]);

  const handleOpenExistingSourceUploadModal = useCallback(async () => {
    if (!existingSourceFolderId) {
      await loadExistingSourceData();
    }
    setShowExistingSourceUploadModal(true);
  }, [existingSourceFolderId, loadExistingSourceData]);

  const refreshRoutesTabDataAfterUpload = useCallback(async () => {
    const retryDelaysMs = [0, 700, 1600];
    setRoutesMatrixLoading(true);
    try {
      for (const delayMs of retryDelaysMs) {
        if (delayMs > 0) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
        await loadRoutesTabData({ manageLoadingState: false });
      }
    } finally {
      setRoutesMatrixLoading(false);
    }
  }, [loadRoutesTabData]);

  const refreshExistingSourceDataAfterUpload = useCallback(
    async (args = {}) => {
      const uploadedFiles = Array.isArray(args?.uploadedFiles)
        ? args.uploadedFiles
        : [];

      // Optimistic append — instant feedback before the backend list call returns.
      // Only for S3 projects (Drive path's rows require a real googleDriveId, which
      // we don't have yet until the Drive folder listing catches up).
      if (sourceStorage === "s3" && uploadedFiles.length > 0) {
        const nowIso = new Date().toISOString();
        const optimisticRows = uploadedFiles
          .map((f) => ({
            name: String(f?.name || "").trim(),
            size: Number(f?.size || 0),
          }))
          .filter((f) => f.name && isLasLazFileName(f.name))
          .map((f) => ({
            id: `s3:source/${f.name}`,
            googleDriveId: `s3:source/${f.name}`,
            name: f.name,
            s3Key: `source/${f.name}`,
            storage: "s3",
            size: f.size,
            modified: nowIso,
            created: nowIso,
            _optimistic: true,
          }));

        if (optimisticRows.length > 0) {
          setExistingSourceFolderId((prev) => prev || "s3:source");
          setExistingSourceFiles((prev) => {
            const existing = Array.isArray(prev) ? prev : [];
            const existingKeys = new Set(
              existing.map((r) => r?.s3Key || r?.googleDriveId),
            );
            const additions = optimisticRows.filter(
              (r) => !existingKeys.has(r.s3Key),
            );
            return [...additions, ...existing];
          });
        }
      }

      // Background reconcile — S3 is strongly consistent; one call is enough.
      // Drive path keeps the original retry cadence (Drive listing has a 30s cache).
      if (sourceStorage === "s3") {
        setExistingSourceLoading(true);
        try {
          await loadExistingSourceData({ manageLoadingState: false });
        } finally {
          setExistingSourceLoading(false);
        }
        return;
      }

      const retryDelaysMs = [0, 700, 1600];
      setExistingSourceLoading(true);
      try {
        for (const delayMs of retryDelaysMs) {
          if (delayMs > 0) {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
          }
          await loadExistingSourceData({ manageLoadingState: false });
        }
      } finally {
        setExistingSourceLoading(false);
      }
    },
    [loadExistingSourceData, sourceStorage],
  );

  const selectedRouteUploadFiles = useMemo(() => {
    const selectedIds = new Set(
      (uploadTableSelection.routes || [])
        .map((id) => normalizeDriveFolderId(id))
        .filter(Boolean),
    );
    return (Array.isArray(routesSourceFiles) ? routesSourceFiles : []).filter(
      (file) => selectedIds.has(normalizeDriveFolderId(file?.googleDriveId)),
    );
  }, [routesSourceFiles, uploadTableSelection.routes]);

  const selectedModelUploadFiles = useMemo(() => {
    const selectedIds = new Set(
      (uploadTableSelection.model || [])
        .map((id) => normalizeDriveFolderId(id))
        .filter(Boolean),
    );
    return (Array.isArray(modelFiles) ? modelFiles : []).filter((file) =>
      selectedIds.has(normalizeDriveFolderId(file?.googleDriveId)),
    );
  }, [modelFiles, uploadTableSelection.model]);

  const selectedExistingSourceUploadFiles = useMemo(() => {
    const selectedIds = new Set(
      (uploadTableSelection.source || [])
        .map((id) => normalizeDriveFolderId(id))
        .filter(Boolean),
    );
    return (
      Array.isArray(existingSourceFiles) ? existingSourceFiles : []
    ).filter((file) =>
      selectedIds.has(normalizeDriveFolderId(file?.googleDriveId)),
    );
  }, [existingSourceFiles, uploadTableSelection.source]);

  const uploadDeleteBusy = uploadFileDeletePendingIds.length > 0;

  const setUploadTableSelectionForScope = useCallback((scope, nextIds) => {
    setUploadTableSelection((prev) => ({
      ...prev,
      [scope]: Array.from(
        new Set(
          (Array.isArray(nextIds) ? nextIds : [])
            .map((id) => normalizeDriveFolderId(id))
            .filter(Boolean),
        ),
      ),
    }));
  }, []);

  const toggleUploadTableFileSelection = useCallback(
    (scope, file) => {
      if (uploadDeleteBusy) return;
      const driveFileId = normalizeDriveFolderId(file?.googleDriveId);
      if (!driveFileId) return;
      setUploadTableSelection((prev) => {
        const current = Array.isArray(prev?.[scope]) ? prev[scope] : [];
        const exists = current.includes(driveFileId);
        return {
          ...prev,
          [scope]: exists
            ? current.filter((id) => id !== driveFileId)
            : [...current, driveFileId],
        };
      });
    },
    [uploadDeleteBusy],
  );

  const handleToggleAllUploadTableFiles = useCallback(
    (scope, files) => {
      if (uploadDeleteBusy) return;
      const normalizedIds = (Array.isArray(files) ? files : [])
        .map((file) => normalizeDriveFolderId(file?.googleDriveId))
        .filter(Boolean);
      if (normalizedIds.length === 0) {
        setUploadTableSelectionForScope(scope, []);
        return;
      }
      setUploadTableSelection((prev) => {
        const current = Array.isArray(prev?.[scope]) ? prev[scope] : [];
        const allSelected = normalizedIds.every((id) => current.includes(id));
        return {
          ...prev,
          [scope]: allSelected ? [] : normalizedIds,
        };
      });
    },
    [setUploadTableSelectionForScope, uploadDeleteBusy],
  );

  const handleRequestUploadFileDelete = useCallback(
    (files, folderDriveId, folderLabel) => {
      const normalizedFiles = (Array.isArray(files) ? files : [files])
        .filter(Boolean)
        .map((file) => ({
          ...file,
          googleDriveId: normalizeDriveFolderId(file?.googleDriveId),
          isFolder: false,
        }))
        .filter((file) => file.googleDriveId);

      if (!effectiveProjectId || normalizedFiles.length === 0) {
        showNotification(
          "Could not remove file: missing Google Drive file ID.",
        );
        return;
      }

      setUploadFileDeleteCandidate({
        files: normalizedFiles,
        folderDriveId: normalizeDriveFolderId(
          folderDriveId || normalizedFiles[0]?.parentDriveId,
        ),
        folderLabel:
          String(folderLabel || "Source folder").trim() || "Source folder",
      });
    },
    [effectiveProjectId, showNotification],
  );

  const removeUploadFilesFromLocalState = useCallback(
    (files) => {
      const driveFileIds = new Set(
        (Array.isArray(files) ? files : [])
          .map((file) => normalizeDriveFolderId(file?.googleDriveId))
          .filter(Boolean),
      );

      if (driveFileIds.size === 0) {
        return;
      }

      setRoutesSourceFiles((prev) =>
        (Array.isArray(prev) ? prev : []).filter(
          (item) =>
            !driveFileIds.has(normalizeDriveFolderId(item?.googleDriveId)),
        ),
      );

      setModelFiles((prev) =>
        (Array.isArray(prev) ? prev : []).filter(
          (item) =>
            !driveFileIds.has(normalizeDriveFolderId(item?.googleDriveId)),
        ),
      );

      setExistingSourceFiles((prev) =>
        (Array.isArray(prev) ? prev : []).filter(
          (item) =>
            !driveFileIds.has(normalizeDriveFolderId(item?.googleDriveId)),
        ),
      );

      setRoutesXmlFiles((prev) => {
        const next = (Array.isArray(prev) ? prev : []).filter(
          (item) =>
            !driveFileIds.has(normalizeDriveFolderId(item?.googleDriveId)),
        );
        writeRoutesXmlCache(effectiveProjectId, next);
        return next;
      });

      setRoutesMatrixSelection((prev) => {
        if (!prev || typeof prev !== "object") {
          return prev;
        }
        const next = { ...prev };
        driveFileIds.forEach((driveFileId) => {
          delete next[driveFileId];
        });
        return next;
      });

      setUploadTableSelection((prev) => ({
        ...prev,
        routes: (Array.isArray(prev?.routes) ? prev.routes : []).filter(
          (id) => !driveFileIds.has(normalizeDriveFolderId(id)),
        ),
        model: (Array.isArray(prev?.model) ? prev.model : []).filter(
          (id) => !driveFileIds.has(normalizeDriveFolderId(id)),
        ),
        source: (Array.isArray(prev?.source) ? prev.source : []).filter(
          (id) => !driveFileIds.has(normalizeDriveFolderId(id)),
        ),
      }));
    },
    [effectiveProjectId],
  );

  const handleConfirmUploadFileDelete = useCallback(async () => {
    const candidate = uploadFileDeleteCandidate;
    const candidateFiles = Array.isArray(candidate?.files)
      ? candidate.files
      : [];
    if (!effectiveProjectId || candidateFiles.length === 0) {
      return;
    }

    // Homogeneity check — LAS table is always one storage per project.
    const isS3 = candidateFiles.every((f) => f?.storage === "s3");
    const isDrive = candidateFiles.every((f) => f?.storage !== "s3");
    if (!isS3 && !isDrive) {
      showNotification("Cannot mix Drive and S3 files in a single delete.");
      return;
    }

    // `googleDriveId` is synthetic ("s3:<key>") for S3 rows; real for Drive rows.
    // Either way it's the stable selector used by removeUploadFilesFromLocalState.
    const pendingIds = candidateFiles
      .map((f) => normalizeDriveFolderId(f?.googleDriveId))
      .filter(Boolean);
    if (pendingIds.length === 0) {
      return;
    }

    setUploadFileDeletePendingIds(pendingIds);
    try {
      let filesToRemoveLocally = candidateFiles;

      if (isS3) {
        const keys = candidateFiles
          .map((f) => String(f?.s3Key || "").trim())
          .filter(Boolean);
        if (keys.length === 0) {
          throw new Error("Selected S3 files are missing s3Key metadata.");
        }
        const result = await fileService.deleteS3SourceObjects(
          effectiveProjectId,
          keys,
        );
        const errorsList = Array.isArray(result?.errors) ? result.errors : [];
        if (errorsList.length > 0) {
          console.warn("Partial S3 delete errors:", errorsList);
          const failedKeys = new Set(
            errorsList.map((e) => e?.key).filter(Boolean),
          );
          filesToRemoveLocally = candidateFiles.filter(
            (f) => !failedKeys.has(String(f?.s3Key || "").trim()),
          );
          showNotification(
            `Removed ${keys.length - errorsList.length} of ${keys.length} file(s); ${errorsList.length} failed.`,
          );
        }
      } else {
        await fileService.deleteGoogleDriveFiles(
          effectiveProjectId,
          candidateFiles,
          candidate.folderDriveId || candidateFiles[0]?.parentDriveId || null,
        );
      }

      removeUploadFilesFromLocalState(filesToRemoveLocally);
    } catch (error) {
      console.error("Failed to remove upload-step file:", error);
      showNotification(
        error?.message ||
          `Failed to remove ${candidateFiles.length === 1 ? "file" : "files"} from ${candidate.folderLabel}.`,
      );
    } finally {
      setUploadFileDeletePendingIds([]);
      setUploadFileDeleteCandidate(null);
    }
  }, [
    effectiveProjectId,
    removeUploadFilesFromLocalState,
    showNotification,
    uploadFileDeleteCandidate,
  ]);

  useEffect(() => {
    const validRouteIds = new Set(
      (Array.isArray(routesSourceFiles) ? routesSourceFiles : [])
        .map((file) => normalizeDriveFolderId(file?.googleDriveId))
        .filter(Boolean),
    );
    const validModelIds = new Set(
      (Array.isArray(modelFiles) ? modelFiles : [])
        .map((file) => normalizeDriveFolderId(file?.googleDriveId))
        .filter(Boolean),
    );
    const validSourceIds = new Set(
      (Array.isArray(existingSourceFiles) ? existingSourceFiles : [])
        .map((file) => normalizeDriveFolderId(file?.googleDriveId))
        .filter(Boolean),
    );
    setUploadTableSelection((prev) => {
      const nextRoutes = (
        Array.isArray(prev?.routes) ? prev.routes : []
      ).filter((id) => validRouteIds.has(normalizeDriveFolderId(id)));
      const nextModel = (Array.isArray(prev?.model) ? prev.model : []).filter(
        (id) => validModelIds.has(normalizeDriveFolderId(id)),
      );
      const nextSource = (
        Array.isArray(prev?.source) ? prev.source : []
      ).filter((id) => validSourceIds.has(normalizeDriveFolderId(id)));
      const sameRoutes =
        nextRoutes.length ===
        (Array.isArray(prev?.routes) ? prev.routes.length : 0);
      const sameModel =
        nextModel.length ===
        (Array.isArray(prev?.model) ? prev.model.length : 0);
      const sameSource =
        nextSource.length ===
        (Array.isArray(prev?.source) ? prev.source.length : 0);
      if (sameRoutes && sameModel && sameSource) {
        return prev;
      }
      return {
        ...prev,
        routes: nextRoutes,
        model: nextModel,
        source: nextSource,
      };
    });
  }, [routesSourceFiles, modelFiles, existingSourceFiles]);

  useEffect(() => {
    setRoutesMatrixSelection((prev) => {
      const next = {};
      for (const routeFile of routesXmlFiles) {
        const rowKey = normalizeDriveFolderId(routeFile?.googleDriveId);
        if (!rowKey) continue;
        const previousRow = prev[rowKey] || {};
        const nextRow = {};
        for (const taskOption of routesTabTaskOptions) {
          nextRow[taskOption] = Boolean(previousRow[taskOption]);
        }
        next[rowKey] = nextRow;
      }
      return next;
    });
  }, [routesXmlFiles, routesTabTaskOptions]);

  useEffect(() => {
    setHasSubmittedRsaRun(false);
  }, [effectiveProjectId, selectedTasks]);

  // --- State for Dashboard ---
  const [dashboardSelectedRun, setDashboardSelectedRun] = useState(null);
  const [optimisticRuns, setOptimisticRuns] = useState([]);
  const [pendingDashboardRunId, setPendingDashboardRunId] = useState(null);
  const [pendingLocalRunId, setPendingLocalRunId] = useState(null);
  const [dashboardDeletingRunId, setDashboardDeletingRunId] = useState(null);
  const [dashboardDeleteCandidateRunId, setDashboardDeleteCandidateRunId] =
    useState(null);
  const [dashboardDeletingAllRuns, setDashboardDeletingAllRuns] =
    useState(false);

  // Connect Dashboard to backend/Redis: load user runs and build projects from runs
  const dashboardUserEmail = sessionStorage.getItem("userEmail") || null;
  const {
    runs: apiRuns,
    loading: dashboardLoading,
    error: dashboardError,
    refresh: refreshDashboardRuns,
    removeRun: removeDashboardRun,
    runProgress: dashboardRunProgress,
  } = useUserRuns(dashboardUserEmail, {
    autoRefresh: true,
    refreshInterval: 60000,
    connectToActiveStreams: false,
    projectType: normalizedProjectType || null,
  });

  // Merge API runs with optimistic runs (optimistic = just submitted; dedupe by run_id, API wins)
  const displayRuns = useMemo(() => {
    const apiRunIds = new Set((apiRuns || []).map((r) => r.run_id));
    const onlyOptimistic = (optimisticRuns || []).filter(
      (o) => !apiRunIds.has(o.run_id),
    );
    const merged = [...(apiRuns || []), ...onlyOptimistic];
    merged.sort((a, b) => {
      const tA = a.last_updated?.getTime() ?? a.created_at?.getTime() ?? 0;
      const tB = b.last_updated?.getTime() ?? b.created_at?.getTime() ?? 0;
      return tB - tA;
    });
    return merged;
  }, [apiRuns, optimisticRuns]);

  // Backward compatibility: alias for any leftover references (e.g. hot-reload)
  const dashboardMockRuns = displayRuns;

  // Keep selected run synced with latest run object coming from live updates.
  useEffect(() => {
    if (!dashboardSelectedRun?.run_id) return;
    const latestSelected = displayRuns.find(
      (r) => r.run_id === dashboardSelectedRun.run_id,
    );
    if (latestSelected && latestSelected !== dashboardSelectedRun) {
      setDashboardSelectedRun(latestSelected);
    }
  }, [dashboardSelectedRun, displayRuns]);

  // RSA-model launch UX: after launching a run, select it once it appears in the list.
  // Do not force displayRuns[0] when the user picks another run - that was resetting selection.
  useEffect(() => {
    if (!isRsaModelProject || activeMainTab !== "dashboard") return;
    if (!Array.isArray(displayRuns) || displayRuns.length === 0) return;

    if (pendingDashboardRunId) {
      const pendingRun = displayRuns.find(
        (r) => r.run_id === pendingDashboardRunId,
      );
      if (pendingRun) {
        if (
          !dashboardSelectedRun ||
          dashboardSelectedRun.run_id !== pendingRun.run_id
        ) {
          setDashboardSelectedRun(pendingRun);
        }
        setPendingDashboardRunId(null);
      }
      return;
    }

    if (!dashboardSelectedRun?.run_id) {
      setDashboardSelectedRun(displayRuns[0]);
      return;
    }
    const stillThere = displayRuns.some(
      (r) => r.run_id === dashboardSelectedRun.run_id,
    );
    if (!stillThere) {
      setDashboardSelectedRun(displayRuns[0]);
    }
  }, [
    isRsaModelProject,
    activeMainTab,
    displayRuns,
    pendingDashboardRunId,
    dashboardSelectedRun,
  ]);

  // Build project tiles from runs (group by project_id)
  const dashboardProjectsData = useMemo(() => {
    const byProject = new Map();
    for (const run of displayRuns) {
      const id = run.project_id ?? "global";
      if (!byProject.has(id)) {
        byProject.set(id, {
          id,
          name: run.project_name ?? run.name ?? `Project ${id}`,
          description: `Pipeline runs for project ${id}`,
          total_analyses: 0,
          completed_analyses: 0,
          running_analyses: 0,
          last_updated: run.last_updated ?? run.created_at ?? new Date(),
        });
      }
      const p = byProject.get(id);
      p.total_analyses += 1;
      if (run.status === "completed") p.completed_analyses += 1;
      if (run.status === "processing" || run.status === "running")
        p.running_analyses += 1;
      const runTime = run.last_updated?.getTime() ?? run.created_at?.getTime();
      if (runTime && runTime > (p.last_updated?.getTime() ?? 0))
        p.last_updated = run.last_updated ?? run.created_at;
    }
    return Array.from(byProject.values()).sort(
      (a, b) =>
        (b.last_updated?.getTime() ?? 0) - (a.last_updated?.getTime() ?? 0),
    );
  }, [displayRuns]);

  const handleBrowseDeliverables = useCallback(() => {
    if (typeof onGoToFiles !== "function") {
      return;
    }

    if (effectiveDeliverablesFolderTreeId) {
      onGoToFiles({
        id: effectiveDeliverablesFolderTreeId,
        name: deliverablesFolderInfo.name || "Deliverables",
      });
      return;
    }

    onGoToFiles();
  }, [
    deliverablesFolderInfo.name,
    effectiveDeliverablesFolderTreeId,
    onGoToFiles,
  ]);

  const previousDeliverablesBreadcrumbStep = useMemo(
    () =>
      deliverablesCurrentPath.length > 1
        ? deliverablesCurrentPath[deliverablesCurrentPath.length - 2]
        : null,
    [deliverablesCurrentPath],
  );

  const previousDeliverablesBreadcrumbTooltip =
    previousDeliverablesBreadcrumbStep
      ? `Back to ${previousDeliverablesBreadcrumbStep.name || "previous folder"}`
      : "Back";

  const deliverablesBreadcrumbItems = useMemo(() => {
    const pathItems =
      deliverablesCurrentPath.length > 0
        ? deliverablesCurrentPath
        : deliverablesScopedRootFolder
          ? [
              {
                id: deliverablesScopedRootFolder.id,
                name: deliverablesScopedRootFolder.name,
              },
            ]
          : [];

    return pathItems.map((pathItem, index) => ({
      ...pathItem,
      displayName:
        index === 0 ? "Deliverables" : pathItem.name || "Untitled folder",
    }));
  }, [deliverablesCurrentPath, deliverablesScopedRootFolder]);

  const handleDeliverablesRefresh = useCallback(() => {
    deliverablesExplorerRef.current?.handleRefresh?.();
  }, []);

  const handleDeliverablesDownload = useCallback((e) => {
    e.stopPropagation();
    deliverablesExplorerRef.current?.handleDownload?.();
  }, []);

  const handleDeliverablesDelete = useCallback((e) => {
    e.stopPropagation();
    deliverablesExplorerRef.current?.handleDelete?.();
  }, []);

  const handleDeliverablesBreadcrumbClick = useCallback(
    (index, e) => {
      e.stopPropagation();
      const nextPath = deliverablesCurrentPath.slice(0, index + 1);
      if (nextPath.length === 0) {
        return;
      }
      setDeliverablesCurrentPath(nextPath);
      const targetStep = nextPath[nextPath.length - 1];
      setDeliverablesSelectedFolder({
        id: targetStep.id,
        name: targetStep.name,
      });
    },
    [deliverablesCurrentPath],
  );

  const handleDeliverablesBack = useCallback(
    (e) => {
      e.stopPropagation();
      if (!previousDeliverablesBreadcrumbStep) {
        return;
      }
      setDeliverablesSelectedFolder({
        id: previousDeliverablesBreadcrumbStep.id,
        name: previousDeliverablesBreadcrumbStep.name,
      });
    },
    [previousDeliverablesBreadcrumbStep],
  );

  // Prune optimistic runs older than 5 min (API should have picked them up)
  useEffect(() => {
    if (optimisticRuns.length === 0) return;
    const id = setInterval(
      () =>
        setOptimisticRuns((prev) =>
          prev.filter((o) => {
            const age =
              Date.now() -
              (o.created_at?.getTime() ?? o.last_updated?.getTime() ?? 0);
            return age < 5 * 60 * 1000;
          }),
        ),
      60 * 1000,
    );
    return () => clearInterval(id);
  }, [optimisticRuns.length]);

  // Legacy mock data (kept for reference; Dashboard now uses displayRuns + dashboardProjectsData)
  const dashboardMockAnalysesData = [
    {
      id: "analysis_123",
      project_name: "Highway LiDAR Analysis",
      project_id: "project_789",
      status: "running",
      start_time: new Date(Date.now() - 2 * 60 * 60 * 1000),
      progress: { percentage: 65, completed_steps: 3, total_steps: 6 },
      steps: [
        {
          name: "tiling",
          display_name: "File Tiling",
          status: "completed",
          progress: 100,
          description:
            "Breaking large LiDAR files into manageable tiles for processing",
        },
        {
          name: "csf",
          display_name: "Ground Classification",
          status: "completed",
          progress: 100,
          description: "Classifying ground points using CSF algorithm",
        },
        {
          name: "car_removal",
          display_name: "Vehicle Removal",
          status: "running",
          progress: 75,
          description:
            "Removing vehicles and other moving objects from the point cloud",
        },
        {
          name: "road_detection",
          display_name: "Road Detection",
          status: "pending",
          progress: 0,
          description: "Detecting road boundaries",
        },
        {
          name: "embankment",
          display_name: "Embankment Analysis (EMB)",
          status: "pending",
          progress: 0,
          description: "Analyzing road embankments",
        },
        {
          name: "report_generation",
          display_name: "Report Generation",
          status: "pending",
          progress: 0,
          description: "Generating final analysis report",
        },
      ],
    },
    {
      id: "analysis_456",
      project_name: "Urban Road Analysis",
      project_id: "project_789",
      status: "completed",
      start_time: new Date(Date.now() - 4 * 60 * 60 * 1000),
      progress: { percentage: 100, completed_steps: 6, total_steps: 6 },
      steps: [
        {
          name: "tiling",
          display_name: "File Tiling",
          status: "completed",
          progress: 100,
          description:
            "Breaking large LiDAR files into manageable tiles for processing",
        },
        {
          name: "csf",
          display_name: "Ground Classification",
          status: "completed",
          progress: 100,
          description: "Classifying ground points using CSF algorithm",
        },
        {
          name: "car_removal",
          display_name: "Vehicle Removal",
          status: "completed",
          progress: 100,
          description:
            "Removing vehicles and other moving objects from the point cloud",
        },
        {
          name: "road_detection",
          display_name: "Road Detection",
          status: "completed",
          progress: 100,
          description: "Detecting road boundaries",
        },
        {
          name: "embankment",
          display_name: "Embankment Analysis (EMB)",
          status: "completed",
          progress: 100,
          description: "Analyzing road embankments",
        },
        {
          name: "report_generation",
          display_name: "Report Generation",
          status: "completed",
          progress: 100,
          description: "Generating final analysis report",
        },
      ],
    },
    {
      id: "analysis_789",
      project_name: "Bridge Analysis Project",
      project_id: "project_452",
      status: "running",
      start_time: new Date(Date.now() - 30 * 60 * 1000),
      progress: { percentage: 25, completed_steps: 1, total_steps: 6 },
      steps: [
        {
          name: "tiling",
          display_name: "File Tiling",
          status: "completed",
          progress: 100,
          description:
            "Breaking large LiDAR files into manageable tiles for processing",
        },
        {
          name: "csf",
          display_name: "Ground Classification",
          status: "running",
          progress: 50,
          description: "Classifying ground points using CSF algorithm",
        },
        {
          name: "car_removal",
          display_name: "Vehicle Removal",
          status: "pending",
          progress: 0,
          description:
            "Removing vehicles and other moving objects from the point cloud",
        },
        {
          name: "road_detection",
          display_name: "Road Detection",
          status: "pending",
          progress: 0,
          description: "Detecting road boundaries",
        },
        {
          name: "embankment",
          display_name: "Embankment Analysis (EMB)",
          status: "pending",
          progress: 0,
          description: "Analyzing road embankments",
        },
        {
          name: "report_generation",
          display_name: "Report Generation",
          status: "pending",
          progress: 0,
          description: "Generating final analysis report",
        },
      ],
    },
    {
      id: "analysis_486",
      project_name: "West Kingman Interchange Analysis",
      project_id: "project_486",
      status: "pending",
      start_time: new Date(Date.now() - 15 * 60 * 1000),
      progress: { percentage: 0, completed_steps: 0, total_steps: 6 },
      steps: [
        {
          name: "tiling",
          display_name: "File Tiling",
          status: "pending",
          progress: 0,
          description:
            "Breaking large LiDAR files into manageable tiles for processing",
        },
        {
          name: "csf",
          display_name: "Ground Classification",
          status: "pending",
          progress: 0,
          description: "Classifying ground points using CSF algorithm",
        },
        {
          name: "car_removal",
          display_name: "Vehicle Removal",
          status: "pending",
          progress: 0,
          description:
            "Removing vehicles and other moving objects from the point cloud",
        },
        {
          name: "road_detection",
          display_name: "Road Detection",
          status: "pending",
          progress: 0,
          description: "Detecting road boundaries",
        },
        {
          name: "embankment",
          display_name: "Embankment Analysis (EMB)",
          status: "pending",
          progress: 0,
          description: "Analyzing road embankments",
        },
        {
          name: "report_generation",
          display_name: "Report Generation",
          status: "pending",
          progress: 0,
          description: "Generating final analysis report",
        },
      ],
    },
    {
      id: "analysis_416",
      project_name: "SR 516 Lake-Orange County Analysis",
      project_id: "project_416",
      status: "completed",
      start_time: new Date(Date.now() - 6 * 60 * 60 * 1000),
      progress: { percentage: 100, completed_steps: 6, total_steps: 6 },
      steps: [
        {
          name: "tiling",
          display_name: "File Tiling",
          status: "completed",
          progress: 100,
          description:
            "Breaking large LiDAR files into manageable tiles for processing",
        },
        {
          name: "csf",
          display_name: "Ground Classification",
          status: "completed",
          progress: 100,
          description: "Classifying ground points using CSF algorithm",
        },
        {
          name: "car_removal",
          display_name: "Vehicle Removal",
          status: "completed",
          progress: 100,
          description:
            "Removing vehicles and other moving objects from the point cloud",
        },
        {
          name: "road_detection",
          display_name: "Road Detection",
          status: "completed",
          progress: 100,
          description: "Detecting road boundaries",
        },
        {
          name: "embankment",
          display_name: "Embankment Analysis (EMB)",
          status: "completed",
          progress: 100,
          description: "Analyzing road embankments",
        },
        {
          name: "report_generation",
          display_name: "Report Generation",
          status: "completed",
          progress: 100,
          description: "Generating final analysis report",
        },
      ],
    },
  ];

  const dashboardMockProjectsData = [
    {
      id: "project_789",
      name: "CFX SR-538 Poinciana Pkwy",
      description:
        "Comprehensive analysis of highway sections for maintenance planning",
      total_analyses: 2,
      completed_analyses: 1,
      running_analyses: 1,
      last_updated: new Date(Date.now() - 30 * 60 * 1000),
    },
    {
      id: "project_452",
      name: "ADOT Climbing Passing RSA",
      description: "Structural analysis of bridge components using LiDAR data",
      total_analyses: 1,
      completed_analyses: 0,
      running_analyses: 1,
      last_updated: new Date(Date.now() - 30 * 60 * 1000),
    },
    {
      id: "project_486",
      name: "ADOT West Kingman Interchange",
      description: "Structural analysis of bridge components using LiDAR data",
      total_analyses: 1,
      completed_analyses: 0,
      running_analyses: 0,
      last_updated: new Date(Date.now() - 30 * 60 * 1000),
    },
    {
      id: "project_416",
      name: "CFX SR 516 Lake-Orange County",
      description: "Structural analysis of bridge components using LiDAR data",
      total_analyses: 1,
      completed_analyses: 1,
      running_analyses: 0,
      last_updated: new Date(Date.now() - 30 * 60 * 1000),
    },
    {
      id: "project_521",
      name: "CFX SR-538 Poinciana Pkwy",
      description:
        "Comprehensive analysis of highway sections for maintenance planning",
      total_analyses: 3,
      completed_analyses: 2,
      running_analyses: 1,
      last_updated: new Date(Date.now() - 45 * 60 * 1000),
    },
    {
      id: "project_623",
      name: "ADOT Climbing Passing RSA",
      description: "Structural analysis of bridge components using LiDAR data",
      total_analyses: 2,
      completed_analyses: 1,
      running_analyses: 1,
      last_updated: new Date(Date.now() - 20 * 60 * 1000),
    },
    {
      id: "project_734",
      name: "ADOT West Kingman Interchange",
      description: "Structural analysis of bridge components using LiDAR data",
      total_analyses: 1,
      completed_analyses: 0,
      running_analyses: 1,
      last_updated: new Date(Date.now() - 15 * 60 * 1000),
    },
  ];

  // Auto-select first run if none selected (Dashboard uses displayRuns)
  useEffect(() => {
    if (
      activeMainTab === "dashboard" &&
      displayRuns.length > 0 &&
      !dashboardSelectedRun
    ) {
      setDashboardSelectedRun(displayRuns[0]);
    }
  }, [activeMainTab, displayRuns.length]);

  const handleDeleteProject = async (projectId) => {
    const userEmail = sessionStorage.getItem("userEmail");
    if (!userEmail) {
      showNotification("Please log in to remove projects.");
      return;
    }
    try {
      await progressService.deleteProjectRuns(userEmail, projectId);
      setOptimisticRuns((prev) =>
        prev.filter((r) => r.project_id !== projectId),
      );
      refreshDashboardRuns();
      if (dashboardSelectedRun?.project_id === projectId) {
        setDashboardSelectedRun(null);
      }
    } catch (err) {
      console.error("Delete project runs failed:", err);
      showNotification(
        err.response?.data?.error || "Failed to remove project.",
      );
    }
  };

  const handleRequestDeleteDashboardRun = useCallback(
    (run) => {
      const runId = String(run?.run_id || run?.full_run_id || "").trim();
      if (!runId) {
        showNotification("Could not remove run: missing run ID.");
        return;
      }
      if (!dashboardUserEmail) {
        showNotification("Please log in to remove runs.");
        return;
      }
      setDashboardDeleteCandidateRunId(runId);
    },
    [dashboardUserEmail, showNotification],
  );

  const handleCancelDeleteDashboardRun = useCallback(() => {
    if (dashboardDeletingRunId) return;
    setDashboardDeleteCandidateRunId(null);
  }, [dashboardDeletingRunId]);

  const handleDeleteDashboardRun = useCallback(
    async (run) => {
      const runId = String(run?.run_id || run?.full_run_id || "").trim();
      if (!runId) {
        showNotification("Could not remove run: missing run ID.");
        return;
      }
      if (!dashboardUserEmail) {
        showNotification("Please log in to remove runs.");
        return;
      }
      setDashboardDeleteCandidateRunId(null);
      setDashboardDeletingRunId(runId);
      try {
        await removeDashboardRun(runId);
        setOptimisticRuns((prev) =>
          prev.filter((entry) => String(entry?.run_id || "") !== runId),
        );
        if (dashboardSelectedRun?.run_id === runId) {
          setDashboardSelectedRun(null);
        }
        // showNotification('Run removed from dashboard.');
        refreshDashboardRuns(true);
      } catch (err) {
        console.error("Delete run failed:", err);
        showNotification(
          err.response?.data?.error || err.message || "Failed to remove run.",
        );
      } finally {
        setDashboardDeletingRunId(null);
      }
    },
    [
      dashboardSelectedRun,
      dashboardUserEmail,
      refreshDashboardRuns,
      removeDashboardRun,
      showNotification,
    ],
  );

  const handleDeleteAllDashboardRuns = useCallback(
    async (runsToDelete) => {
      if (!dashboardUserEmail) {
        showNotification("Please log in to remove runs.");
        return;
      }
      const runIds = (runsToDelete || [])
        .map((run) => String(run?.run_id || run?.full_run_id || "").trim())
        .filter(Boolean);
      if (!runIds.length) return;

      // Cancel any pending single-run confirmation while bulk delete runs.
      setDashboardDeleteCandidateRunId(null);
      setDashboardDeletingAllRuns(true);
      const failedIds = [];
      try {
        for (const runId of runIds) {
          try {
            await removeDashboardRun(runId);
            setOptimisticRuns((prev) =>
              prev.filter((entry) => String(entry?.run_id || "") !== runId),
            );
          } catch (err) {
            console.error(`Delete run ${runId} failed:`, err);
            failedIds.push(runId);
          }
        }
        const deletedIds = new Set(
          runIds.filter((id) => !failedIds.includes(id)),
        );
        if (
          dashboardSelectedRun &&
          deletedIds.has(String(dashboardSelectedRun.run_id || ""))
        ) {
          setDashboardSelectedRun(null);
        }
        if (failedIds.length) {
          showNotification(
            `Removed ${deletedIds.size} run${deletedIds.size === 1 ? "" : "s"}, ${failedIds.length} failed.`,
          );
        }
        refreshDashboardRuns(true);
      } finally {
        setDashboardDeletingAllRuns(false);
      }
    },
    [
      dashboardSelectedRun,
      dashboardUserEmail,
      refreshDashboardRuns,
      removeDashboardRun,
      showNotification,
    ],
  );

  // Function to fetch projects from the API and reset dropdown state.
  const fetchProjects = async () => {
    console.log("🔧 RSAAnalysis - fetchProjects called");
    setIsLoadingProjects(true);
    try {
      // Use API_BASE_URL for local development, relative URL for production (CloudFront)
      const url =
        ENVIRONMENT === "local"
          ? `${API_BASE_URL}/api/projectsSearch?query=`
          : "/api/projectsSearch?query=";
      console.log("🔧 RSAAnalysis - Making request to:", url);
      console.log("🔧 RSAAnalysis - ENVIRONMENT:", ENVIRONMENT);
      console.log("🔧 RSAAnalysis - API_BASE_URL:", API_BASE_URL);
      const response = await axios.get(url);
      setAllProjects(response.data);
      sessionStorage.setItem("allProjects", JSON.stringify(response.data));
      // Clear dropdown selections and search results
      setSearchResults([]);
      setSelectedProjectId(projectId ?? null);
      setExistingProjectName("");
      console.log("[OK] RSAAnalysis - Projects refreshed from API");
    } catch (error) {
      console.error("❌ RSAAnalysis - Error refreshing projects:", error);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const fetchRsaMileagesConfig = async () => {
    const cacheKey = "rsaMileagesConfig";
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        const cachedGroups = parsed?.groups_mileages;
        const cachedProjects = parsed?.projects_per_group;
        const cachedIsdManeuvers = parsed?.groups_isd_maneuvers;
        const hasCachedGroups =
          cachedGroups &&
          typeof cachedGroups === "object" &&
          Object.keys(cachedGroups).length > 0;
        const hasCachedProjects =
          cachedProjects &&
          typeof cachedProjects === "object" &&
          Object.keys(cachedProjects).length > 0;
        const hasCachedIsdManeuvers =
          cachedIsdManeuvers &&
          typeof cachedIsdManeuvers === "object" &&
          Object.keys(cachedIsdManeuvers).length > 0;
        if (hasCachedGroups && hasCachedProjects) {
          // Use cache for immediate render, then refresh from API below.
          setRsaGroupsMileages(cachedGroups);
          setRsaProjectsPerGroup(cachedProjects);
        }
        if (hasCachedIsdManeuvers) {
          setRsaGroupsIsdManeuvers(cachedIsdManeuvers);
        }
      }
    } catch (cacheError) {
      console.warn("Failed to parse cached RSA mileages config:", cacheError);
    }

    try {
      const url =
        ENVIRONMENT === "local"
          ? `${API_BASE_URL}/api/secrets/rsa-mileages-config`
          : "/api/secrets/rsa-mileages-config";
      const response = await axios.get(url);
      const groups = response.data?.groups_mileages;
      const projects = response.data?.projects_per_group;
      const isdManeuvers = response.data?.groups_isd_maneuvers;
      if (groups && typeof groups === "object") {
        setRsaGroupsMileages(groups);
      }
      if (projects && typeof projects === "object") {
        setRsaProjectsPerGroup(projects);
      }
      if (isdManeuvers && typeof isdManeuvers === "object") {
        setRsaGroupsIsdManeuvers(isdManeuvers);
      } else {
        setRsaGroupsIsdManeuvers({});
      }
      const hasGroups =
        groups && typeof groups === "object" && Object.keys(groups).length > 0;
      const hasProjects =
        projects &&
        typeof projects === "object" &&
        Object.keys(projects).length > 0;
      const hasIsdManeuvers =
        isdManeuvers &&
        typeof isdManeuvers === "object" &&
        Object.keys(isdManeuvers).length > 0;
      if (hasGroups || hasProjects || hasIsdManeuvers) {
        sessionStorage.setItem(
          cacheKey,
          JSON.stringify({
            groups_mileages: groups || {},
            projects_per_group: projects || {},
            groups_isd_maneuvers: isdManeuvers || {},
          }),
        );
      } else {
        sessionStorage.removeItem(cacheKey);
      }
    } catch (error) {
      console.error("Failed to fetch RSA mileages config:", error);
      setRsaGroupsMileages({});
      setRsaProjectsPerGroup({});
      setRsaGroupsIsdManeuvers({});
    }
  };

  const fetchRsaUsageLedgerState = useCallback(async (projectIdForUsage) => {
    const normalizedProjectId = String(projectIdForUsage ?? "").trim();
    if (!normalizedProjectId) {
      setRsaUsageLedgerState(null);
      return;
    }
    setRsaUsageLedgerLoading(true);
    try {
      const isProductionDomain =
        window.location.hostname.includes("123bimlive.com");
      const url =
        ENVIRONMENT === "production" || isProductionDomain
          ? `/api/projects/${normalizedProjectId}/rsa-usage-ledger-state`
          : `${API_BASE_URL}/api/projects/${normalizedProjectId}/rsa-usage-ledger-state`;
      const response = await axios.get(url);
      const payload =
        response.data && typeof response.data === "object"
          ? response.data
          : null;
      setRsaUsageLedgerState(payload);
    } catch (error) {
      console.warn("Failed to fetch RSA usage ledger state:", error);
      setRsaUsageLedgerState(null);
    } finally {
      setRsaUsageLedgerLoading(false);
    }
  }, []);

  // Function to fetch shared drives from the API
  const fetchSharedDrives = async () => {
    try {
      const url =
        ENVIRONMENT === "local"
          ? `${API_BASE_URL}/api/google-drive/shared-drives`
          : "/api/google-drive/shared-drives";
      console.log("🔄 RSAAnalysis - Preloading shared drives from:", url);
      const response = await axios.get(url);
      // Handle both response formats and ensure we always have an array
      const drives =
        response.data?.drives || response.data?.shared_drives || [];
      const drivesArray = Array.isArray(drives) ? drives : [];
      // Cache in sessionStorage for FolderTreePopup to use
      sessionStorage.setItem("sharedDrives", JSON.stringify(drivesArray));
      console.log(
        "[OK] RSAAnalysis - Shared drives preloaded and cached:",
        drivesArray.length,
      );
    } catch (err) {
      console.error("❌ RSAAnalysis - Error preloading shared drives:", err);
      // Don't show error to user, just log it
    }
  };

  // Helper function to get token (shared with StorageConnectionModal)
  const getAuthToken = async () => {
    try {
      // First, try to get token from AWS Amplify Auth (primary method)
      try {
        const session = await Auth.currentSession();
        if (session && session.getIdToken()) {
          const idToken = session.getIdToken().getJwtToken();
          console.log("[OK] [getAuthToken] Got token from Amplify Auth");
          return idToken;
        }
      } catch (amplifyError) {
        console.log(
          "â„¹ï¸ [getAuthToken] Amplify Auth not available, trying other methods:",
          amplifyError.message,
        );
      }

      // Fallback: Check multiple possible keys in storage (in order of likelihood)
      let token = sessionStorage.getItem("bimLiveAuthToken"); // Primary key used by this app

      if (!token) {
        token = sessionStorage.getItem("token");
      }
      if (!token) {
        token = sessionStorage.getItem("cognito_token");
      }
      if (!token) {
        token = sessionStorage.getItem("idToken");
      }
      if (!token) {
        // Check localStorage as fallback
        token =
          localStorage.getItem("bimLiveAuthToken") ||
          localStorage.getItem("token") ||
          localStorage.getItem("cognito_token") ||
          localStorage.getItem("idToken");
      }

      // Check cookies as last resort
      if (!token) {
        const cookies = document.cookie.split(";");
        for (let cookie of cookies) {
          const [name, value] = cookie.trim().split("=");
          if (name === "cognito_token" || name === "token") {
            token = value;
            break;
          }
        }
      }

      console.log(
        "🔍 [getAuthToken] Raw token from storage:",
        token ? `${token.substring(0, 20)}...` : "null",
      );

      if (!token) {
        console.warn(
          "[WARN] [getAuthToken] No token found in sessionStorage, localStorage, cookies, or Amplify",
        );
        console.log(
          "🔍 [getAuthToken] Available sessionStorage keys:",
          Object.keys(sessionStorage),
        );
        return null;
      }

      // Try to parse as JSON (token might be stored as JSON string)
      try {
        const parsed = JSON.parse(token);
        console.log(
          "[OK] [getAuthToken] Token is JSON, parsed structure:",
          typeof parsed,
          parsed ? Object.keys(parsed) : "null",
        );

        if (parsed && typeof parsed === "string") {
          // Token is a JSON stringified string
          console.log("[OK] [getAuthToken] Using parsed string token");
          return parsed;
        } else if (parsed && parsed.token) {
          // Token is an object with a token property
          console.log("[OK] [getAuthToken] Using parsed.token");
          return parsed.token;
        } else if (parsed && parsed.idToken) {
          // Token is an object with an idToken property (Cognito)
          console.log("[OK] [getAuthToken] Using parsed.idToken");
          return parsed.idToken;
        } else if (parsed && parsed.accessToken) {
          // Token is an object with an accessToken property
          console.log("[OK] [getAuthToken] Using parsed.accessToken");
          return parsed.accessToken;
        } else {
          // If parsed is an object but we don't know the structure, log it
          console.warn(
            "[WARN] [getAuthToken] Parsed token is object but no known property found:",
            parsed,
          );
          // Try to return the whole object as string (some APIs accept this)
          return JSON.stringify(parsed);
        }
      } catch (parseError) {
        // Not JSON, use as-is (raw JWT token)
        console.log(
          "[OK] [getAuthToken] Token is not JSON, using as-is (raw JWT)",
        );
        return token;
      }
    } catch (e) {
      console.error("❌ [getAuthToken] Error getting auth token:", e);
      return null;
    }
  };

  // Load user's storage connections
  const loadStorageConnections = async () => {
    try {
      const token = await getAuthToken();
      if (!token) {
        // User not logged in - use system Google Drive
        setUseSystemGoogleDrive(true);
        setSelectedStorageProvider("system_google_drive");
        return;
      }

      const url =
        ENVIRONMENT === "local"
          ? `${API_BASE_URL}/api/storage/connections`
          : "/api/storage/connections";

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const connections = response.data.connections || [];
      setStorageConnections(connections);

      // Check for default connection
      const defaultConn = connections.find((c) => c.is_default && c.is_active);
      if (defaultConn) {
        setSelectedConnection(defaultConn);
        setSelectedStorageProvider(defaultConn.provider_type);
        setUseSystemGoogleDrive(false);
      } else {
        // No default connection - use system Google Drive
        setSelectedStorageProvider("system_google_drive");
        setUseSystemGoogleDrive(true);
        setSelectedConnection(null);
      }
    } catch (error) {
      console.error("Error loading storage connections:", error);
      // Fallback to system Google Drive
      setUseSystemGoogleDrive(true);
      setSelectedStorageProvider("system_google_drive");
    }
  };

  // Check which providers user has connected
  const hasGoogleDriveConnection = storageConnections.some(
    (c) => c.provider_type === "google_drive" && c.is_active,
  );
  const hasS3Connection = storageConnections.some(
    (c) => c.provider_type === "s3" && c.is_active,
  );
  const hasAzureConnection = storageConnections.some(
    (c) => c.provider_type === "azure" && c.is_active,
  );
  const hasGCPConnection = storageConnections.some(
    (c) => c.provider_type === "gcp" && c.is_active,
  );

  // Check for OAuth callback success
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const storageConnected = urlParams.get("storage_connected");
    const success = urlParams.get("success");

    if (storageConnected && success === "true") {
      // Reload storage connections
      loadStorageConnections();

      // Show success message with proper provider name
      const providerNames = {
        google_drive: "Google Drive",
        azure: "Microsoft Azure",
        gcp: "Google Cloud Storage",
        s3: "AWS S3",
      };
      const providerName = providerNames[storageConnected] || storageConnected;
      alert(`${providerName} connected successfully!`);

      // Clean up only the OAuth callback params and preserve the current project/tab state.
      const nextParams = new URLSearchParams(window.location.search);
      nextParams.delete("storage_connected");
      nextParams.delete("success");
      const nextSearch = nextParams.toString();
      const newUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}`;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  // On mount, preload projects and shared drives from API in the background
  // This ensures the dropdowns are ready when the user opens them
  useEffect(() => {
    // First, load from sessionStorage if available (for immediate display)
    const storedProjects = sessionStorage.getItem("allProjects");
    if (storedProjects) {
      const parsedProjects = JSON.parse(storedProjects);
      setAllProjects(parsedProjects);
      setSearchResults([]); // Keep dropdown empty initially
      console.log("[OK] Projects loaded from sessionStorage (cached)");
    }

    // Load user's storage connections
    loadStorageConnections();
    fetchRsaMileagesConfig();

    // Always fetch fresh data in the background on first mount
    // This ensures the dropdowns are ready when user opens them
    console.log(
      "🔄 Preloading projects and shared drives from API on RSA-Analysis mount...",
    );
    fetchProjects().catch((error) => {
      console.error("❌ Failed to preload projects:", error);
      // Don't show error to user, just log it - cached data will be used if available
    });

    // Preload shared drives in the background
    fetchSharedDrives().catch((error) => {
      console.error("❌ Failed to preload shared drives:", error);
      // Don't show error to user, just log it
    });
  }, []);

  useEffect(() => {
    if (!effectiveProjectId) {
      setRsaUsageLedgerState(null);
      setRsaUsageLedgerLoading(false);
      return;
    }
    fetchRsaUsageLedgerState(effectiveProjectId);
  }, [effectiveProjectId, fetchRsaUsageLedgerState]);

  // Persist requests in localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("requests", JSON.stringify(requests));
  }, [requests]);

  // Load persisted requests from localStorage on mount
  useEffect(() => {
    const storedRequests = localStorage.getItem("requests");
    if (storedRequests) {
      setRequests(JSON.parse(storedRequests));
    }
  }, []);

  // Handle click outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Update CSS custom properties for dynamic layout when services change
  useEffect(() => {
    const rightPanels = document.querySelector(".right-panels");
    if (rightPanels) {
      const hasEmbankmentPanel =
        services.includes("horizontal") || services.includes("embankment");
      rightPanels.style.setProperty(
        "--embankment-panel-width",
        hasEmbankmentPanel ? "380px" : "0px",
      );
    }
  }, [services]);

  // Filter the cached projects locally as the user type.
  const handleInputChange = (inputValue) => {
    console.log("handleInputChange called with:", inputValue);
    console.log("allProjects length:", allProjects.length);
    console.log("allProjects sample:", allProjects.slice(0, 3));

    if (!inputValue) {
      setSearchResults([]);
      setDropdownOpen(false);
      return inputValue;
    }

    // Always open dropdown when typing
    setDropdownOpen(true);

    const filtered = allProjects.filter((proj) =>
      proj.name.toLowerCase().includes(inputValue.toLowerCase()),
    );

    console.log("Filtered results:", filtered);
    console.log("Setting searchResults to:", filtered);
    setSearchResults(filtered);

    return inputValue;
  };

  // Modified handleSubmit: use new payload structure with analysis_template_ids
  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!effectiveProjectId) {
      alert("Please open a project before running analysis.");
      return;
    }

    if (selectedTasks.length === 0) {
      alert("Please select at least one task.");
      return;
    }

    const selectedFolderIds = (selectedFolders || [])
      .map((f) => f.id ?? f.folder_id ?? f.folderId)
      .filter((id) => id != null && id !== "")
      .map(String);

    if (
      !isRsaModelProject &&
      routesXmlFiles.length === 0 &&
      selectedFolderIds.length === 0
    ) {
      alert("Please select at least one folder from the folder tree.");
      return;
    }

    let requestId = null;
    let localRunId = null;
    try {
      const hasRoutes = routesXmlFiles.length > 0;
      const selectedRouteCountByTask = {};
      for (const taskName of selectedTasks) {
        selectedRouteCountByTask[taskName] = 0;
      }
      if (hasRoutes) {
        for (const routeFile of routesXmlFiles || []) {
          const routeDriveId = normalizeDriveFolderId(routeFile?.googleDriveId);
          if (!routeDriveId) {
            continue;
          }
          const rowSelection = routesMatrixSelection?.[routeDriveId] || {};
          for (const taskName of selectedTasks) {
            if (rowSelection[taskName]) {
              selectedRouteCountByTask[taskName] =
                (selectedRouteCountByTask[taskName] || 0) + 1;
            }
          }
        }
      }
      const selectedTaskNamesForSubmission = hasRoutes
        ? selectedTasks.filter(
            (taskName) => (selectedRouteCountByTask[taskName] || 0) > 0,
          )
        : [...selectedTasks];

      if (selectedTaskNamesForSubmission.length === 0) {
        const emptySelectionMessage = hasRoutes
          ? "Select at least one route assignment for the analyses you want to run."
          : "Please select at least one task.";
        alert(emptySelectionMessage);
        return;
      }

      const projectRecord = Array.isArray(allProjects)
        ? allProjects.find(
            (proj) =>
              String(proj?.id ?? proj?.Id ?? "") === String(effectiveProjectId),
          )
        : null;
      const projectNameFromState = String(existingProjectName || "").trim();
      const projectNameFromProp = String(projectName || "").trim();
      const projectNameFromRecord = String(
        projectRecord?.name ?? projectRecord?.Name ?? "",
      ).trim();
      const resolvedProjectName =
        projectNameFromState || projectNameFromProp || projectNameFromRecord;
      const requestProjectName =
        resolvedProjectName || `Project ${effectiveProjectId}`;
      // Build analysis_template_ids array from selected tasks (non-rsa-model default behavior).
      let analysisTemplateIds = selectedTaskNamesForSubmission
        .map((taskName) => analysisTemplateIdMapping[taskName])
        .filter((id) => id && id !== ""); // Filter out empty or undefined IDs

      if (analysisTemplateIds.length === 0) {
        alert("No valid analysis templates found for selected tasks.");
        return;
      }

      const normalizedProjectTypeForPayload = isRsaModelProject
        ? "rsa-model"
        : "rsa-existing";
      const user_email = sessionStorage.getItem("userEmail") || "";

      // --- PHASE 1 COMPLETE: inject optimistic run and switch to dashboard ---
      localRunId = `local_${Date.now()}`;
      const optimisticRun = {
        run_id: localRunId,
        project_id: effectiveProjectId,
        project_type: normalizedProjectTypeForPayload,
        project_name: requestProjectName,
        name: requestProjectName,
        description: `Pipeline run for project ${effectiveProjectId}`,
        status: "pending",
        _isQueuing: true,
        created_at: new Date(),
        last_updated: new Date(),
        user_email,
        services: selectedTaskNamesForSubmission || [],
        overall_progress: 0,
        total_analyses: 1,
        completed_analyses: 0,
        running_analyses: 0,
      };
      setPendingLocalRunId(localRunId);
      setOptimisticRuns((prev) => [optimisticRun, ...prev]);
      setActiveMainTab("dashboard");

      let pathFoldersPayload = [];
      let modelFolderIds = [];
      let outputFolderId = "";
      if (hasRoutes) {
        try {
          const rsaModelPayload =
            await buildRsaFoldersPayloadFromProject(effectiveProjectId);
          pathFoldersPayload = Array.isArray(rsaModelPayload?.path_folders)
            ? rsaModelPayload.path_folders
            : [];
          modelFolderIds = Array.isArray(rsaModelPayload?.model_folder_id)
            ? rsaModelPayload.model_folder_id
            : [];
          outputFolderId = String(
            rsaModelPayload?.output_folder_id ?? "",
          ).trim();

          // For rsa-model: enforce index alignment between analysis_template_ids and path_folders.
          // Both arrays are rebuilt from the same ordered selected-task list.
          const pathFolderByConfigType = new Map(
            (pathFoldersPayload || [])
              .map((item) => {
                const configType = String(item?.config_type ?? "")
                  .trim()
                  .toUpperCase();
                const pathFolderId = String(item?.path_folder_id ?? "").trim();
                if (!configType || !pathFolderId) {
                  return null;
                }
                return [
                  configType,
                  { config_type: configType, path_folder_id: pathFolderId },
                ];
              })
              .filter(Boolean),
          );

          const seenConfigTypes = new Set();
          const alignedRows = [];
          const missingConfigTypes = [];

          for (const taskName of selectedTaskNamesForSubmission) {
            const templateId = String(
              analysisTemplateIdMapping[taskName] ?? "",
            ).trim();
            if (!templateId) {
              continue;
            }
            const folderCode = TASK_OPTION_TO_FOLDER_CODE[taskName];
            const configType = String(
              RSA_FOLDER_CODE_TO_SUFFIX[folderCode] ?? "",
            )
              .trim()
              .toUpperCase();
            if (!configType || seenConfigTypes.has(configType)) {
              continue;
            }
            seenConfigTypes.add(configType);

            const matchedPathFolder = pathFolderByConfigType.get(configType);
            if (!matchedPathFolder) {
              missingConfigTypes.push(configType);
              continue;
            }

            alignedRows.push({
              template_id: templateId,
              config_type: matchedPathFolder.config_type,
              path_folder_id: matchedPathFolder.path_folder_id,
            });
          }

          if (missingConfigTypes.length > 0) {
            if (localRunId) {
              setOptimisticRuns((prev) =>
                prev.filter((r) => r.run_id !== localRunId),
              );
              setPendingLocalRunId(null);
            }
            showNotification(
              `Missing RSA folder(s) for selected config type(s): ${missingConfigTypes.join(", ")}`,
            );
            return;
          }

          if (alignedRows.length === 0) {
            if (localRunId) {
              setOptimisticRuns((prev) =>
                prev.filter((r) => r.run_id !== localRunId),
              );
              setPendingLocalRunId(null);
            }
            showNotification(
              "No analyses with routes to submit. Add route XML files to the RSA folders for the selected analyses, or deselect analyses that list 0 routes.",
            );
            return;
          }

          const orderedAlignedRows = [...alignedRows].sort((a, b) =>
            a.config_type.localeCompare(b.config_type),
          );

          analysisTemplateIds = orderedAlignedRows.map(
            (row) => row.template_id,
          );
          pathFoldersPayload = orderedAlignedRows.map((row) => ({
            config_type: row.config_type,
            path_folder_id: row.path_folder_id,
          }));
        } catch (folderPayloadError) {
          console.warn(
            "Failed to build rsa-model folder payload:",
            folderPayloadError,
          );
        }
      } else {
        try {
          const deliverablesInfo = await resolveDeliverablesFolderForProject(
            effectiveProjectId,
            Array.isArray(projectFilesSnapshot) &&
              projectFilesSnapshot.length > 0
              ? projectFilesSnapshot
              : null,
          );
          outputFolderId = String(deliverablesInfo?.driveId ?? "").trim();
        } catch (deliverablesResolveError) {
          console.warn(
            "Failed to resolve rsa-existing deliverables folder:",
            deliverablesResolveError,
          );
        }
      }

      if (!outputFolderId) {
        if (localRunId) {
          setOptimisticRuns((prev) =>
            prev.filter((r) => r.run_id !== localRunId),
          );
          setPendingLocalRunId(null);
        }
        showNotification(
          "Deliverables folder google_drive_id was not found. Please set it in the project's MySQL row.",
        );
        return;
      }

      let folderIds = hasRoutes
        ? Array.from(
            new Set(
              [
                ...(sourceStorage === "s3" ? [] : modelFolderIds),
                ...pathFoldersPayload.map((item) => item?.path_folder_id),
              ]
                .filter((id) => id != null && String(id).trim() !== "")
                .map(String),
            ),
          )
        : selectedFolderIds;

      // Prepend Source folder ID so the Pipeline knows where LAS files live.
      // For S3-source projects, Pipeline discovers the bucket via SSM
      // (is_project_s3_source(project_id)), not via a folder ID — so we skip the
      // unshift to avoid sending the frontend's 's3:source' sentinel to the backend.
      if (!isRsaModelProject && sourceStorage !== "s3") {
        const sourceDriveId = normalizeDriveFolderId(existingSourceFolderId);
        if (sourceDriveId && !folderIds.includes(sourceDriveId)) {
          folderIds.unshift(sourceDriveId);
        }
      }

      if (folderIds.length === 0) {
        alert("No valid folder IDs found for this project.");
        return;
      }

      // Generate a unique id for this request
      requestId = Date.now();
      // Create a new request record with a pending status
      const newRequestEntry = {
        id: requestId,
        projectType: isRsaModelProject ? "rsa-model" : "existing",
        folderIds,
        projectName: requestProjectName,
        status: "pending",
        response: "",
      };
      setRequests((prev) => [...prev, newRequestEntry]);

      const setupWktInputValue = document.querySelector(
        ".setup-wkt-input-wrap .setup-wkt-input",
      )?.value;
      const payloadWkt =
        normalizeWktInput(
          String(setupWktInputValue ?? mapOverlayLocalConfig?.wkt ?? "").trim(),
        ) || "";
      const selectedServicesForPayload = Array.from(
        new Set(
          selectedTaskNamesForSubmission
            .map((taskName) => TASK_OPTION_TO_SERVICE[taskName])
            .filter(Boolean),
        ),
      );
      const selectedRouteCountByService = selectedServicesForPayload.reduce(
        (acc, serviceName) => {
          acc[serviceName] = 0;
          return acc;
        },
        {},
      );
      for (const [taskName, routeCount] of Object.entries(
        selectedRouteCountByTask,
      )) {
        const serviceName = TASK_OPTION_TO_SERVICE[taskName];
        if (!serviceName || !selectedServicesForPayload.includes(serviceName)) {
          continue;
        }
        selectedRouteCountByService[serviceName] = routeCount;
      }

      const rawRunMetrics =
        rsaRunMetricsRef.current && typeof rsaRunMetricsRef.current === "object"
          ? rsaRunMetricsRef.current
          : {};

      const safeMetricsMap = (value) =>
        value && typeof value === "object" ? value : {};
      const pickSelectedServiceMetrics = (sourceMap) => {
        const out = {};
        for (const serviceName of selectedServicesForPayload) {
          const num = Number(sourceMap[serviceName]);
          out[serviceName] = Number.isFinite(num) ? num : 0;
        }
        return out;
      };
      const sumMetricValues = (obj) =>
        Object.values(obj || {}).reduce((sum, val) => {
          const num = Number(val);
          return Number.isFinite(num) ? sum + num : sum;
        }, 0);

      const mileageByService = pickSelectedServiceMetrics(
        safeMetricsMap(rawRunMetrics.mileage_by_service),
      );
      const maneuversByService = pickSelectedServiceMetrics(
        safeMetricsMap(rawRunMetrics.maneuvers_by_service),
      );
      const routeCountByService = pickSelectedServiceMetrics(
        safeMetricsMap(rawRunMetrics.route_count_by_service),
      );
      if (hasRoutes) {
        for (const serviceName of selectedServicesForPayload) {
          routeCountByService[serviceName] =
            Number(selectedRouteCountByService[serviceName]) || 0;
        }
      }

      // Derive engine=v2 analysis_type from whatever the user clicked in
      // the tasks-buttons-container (state lives in `selectedTasks`).
      // Task labels are e.g. "Stopping Sight Distance (SSD)" — extract the
      // 3-letter code in parens. Engine=v2's _engine_v2_dispatch reads
      // data.get("analysis_type") and defaults to "SSD" if absent, so
      // sending it explicitly is required for any non-SSD run.
      // We use `selectedTasks` (raw button state) rather than
      // `selectedTaskNamesForSubmission` (filtered by route-count > 0)
      // because the user's chosen analysis type shouldn't depend on
      // whether routes were picked for it yet.
      const deriveAnalysisType = (taskNames) => {
        for (const name of taskNames || []) {
          const match = String(name).match(/\(([A-Z]{3})\)\s*$/);
          if (match) return match[1];
        }
        return null;
      };
      const analysisTypeForPayload = deriveAnalysisType(selectedTasks);

      const runLoggingPayload = {
        trigger_source: "toggle-btn-rsa-run",
        triggered_at: new Date().toISOString(),
        selected_tasks: [...selectedTaskNamesForSubmission],
        selected_services: [...selectedServicesForPayload],
        submitted_analysis_template_ids: [...analysisTemplateIds],
        analysis_metrics: {
          mileage_by_service: mileageByService,
          maneuvers_by_service: maneuversByService,
          route_count_by_service: routeCountByService,
          mileage_total_miles: sumMetricValues(mileageByService),
          maneuvers_total: sumMetricValues(maneuversByService),
          route_total: sumMetricValues(routeCountByService),
          selected_project_id: effectiveProjectId,
          metrics_generated_at:
            rawRunMetrics.metrics_generated_at || new Date().toISOString(),
        },
      };
      const processPayload = hasRoutes
        ? {
            project_id: effectiveProjectId,
            project_type: normalizedProjectTypeForPayload,
            projectType: normalizedProjectTypeForPayload,
            source_project_id: effectiveProjectId,
            folder_ids: Array.from(new Set([...folderIds].filter(Boolean))),
            analysis_template_ids: analysisTemplateIds,
            ...(analysisTypeForPayload && {
              analysis_type: analysisTypeForPayload,
            }),
            horizontal_data: horizontalData || {},
            embankment_data: EmbankmentData || {},
            path_folders: pathFoldersPayload,
            output_folder_id: outputFolderId,
            wkt: payloadWkt,
            project_name: requestProjectName,
            user_email,
            selected_task_names: [...selectedTaskNamesForSubmission],
            selected_services: [...selectedServicesForPayload],
            trigger_source: "toggle-btn-rsa-run",
            run_logging: runLoggingPayload,
          }
        : {
            project_id: effectiveProjectId,
            project_type: normalizedProjectTypeForPayload,
            projectType: normalizedProjectTypeForPayload,
            source_project_id: effectiveProjectId,
            project_name: requestProjectName,
            folder_ids: Array.from(new Set([...folderIds].filter(Boolean))),
            analysis_template_ids: analysisTemplateIds,
            ...(analysisTypeForPayload && {
              analysis_type: analysisTypeForPayload,
            }),
            horizontal_data: horizontalData || {},
            embankment_data: EmbankmentData || {},
            path_data:
              rsaPathsHook.paths.find(
                (p) => p.pathData?.geometry?.coordinates?.length >= 2,
              )?.pathData ?? null,
            output_folder_id: outputFolderId,
            user_email,
            selected_task_names: [...selectedTaskNamesForSubmission],
            selected_services: [...selectedServicesForPayload],
            trigger_source: "toggle-btn-rsa-run",
            run_logging: runLoggingPayload,
          };

      // ── Sync JSON path files: hide irrelevant, expose relevant ──────────
      const xmlToJsonMap = rsaPathsHook.xmlToJsonMap || {};
      if (Object.keys(xmlToJsonMap).length > 0 && effectiveSetupFolderId) {
        try {
          // Derive selected XML filenames from route selection matrix
          const selectedXmlFilenames = new Set();
          for (const routeFile of routesXmlFiles || []) {
            const routeDriveId = normalizeDriveFolderId(
              routeFile?.googleDriveId,
            );
            if (!routeDriveId) continue;
            const rowSelection = routesMatrixSelection?.[routeDriveId] || {};
            const isSelected = selectedTaskNamesForSubmission.some(
              (task) => rowSelection[task],
            );
            if (isSelected && routeFile.name) {
              selectedXmlFilenames.add(routeFile.name);
            }
          }

          // Look up relevant JSON filenames from the mapping
          const relevantJsonFiles = new Set();
          for (const xmlName of selectedXmlFilenames) {
            const entry = xmlToJsonMap[xmlName];
            if (entry?.jsonFilename) {
              relevantJsonFiles.add(entry.jsonFilename);
            }
          }

          if (relevantJsonFiles.size > 0) {
            const isProductionDomainSync =
              window.location.hostname.includes("123bimlive.com");
            const syncBaseUrl =
              ENVIRONMENT === "production" || isProductionDomainSync
                ? ""
                : API_BASE_URL;
            const syncUrl = `${syncBaseUrl}/api/projects/${effectiveProjectId}/rsa-sync-json-files`;
            await axios.post(
              syncUrl,
              {
                source_folder_id: effectiveSetupFolderId,
                relevant_json_files: Array.from(relevantJsonFiles),
              },
              { headers: { "Content-Type": "application/json" } },
            );
            console.log(
              "[OK] JSON file sync completed. Relevant:",
              Array.from(relevantJsonFiles),
            );
          }
        } catch (syncErr) {
          console.warn("JSON file sync failed (non-blocking):", syncErr);
        }
      }

      // New server endpoint for RSA analysis
      // Use relative URL for production (goes through CDN /rsa/* behavior), full URL for local/server
      // Check if we're on production domain (123bimlive.com) to ensure correct routing
      const isProductionDomain =
        window.location.hostname.includes("123bimlive.com");
      // Engine=v2 routing — v1 is deprecated as of 2026-06-06; every
      // dispatch goes through engine=v2 on every host (dev, prod, local).
      // Backend detection is _request_engine_v2 in pipeline_routes.py
      // (?engine=v2 query string or X-Engine: v2 header).
      const baseProcessUrl =
        ENVIRONMENT === "production" || isProductionDomain
          ? "/rsa/process"
          : `${processApi}/process`;
      const processUrl = `${baseProcessUrl}?engine=v2`;

      console.log("ðŸš€ Making API call to process endpoint:", processUrl);
      console.log("ðŸ”§ ENVIRONMENT:", ENVIRONMENT);
      console.log("ðŸ”§ isProductionDomain:", isProductionDomain);
      console.log("ðŸ”§ window.location.hostname:", window.location.hostname);
      console.log("ðŸ“¦ Payload:", processPayload);

      const processResponse = await axios.post(processUrl, processPayload, {
        headers: { "Content-Type": "application/json" },
      });

      console.log("[OK] Process API call successful:", processResponse.data);
      setHasSubmittedRsaRun(true);

      // Update the request record for the existing project.
      setRequests((prev) =>
        prev.map((req) =>
          req.id === requestId
            ? {
                ...req,
                status: "completed",
                response: `Process completed: ${processResponse.data.message}`,
              }
            : req,
        ),
      );

      // Optimistic UI: upgrade the queuing tile or add a new run tile to Dashboard
      const runId = processResponse.data?.run_id;
      if (
        runId &&
        (processResponse.status === 200 || processResponse.status === 202)
      ) {
        if (localRunId) {
          // Upgrade the queuing placeholder to a real processing run
          setOptimisticRuns((prev) =>
            prev.map((r) =>
              r.run_id === localRunId
                ? {
                    ...r,
                    run_id: runId,
                    status: "processing",
                    _isQueuing: false,
                    running_analyses: 1,
                  }
                : r,
            ),
          );
          setPendingLocalRunId(null);
        } else {
          // Non-RSA-model: no placeholder was created, add fresh optimistic entry
          const newRun = {
            run_id: runId,
            project_id: effectiveProjectId,
            project_type: normalizedProjectTypeForPayload,
            project_name: requestProjectName,
            name: requestProjectName,
            description: `Pipeline run for project ${effectiveProjectId}`,
            status: "processing",
            created_at: new Date(),
            last_updated: new Date(),
            user_email: user_email,
            services: selectedTaskNamesForSubmission || [],
            overall_progress: 0,
            total_analyses: 1,
            completed_analyses: 0,
            running_analyses: 1,
          };
          setOptimisticRuns((prev) => [newRun, ...prev]);
          setDashboardSelectedRun(newRun);
        }
        refreshDashboardRuns();
        if (isRsaModelProject) {
          setPendingDashboardRunId(runId);
        }
      } else if (
        isRsaModelProject &&
        (processResponse.status === 200 || processResponse.status === 202)
      ) {
        // Fallback: launch succeeded but run_id not returned immediately.
        if (localRunId) {
          setOptimisticRuns((prev) =>
            prev.filter((r) => r.run_id !== localRunId),
          );
          setPendingLocalRunId(null);
        }
        refreshDashboardRuns();
        setPendingDashboardRunId(null);
      }

      fetchRsaUsageLedgerState(effectiveProjectId);

      // Show success message - user stays on RSA-Analysis page (or moved to Dashboard)
      showNotification("Analysis submitted successfully!");
    } catch (error) {
      console.error("Process API call failed:", error);
      console.error("❌ Error response:", error.response?.data);

      // Update the request record with an error message if the API call fails.
      if (requestId != null) {
        setRequests((prev) =>
          prev.map((req) =>
            req.id === requestId
              ? {
                  ...req,
                  status: "error",
                  response:
                    error.response?.data?.message ||
                    "There was an error submitting the payload(s).",
                }
              : req,
          ),
        );
      }

      // Remove queuing placeholder tile if it exists
      if (localRunId) {
        setOptimisticRuns((prev) =>
          prev.filter((r) => r.run_id !== localRunId),
        );
        setPendingLocalRunId(null);
      }
      // Don't redirect on error - show error to user instead
      showNotification(
        `Error submitting analysis: ${error.response?.data?.message || error.message || "Unknown error"}`,
      );
    }
  };

  // Save RSAPATH XML files to Source folder + persist lanes to MongoDB
  const saveLasSeparationPathToDrive = async () => {
    const wkt = normalizeWktInput(
      String(mapOverlayLocalConfig?.wkt ?? "").trim(),
    );
    const xmlState = String(mapOverlayLocalConfig?.state ?? "").trim();
    let xmlLanes = rsaPathsHook.lanes.filter(
      (l) =>
        l.waypoints?.length >= 2 ||
        l.pathData?.geometry?.coordinates?.length >= 2,
    );
    // When no explicit lanes exist, promote qualifying paths to lane-like objects
    if (xmlLanes.length === 0) {
      xmlLanes = rsaPathsHook.paths
        .filter(
          (p) =>
            p.waypoints?.length >= 2 ||
            p.pathData?.geometry?.coordinates?.length >= 2,
        )
        .map((p) => ({
          ...p,
          pathId: p.id,
          lanePosition: "LL",
          direction: "P",
          speedSegments: [],
        }));
    }
    if (xmlLanes.length === 0) {
      showNotification("Add at least one lane with waypoints before saving.");
      return;
    }
    if (!wkt) {
      showNotification("Enter WKT CRS in Setup before saving.");
      return;
    }
    if (!xmlState) {
      showNotification("Fill in State in Setup before saving.");
      return;
    }

    const isProductionDomain =
      window.location.hostname.includes("123bimlive.com");
    const baseUrl =
      ENVIRONMENT === "production" || isProductionDomain ? "" : API_BASE_URL;
    const uploadUrl = baseUrl
      ? `${API_BASE_URL}/api/google-drive/upload-json`
      : "/api/google-drive/upload-json";

    // Resolve Source/Routes folder for XML upload (create Routes if needed).
    // Lifted above the try below so the pre-save conflict check can use it.
    let sourceFolderId = null;
    try {
      const sourceId = await fetchProjectSetupFolderId(projectId);
      if (sourceId) {
        const subfolderUrl = baseUrl
          ? `${API_BASE_URL}/api/google-drive/get-or-create-subfolder`
          : "/api/google-drive/get-or-create-subfolder";
        const routesRes = await axios.post(
          subfolderUrl,
          {
            parent_id: sourceId,
            folder_name: "Routes",
          },
          { headers: { "Content-Type": "application/json" } },
        );
        sourceFolderId = routesRes.data?.folder_id || null;
      }
    } catch (err) {
      console.warn(
        "Could not resolve Source/Routes folder for XML upload:",
        err,
      );
    }

    // Pre-save conflict check: if any planned filenames already exist in
    // Source/Routes, prompt the user before uploading. Skip silently if we
    // couldn't resolve the folder (the loop below will surface that error).
    let overwriteExistingXmls = false;
    if (sourceFolderId) {
      const plannedFilenames = planRsaXmlFilenames(
        xmlLanes,
        rsaPathsHook.paths,
        xmlState,
      ).map((p) => p.filename);
      if (plannedFilenames.length > 0) {
        try {
          const checkUrl = baseUrl
            ? `${API_BASE_URL}/api/projects/${projectId}/check-routes-folder-conflicts`
            : `/api/projects/${projectId}/check-routes-folder-conflicts`;
          const checkRes = await axios.post(
            checkUrl,
            { folder_id: sourceFolderId, filenames: plannedFilenames },
            { headers: { "Content-Type": "application/json" } },
          );
          const existing = checkRes?.data?.existing || [];
          if (existing.length > 0) {
            const decision = await new Promise((resolve) => {
              setXmlOverwriteWarning({
                filenames: existing,
                resolve: (choice) => {
                  setXmlOverwriteWarning(null);
                  resolve(choice);
                },
              });
            });
            if (decision !== "overwrite") {
              return { status: "cancelled" };
            }
            overwriteExistingXmls = true;
          }
        } catch (checkErr) {
          console.warn(
            "[xml_check] pre-save conflict check failed, proceeding:",
            checkErr,
          );
        }
      }
    }

    setSavingLasSeparationPath(true);
    try {
      const escXml = (s) =>
        String(s)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&apos;");
      const lanePositionLabels = {
        LL: "Left Lane",
        CL: "Center Lane",
        RL: "Right Lane",
      };
      const ISD_CASE_TO_CODE = {
        [ISD_CASES.LEFT_TURN]: "B1",
        [ISD_CASES.RIGHT_TURN]: "B2",
        [ISD_CASES.F_CASE]: "F",
      };
      const ISD_CASE_TO_NOTE = {
        [ISD_CASES.LEFT_TURN]: "Left Turn",
        [ISD_CASES.RIGHT_TURN]: "Right Turn",
        [ISD_CASES.F_CASE]: "Left turn from major road",
      };
      const proj4FromValidate = wktValidation?.crs?.proj4;

      const allPaths = rsaPathsHook.paths;
      const routeNameCounter = {};
      const pathSegmentMap = {};
      for (const p of allPaths) {
        const name = String(p.label ?? "").trim();
        if (!routeNameCounter[name]) routeNameCounter[name] = 1;
        pathSegmentMap[p.id] = routeNameCounter[name];
        routeNameCounter[name] += 1;
      }

      let xmlUploadCount = 0;
      const newXmlToJsonMap = {};
      for (const lane of xmlLanes) {
        try {
          const parentPath = allPaths.find((p) => p.id === lane.pathId);
          const routeNumber = String(lane.label ?? "").trim();
          if (!routeNumber) continue;
          const segmentNumber = pathSegmentMap[lane.pathId] ?? 1;
          const direction = lane.direction ?? "P";
          const lanePosition = lane.lanePosition ?? "LL";

          let laneWaypoints;
          if (lane.waypoints?.length >= 2) {
            laneWaypoints = lane.waypoints;
          } else {
            laneWaypoints = lane.pathData.geometry.coordinates.map((c) => ({
              lat: c[1],
              lng: c[0],
            }));
          }
          const points10ft = getPathCoordinatesEveryFeet(laneWaypoints, 10);
          if (!points10ft || points10ft.length === 0) continue;

          const projected = await latLngToProjectedXYAsync(
            points10ft,
            wkt,
            0,
            proj4FromValidate,
          );
          if (
            !projected.length ||
            projected.some(
              ([x, y]) => !Number.isFinite(x) || !Number.isFinite(y),
            )
          )
            continue;

          const laneCoords = lane.pathData?.geometry?.coordinates;
          const totalMi =
            (laneCoords?.length >= 2
              ? getPathLengthMiles(laneCoords)
              : getPathLengthMiles(lane.waypoints)) || 0;

          const speedSegments = Array.isArray(lane.speedSegments)
            ? [...lane.speedSegments]
                .filter((seg) => Number.isFinite(Number(seg?.mileMark)))
                .sort((a, b) => Number(a.mileMark) - Number(b.mileMark))
            : [];
          const firstSpeed =
            speedSegments.length > 0 &&
            Number.isFinite(Number(speedSegments[0].speedMph))
              ? Number(speedSegments[0].speedMph)
              : 45;

          const pathId = `${routeNumber}-${segmentNumber}-${lanePosition}-${direction}`;
          const note = lanePositionLabels[lanePosition] || "Lane";
          const beginMile = direction === "N" ? totalMi.toFixed(4) : "0.0000";
          const endMile = direction === "N" ? "0.0000" : totalMi.toFixed(4);

          let speedLimitsXml = "";
          if (speedSegments.length > 0) {
            speedLimitsXml = speedSegments
              .map((seg, idx) => {
                const startMile = Number(seg.mileMark);
                const endMileSeg =
                  idx < speedSegments.length - 1
                    ? Number(speedSegments[idx + 1].mileMark)
                    : totalMi;
                const speed =
                  Number.isFinite(Number(seg.speedMph)) &&
                  Number(seg.speedMph) > 0
                    ? Number(seg.speedMph)
                    : 45;
                return `    <SPEED_AREA Start="${startMile.toFixed(4)}" End="${endMileSeg.toFixed(4)}" Speed="${speed}" />`;
              })
              .join("\n");
          }

          const pointsXml = projected
            .map(([x, y, z]) => [
              Number(x).toFixed(2),
              Number(y).toFixed(2),
              Number(z || 0).toFixed(2),
            ])
            // Drop consecutive coincident points. A duplicate point is a
            // zero-length segment, which makes SVR's left-turn (B1)
            // RunAnalysis throw "System.Exception: Lines are parallel".
            .filter(
              (p, i, arr) =>
                i === 0 || p[0] !== arr[i - 1][0] || p[1] !== arr[i - 1][1],
            )
            .map(([x, y, z]) => `        <P>${x},${y},${z}</P>`)
            .join("\n");

          // Build INTERSECTION_POINTS for this lane: only ISDs explicitly
          // attached to this lane, PD case skipped (not implemented yet).
          let intersectionsBlockXml =
            "<INTERSECTION_POINTS></INTERSECTION_POINTS>";
          const laneIntersections = (rsaPathsHook.intersections || []).filter(
            (isd) =>
              isd.rsaPathId === lane.id &&
              Boolean(ISD_CASE_TO_CODE[isd.isdCase]) &&
              Number.isFinite(isd.insertionPoint?.lat) &&
              Number.isFinite(isd.insertionPoint?.lng),
          );
          if (laneIntersections.length > 0) {
            const sortedIsds = [...laneIntersections].sort((a, b) => {
              const an = Number(a.intersectionName);
              const bn = Number(b.intersectionName);
              const aNum = Number.isFinite(an);
              const bNum = Number.isFinite(bn);
              if (aNum && bNum && an !== bn) return an - bn;
              if (aNum && !bNum) return -1;
              if (!aNum && bNum) return 1;
              if (!aNum && !bNum) {
                const cmp = String(a.intersectionName).localeCompare(
                  String(b.intersectionName),
                );
                if (cmp !== 0) return cmp;
              }
              return (a.maneuverIndex || 0) - (b.maneuverIndex || 0);
            });

            // Batch-project all insertion + (F-case) opposite points in one
            // call so we share the projection setup with the lane points.
            const isdPtsToProject = [];
            const isdInsertIdx = [];
            const isdOppIdx = [];
            for (const isd of sortedIsds) {
              isdInsertIdx.push(isdPtsToProject.length);
              isdPtsToProject.push({
                lat: isd.insertionPoint.lat,
                lng: isd.insertionPoint.lng,
              });
              const opp =
                isd.isdCase === ISD_CASES.F_CASE
                  ? isd.oppositePoint?.insertionPoint
                  : null;
              if (opp && Number.isFinite(opp.lat) && Number.isFinite(opp.lng)) {
                isdOppIdx.push(isdPtsToProject.length);
                isdPtsToProject.push({ lat: opp.lat, lng: opp.lng });
              } else {
                isdOppIdx.push(-1);
              }
            }
            const isdProjected = await latLngToProjectedXYAsync(
              isdPtsToProject,
              wkt,
              0,
              proj4FromValidate,
            );

            const isdLines = [];
            for (let i = 0; i < sortedIsds.length; i++) {
              const isd = sortedIsds[i];
              const proj = isdProjected[isdInsertIdx[i]];
              if (
                !proj ||
                !Number.isFinite(proj[0]) ||
                !Number.isFinite(proj[1])
              ) {
                continue;
              }
              const caseCode = ISD_CASE_TO_CODE[isd.isdCase];
              const note = ISD_CASE_TO_NOTE[isd.isdCase] || "";
              const px = Number(proj[0]).toFixed(2);
              const py = Number(proj[1]).toFixed(2);
              const pz = Number(proj[2] || 0).toFixed(2);
              const azimuth = Number.isFinite(Number(isd.azimuth))
                ? Number(isd.azimuth).toFixed(2)
                : "0.00";
              const idStr = `INTP-${isd.intersectionName}-${isd.maneuverIndex}-${caseCode}`;

              let oppositeLine = "";
              if (isdOppIdx[i] >= 0) {
                const oProj = isdProjected[isdOppIdx[i]];
                if (
                  oProj &&
                  Number.isFinite(oProj[0]) &&
                  Number.isFinite(oProj[1])
                ) {
                  const ox = Number(oProj[0]).toFixed(2);
                  const oy = Number(oProj[1]).toFixed(2);
                  const oz = Number(oProj[2] || 0).toFixed(2);
                  oppositeLine = `\n      <OppositePoint>${ox},${oy},${oz}</OppositePoint>`;
                }
              }

              isdLines.push(
                `    <INTERSECTION>
      <IntersectionName>${escXml(isd.intersectionName)}</IntersectionName>
      <SecondaryRoute>${escXml(isd.secondaryRoute)}</SecondaryRoute>
      <ID>${escXml(idStr)}</ID>
      <P>${px},${py},${pz}</P>
      <Azimuth>${azimuth}</Azimuth>
      <TrafficControlCase>${caseCode}</TrafficControlCase>
      <Note>${escXml(note)}</Note>${oppositeLine}
      <PathId>${escXml(pathId)}</PathId>
    </INTERSECTION>`,
              );
            }
            if (isdLines.length > 0) {
              intersectionsBlockXml = `<INTERSECTION_POINTS>\n${isdLines.join("\n")}\n  </INTERSECTION_POINTS>`;
            }
          }

          const xml = `<?xml version='1.0' encoding='utf-8'?>
<RSAPATH>
  <DIRECTION>${escXml(direction)}</DIRECTION>
  <ROUTE_NUMB>${escXml(routeNumber)}</ROUTE_NUMB>
  <PATH_UNITS>Meter</PATH_UNITS>
  <SPEED_LIMIT>${firstSpeed}</SPEED_LIMIT>
  <SPEED_UNITS>MPH</SPEED_UNITS>
  <CYCLE>2024</CYCLE>
  <STATE>${escXml(xmlState.toUpperCase())}</STATE>
  <APERTURE>0.25</APERTURE>
  <SPEED_LIMITS>
${speedLimitsXml}
  </SPEED_LIMITS>
  <WKT>true</WKT>
  <WKTSTRING>${String(wkt).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</WKTSTRING>
  ${intersectionsBlockXml}
  <PATH_GROUP>
    <PATH>
      <PATH_ID>${escXml(pathId)}</PATH_ID>
      <NOTE>${escXml(note)}</NOTE>
      <CODE>${escXml(lanePosition)}</CODE>
      <BEGIN_MILE>${beginMile}</BEGIN_MILE>
      <END_MILE>${endMile}</END_MILE>
      <CAD_HANDLE>45B,45A</CAD_HANDLE>
      <POINTS>
${pointsXml}
      </POINTS>
    </PATH>
  </PATH_GROUP>
</RSAPATH>
`;
          const xmlFilename = `${xmlState.toUpperCase()}_${routeNumber}_${segmentNumber}_${lanePosition}_${direction}.xml`;
          if (sourceFolderId) {
            await axios.post(
              uploadUrl,
              {
                folder_id: sourceFolderId,
                filename: xmlFilename,
                content: xml,
                overwrite: overwriteExistingXmls,
              },
              { headers: { "Content-Type": "application/json" } },
            );
            xmlUploadCount += 1;
          }
          // Build XML → JSON path file mapping
          if (parentPath) {
            const jsonFilename = `LasSeparationPath_${sanitizePathLabel(parentPath.label)}.json`;
            newXmlToJsonMap[xmlFilename] = {
              jsonFilename,
              pathId: parentPath.id,
              pathLabel: parentPath.label,
            };
          }
        } catch (xmlErr) {
          console.warn("XML upload failed for lane", lane.id, xmlErr);
        }
      }
      if (xmlUploadCount === 0 && !sourceFolderId) {
        showNotification(
          "Could not resolve Source/Routes folder — XML files were not uploaded.",
        );
      }

      // Persist XML → JSON path file mapping (auto-saves to MySQL via useRsaPaths)
      if (Object.keys(newXmlToJsonMap).length > 0) {
        rsaPathsHook.actions.setXmlToJsonMap({
          ...(rsaPathsHook.xmlToJsonMap || {}),
          ...newXmlToJsonMap,
        });
      }

      // (Previously also wrote to MongoDB pipeline_project_lanes for cross-
      // user sharing. Removed: MySQL rsa_project_paths is the single source
      // of truth — auto-saved on every edit and read by every user on
      // project load. Drift between the two stores caused stale paths to
      // resurrect after a MySQL wipe.)
    } catch (e) {
      console.error("saveLasSeparationPathToDrive", e);
      showNotification("Failed to save: " + (e.message || String(e)));
      return { status: "failed" };
    } finally {
      setSavingLasSeparationPath(false);
    }
    return { status: "completed" };
  };

  // Create Ortho Maps: build LasSeparationPath.json from path/lane data, upload to LAS folder, run pipeline
  // Polls an ortho-maps job to completion, streaming tiles onto the map and
  // recording the terminal status onto the path. Used by createOrthoMaps
  // (after submit) and by the reconnect effect (later task) for jobs found
  // 'running' on load. Idempotent per jobId within a tab.
  const trackOrthoJob = async ({ pathId, jobId, sanitizedName, label }) => {
    if (trackedOrthoJobIdsRef.current.has(jobId)) return;
    trackedOrthoJobIdsRef.current.add(jobId);
    setCreatingOrthoMapsByPath((prev) => ({ ...prev, [pathId]: true }));
    const isProductionDomain =
      window.location.hostname.includes("123bimlive.com");
    const baseUrl =
      ENVIRONMENT === "production" || isProductionDomain ? "" : API_BASE_URL;
    const statusBaseUrl = baseUrl
      ? `${API_BASE_URL}/api/rsa/ortho-maps-pipeline`
      : "/api/rsa/ortho-maps-pipeline";
    const displayedTileIds = new Set();
    try {
      const pollResult = await new Promise((resolve, reject) => {
        const poll = setInterval(async () => {
          try {
            const statusRes = await axios.get(
              `${statusBaseUrl}/${jobId}/status`,
              { timeout: 10000 },
            );
            const { status, progress, message, tiles } = statusRes.data;
            setOrthoProgressByPath((prev) => ({
              ...prev,
              [pathId]: { progress: progress || 0, message: message || "" },
            }));
            // Stream tiles onto map as they arrive — use local_url for instant display
            if (Array.isArray(tiles) && tiles.length > 0) {
              const newTiles = tiles.filter(
                (t) =>
                  (t?.local_url || t?.png_file_id) &&
                  !displayedTileIds.has(t.name),
              );
              if (newTiles.length > 0) {
                const wktStr = normalizeWktInput(
                  String(mapOverlayLocalConfig?.wkt ?? "").trim(),
                );
                const p4 = wktValidation?.crs?.proj4;
                for (const tile of newTiles) {
                  try {
                    const corners = tile.projected_corners;
                    if (Array.isArray(corners) && corners.length === 4) {
                      const xy = corners.map((p) => [
                        Number(p?.x),
                        Number(p?.y),
                      ]);
                      if (
                        xy.every(
                          ([x, y]) => Number.isFinite(x) && Number.isFinite(y),
                        )
                      ) {
                        const quad = await projectedPointsToLatLngAsync(
                          xy,
                          wktStr,
                          p4,
                        );
                        const b = latLngPointsToBounds(quad);
                        if (b) {
                          const localUrl = tile.local_url
                            ? baseUrl
                              ? `${API_BASE_URL}${tile.local_url}`
                              : tile.local_url
                            : null;
                          setOrthoTiles((prev) => [
                            ...prev,
                            {
                              name: tile.name,
                              pngFileId: tile.png_file_id || null,
                              pathId,
                              localUrl,
                              bounds: b,
                              quad,
                            },
                          ]);
                          displayedTileIds.add(tile.name);
                        }
                      }
                    }
                  } catch (projErr) {
                    // Tile dropped from streaming; the post-completion
                    // recovery pass below will retry it sequentially. Log
                    // so we can see when transient proj4 worker errors hit.
                    console.warn(
                      `[trackOrthoJob] streaming projection failed for tile '${tile?.name}', will retry after completion:`,
                      projErr,
                    );
                  }
                }
              }
            }
            if (status === "completed") {
              clearInterval(poll);
              resolve(statusRes.data);
            } else if (status === "failed") {
              clearInterval(poll);
              reject(new Error(message || "Pipeline failed"));
            }
          } catch (pollErr) {
            if (pollErr.response?.status === 404) {
              // Job gone from Redis (2h TTL). The worker's CAS write may have
              // already set a terminal status on the path; if not, fall back
              // to the real artifact — does the maps_2000 folder have tiles?
              clearInterval(poll);
              try {
                const latest = rsaPathsHook.paths.find((p) => p.id === pathId);
                if (latest?.orthoJob?.status === "completed") {
                  resolve({
                    message: latest.orthoJob.message || "",
                    tiles: [],
                  });
                  return;
                }
                const folderId2 = latest?.maps2000FolderId;
                const tilesUrl = baseUrl
                  ? `${API_BASE_URL}/api/rsa/ortho-tiles`
                  : "/api/rsa/ortho-tiles";
                const r = await axios.get(tilesUrl, {
                  params: {
                    maps_folder_id: folderId2,
                    folder_id: effectiveSetupFolderId,
                    path_name: sanitizedName,
                  },
                  timeout: 15000,
                });
                if (Array.isArray(r.data?.tiles) && r.data.tiles.length > 0) {
                  resolve({
                    message: "Ortho maps already complete.",
                    tiles: [],
                  });
                } else {
                  reject(
                    new Error(
                      "Ortho job is no longer running and no tiles were found.",
                    ),
                  );
                }
              } catch (fbErr) {
                reject(
                  new Error(
                    "Ortho job status unavailable: " + (fbErr.message || fbErr),
                  ),
                );
              }
              return;
            }
            console.warn(
              "[trackOrthoJob] poll error (will retry):",
              pollErr.message,
            );
          }
        }, 2000);
        setTimeout(() => {
          clearInterval(poll);
          reject(new Error("Pipeline timed out after 4 hours"));
        }, 14400000);
      });

      console.log("[trackOrthoJob] pipeline completed:", pollResult);

      // Reconcile maps2000FolderId from the completion payload. The submit
      // handler pre-creates the Drive folder when possible, but if that step
      // fails (or for any reason returned null in the 202), the worker
      // creates the folder lazily during upload. Without this fallback the
      // path's maps2000FolderId stays unset, maps2000ByPath[pathId] stays
      // false, and the button keeps showing "Create Ortho Maps" even though
      // tiles already render on the map.
      const completedFolderId = pollResult?.maps_folder_id;
      if (completedFolderId) {
        const latestPath = rsaPathsHook.paths.find((p) => p.id === pathId);
        if (latestPath && latestPath.maps2000FolderId !== completedFolderId) {
          rsaPathsHook.actions.updatePath(pathId, {
            maps2000FolderId: completedFolderId,
            orthoMapsName: sanitizedName,
          });
        }
      }

      // Defer the success toast until the rendering layer has caught up
      // (handled by the pendingOrthoSuccessMessage useEffect). Falls back
      // to a 30s timeout if rendering never reaches the expected count.
      setPendingOrthoSuccessMessage({
        message: pollResult.message || `Ortho maps created for ${label}.`,
        expectedTileCount: Array.isArray(pollResult.tiles)
          ? pollResult.tiles.length
          : 0,
        startTime: Date.now(),
      });

      // Reconcile state with the final tile list:
      // 1) Recover any tiles that the streaming loop silently dropped
      //    (the streaming `for` loop runs concurrently across overlapping
      //    poll ticks, and status=completed can arrive while one poll is
      //    still mid-projection. Tiles whose projection threw are absent
      //    from displayedTileIds and from orthoTiles — without this pass
      //    they'd be permanently missing from the map.)
      // 2) Backfill png_file_id on existing tiles for persistence.
      if (Array.isArray(pollResult.tiles) && pollResult.tiles.length > 0) {
        const wktStr = normalizeWktInput(
          String(mapOverlayLocalConfig?.wkt ?? "").trim(),
        );
        const p4 = wktValidation?.crs?.proj4;
        const recovered = [];
        for (const ft of pollResult.tiles) {
          if (!ft?.name || displayedTileIds.has(ft.name)) continue;
          if (!(ft?.local_url || ft?.png_file_id)) continue;
          try {
            const corners = ft.projected_corners;
            if (!Array.isArray(corners) || corners.length !== 4) continue;
            const xy = corners.map((p) => [Number(p?.x), Number(p?.y)]);
            if (
              !xy.every(([x, y]) => Number.isFinite(x) && Number.isFinite(y))
            ) {
              continue;
            }
            const quad = await projectedPointsToLatLngAsync(xy, wktStr, p4);
            const b = latLngPointsToBounds(quad);
            if (!b) continue;
            const localUrl = ft.local_url
              ? baseUrl
                ? `${API_BASE_URL}${ft.local_url}`
                : ft.local_url
              : null;
            recovered.push({
              name: ft.name,
              pngFileId: ft.png_file_id || null,
              pathId,
              localUrl,
              bounds: b,
              quad,
            });
            displayedTileIds.add(ft.name);
          } catch (recErr) {
            console.warn(
              `[trackOrthoJob] recovery projection failed for tile '${ft.name}':`,
              recErr,
            );
          }
        }
        if (recovered.length > 0) {
          console.log(
            `[trackOrthoJob] recovered ${recovered.length} tile(s) the streaming loop dropped`,
          );
        }

        setOrthoTiles((prev) => {
          let changed = false;
          // Backfill pngFileId on tiles already in state.
          const updated = prev.map((t) => {
            if (t.pngFileId) return t;
            const finalTile = pollResult.tiles.find((ft) => ft.name === t.name);
            if (finalTile?.png_file_id) {
              changed = true;
              return { ...t, pngFileId: finalTile.png_file_id };
            }
            return t;
          });
          // Append recovered tiles, deduping by name in case a slow streaming
          // call landed in state between recovery and this setter.
          if (recovered.length > 0) {
            const existingNames = new Set(updated.map((t) => t.name));
            const additions = recovered.filter(
              (t) => !existingNames.has(t.name),
            );
            if (additions.length > 0) {
              return [...updated, ...additions];
            }
          }
          return changed ? updated : prev;
        });
      }
      rsaPathsHook.actions.updatePath(pathId, {
        orthoJob: {
          jobId,
          status: "completed",
          progress: 100,
          message: pollResult.message || `Ortho maps created for ${label}.`,
          updatedAt: new Date().toISOString(),
        },
      });
    } catch (e) {
      console.error("[trackOrthoJob] ERROR:", e);
      const errMsg = e.response?.data?.error || e.message || String(e);
      showNotification("Failed to create ortho maps: " + errMsg);
      rsaPathsHook.actions.updatePath(pathId, {
        orthoJob: {
          jobId,
          status: "failed",
          progress: 0,
          message: errMsg,
          updatedAt: new Date().toISOString(),
        },
      });
    } finally {
      trackedOrthoJobIdsRef.current.delete(jobId);
      setCreatingOrthoMapsByPath((prev) => ({ ...prev, [pathId]: false }));
      setOrthoProgressByPath((prev) => {
        const next = { ...prev };
        delete next[pathId];
        return next;
      });
    }
  };

  const createOrthoMaps = async (pathId) => {
    const wkt = normalizeWktInput(
      String(mapOverlayLocalConfig?.wkt ?? "").trim(),
    );
    if (!wkt) {
      showNotification("Enter WKT CRS in Setup before creating ortho maps.");
      return;
    }

    const folderId = effectiveSetupFolderId;
    if (!folderId) {
      showNotification("Select a LAS folder before creating ortho maps.");
      return;
    }

    // Find the target path by ID
    const targetPath = rsaPathsHook.paths.find((p) => p.id === pathId);
    if (!targetPath) {
      showNotification("Path not found.");
      return;
    }

    // Find best source geometry: prefer lanes of this path, then the path itself
    let sourceLane = rsaPathsHook.lanes.find(
      (l) =>
        l.pathId === pathId &&
        (l.waypoints?.length >= 2 ||
          l.pathData?.geometry?.coordinates?.length >= 2),
    );
    if (!sourceLane) {
      sourceLane = targetPath;
      if (
        !(
          sourceLane.waypoints?.length >= 2 ||
          sourceLane.pathData?.geometry?.coordinates?.length >= 2
        )
      ) {
        showNotification(
          "Draw a path with at least 2 waypoints before creating ortho maps.",
        );
        return;
      }
    }

    setCreatingOrthoMapsByPath((prev) => ({ ...prev, [pathId]: true }));
    try {
      const proj4FromValidate = wktValidation?.crs?.proj4;
      const isProductionDomain =
        window.location.hostname.includes("123bimlive.com");
      const baseUrl =
        ENVIRONMENT === "production" || isProductionDomain ? "" : API_BASE_URL;
      const sanitizedName = sanitizePathLabel(targetPath.label);
      console.log(
        "[createOrthoMaps] pathId=",
        pathId,
        "label=",
        targetPath.label,
        "sanitized=",
        sanitizedName,
        "folderId=",
        folderId,
      );

      // Get path geometry in lat/lng — prefer the full road-snapped polyline
      let waypoints;
      const pathCoords = sourceLane.pathData?.geometry?.coordinates;
      if (Array.isArray(pathCoords) && pathCoords.length >= 2) {
        waypoints = pathCoords.map((c) => ({ lat: c[1], lng: c[0] }));
      } else if (sourceLane.waypoints?.length >= 2) {
        waypoints = sourceLane.waypoints;
      } else {
        showNotification("No path geometry found.");
        return;
      }

      // Quick check: does the path overlap any LAS bounding box?
      if (lasBoundingBoxes.length > 0) {
        const pathOverlapsLas = waypoints.some((wp) =>
          lasBoundingBoxes.some(
            (b) =>
              wp.lat >= b.south &&
              wp.lat <= b.north &&
              wp.lng >= b.west &&
              wp.lng <= b.east,
          ),
        );
        if (!pathOverlapsLas) {
          showNotification(
            "Path is outside all LAS bounding boxes — no ortho tiles can be created. Move the path to overlap the LAS data.",
          );
          return;
        }
      }

      // Project to XY coordinates
      const points10ft = getPathCoordinatesEveryFeet(waypoints, 10);
      if (!points10ft || points10ft.length === 0) {
        showNotification("Could not interpolate path coordinates.");
        return;
      }
      const projected = await latLngToProjectedXYAsync(
        points10ft,
        wkt,
        0,
        proj4FromValidate,
      );
      if (
        !projected.length ||
        projected.some(([x, y]) => !Number.isFinite(x) || !Number.isFinite(y))
      ) {
        showNotification("Coordinate projection failed — check WKT CRS.");
        return;
      }

      // Build GeoJSON
      const refLabel = targetPath.label || "Path";
      const geojson = {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            properties: { ref: refLabel },
            Offset: 150.0,
            DesiredDistance: 150.0,
            PointsNumber: "-1",
            geometryUnites: "",
            geometry: {
              type: "LineString",
              coordinates: projected.map(([x, y, z]) => [x, y, z || 0]),
            },
          },
        ],
      };

      const geojsonStr = JSON.stringify(geojson, null, 2);

      // Submit async pipeline job with path_name
      const pipelineUrl = baseUrl
        ? `${API_BASE_URL}/api/rsa/ortho-maps-pipeline`
        : "/api/rsa/ortho-maps-pipeline";
      const submitRes = await axios.post(
        pipelineUrl,
        {
          folder_id: folderId,
          geojson_content: geojsonStr,
          path_name: sanitizedName,
          path_id: pathId,
          // project_id routes LAS fetch to S3 for flagged projects
          // (SSM <name>_source_storage=s3). Drive-only projects ignore it.
          ...(effectiveProjectId != null
            ? { project_id: effectiveProjectId }
            : {}),
        },
        { headers: { "Content-Type": "application/json" }, timeout: 30000 },
      );

      if (submitRes.status !== 202 || !submitRes.data?.job_id) {
        showNotification(
          "Unexpected response from pipeline: " +
            (submitRes.data?.message || submitRes.status),
        );
        return;
      }

      const jobId = submitRes.data.job_id;
      // Store the pre-created maps_2000 folder ID + sanitized name in path state.
      // orthoMapsName is the source of truth used by the delete-path endpoint
      // to find LasSeparationPath_<name>.json and any _backup_* siblings,
      // so it survives a subsequent path rename.
      const mapsFolderId = submitRes.data.maps_folder_id;
      const runningOrthoJob = {
        jobId,
        status: "running",
        progress: 0,
        message: "Starting…",
        updatedAt: new Date().toISOString(),
      };
      rsaPathsHook.actions.updatePath(pathId, {
        ...(mapsFolderId
          ? { maps2000FolderId: mapsFolderId, orthoMapsName: sanitizedName }
          : {}),
        orthoJob: runningOrthoJob,
      });
      await trackOrthoJob({
        pathId,
        jobId,
        sanitizedName,
        label: targetPath.label,
      });
    } catch (e) {
      console.error("[createOrthoMaps] ERROR:", e);
      const errMsg = e.response?.data?.error || e.message || String(e);
      showNotification("Failed to create ortho maps: " + errMsg);
    } finally {
      // trackOrthoJob clears the flag on success/failure, but the submit-phase
      // early-returns (geometry/bbox/interpolation/projection/non-202) exit
      // before trackOrthoJob runs — clear it here so the button can't stick.
      setCreatingOrthoMapsByPath((prev) => ({ ...prev, [pathId]: false }));
    }
  };

  // Reconnect to ortho-maps jobs that were 'running' when the tab was closed
  // or refreshed. paths rehydrate from IndexedDB cache (instant) then backend;
  // either way this fires and resumes polling for jobs not already tracked in
  // this tab. The trackedOrthoJobIdsRef guard inside trackOrthoJob makes this
  // idempotent across the frequent paths re-renders.
  useEffect(() => {
    const pending = pathsNeedingReconnect(
      rsaPathsHook.paths,
      trackedOrthoJobIdsRef.current,
    );
    if (pending.length === 0) return;
    for (const { pathId, jobId } of pending) {
      const p = rsaPathsHook.paths.find((x) => x.id === pathId);
      const sanitizedName =
        p?.orthoMapsName || (p?.label ? sanitizePathLabel(p.label) : "Route");
      // Fire-and-forget: trackOrthoJob handles its own errors and writes the
      // terminal status. Already-uploaded tiles are painted by the resumed
      // poll (its displayedTileIds starts empty so it repaints all reported
      // tiles) and by the existing "Auto-load ortho tiles" effect once
      // maps2000ByPath reflects the completed folder — so no .then() repaint
      // is needed here.
      trackOrthoJob({
        pathId,
        jobId,
        sanitizedName,
        label: p?.label || "Path",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rsaPathsHook.paths]);

  // ─── Auto-trace lane ────────────────────────────────────────────────────
  // Arm seed-pick mode: the next map click becomes the trace seed.
  const startAutoTrace = (pathId) => {
    const path = rsaPathsHook.paths.find((p) => p.id === pathId);
    if (!path) return;
    if (!path.maps2000FolderId && !path.orthoMapsName) {
      showNotification("Create ortho maps for this route before auto-tracing.");
      return;
    }
    rsaPathsHook.actions.setActivePath?.(pathId);
    setTraceSeed({
      active: true,
      pathId,
      appendLaneId: null,
      firstPoint: null,
    });
    showNotification(
      "Click the start point on the lane marking, then a second point in the direction to trace.",
    );
  };

  // First click sets the start point; second click sets the direction and fires
  // the trace (forward only, in the start→direction heading).
  const handleTraceSeedPicked = (latLng) => {
    if (!traceSeed.active || !latLng) return;
    if (!traceSeed.firstPoint) {
      setTraceSeed((prev) => ({ ...prev, firstPoint: latLng }));
      showNotification(
        "Now click a second point further along the lane to set the trace direction.",
      );
      return;
    }
    const { pathId, appendLaneId, firstPoint } = traceSeed;
    setTraceSeed({
      active: false,
      pathId: null,
      appendLaneId: null,
      firstPoint: null,
    });
    if (pathId) traceLane(pathId, firstPoint, latLng, appendLaneId);
  };

  const traceLane = async (pathId, startLatLng, dirLatLng, appendLaneId) => {
    const wkt = normalizeWktInput(
      String(mapOverlayLocalConfig?.wkt ?? "").trim(),
    );
    if (!wkt) {
      showNotification("Enter WKT CRS in Setup before auto-tracing.");
      return;
    }
    const folderId = effectiveSetupFolderId;
    if (!folderId) {
      showNotification("Select a LAS folder before auto-tracing.");
      return;
    }
    const path = rsaPathsHook.paths.find((p) => p.id === pathId);
    if (!path) {
      showNotification("Route not found.");
      return;
    }
    const proj4FromValidate = wktValidation?.crs?.proj4;
    try {
      const proj = await latLngToProjectedXYAsync(
        [startLatLng, dirLatLng],
        wkt,
        0,
        proj4FromValidate,
      );
      const sx = proj?.[0]?.[0];
      const sy = proj?.[0]?.[1];
      const ex = proj?.[1]?.[0];
      const ey = proj?.[1]?.[1];
      if (![sx, sy, ex, ey].every(Number.isFinite)) {
        showNotification("Could not project the seed points — check WKT CRS.");
        return;
      }
      const hx = ex - sx;
      const hy = ey - sy;
      const hlen = Math.hypot(hx, hy);
      if (hlen < 1e-6) {
        showNotification(
          "The two points are too close — click them further apart.",
        );
        return;
      }

      // Measure how many projected world units span 1 metre on the ground, so the
      // backend (whose distance params are in feet) scales correctly to this CRS.
      // ≈1 for metric CRSs (UTM, units=Meter), ≈3.28 for US-foot State Plane.
      let worldUnitsPerMeter = 1.0;
      try {
        const oneMeterNorth = {
          lat: startLatLng.lat + 1 / 111320,
          lng: startLatLng.lng,
        };
        const scalePair = await latLngToProjectedXYAsync(
          [startLatLng, oneMeterNorth],
          wkt,
          0,
          proj4FromValidate,
        );
        if (scalePair?.length === 2) {
          const d = Math.hypot(
            scalePair[1][0] - scalePair[0][0],
            scalePair[1][1] - scalePair[0][1],
          );
          if (Number.isFinite(d) && d > 1e-6) worldUnitsPerMeter = d;
        }
      } catch (scaleErr) {
        console.warn(
          "[traceLane] world-unit scale probe failed, assuming meters:",
          scaleErr,
        );
      }

      const isProductionDomain =
        window.location.hostname.includes("123bimlive.com");
      const baseUrl =
        ENVIRONMENT === "production" || isProductionDomain ? "" : API_BASE_URL;
      const traceUrl = baseUrl
        ? `${API_BASE_URL}/api/rsa/lane-trace`
        : "/api/rsa/lane-trace";
      const sanitizedName = path.orthoMapsName || sanitizePathLabel(path.label);

      setLaneTraceProgress((prev) => ({
        ...prev,
        [pathId]: { progress: 0, message: "Starting…" },
      }));
      const submitRes = await axios.post(
        traceUrl,
        {
          folder_id: folderId,
          ...(path.maps2000FolderId
            ? { maps_folder_id: path.maps2000FolderId }
            : {}),
          path_name: sanitizedName,
          seed: { x: sx, y: sy },
          heading: { x: hx / hlen, y: hy / hlen },
          world_units_per_meter: worldUnitsPerMeter,
          ...(effectiveProjectId != null
            ? { project_id: effectiveProjectId }
            : {}),
        },
        { headers: { "Content-Type": "application/json" }, timeout: 30000 },
      );
      if (submitRes.status !== 202 || !submitRes.data?.job_id) {
        setLaneTraceProgress((prev) => ({ ...prev, [pathId]: undefined }));
        showNotification(
          "Unexpected response from lane-trace: " +
            (submitRes.data?.message || submitRes.status),
        );
        return;
      }
      await trackLaneTraceJob({
        pathId,
        jobId: submitRes.data.job_id,
        appendLaneId,
        wkt,
        p4: proj4FromValidate,
      });
    } catch (e) {
      console.error("[traceLane] ERROR:", e);
      setLaneTraceProgress((prev) => ({ ...prev, [pathId]: undefined }));
      const errMsg = e.response?.data?.error || e.message || String(e);
      showNotification("Lane trace failed: " + errMsg);
    }
  };

  const trackLaneTraceJob = async ({
    pathId,
    jobId,
    appendLaneId,
    wkt,
    p4,
  }) => {
    if (trackedLaneTraceJobIdsRef.current.has(jobId)) return;
    trackedLaneTraceJobIdsRef.current.add(jobId);
    const isProductionDomain =
      window.location.hostname.includes("123bimlive.com");
    const baseUrl =
      ENVIRONMENT === "production" || isProductionDomain ? "" : API_BASE_URL;
    const statusBase = baseUrl
      ? `${API_BASE_URL}/api/rsa/lane-trace`
      : "/api/rsa/lane-trace";
    try {
      const data = await new Promise((resolve, reject) => {
        const poll = setInterval(async () => {
          try {
            const res = await axios.get(`${statusBase}/${jobId}/status`, {
              timeout: 15000,
            });
            const { status, progress, message } = res.data;
            setLaneTraceProgress((prev) => ({
              ...prev,
              [pathId]: { progress: progress || 0, message: message || "" },
            }));
            if (status === "completed") {
              clearInterval(poll);
              resolve(res.data);
            } else if (status === "failed") {
              clearInterval(poll);
              reject(new Error(message || "Trace failed"));
            }
          } catch (pollErr) {
            if (pollErr.response?.status === 404) {
              clearInterval(poll);
              reject(new Error("Lane-trace job is no longer available."));
              return;
            }
            console.warn(
              "[trackLaneTraceJob] poll error (will retry):",
              pollErr.message,
            );
          }
        }, 2000);
        setTimeout(
          () => {
            clearInterval(poll);
            reject(new Error("Lane trace timed out"));
          },
          60 * 60 * 1000,
        );
      });

      const rawPoints = Array.isArray(data.points) ? data.points : [];
      if (rawPoints.length < 2) {
        showNotification(
          "Trace produced no usable line — try a seed closer to the marking.",
        );
        return;
      }
      const latlng = await projectedPointsToLatLngAsync(
        rawPoints.map((p) => [Number(p.x), Number(p.y)]),
        wkt,
        p4,
      );
      if (!Array.isArray(latlng) || latlng.length < 2) {
        showNotification(
          "Could not project the traced line back to map coordinates.",
        );
        return;
      }
      // Re-attach the `ai_anchor: true` flag from the backend's source point
      // onto the projected lat/lng — projectedPointsToLatLngAsync only returns
      // `{lat, lng}`, so the flag would otherwise be lost. Used downstream for
      // the purple marker tint; ignored on export.
      const waypointsWithFlags = latlng.map((wp, i) => {
        const src = rawPoints[i];
        return src && src.ai_anchor === true ? { ...wp, ai_anchor: true } : wp;
      });

      let laneId = appendLaneId;
      if (appendLaneId) {
        // Continuation: orient the new segment so it connects to the existing
        // lane's nearest end, then append (skipping a near-duplicate join point).
        const existing = rsaPathsHook.lanes.find((l) => l.id === appendLaneId);
        const prevWp = existing?.waypoints || [];
        let seg = waypointsWithFlags;
        if (prevWp.length > 0) {
          const last = prevWp[prevWp.length - 1];
          const dFirst = Math.hypot(
            seg[0].lat - last.lat,
            seg[0].lng - last.lng,
          );
          const dLast = Math.hypot(
            seg[seg.length - 1].lat - last.lat,
            seg[seg.length - 1].lng - last.lng,
          );
          if (dLast < dFirst) seg = [...seg].reverse();
        }
        rsaPathsHook.actions.setLaneWaypoints(appendLaneId, [
          ...prevWp,
          ...seg,
        ]);
      } else {
        laneId = rsaPathsHook.actions.addLane(pathId, {
          waypoints: waypointsWithFlags,
          label: "Auto Lane",
          creationMode: "manual",
        });
      }

      // Tag the lane as auto-traced (used by the manual Edit/Resume affordance).
      // Set on both initial creation and continuation — idempotent.
      rsaPathsHook.actions.updateLane(laneId, { autoTraced: true });

      const stop = data.stop;
      if (
        stop &&
        Number.isFinite(Number(stop.x)) &&
        Number.isFinite(Number(stop.y))
      ) {
        // Trace stopped because the algorithm lost the paint. Enter bridge mode
        // so the user can place anchors through the bad stretch and resume.
        rsaPathsHook.actions.setActiveLane(laneId);
        rsaPathsHook.actions.enterLaneEditMode();
        const entryMode =
          stop.reason === "lost_line_post_ai"
            ? "lost_line_post_ai"
            : "lost_line";
        setBridge({
          active: true,
          laneId,
          pathId,
          entryMode,
        });
        showNotification(
          "Trace stopped — bridge mode active. Add or remove waypoints, then Resume Auto.",
        );
      } else {
        showNotification("Lane traced.");
      }
    } catch (e) {
      console.error("[trackLaneTraceJob] ERROR:", e);
      showNotification("Lane trace failed: " + (e.message || String(e)));
    } finally {
      trackedLaneTraceJobIdsRef.current.delete(jobId);
      setLaneTraceProgress((prev) => {
        const next = { ...prev };
        delete next[pathId];
        return next;
      });
    }
  };

  const enterBridgeManually = (laneId, pathId) => {
    if (!laneId || !pathId) return;
    rsaPathsHook.actions.setActiveLane(laneId);
    rsaPathsHook.actions.enterLaneEditMode();
    setBridge({ active: true, laneId, pathId, entryMode: "manual" });
  };

  const resumeAutoFromBridge = () => {
    if (!bridge.active || !bridge.laneId || !bridge.pathId) return;
    const lane = rsaPathsHook.lanes.find((l) => l.id === bridge.laneId);
    const wps = lane?.waypoints || [];
    if (wps.length < 2) {
      showNotification(
        "Place at least 2 anchors to set direction, then Resume Auto.",
      );
      return;
    }
    const startLatLng = wps[wps.length - 2];
    const dirLatLng = wps[wps.length - 1];
    rsaPathsHook.actions.exitLaneEditMode();
    setBridge({ active: false, laneId: null, pathId: null, entryMode: null });
    traceLane(bridge.pathId, startLatLng, dirLatLng, bridge.laneId);
  };

  const endBridge = () => {
    rsaPathsHook.actions.exitLaneEditMode();
    setBridge({ active: false, laneId: null, pathId: null, entryMode: null });
  };

  // Legacy service mapping (kept for backward compatibility if needed)
  const serviceMapping = {
    Tiling: "tiling",
    CSF: "csf",
    "Road Detection": "road_detection",
    "Car Removal": "car_removal",
    "File Transfer": "file_transfer",
    Analysis: "create_rsa",
    "PSD Classification": "psd_classification",
    Horizontal: "horizontal",
    Embankment: "embankment",
    "Automatic Reports": "automatic_reports",
    // New task name mappings (with abbreviations)
    "Stopping Sight Distance (SSD)": "stopping_sight_distance",
    "Passing Sight Distance (PSD)": "passing_sight_distance",
    "Flat Spot Analysis (FSA)": "flat_spot_analysis",
    "Intersection Sight Distance (ISD)": "intersection_sight_distance",
    "Sunglare Analysis (GLR)": "sunglare_analysis",
    "Guardrails Warrant (GRW)": "guardrails_warrant",
    "Horizontal Curve Analysis (HCA)": "horizontal",
    "Existing Guardrails (EXG)": "existing_guardrails",
    "Embankment Analysis (EMB)": "embankment",
    "Clear Zone Analysis (CLZ)": "clear_zone_analysis",
  };

  // Function to handle clear button click
  const handleClearClick = () => {
    setShowClearPopup(true);
  };

  // Function to clear all form data
  const handleClearConfirm = () => {
    // Reset all form states
    setExistingProjectName("");
    setSelectedProjectId(projectId ?? null);
    setServices([]);
    setAnalysisTypes([]);
    setSelectedTasks([]);
    setSelectedFolders([]);
    setSelectedDriveName("");
    setLidarType("");
    setHorizontalData(null);
    setEmbankmentData(null);
    setManualLaneCreationActive(false);
    setActiveManualLaneId(null);
    rsaPathsHook.actions.reset();
    setGroupSize(25);
    setShowClearPopup(false);
  };

  // Function to cancel clear operation
  const handleClearCancel = () => {
    setShowClearPopup(false);
  };

  // Map edits the active segment's waypoints.
  // Uses refs so stale closures (e.g. async geocoding callbacks) always see current state.
  const pathSetWaypoints = useCallback(
    (wpsOrUpdater) => {
      const entityType = activeRsaEntityTypeRef.current;
      const lane = activeRsaLaneRef.current;
      const path = activeRsaPathRef.current;

      if (entityType === "lane" && lane?.id) {
        const current = Array.isArray(lane.waypoints) ? lane.waypoints : [];
        const newWaypoints =
          typeof wpsOrUpdater === "function"
            ? wpsOrUpdater(current)
            : wpsOrUpdater;
        rsaPathsHook.actions.setLaneWaypoints(
          lane.id,
          Array.isArray(newWaypoints) ? newWaypoints : current,
        );
        return;
      }
      if (!path?.id) {
        const current = EMPTY_WAYPOINTS;
        const newWaypoints =
          typeof wpsOrUpdater === "function"
            ? wpsOrUpdater(current)
            : wpsOrUpdater;
        if (!Array.isArray(newWaypoints) || newWaypoints.length === 0) return;
        const createdPathId = rsaPathsHook.actions.addPath();
        rsaPathsHook.actions.setPathWaypoints(createdPathId, newWaypoints);
        return;
      }
      const current = Array.isArray(path.waypoints) ? path.waypoints : [];
      const newWaypoints =
        typeof wpsOrUpdater === "function"
          ? wpsOrUpdater(current)
          : wpsOrUpdater;
      rsaPathsHook.actions.setPathWaypoints(
        path.id,
        Array.isArray(newWaypoints) ? newWaypoints : current,
      );
    },
    [rsaPathsHook.actions],
  ); // refs are stable; only actions needed in deps

  const pathOnPathChange = useCallback(
    (data) => {
      const entityType = activeRsaEntityTypeRef.current;
      const lane = activeRsaLaneRef.current;
      const path = activeRsaPathRef.current;

      if (entityType === "lane" && lane?.id) {
        if (!data) {
          rsaPathsHook.actions.setLaneData(lane.id, null);
          return;
        }
        const prevProps =
          lane?.pathData?.properties &&
          typeof lane.pathData.properties === "object"
            ? lane.pathData.properties
            : {};
        const nextProps =
          data?.properties && typeof data.properties === "object"
            ? data.properties
            : {};
        rsaPathsHook.actions.setLaneData(lane.id, {
          ...data,
          properties: {
            ...prevProps,
            ...nextProps,
            generatedLane: true,
            ...(lane.creationMode === "manual" ? { manualLane: true } : {}),
            ...(lane.sourcePathId ? { sourcePathId: lane.sourcePathId } : {}),
            ...(lane.side ? { side: lane.side } : {}),
            ...(lane.offsetFeet != null ? { offsetFeet: lane.offsetFeet } : {}),
            ...(lane.directionMode
              ? { directionMode: lane.directionMode }
              : {}),
          },
        });
        return;
      }

      if (path?.id) {
        if (!data) {
          rsaPathsHook.actions.setPathData(path.id, null);
          return;
        }
        const prevProps =
          path?.pathData?.properties &&
          typeof path.pathData.properties === "object"
            ? path.pathData.properties
            : {};
        const nextProps =
          data?.properties && typeof data.properties === "object"
            ? data.properties
            : {};
        rsaPathsHook.actions.setPathData(path.id, {
          ...data,
          properties: {
            ...prevProps,
            ...nextProps,
          },
        });
      }
    },
    [rsaPathsHook.actions],
  ); // refs are stable; only actions needed in deps

  /**
   * createOffsetLaneForPath — new-model variant of createOffsetLane.
   * Accepts { sourcePathId, directionMode, side, offsetFeet } (RsaPath IDs, not array indices).
   * Computes the same WKT offset geometry then saves the result into rsaPathsHook.
   */
  const createOffsetLaneForPath = useCallback(
    async ({
      sourcePathId,
      directionMode,
      side,
      offsetFeet: offsetFeetRaw,
    }) => {
      // Source can be either a Path or a Lane (both identified by id).
      const sourcePath = rsaPathsHook.paths.find((p) => p.id === sourcePathId);
      const sourceLane = sourcePath
        ? null
        : rsaPathsHook.lanes.find((l) => l.id === sourcePathId);
      const sourceEntity = sourcePath || sourceLane;
      if (!sourceEntity) {
        showNotification("Selected source was not found.");
        return;
      }
      const sourceCoords =
        sourceEntity?.pathData?.geometry?.coordinates ??
        (Array.isArray(sourceEntity?.waypoints)
          ? sourceEntity.waypoints.map((wp) => [
              Number(wp?.lng),
              Number(wp?.lat),
            ])
          : null);
      if (!Array.isArray(sourceCoords) || sourceCoords.length < 2) {
        showNotification("Selected source has no line geometry.");
        return;
      }
      const wkt = normalizeWktInput(
        String(mapOverlayLocalConfig?.wkt ?? "").trim(),
      );
      if (!wkt) {
        showNotification(
          "Enter WKT CRS in Setup -> Local (x, y + WKT) before creating lane offsets.",
        );
        return;
      }
      const parsedOffsetFeet = Number(offsetFeetRaw);
      if (!Number.isFinite(parsedOffsetFeet) || parsedOffsetFeet < 0) {
        showNotification("Offset must be zero or greater in feet.");
        return;
      }
      const baseLatLng = sourceCoords
        .map((c) => {
          const lng = Array.isArray(c) ? Number(c[0]) : Number(c?.lng);
          const lat = Array.isArray(c) ? Number(c[1]) : Number(c?.lat);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          return { lat, lng };
        })
        .filter(Boolean);
      if (baseLatLng.length < 2) {
        showNotification("Source path coordinates are invalid.");
        return;
      }

      const orderedLatLng =
        directionMode === "reverse" ? [...baseLatLng].reverse() : baseLatLng;
      const isCopyZeroOffset = parsedOffsetFeet === 0;

      // True copy (offset=0): bypass projection entirely so the lane coordinates
      // exactly match the source. The lat/lng → projected → offset(0) → lat/lng
      // round-trip drifts by tiny float-precision amounts and the result no
      // longer aligns perfectly with the source on the map.
      let offsetLatLng;
      if (isCopyZeroOffset) {
        offsetLatLng = orderedLatLng.map((p) => ({ lat: p.lat, lng: p.lng }));
      } else {
        const proj4FromValidate = wktValidation?.crs?.proj4;
        const projected = await latLngToProjectedXYAsync(
          orderedLatLng,
          wkt,
          0,
          proj4FromValidate,
        );
        if (!Array.isArray(projected) || projected.length < 2) {
          showNotification("Failed to project source path for lane offset.");
          return;
        }

        const projectedXY = projected
          .map((p) => [Number(p?.[0]), Number(p?.[1])])
          .filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y));
        if (projectedXY.length < 2) {
          showNotification("Projected source path is invalid.");
          return;
        }

        const unitsPerFoot = getProjectedUnitsPerFoot({
          wkt,
          proj4String: proj4FromValidate,
        });
        const absOffsetUnits = Math.abs(parsedOffsetFeet) * unitsPerFoot;
        const signedOffsetUnits = (side === "right" ? -1 : 1) * absOffsetUnits;
        const offsetXY = offsetPolylineXY(projectedXY, signedOffsetUnits);
        if (offsetXY.length < 2) {
          showNotification("Could not compute offset lane geometry.");
          return;
        }

        offsetLatLng = await projectedPointsToLatLngAsync(
          offsetXY,
          wkt,
          proj4FromValidate,
        );
        if (!Array.isArray(offsetLatLng) || offsetLatLng.length < 2) {
          showNotification(
            "Could not transform offset lane back to map coordinates.",
          );
          return;
        }
      }

      // For a true copy (offset=0) give the lane editable nodes that track the
      // source: a lane's own nodes verbatim, or on-curve samples of a path's
      // geometry (never the path's sparse Google click-points, which sit off the
      // snapped route). The centripetal spline through these nodes follows the
      // source without twisting, and the lane stays reshapeable in edit mode.
      // Offset (non-copy) lanes keep the plain adaptive sampling.
      const sampledWaypoints = isCopyZeroOffset
        ? resolveCopiedLaneNodes({
            isLaneSource: !!sourceLane,
            sourceWaypoints: sourceEntity?.waypoints,
            geometryLatLng: offsetLatLng,
            reverse: directionMode === "reverse",
          })
        : sampleLaneWaypoints(offsetLatLng);
      const sideLabel = isCopyZeroOffset
        ? normalizeLaneXmlName(`Copy of ${sourceEntity.label ?? "Lane"}`)
        : side === "right"
          ? normalizeLaneXmlName("Right Shoulder")
          : normalizeLaneXmlName("Left Shoulder");
      const parentPathId = sourcePath?.id ?? sourceLane?.pathId;
      if (!parentPathId) {
        showNotification("Could not resolve parent route for the new lane.");
        return;
      }
      rsaPathsHook.actions.addLane(parentPathId, {
        laneType: isCopyZeroOffset
          ? "custom"
          : side === "right"
            ? "right-shoulder"
            : "left-shoulder",
        label: sideLabel,
        waypoints: sampledWaypoints,
        pathData: {
          type: "Feature",
          geometry: {
            type: "LineString",
            // Store the geometry the renderer will actually draw (the centripetal
            // spline through the nodes) so the persistent overlay and the active
            // line coincide — otherwise they bow apart into a "double line" until
            // an edit reconciles them. <=2 nodes fall back to the exact geometry
            // (useBaseline draws pathData verbatim).
            coordinates: generatedLaneGeometryCoords(
              sampledWaypoints,
              offsetLatLng,
            ),
          },
          properties: {
            generatedLane: true,
            sourcePathId,
            side,
            offsetFeet: parsedOffsetFeet,
            directionMode,
          },
        },
        creationMode: "offset",
        sourcePathId,
        side,
        offsetFeet: parsedOffsetFeet,
        directionMode,
      });
    },
    [
      rsaPathsHook.paths,
      rsaPathsHook.lanes,
      rsaPathsHook.actions,
      mapOverlayLocalConfig?.wkt,
      wktValidation?.crs?.proj4,
      showNotification,
    ],
  );

  const startManualLaneCreation = useCallback(
    (pathIdFromUi) => {
      const targetPathId =
        pathIdFromUi || activeRsaPath?.id || rsaPathsHook.paths[0]?.id || null;
      // Only short-circuit when placement is *currently* active — otherwise a
      // stale activeManualLaneId from a previously-finished lane would cause
      // Start Manual Lane to re-engage Continue mode on that old lane instead
      // of creating a fresh one.
      const existingManualLane =
        manualLaneCreationActive && activeManualLaneId
          ? rsaPathsHook.lanes.find(
              (lane) =>
                lane.id === activeManualLaneId &&
                lane.creationMode === "manual" &&
                lane.pathId === targetPathId,
            )
          : null;

      if (existingManualLane) {
        rsaPathsHook.actions.setActiveLane(existingManualLane.id);
        return;
      }

      if (!targetPathId) {
        showNotification(
          "Add and select a path before starting a manual lane.",
        );
        return;
      }

      const createdLaneId = rsaPathsHook.actions.addLane(targetPathId, {
        laneType: "custom",
        label: normalizeLaneXmlName(
          `Manual Lane ${(rsaPathsHook.lanes.length || 0) + 1}`,
        ),
        creationMode: "manual",
        sourcePathId: targetPathId,
        waypoints: [],
        pathData: null,
      });
      setActiveManualLaneId(createdLaneId);
      setManualLaneCreationActive(true);
    },
    [
      activeManualLaneId,
      activeRsaPath,
      manualLaneCreationActive,
      rsaPathsHook.actions,
      rsaPathsHook.lanes,
      rsaPathsHook.paths,
    ],
  );

  const stopManualLaneCreation = useCallback(() => {
    setManualLaneCreationActive(false);
  }, []);

  const resumeManualLane = useCallback(
    (laneId) => {
      const lane = rsaPathsHook.lanes.find((l) => l.id === laneId);
      if (!lane) return;
      // Promote offset/copy lanes to manual so the next map click extends from
      // the last existing waypoint instead of being ignored.
      if (lane.creationMode !== "manual") {
        rsaPathsHook.actions.updateLane(laneId, { creationMode: "manual" });
      }
      rsaPathsHook.actions.setActiveLane(laneId);
      setActiveManualLaneId(laneId);
      setManualLaneCreationActive(true);
    },
    [rsaPathsHook.lanes, rsaPathsHook.actions],
  );

  useEffect(() => {
    if (activeManualLaneId == null) return;
    const lane = rsaPathsHook.lanes.find(
      (entry) => entry.id === activeManualLaneId,
    );
    if (lane?.creationMode === "manual") return;
    setManualLaneCreationActive(false);
    setActiveManualLaneId(null);
  }, [rsaPathsHook.lanes, activeManualLaneId]);

  useEffect(() => {
    if (pathsPanelMode === "path-creator") return;
    if (!manualLaneCreationActive) return;
    setManualLaneCreationActive(false);
    setActiveManualLaneId(null);
  }, [pathsPanelMode, manualLaneCreationActive]);

  // Manual-mode resilience: useRsaPaths state can be rehydrated mid-placement
  // (IndexedDB cache restore, 409-triggered reload, etc.) which sets
  // `activeLaneId: null` and `activeEntityType: 'path'` in the HYDRATE
  // reducer. After that, pathSetWaypoints would route map clicks to the
  // parent path instead of the lane — the user perceives this as "manual
  // mode exited after a few seconds". Re-pin the active lane whenever it
  // drifts away from our manual lane id, as long as the lane still exists.
  useEffect(() => {
    if (!manualLaneCreationActive) return;
    if (activeManualLaneId == null) return;
    if (rsaPathsHook.activeLaneId === activeManualLaneId) return;
    const laneStillExists = rsaPathsHook.lanes.some(
      (l) => l.id === activeManualLaneId && l.creationMode === "manual",
    );
    if (laneStillExists) {
      rsaPathsHook.actions.setActiveLane(activeManualLaneId);
    }
  }, [
    manualLaneCreationActive,
    activeManualLaneId,
    rsaPathsHook.activeLaneId,
    rsaPathsHook.lanes,
    rsaPathsHook.actions,
  ]);

  const fullPathPathData = null;
  const isGeneratedLaneActive =
    activeRsaEntityType === "lane" && Boolean(activeRsaLane);
  const isManualLaneEditingActive =
    manualLaneCreationActive &&
    activeManualLaneId != null &&
    activeRsaLane?.id === activeManualLaneId &&
    activeRsaLane?.creationMode === "manual";
  const activeManualLanePointCount = useMemo(() => {
    if (activeManualLaneId == null) return 0;
    const lane = rsaPathsHook.lanes.find(
      (entry) => entry.id === activeManualLaneId,
    );
    const points = lane?.waypoints;
    return Array.isArray(points) ? points.length : 0;
  }, [rsaPathsHook.lanes, activeManualLaneId]);
  const persistentLaneOverlays = useMemo(() => {
    // Always render ALL paths and lanes (including the active one). The
    // active entity ALSO renders as activeSegmentPathData with its own
    // waypoint/edit styling on top — but the underlying persistent line
    // stays drawn so the parent Route doesn't disappear when a child lane
    // is activated. Visibility is controlled solely by the eye-button —
    // both in the Path Creator panel and in the Intersection picker.
    return rsaLegacySegments
      .map((seg, idx) => ({ seg, idx }))
      .filter(({ seg }) => {
        if (seg?.visible === false) return false;
        const hasPathData =
          Array.isArray(seg?.pathData?.geometry?.coordinates) &&
          seg.pathData.geometry.coordinates.length >= 2;
        const hasWaypoints =
          Array.isArray(seg?.waypoints) && seg.waypoints.length >= 2;
        return hasPathData || hasWaypoints;
      })
      .map(({ seg, idx }) => {
        const hasPathData =
          Array.isArray(seg?.pathData?.geometry?.coordinates) &&
          seg.pathData.geometry.coordinates.length >= 2;
        const pathData = hasPathData
          ? seg.pathData
          : {
              type: "Feature",
              geometry: {
                type: "LineString",
                coordinates: seg.waypoints.map((w) => [w.lng, w.lat]),
              },
            };
        return {
          pathData,
          color:
            SEGMENT_COLORS[(seg.colorIndex ?? idx) % SEGMENT_COLORS.length],
        };
      });
  }, [rsaLegacySegments, activeRsaLegacyIndex]);

  // Geometry of the path/lane the user selected in the Intersection picker,
  // surfaced to PathSelectorMap as a highlight polyline overlay on top of
  // the normal path rendering.
  const selectedRsaHighlightPathData = useMemo(() => {
    if (pathsPanelMode !== "intersection") return null;
    if (!selectedRsaTargetId) return null;
    const allTargets = [
      ...(rsaPathsHook?.paths ?? []),
      ...(rsaPathsHook?.lanes ?? []),
    ];
    const target = allTargets.find((t) => t.id === selectedRsaTargetId);
    const coords = target?.pathData?.geometry?.coordinates;
    if (Array.isArray(coords) && coords.length >= 2) return target.pathData;
    // Fallback: synthesize pathData from waypoints if pathData isn't set yet.
    const wpts = target?.waypoints;
    if (Array.isArray(wpts) && wpts.length >= 2) {
      return {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: wpts.map((w) => [w.lng, w.lat]),
        },
      };
    }
    return null;
  }, [
    pathsPanelMode,
    selectedRsaTargetId,
    rsaPathsHook?.paths,
    rsaPathsHook?.lanes,
  ]);
  const latestRoutesUploadTime = useMemo(() => {
    const latest = (routesSourceFiles || []).reduce((best, item) => {
      const stamp = item?.modified || item?.created || "";
      if (!stamp) return best;
      if (!best) return stamp;
      return new Date(stamp).getTime() > new Date(best).getTime()
        ? stamp
        : best;
    }, "");
    return latest ? formatUploadTimestamp(latest) : "No uploads yet";
  }, [routesSourceFiles]);
  const latestModelUploadTime = useMemo(() => {
    const latest = (modelFiles || []).reduce((best, item) => {
      const stamp = item?.modified || item?.created || "";
      if (!stamp) return best;
      if (!best) return stamp;
      return new Date(stamp).getTime() > new Date(best).getTime()
        ? stamp
        : best;
    }, "");
    return latest ? formatUploadTimestamp(latest) : "No uploads yet";
  }, [modelFiles]);
  const latestExistingSourceUploadTime = useMemo(() => {
    const latest = (existingSourceFiles || []).reduce((best, item) => {
      const stamp = item?.modified || item?.created || "";
      if (!stamp) return best;
      if (!best) return stamp;
      return new Date(stamp).getTime() > new Date(best).getTime()
        ? stamp
        : best;
    }, "");
    return latest ? formatUploadTimestamp(latest) : "No uploads yet";
  }, [existingSourceFiles]);
  const hasExistingRoutesReady = useMemo(
    () =>
      rsaLegacySegments.some(
        (segment) =>
          (Array.isArray(segment?.pathData?.geometry?.coordinates) &&
            segment.pathData.geometry.coordinates.length >= 2) ||
          (Array.isArray(segment?.waypoints) && segment.waypoints.length >= 2),
      ),
    [rsaLegacySegments],
  );
  const canProceedToSetup = isRsaModelProject
    ? routesXmlFiles.length > 0 && modelFiles.length > 0
    : existingSourceFiles.length > 0;
  const existingStepComplete = useMemo(
    () => ({
      upload: existingSourceFiles.length > 0 || routesXmlFiles.length > 0,
      setup:
        hasVisitedSetupTab &&
        wktInputLocked &&
        Boolean(String(mapOverlayLocalConfig?.wkt ?? "").trim()),
      paths: hasExistingRoutesReady,
      analysis: hasSubmittedRsaRun,
    }),
    [
      existingSourceFiles.length,
      routesXmlFiles.length,
      hasExistingRoutesReady,
      hasSubmittedRsaRun,
      hasVisitedSetupTab,
      mapOverlayLocalConfig?.wkt,
      wktInputLocked,
    ],
  );

  return (
    <RsaPathsContext.Provider value={{ ...rsaPathsHook, SEGMENT_COLORS }}>
      <div className="w100RSA">
        {/* Slide-down notification from top */}
        {notification.message && (
          <div
            className={`rsa-slide-notification ${notification.leaving ? "rsa-slide-notification-leaving" : ""}`}
            role="alert"
          >
            <img
              src={rdvShadowImage}
              alt="Notification icon"
              className="rsa-slide-notification-icon"
            />
            <span className="rsa-slide-notification-message">
              {notification.message}
            </span>
          </div>
        )}
        <div
          className={`main-content-area ${activeMainTab === "dashboard" ? "main-content-area-full-width" : ""}`}
        >
          {/* Non-developers on a gated tab: useEffect above switches to Analysis - no Navigate to /projects */}
          {/* Navigation: wizard stepper for RSA model, classic tabs for other project types */}
          {isRsaModelProject ? (
            (() => {
              const wizardSteps = [
                {
                  key: "upload",
                  label: "Upload Files",
                  hint: "Add your route XML files",
                  iconClass: "fa-solid fa-arrow-up-from-bracket",
                },
                {
                  key: "setup",
                  label: "Setup",
                  hint: "Set coordinate system (WKT)",
                  iconClass: "fa-solid fa-gear",
                },
                {
                  key: "routes",
                  label: "Tasks & Routes",
                  hint: "Select analyses and assign files",
                  iconClass: "fa-solid fa-list-check",
                },
                {
                  key: "dashboard",
                  label: "Dashboard",
                  hint: "Run analysis and view results",
                  iconClass: "fa-solid fa-chart-pie",
                },
                {
                  key: "deliverables",
                  label: "Deliverables",
                  hint: "Browse generated output files",
                },
              ];
              const stepComplete = {
                upload: routesXmlFiles.length > 0,
                setup:
                  hasVisitedSetupTab &&
                  wktInputLocked &&
                  Boolean(mapOverlayLocalConfig?.wkt),
                routes: hasSubmittedRsaRun,
                dashboard: false,
              };
              return (
                <div className="rsa-wizard-stepper">
                  {wizardSteps.map((step, idx) => {
                    const isActive = activeMainTab === step.key;
                    const isDone = stepComplete[step.key];
                    return (
                      <React.Fragment key={step.key}>
                        <button
                          type="button"
                          className={`rsa-wizard-step${isActive ? " rsa-wizard-step-active" : ""}${isDone ? " rsa-wizard-step-done" : ""}${idx === 0 ? " rsa-wizard-step-first" : ""}${idx === wizardSteps.length - 1 ? " rsa-wizard-step-last" : ""}${step.key === "dashboard" ? " rsa-wizard-step-dashboard" : ""}${step.key === "deliverables" ? " rsa-wizard-step-deliverables" : ""}`}
                          onClick={() => setActiveMainTab(step.key)}
                          title={step.hint}
                        >
                          <span className="rsa-wizard-step-label">
                            {step.iconClass && (
                              <i
                                className={`rsa-wizard-step-icon rsa-wizard-step-icon-step ${step.iconClass}`}
                                aria-hidden
                              />
                            )}
                            {step.key === "deliverables" && (
                              <i className="fa-solid fa-cube" aria-hidden />
                            )}
                            {step.label}
                          </span>
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
              );
            })()
          ) : (
            <div className="main-content-tabs">
              {/* Other project types: Upload - Setup - Paths - Analysis - Dashboard */}
              <button
                type="button"
                className={`main-content-tab main-content-tab-upload ${activeMainTab === "upload" ? "active" : ""}${existingStepComplete.upload ? " main-content-tab-done" : ""}`}
                onClick={() => setActiveMainTab("upload")}
              >
                <i className="fa-solid fa-arrow-up-from-bracket" />
                <span>Upload Files</span>
              </button>
              <button
                type="button"
                className={`main-content-tab main-content-tab-setup ${activeMainTab === "setup" ? "active" : ""}${existingStepComplete.setup ? " main-content-tab-done" : ""}`}
                onClick={() => setActiveMainTab("setup")}
              >
                <i className="fa-solid fa-gear" />
                <span>Setup</span>
              </button>
              <button
                type="button"
                className={`main-content-tab main-content-tab-paths ${activeMainTab === "paths" ? "active" : ""}${existingStepComplete.paths ? " main-content-tab-done" : ""}`}
                onClick={() => setActiveMainTab("paths")}
              >
                <i className="fa-solid fa-map-location-dot" />
                <span>Routes</span>
              </button>
              <button
                type="button"
                className={`main-content-tab main-content-tab-analysis ${activeMainTab === "analysis" ? "active" : ""}${existingStepComplete.analysis ? " main-content-tab-done" : ""}`}
                onClick={() => setActiveMainTab("analysis")}
              >
                <i className="fas fa-chart-line" />
                <span>Analysis</span>
              </button>
              <button
                type="button"
                className={`main-content-tab main-content-tab-dashboard ${activeMainTab === "dashboard" ? "active" : ""}`}
                onClick={() => setActiveMainTab("dashboard")}
              >
                <i className="fas fa-chart-pie" />
                <span>Dashboard</span>
              </button>
              <button
                type="button"
                className={`main-content-tab main-content-tab-deliverables ${activeMainTab === "deliverables" ? "active" : ""}`}
                onClick={() => setActiveMainTab("deliverables")}
              >
                <i className="fa-solid fa-cube" />
                <span>Deliverables</span>
              </button>
            </div>
          )}

          {/* Tab Content Container */}
          <div className="main-content-tabs-container">
            {/* ── Upload Files tab panel ──────────────────── */}
            <div
              className={`main-content-tab-panel main-content-tab-panel-upload rsa-wizard-step-page${activeMainTab === "upload" ? " active" : ""}`}
            >
              <div className="rsa-upload-step-panel rsa-wizard-step-content">
                <div className="rsa-upload-step-body">
                  {isRsaModelProject ? (
                    <>
                      <div className="rsa-upload-step-section">
                        <div className="rsa-upload-step-status rsa-upload-step-status-toggle">
                          <div className="rsa-upload-step-status-copy">
                            <div className="rsa-upload-step-status-copy-header">
                              <p className="rsa-upload-step-heading">
                                Upload your route files
                              </p>
                            </div>
                            <p className="rsa-upload-step-hint">
                              Upload DXF files into the project's{" "}
                              <strong>Source / Routes</strong> folder.
                            </p>
                          </div>
                          <span className="rsa-upload-step-status-row">
                            <span className="rsa-upload-step-status-summary">
                              {noStorageConfigured ? (
                                <>
                                  <i
                                    className="fas fa-circle-exclamation rsa-upload-step-status-icon empty"
                                    aria-hidden
                                  />
                                  <span className="rsa-upload-step-status-text">
                                    no storage found for this project
                                  </span>
                                </>
                              ) : routesMatrixLoading &&
                                routesSourceFiles.length === 0 ? (
                                <>
                                  <i
                                    className="fas fa-spinner fa-spin rsa-upload-step-status-icon"
                                    aria-hidden
                                  />
                                  <span className="rsa-upload-step-status-text">
                                    Checking Source / Routes…
                                  </span>
                                </>
                              ) : routesSourceFiles.length > 0 ? (
                                <>
                                  <i
                                    className="fas fa-circle-check rsa-upload-step-status-icon ready"
                                    aria-hidden
                                  />
                                  <span className="rsa-upload-step-status-text">
                                    {routesXmlFiles.length} Route Files,{" "}
                                    {
                                      routesSourceFiles.filter((file) =>
                                        isDxfFileName(file.name),
                                      ).length
                                    }{" "}
                                    DXF
                                    {routesMatrixLoading && (
                                      <i
                                        className="fas fa-rotate fa-spin rsa-upload-step-status-icon refreshing"
                                        title="Refreshing…"
                                        aria-hidden
                                      />
                                    )}
                                    <span className="rsa-upload-step-status-subtext">
                                      Latest upload: {latestRoutesUploadTime}
                                    </span>
                                  </span>
                                  <span className="rsa-upload-step-status-count">
                                    {routesSourceFiles.length} file
                                    {routesSourceFiles.length !== 1 ? "s" : ""}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-triangle-exclamation rsa-upload-step-status-icon empty"
                                    aria-hidden
                                  />
                                  <span className="rsa-upload-step-status-text">
                                    No files found in Source / Routes yet
                                    <span className="rsa-upload-step-status-subtext">
                                      Latest upload: {latestRoutesUploadTime}
                                    </span>
                                  </span>
                                </>
                              )}
                            </span>
                            <span className="rsa-upload-step-status-actions">
                              <span
                                className="rsa-wizard-nav-btn rsa-wizard-nav-btn-primary rsa-upload-step-status-action"
                                role="button"
                                tabIndex={-1}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenRoutesUploadModal();
                                }}
                              >
                                <i className="fas fa-upload" aria-hidden />{" "}
                                Upload DXF / RSA Route Files
                              </span>
                            </span>
                          </span>
                        </div>

                        {routesSourceFiles.length > 0 && (
                          <div className="rsa-upload-step-files-table-wrap">
                            <div className="rsa-upload-step-files-table-toolbar">
                              <div className="rsa-upload-step-files-table-toolbar-meta">
                                <span className="rsa-upload-step-files-table-toolbar-title">
                                  Source / Routes files
                                </span>
                                <span className="rsa-upload-step-files-table-toolbar-subtext">
                                  {selectedRouteUploadFiles.length > 0
                                    ? `${selectedRouteUploadFiles.length} selected`
                                    : "Click rows to select multiple files"}
                                </span>
                              </div>
                              <div className="rsa-upload-step-files-table-toolbar-actions">
                                {selectedRouteUploadFiles.length >= 2 && (
                                  <button
                                    type="button"
                                    className="rsa-upload-step-files-toolbar-btn rsa-upload-step-files-toolbar-btn-danger"
                                    onClick={() =>
                                      handleRequestUploadFileDelete(
                                        selectedRouteUploadFiles,
                                        routesFolderId,
                                        "Source / Routes",
                                      )
                                    }
                                    disabled={uploadDeleteBusy}
                                  >
                                    Remove selected
                                    <i className="fas fa-trash" aria-hidden />
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="rsa-upload-step-files-table-scroll">
                              <table className="rsa-upload-step-files-table">
                                <thead>
                                  <tr>
                                    <th className="rsa-upload-step-files-select-col">
                                      <button
                                        type="button"
                                        className={`rsa-upload-step-files-select-toggle${
                                          selectedRouteUploadFiles.length ===
                                          routesSourceFiles.length
                                            ? " is-selected"
                                            : ""
                                        }`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleToggleAllUploadTableFiles(
                                            "routes",
                                            routesSourceFiles,
                                          );
                                        }}
                                        disabled={uploadDeleteBusy}
                                        aria-label={
                                          selectedRouteUploadFiles.length ===
                                          routesSourceFiles.length
                                            ? "Clear all selected route files"
                                            : "Select all route files"
                                        }
                                      >
                                        <i
                                          className="fas fa-check"
                                          aria-hidden
                                        />
                                      </button>
                                    </th>
                                    <th className="rsa-upload-step-files-index-col">
                                      #
                                    </th>
                                    <th>File</th>
                                    <th className="rsa-upload-step-files-type-col">
                                      Type
                                    </th>
                                    <th className="rsa-upload-step-files-state-col">
                                      State
                                    </th>
                                    <th className="rsa-upload-step-files-time-col">
                                      Uploaded
                                    </th>
                                    <th className="rsa-upload-step-files-action-col">
                                      Remove
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {routesSourceFiles.map((routeFile, index) => {
                                    const routeFileId = normalizeDriveFolderId(
                                      routeFile.googleDriveId,
                                    );
                                    const isSelected = (
                                      uploadTableSelection.routes || []
                                    ).includes(routeFileId);
                                    const routeFileState = isXmlFileName(
                                      routeFile?.name,
                                    )
                                      ? routesStateByDriveId[routeFileId] ||
                                        "..."
                                      : "-";
                                    return (
                                      <tr
                                        key={
                                          routeFile.googleDriveId ||
                                          routeFile.id ||
                                          routeFile.name ||
                                          index
                                        }
                                        className={`rsa-upload-step-files-row${isSelected ? " is-selected" : ""}`}
                                        onClick={() =>
                                          toggleUploadTableFileSelection(
                                            "routes",
                                            routeFile,
                                          )
                                        }
                                        aria-selected={isSelected}
                                      >
                                        <td className="rsa-upload-step-files-select-cell">
                                          <button
                                            type="button"
                                            className={`rsa-upload-step-files-select-toggle${isSelected ? " is-selected" : ""}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleUploadTableFileSelection(
                                                "routes",
                                                routeFile,
                                              );
                                            }}
                                            disabled={uploadDeleteBusy}
                                            aria-label={
                                              isSelected
                                                ? `Deselect ${routeFile.name}`
                                                : `Select ${routeFile.name}`
                                            }
                                          >
                                            <i
                                              className="fas fa-check"
                                              aria-hidden
                                            />
                                          </button>
                                        </td>
                                        <td className="rsa-upload-step-files-index-cell">
                                          {index + 1}
                                        </td>
                                        <td
                                          className="rsa-upload-step-files-name-cell"
                                          title={routeFile.name}
                                        >
                                          {getDisplayFileName(routeFile.name)}
                                        </td>
                                        <td>
                                          {isDxfFileName(routeFile.name)
                                            ? "DXF"
                                            : "XML"}
                                        </td>
                                        <td
                                          className="rsa-upload-step-files-state-cell"
                                          title={routeFileState}
                                        >
                                          {routeFileState}
                                        </td>
                                        <td className="rsa-upload-step-files-time-cell">
                                          {formatUploadTimestamp(
                                            routeFile.modified ||
                                              routeFile.created,
                                          )}
                                        </td>
                                        <td className="rsa-upload-step-files-action-cell">
                                          <button
                                            type="button"
                                            className="rsa-upload-step-files-delete-btn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRequestUploadFileDelete(
                                                routeFile,
                                                routesFolderId,
                                                "Source / Routes",
                                              );
                                            }}
                                            disabled={uploadDeleteBusy}
                                            title={`Delete ${routeFile.name}`}
                                            aria-label={`Delete ${routeFile.name}`}
                                          >
                                            <i
                                              className={`fas ${
                                                uploadFileDeletePendingIds.includes(
                                                  routeFileId,
                                                )
                                                  ? "fa-spinner fa-spin"
                                                  : "fa-trash"
                                              }`}
                                              aria-hidden
                                            />
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>

                      <div
                        className="rsa-upload-step-section"
                        style={{ order: 1 }}
                      >
                        <div className="rsa-upload-step-status rsa-upload-step-status-toggle">
                          <div className="rsa-upload-step-status-copy">
                            <div className="rsa-upload-step-status-copy-header">
                              <p className="rsa-upload-step-heading">
                                Upload your model file
                              </p>
                            </div>
                            <p className="rsa-upload-step-hint">
                              Upload the Model file into{" "}
                              <strong>Source / Model</strong>.
                            </p>
                          </div>
                          <span className="rsa-upload-step-status-row">
                            <span className="rsa-upload-step-status-summary">
                              {noStorageConfigured ? (
                                <>
                                  <i
                                    className="fas fa-circle-exclamation rsa-upload-step-status-icon empty"
                                    aria-hidden
                                  />
                                  <span className="rsa-upload-step-status-text">
                                    no storage found for this project
                                  </span>
                                </>
                              ) : routesMatrixLoading &&
                                modelFiles.length === 0 ? (
                                <>
                                  <i
                                    className="fas fa-spinner fa-spin rsa-upload-step-status-icon"
                                    aria-hidden
                                  />
                                  <span className="rsa-upload-step-status-text">
                                    Checking Source / Model…
                                  </span>
                                </>
                              ) : modelFolderId && modelFiles.length > 0 ? (
                                <>
                                  <i
                                    className="fas fa-circle-check rsa-upload-step-status-icon ready"
                                    aria-hidden
                                  />
                                  <span className="rsa-upload-step-status-text">
                                    Model files detected
                                    {routesMatrixLoading && (
                                      <i
                                        className="fas fa-rotate fa-spin rsa-upload-step-status-icon refreshing"
                                        title="Refreshing…"
                                        aria-hidden
                                      />
                                    )}
                                    <span className="rsa-upload-step-status-subtext">
                                      Latest upload: {latestModelUploadTime}
                                    </span>
                                  </span>
                                  <span className="rsa-upload-step-status-count">
                                    {modelFiles.length} file
                                    {modelFiles.length !== 1 ? "s" : ""}
                                  </span>
                                </>
                              ) : modelFolderId ? (
                                <>
                                  <i
                                    className="fas fa-triangle-exclamation rsa-upload-step-status-icon empty"
                                    aria-hidden
                                  />
                                  <span className="rsa-upload-step-status-text">
                                    No files found in Source / Model yet
                                    <span className="rsa-upload-step-status-subtext">
                                      Latest upload: {latestModelUploadTime}
                                    </span>
                                  </span>
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-triangle-exclamation rsa-upload-step-status-icon empty"
                                    aria-hidden
                                  />
                                  <span className="rsa-upload-step-status-text">
                                    Source / Model folder was not found
                                  </span>
                                </>
                              )}
                            </span>
                            <span className="rsa-upload-step-status-actions">
                              <span
                                className="rsa-wizard-nav-btn rsa-wizard-nav-btn-primary rsa-upload-step-status-action"
                                role="button"
                                tabIndex={-1}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (modelFolderId) {
                                    handleOpenModelUploadModal();
                                  }
                                }}
                                title={
                                  modelFolderId
                                    ? "Upload model files into Source / Model"
                                    : "Source / Model folder was not found"
                                }
                              >
                                <i className="fas fa-upload" aria-hidden />{" "}
                                Upload Model File
                              </span>
                            </span>
                          </span>
                        </div>

                        {modelFiles.length > 0 && (
                          <div className="rsa-upload-step-files-table-wrap">
                            <div className="rsa-upload-step-files-table-toolbar">
                              <div className="rsa-upload-step-files-table-toolbar-meta">
                                <span className="rsa-upload-step-files-table-toolbar-title">
                                  Source / Model files
                                </span>
                                <span className="rsa-upload-step-files-table-toolbar-subtext">
                                  {selectedModelUploadFiles.length > 0
                                    ? `${selectedModelUploadFiles.length} selected`
                                    : "Click rows to select multiple files"}
                                </span>
                              </div>
                              <div className="rsa-upload-step-files-table-toolbar-actions">
                                <button
                                  type="button"
                                  className="rsa-upload-step-files-toolbar-btn"
                                  onClick={() =>
                                    handleToggleAllUploadTableFiles(
                                      "model",
                                      modelFiles,
                                    )
                                  }
                                  disabled={uploadDeleteBusy}
                                >
                                  {selectedModelUploadFiles.length ===
                                  modelFiles.length
                                    ? "Clear all"
                                    : "Select all"}
                                </button>
                                {selectedModelUploadFiles.length >= 2 && (
                                  <button
                                    type="button"
                                    className="rsa-upload-step-files-toolbar-btn rsa-upload-step-files-toolbar-btn-danger"
                                    onClick={() =>
                                      handleRequestUploadFileDelete(
                                        selectedModelUploadFiles,
                                        modelFolderId,
                                        "Source / Model",
                                      )
                                    }
                                    disabled={uploadDeleteBusy}
                                  >
                                    <i className="fas fa-trash" aria-hidden />{" "}
                                    Remove selected
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="rsa-upload-step-files-table-scroll">
                              <table className="rsa-upload-step-files-table">
                                <thead>
                                  <tr>
                                    <th className="rsa-upload-step-files-select-col">
                                      <button
                                        type="button"
                                        className={`rsa-upload-step-files-select-toggle${
                                          selectedModelUploadFiles.length ===
                                          modelFiles.length
                                            ? " is-selected"
                                            : ""
                                        }`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleToggleAllUploadTableFiles(
                                            "model",
                                            modelFiles,
                                          );
                                        }}
                                        disabled={uploadDeleteBusy}
                                        aria-label={
                                          selectedModelUploadFiles.length ===
                                          modelFiles.length
                                            ? "Clear all selected model files"
                                            : "Select all model files"
                                        }
                                      >
                                        <i
                                          className="fas fa-check"
                                          aria-hidden
                                        />
                                      </button>
                                    </th>
                                    <th className="rsa-upload-step-files-index-col">
                                      #
                                    </th>
                                    <th>Model file</th>
                                    <th className="rsa-upload-step-files-time-col">
                                      Uploaded
                                    </th>
                                    <th className="rsa-upload-step-files-action-col">
                                      Remove
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {modelFiles.map((modelFile, index) => {
                                    const modelFileId = normalizeDriveFolderId(
                                      modelFile.googleDriveId,
                                    );
                                    const isSelected = (
                                      uploadTableSelection.model || []
                                    ).includes(modelFileId);
                                    return (
                                      <tr
                                        key={
                                          modelFile.googleDriveId ||
                                          modelFile.id ||
                                          modelFile.name ||
                                          index
                                        }
                                        className={`rsa-upload-step-files-row${isSelected ? " is-selected" : ""}`}
                                        onClick={() =>
                                          toggleUploadTableFileSelection(
                                            "model",
                                            modelFile,
                                          )
                                        }
                                        aria-selected={isSelected}
                                      >
                                        <td className="rsa-upload-step-files-select-cell">
                                          <button
                                            type="button"
                                            className={`rsa-upload-step-files-select-toggle${isSelected ? " is-selected" : ""}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleUploadTableFileSelection(
                                                "model",
                                                modelFile,
                                              );
                                            }}
                                            disabled={uploadDeleteBusy}
                                            aria-label={
                                              isSelected
                                                ? `Deselect ${modelFile.name}`
                                                : `Select ${modelFile.name}`
                                            }
                                          >
                                            <i
                                              className="fas fa-check"
                                              aria-hidden
                                            />
                                          </button>
                                        </td>
                                        <td className="rsa-upload-step-files-index-cell">
                                          {index + 1}
                                        </td>
                                        <td
                                          className="rsa-upload-step-files-name-cell"
                                          title={modelFile.name}
                                        >
                                          {getDisplayFileName(modelFile.name)}
                                        </td>
                                        <td className="rsa-upload-step-files-time-cell">
                                          {formatUploadTimestamp(
                                            modelFile.modified ||
                                              modelFile.created,
                                          )}
                                        </td>
                                        <td className="rsa-upload-step-files-action-cell">
                                          <button
                                            type="button"
                                            className="rsa-upload-step-files-delete-btn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRequestUploadFileDelete(
                                                modelFile,
                                                modelFolderId,
                                                "Source / Model",
                                              );
                                            }}
                                            disabled={uploadDeleteBusy}
                                            title={`Delete ${modelFile.name}`}
                                            aria-label={`Delete ${modelFile.name}`}
                                          >
                                            <i
                                              className={`fas ${
                                                uploadFileDeletePendingIds.includes(
                                                  modelFileId,
                                                )
                                                  ? "fa-spinner fa-spin"
                                                  : "fa-trash"
                                              }`}
                                              aria-hidden
                                            />
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="rsa-upload-step-section">
                      <div className="rsa-upload-step-status rsa-upload-step-status-toggle">
                        <div className="rsa-upload-step-status-copy">
                          <div className="rsa-upload-step-status-copy-header">
                            <p className="rsa-upload-step-heading">
                              Upload your LAS/LAZ files
                            </p>
                          </div>
                          <p className="rsa-upload-step-hint">
                            Upload LAS/LAZ files into the project's{" "}
                            <strong>Source</strong> folder.
                          </p>
                        </div>
                        <span className="rsa-upload-step-status-row">
                          <span className="rsa-upload-step-status-summary">
                            {noStorageConfigured ? (
                              <>
                                <i
                                  className="fas fa-circle-exclamation rsa-upload-step-status-icon empty"
                                  aria-hidden
                                />
                                <span className="rsa-upload-step-status-text">
                                  no storage found for this project
                                </span>
                              </>
                            ) : existingSourceLoading &&
                              existingSourceFiles.length === 0 ? (
                              <>
                                <i
                                  className="fas fa-spinner fa-spin rsa-upload-step-status-icon"
                                  aria-hidden
                                />
                                <span className="rsa-upload-step-status-text">
                                  Checking Source…
                                </span>
                              </>
                            ) : existingSourceFolderId &&
                              existingSourceFiles.length > 0 ? (
                              <>
                                <i
                                  className="fas fa-circle-check rsa-upload-step-status-icon ready"
                                  aria-hidden
                                />
                                <span className="rsa-upload-step-status-text">
                                  LAS/LAZ files detected
                                  {existingSourceLoading && (
                                    <i
                                      className="fas fa-rotate fa-spin rsa-upload-step-status-icon refreshing"
                                      title="Refreshing…"
                                      aria-hidden
                                    />
                                  )}
                                  <span className="rsa-upload-step-status-subtext">
                                    Latest upload:{" "}
                                    {latestExistingSourceUploadTime}
                                  </span>
                                </span>
                                <span className="rsa-upload-step-status-count">
                                  {existingSourceFiles.length} file
                                  {existingSourceFiles.length !== 1 ? "s" : ""}
                                </span>
                              </>
                            ) : existingSourceFolderId ? (
                              <>
                                <i
                                  className="fas fa-triangle-exclamation rsa-upload-step-status-icon empty"
                                  aria-hidden
                                />
                                <span className="rsa-upload-step-status-text">
                                  No LAS/LAZ files found in Source yet
                                  <span className="rsa-upload-step-status-subtext">
                                    Latest upload:{" "}
                                    {latestExistingSourceUploadTime}
                                  </span>
                                </span>
                              </>
                            ) : (
                              <>
                                <i
                                  className="fas fa-triangle-exclamation rsa-upload-step-status-icon empty"
                                  aria-hidden
                                />
                                <span className="rsa-upload-step-status-text">
                                  Source folder was not found
                                </span>
                              </>
                            )}
                          </span>
                          <span className="rsa-upload-step-status-actions">
                            <span
                              className="rsa-wizard-nav-btn rsa-wizard-nav-btn-primary rsa-upload-step-status-action"
                              role="button"
                              tabIndex={-1}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (existingSourceFolderId) {
                                  handleOpenExistingSourceUploadModal();
                                }
                              }}
                              title={
                                existingSourceFolderId
                                  ? "Upload LAS/LAZ files into Source"
                                  : "Source folder was not found"
                              }
                            >
                              <i className="fas fa-upload" aria-hidden /> Upload
                              LAS / LAZ Files
                            </span>
                          </span>
                        </span>
                      </div>

                      {existingSourceFiles.length > 0 && (
                        <div className="rsa-upload-step-files-table-wrap">
                          <div className="rsa-upload-step-files-table-toolbar">
                            <div className="rsa-upload-step-files-table-toolbar-meta">
                              <span className="rsa-upload-step-files-table-toolbar-title">
                                Source files
                              </span>
                              <span className="rsa-upload-step-files-table-toolbar-subtext">
                                {selectedExistingSourceUploadFiles.length > 0
                                  ? `${selectedExistingSourceUploadFiles.length} selected`
                                  : "Click rows to select multiple files"}
                              </span>
                            </div>
                            <div className="rsa-upload-step-files-table-toolbar-actions">
                              <button
                                type="button"
                                className="rsa-upload-step-files-toolbar-btn"
                                onClick={() =>
                                  handleToggleAllUploadTableFiles(
                                    "source",
                                    existingSourceFiles,
                                  )
                                }
                                disabled={uploadDeleteBusy}
                              >
                                {selectedExistingSourceUploadFiles.length ===
                                existingSourceFiles.length
                                  ? "Clear all"
                                  : "Select all"}
                              </button>
                              {selectedExistingSourceUploadFiles.length >=
                                2 && (
                                <button
                                  type="button"
                                  className="rsa-upload-step-files-toolbar-btn rsa-upload-step-files-toolbar-btn-danger"
                                  onClick={() =>
                                    handleRequestUploadFileDelete(
                                      selectedExistingSourceUploadFiles,
                                      existingSourceFolderId,
                                      "Source",
                                    )
                                  }
                                  disabled={uploadDeleteBusy}
                                >
                                  <i className="fas fa-trash" aria-hidden />{" "}
                                  Remove selected
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="rsa-upload-step-files-table-scroll">
                            <table className="rsa-upload-step-files-table">
                              <thead>
                                <tr>
                                  <th className="rsa-upload-step-files-select-col">
                                    <button
                                      type="button"
                                      className={`rsa-upload-step-files-select-toggle${
                                        selectedExistingSourceUploadFiles.length ===
                                        existingSourceFiles.length
                                          ? " is-selected"
                                          : ""
                                      }`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleAllUploadTableFiles(
                                          "source",
                                          existingSourceFiles,
                                        );
                                      }}
                                      disabled={uploadDeleteBusy}
                                      aria-label={
                                        selectedExistingSourceUploadFiles.length ===
                                        existingSourceFiles.length
                                          ? "Clear all selected source files"
                                          : "Select all source files"
                                      }
                                    >
                                      <i className="fas fa-check" aria-hidden />
                                    </button>
                                  </th>
                                  <th className="rsa-upload-step-files-index-col">
                                    #
                                  </th>
                                  <th>File</th>
                                  <th className="rsa-upload-step-files-type-col">
                                    Type
                                  </th>
                                  <th className="rsa-upload-step-files-time-col">
                                    Uploaded
                                  </th>
                                  <th className="rsa-upload-step-files-action-col">
                                    Remove
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {existingSourceFiles.map(
                                  (sourceFile, index) => {
                                    const sourceFileId = normalizeDriveFolderId(
                                      sourceFile.googleDriveId,
                                    );
                                    const isSelected = (
                                      uploadTableSelection.source || []
                                    ).includes(sourceFileId);
                                    return (
                                      <tr
                                        key={
                                          sourceFile.googleDriveId ||
                                          sourceFile.id ||
                                          sourceFile.name ||
                                          index
                                        }
                                        className={`rsa-upload-step-files-row${isSelected ? " is-selected" : ""}`}
                                        onClick={() =>
                                          toggleUploadTableFileSelection(
                                            "source",
                                            sourceFile,
                                          )
                                        }
                                        aria-selected={isSelected}
                                      >
                                        <td className="rsa-upload-step-files-select-cell">
                                          <button
                                            type="button"
                                            className={`rsa-upload-step-files-select-toggle${isSelected ? " is-selected" : ""}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleUploadTableFileSelection(
                                                "source",
                                                sourceFile,
                                              );
                                            }}
                                            disabled={uploadDeleteBusy}
                                            aria-label={
                                              isSelected
                                                ? `Deselect ${sourceFile.name}`
                                                : `Select ${sourceFile.name}`
                                            }
                                          >
                                            <i
                                              className="fas fa-check"
                                              aria-hidden
                                            />
                                          </button>
                                        </td>
                                        <td className="rsa-upload-step-files-index-cell">
                                          {index + 1}
                                        </td>
                                        <td
                                          className="rsa-upload-step-files-name-cell"
                                          title={sourceFile.name}
                                        >
                                          {sourceFile.name}
                                        </td>
                                        <td>
                                          {String(sourceFile.name || "")
                                            .trim()
                                            .toLowerCase()
                                            .endsWith(".laz")
                                            ? "LAZ"
                                            : "LAS"}
                                        </td>
                                        <td className="rsa-upload-step-files-time-cell">
                                          {formatUploadTimestamp(
                                            sourceFile.modified ||
                                              sourceFile.created,
                                          )}
                                        </td>
                                        <td className="rsa-upload-step-files-action-cell">
                                          <button
                                            type="button"
                                            className="rsa-upload-step-files-delete-btn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRequestUploadFileDelete(
                                                sourceFile,
                                                existingSourceFolderId,
                                                "Source",
                                              );
                                            }}
                                            disabled={uploadDeleteBusy}
                                            title={`Delete ${sourceFile.name}`}
                                            aria-label={`Delete ${sourceFile.name}`}
                                          >
                                            <i
                                              className={`fas ${
                                                uploadFileDeletePendingIds.includes(
                                                  sourceFileId,
                                                )
                                                  ? "fa-spinner fa-spin"
                                                  : "fa-trash"
                                              }`}
                                              aria-hidden
                                            />
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  },
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="rsa-step-footer rsa-step-footer--upload">
                  <div className="rsa-step-footer-group">
                    <button
                      type="button"
                      className="rsa-wizard-nav-btn rsa-wizard-nav-btn-ghost"
                      disabled
                      aria-disabled="true"
                      title="This is the first step"
                    >
                      <i className="fas fa-arrow-left" aria-hidden /> Back
                    </button>
                    <button
                      type="button"
                      className="rsa-wizard-nav-btn rsa-wizard-nav-btn-primary"
                      onClick={() => setActiveMainTab("setup")}
                      disabled={!canProceedToSetup}
                      title={
                        canProceedToSetup
                          ? "Continue to setup"
                          : isRsaModelProject
                            ? "Upload route XML files and at least one model file first"
                            : "Upload at least one LAS/LAZ file into Source first"
                      }
                    >
                      Next: Setup{" "}
                      <i className="fas fa-arrow-right" aria-hidden />
                    </button>
                  </div>
                </div>
              </div>

              {/* Upload modal targeting Source/Routes */}
              <UploadModal
                isOpen={showRoutesUploadModal}
                onClose={() => setShowRoutesUploadModal(false)}
                onUpload={async () => {
                  setShowRoutesUploadModal(false);
                  await refreshRoutesTabDataAfterUpload();
                }}
                projectId={effectiveProjectId}
                currentFolderId={routesFolderId || effectiveProjectId}
                currentFolderGoogleDriveId={routesFolderId || null}
                projectName={null}
                currentFolderName="Source / Routes"
                sourceStorage={sourceStorage}
                warnOnExistingRouteXml
              />
              <UploadModal
                isOpen={showModelUploadModal}
                onClose={() => setShowModelUploadModal(false)}
                onUpload={async () => {
                  setShowModelUploadModal(false);
                  await refreshRoutesTabDataAfterUpload();
                }}
                projectId={effectiveProjectId}
                currentFolderId={modelFolderId || effectiveProjectId}
                currentFolderGoogleDriveId={modelFolderId || null}
                projectName={null}
                currentFolderName="Source / Model"
                sourceStorage={sourceStorage}
              />
              <UploadModal
                isOpen={showExistingSourceUploadModal}
                onClose={() => setShowExistingSourceUploadModal(false)}
                onUpload={async (args) => {
                  setShowExistingSourceUploadModal(false);
                  await refreshExistingSourceDataAfterUpload(args);
                }}
                projectId={effectiveProjectId}
                currentFolderId={existingSourceFolderId || effectiveProjectId}
                currentFolderGoogleDriveId={existingSourceFolderId || null}
                projectName={null}
                currentFolderName="Source"
                sourceStorage={sourceStorage}
                accept=".las,.laz"
              />
              <DeleteConfirmModal
                isOpen={Boolean(uploadFileDeleteCandidate)}
                onClose={() => setUploadFileDeleteCandidate(null)}
                onConfirm={handleConfirmUploadFileDelete}
                items={
                  Array.isArray(uploadFileDeleteCandidate?.files)
                    ? uploadFileDeleteCandidate.files
                    : []
                }
              />
            </div>

            {/* ── Analysis Tab Panel ──────────────────── */}
            <div
              className={`main-content-tab-panel main-content-tab-panel-analysis ${activeMainTab === "analysis" ? "active" : ""}`}
            >
              {isLoadingVisibleTaskOptions && (
                <div
                  className="analysis-tab-panel-loading"
                  role="status"
                  aria-live="polite"
                  aria-busy="true"
                >
                  <i
                    className="fas fa-spinner fa-spin analysis-tab-panel-loading-spinner"
                    aria-hidden
                  />
                  <span
                    className="analysis-tab-panel-loading-text"
                    style={{ paddingLeft: "10px" }}
                  >
                    Loading project analysis options...
                  </span>
                </div>
              )}
              <div className="tab-content-RSA">
                {/* Toggle Buttons for Required Services */}
                <div className="tasks-selection-container">
                  <p className="pHeader">Select Required Tasks:</p>
                  <div className="tasks-buttons-container">
                    {!isLoadingVisibleTaskOptions &&
                      visibleTaskOptions.length === 0 && (
                        <p className="summary-empty">
                          No RSA task folders were found under Source.
                        </p>
                      )}
                    {!isLoadingVisibleTaskOptions &&
                      visibleTaskOptions.map((type) => {
                        const templateId = analysisTemplateIdMapping[type];
                        const isSelected = selectedTasks.includes(type);
                        const taskImage =
                          TASK_IMAGE_CONFIG[type]?.image || null;
                        const taskInfoImage =
                          TASK_INFO_IMAGE_CONFIG[type] || null;

                        return (
                          <button
                            key={type}
                            type="button"
                            onClick={() => {
                              if (templateId) {
                                if (isSelected) {
                                  setSelectedTasks((prev) =>
                                    prev.filter((t) => t !== type),
                                  );
                                  // Also remove from services for backward compatibility
                                  const serviceValue = serviceMapping[type];
                                  if (serviceValue) {
                                    setServices((prev) =>
                                      prev.filter((s) => s !== serviceValue),
                                    );
                                  }
                                } else {
                                  setSelectedTasks((prev) => [...prev, type]);
                                  // Also add to services for backward compatibility
                                  const serviceValue = serviceMapping[type];
                                  if (serviceValue) {
                                    setServices((prev) => [
                                      ...prev,
                                      serviceValue,
                                    ]);
                                  }
                                }
                              } else {
                                console.warn(
                                  `No analysis template ID found for: ${type}`,
                                );
                              }
                            }}
                            className={`toggle-btn-rsa task-button ${isSelected ? "selected" : ""} ${taskImage ? "task-button-with-image" : ""}`}
                            title={type}
                          >
                            {taskImage ? (
                              <>
                                <img
                                  src={taskImage}
                                  alt={type}
                                  className="task-button-image"
                                  loading="lazy"
                                />
                                {isSelected && (
                                  <span
                                    className="task-button-selected-icon"
                                    aria-hidden="true"
                                  >
                                    <i className="fa-solid fa-circle-check"></i>
                                  </span>
                                )}
                                <span
                                  className={`task-button-info-btn ${taskInfoImage ? "has-info-image" : "no-info-image"}`}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (taskInfoImage) openTaskInfoModal(type);
                                  }}
                                  title={
                                    taskInfoImage
                                      ? `Show ${type} info`
                                      : `${type} info unavailable`
                                  }
                                  aria-hidden="true"
                                >
                                  <i className="fa-solid fa-circle-info"></i>
                                </span>
                                <span className="task-button-image-label">
                                  {type}
                                </span>
                              </>
                            ) : (
                              type
                            )}
                          </button>
                        );
                      })}
                  </div>
                </div>

                {/* Render the Folder Tree Popup Modal */}
                {showFolderTreePopup && (
                  <FolderTreePopup
                    onClose={() => setShowFolderTreePopup(false)}
                    onFolderSelect={(folders) =>
                      setSelectedFolders(
                        Array.isArray(folders) ? folders : [folders],
                      )
                    }
                    onDriveSelect={(driveName) =>
                      setSelectedDriveName(driveName)
                    }
                    storageConnection={selectedConnection}
                    useSystemDrive={useSystemGoogleDrive}
                    projectId={effectiveProjectId}
                  />
                )}

                {/* Storage Connection Modal */}
                {showConnectionModal && connectionProvider && (
                  <StorageConnectionModal
                    onClose={() => {
                      setShowConnectionModal(false);
                      setConnectionProvider(null);
                    }}
                    provider={connectionProvider}
                    onConnectionSuccess={() => {
                      // Reload connections after successful connection
                      loadStorageConnections();
                    }}
                  />
                )}

                {/* Render the Clear Confirmation Popup Modal */}
                {showClearPopup && (
                  <div className="TreeModalOverlay">
                    <div className="TreeModalContainer">
                      <div className="modal-header">
                        <h2 className="modal-title">Clear Form</h2>
                      </div>

                      <div className="modal-content">
                        <p className="clear-popup-message">
                          Are you sure you want to completely clear the page?
                          This action cannot be undone.
                        </p>
                      </div>

                      <div className="modal-actions">
                        <button
                          className="modal-btn modal-btn-secondary"
                          onClick={handleClearCancel}
                        >
                          Cancel
                        </button>
                        <button
                          className="modal-btn modal-btn-primary"
                          onClick={handleClearConfirm}
                        >
                          Yes, Clear All
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {taskInfoModal.isOpen && taskInfoModal.image && (
                  <div
                    className="rsa-modal-overlay task-info-modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="task-info-modal-title"
                    onClick={closeTaskInfoModal}
                  >
                    <div
                      className="rsa-modal task-info-modal"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="task-info-modal-header">
                        <h3
                          id="task-info-modal-title"
                          className="task-info-modal-title"
                        >
                          {taskInfoModal.title}
                        </h3>
                        <button
                          type="button"
                          className="task-info-modal-close"
                          onClick={closeTaskInfoModal}
                          aria-label="Close info image"
                        >
                          <i className="fa-solid fa-xmark" aria-hidden />
                        </button>
                      </div>
                      <div className="task-info-modal-body">
                        <img
                          src={taskInfoModal.image}
                          alt={`${taskInfoModal.title} reference`}
                          className="task-info-modal-image"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>
                )}
                {!isRsaModelProject && (
                  <div className="rsa-step-footer rsa-step-footer--routes">
                    <div className="rsa-step-footer-group">
                      <button
                        type="button"
                        className="rsa-wizard-nav-btn rsa-wizard-nav-btn-ghost"
                        onClick={() => setActiveMainTab("paths")}
                      >
                        <i className="fas fa-arrow-left" aria-hidden /> Back:
                        Routes
                      </button>
                      <button
                        type="button"
                        className="rsa-wizard-nav-btn rsa-wizard-nav-btn-primary"
                        onClick={() => setActiveMainTab("dashboard")}
                        disabled={selectedTasks.length === 0}
                        title={
                          selectedTasks.length === 0
                            ? "Select at least one task to continue"
                            : ""
                        }
                      >
                        Next: Dashboard{" "}
                        <i className="fas fa-arrow-right" aria-hidden />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {isRsaModelProject && (
              <div
                className={`main-content-tab-panel main-content-tab-panel-routes rsa-wizard-step-page ${activeMainTab === "routes" ? "active" : ""}`}
              >
                <div className="tab-content-RSA tab-content-RSA-routes rsa-wizard-step-content">
                  {isLoadingVisibleTaskOptions && (
                    <div
                      className="analysis-tab-panel-loading"
                      role="status"
                      aria-live="polite"
                      aria-busy="true"
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <i
                        className="fas fa-spinner fa-spin analysis-tab-panel-loading-spinner"
                        aria-hidden
                      />
                      <span
                        className="analysis-tab-panel-loading-text"
                        style={{ paddingLeft: "10px" }}
                      >
                        Loading analysis options…
                      </span>
                    </div>
                  )}
                  {!isLoadingVisibleTaskOptions &&
                    visibleTaskOptions.length === 0 && (
                      <p className="summary-empty">
                        No RSA task folders found under Source. Upload files and
                        refresh.
                      </p>
                    )}
                  <div className="tasks-buttons-container">
                    {!isLoadingVisibleTaskOptions &&
                      visibleTaskOptions.map((type) => {
                        const templateId = analysisTemplateIdMapping[type];
                        const isSelected = selectedTasks.includes(type);
                        const taskImage =
                          TASK_IMAGE_CONFIG[type]?.image || null;
                        return (
                          <button
                            key={`step3-task-${type}`}
                            type="button"
                            onClick={() => {
                              if (templateId) {
                                if (isSelected) {
                                  setSelectedTasks((prev) =>
                                    prev.filter((t) => t !== type),
                                  );
                                  const serviceValue = serviceMapping[type];
                                  if (serviceValue)
                                    setServices((prev) =>
                                      prev.filter((s) => s !== serviceValue),
                                    );
                                } else {
                                  setSelectedTasks((prev) => [...prev, type]);
                                  const serviceValue = serviceMapping[type];
                                  if (serviceValue)
                                    setServices((prev) => [
                                      ...prev,
                                      serviceValue,
                                    ]);
                                }
                              }
                            }}
                            className={`toggle-btn-rsa task-button ${isSelected ? "selected" : ""} ${taskImage ? "task-button-with-image" : ""}`}
                            title={type}
                          >
                            {taskImage ? (
                              <>
                                <img
                                  src={taskImage}
                                  alt={type}
                                  className="task-button-image"
                                  loading="lazy"
                                />
                                {isSelected && (
                                  <span
                                    className="task-button-selected-icon"
                                    aria-hidden="true"
                                  >
                                    <i className="fa-solid fa-circle-check" />
                                  </span>
                                )}
                                <span className="task-button-image-label">
                                  {type}
                                </span>
                              </>
                            ) : (
                              type
                            )}
                          </button>
                        );
                      })}
                  </div>
                  <div className="rsa-step-footer rsa-step-footer--routes">
                    <div className="rsa-step-footer-group">
                      <button
                        type="button"
                        className="rsa-wizard-nav-btn rsa-wizard-nav-btn-ghost"
                        onClick={() => setActiveMainTab("setup")}
                      >
                        <i className="fas fa-arrow-left" aria-hidden /> Back:
                        Setup
                      </button>
                      <button
                        type="button"
                        className="rsa-wizard-nav-btn rsa-wizard-nav-btn-primary"
                        onClick={() => setActiveMainTab("dashboard")}
                        disabled={selectedTasks.length === 0}
                        title={
                          selectedTasks.length === 0
                            ? "Select at least one task to continue"
                            : ""
                        }
                      >
                        Next: Dashboard{" "}
                        <i className="fas fa-arrow-right" aria-hidden />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Setup Tab Panel */}
            <div
              className={`main-content-tab-panel main-content-tab-panel-setup rsa-wizard-step-page ${activeMainTab === "setup" ? "active" : ""}`}
            >
              {(preloadRsaSetupLoading || loadingSetupFromFolder) && (
                <div className="setup-section-loading-overlay" aria-busy="true">
                  <i
                    className="fas fa-spinner fa-spin setup-section-loading-spinner"
                    aria-hidden
                  />
                  <span className="setup-section-loading-text">
                    Loading Project Setup...
                  </span>
                </div>
              )}
              <div className="tab-content-RSA rsa-wizard-step-content">
                <section className="setup-section setup-section-map-overlay">
                  <h3 className="setup-section-title">
                    Coordinate system setup
                  </h3>

                  <div className="setup-local-config">
                    <div className="setup-wkt-subtitle-row">
                      <h4 className="setup-subtitle">WKT (Well-known text)</h4>
                      <button
                        type="button"
                        className="setup-wkt-help-info-btn"
                        onClick={() => setWktRequirementsHelpModalOpen(true)}
                        aria-label="Supported coordinate system format (WKT) - open help"
                        title="Supported coordinate system format (WKT)"
                      >
                        <i className="fa-solid fa-circle-info" aria-hidden />
                      </button>
                    </div>
                    <div className="setup-wkt-input-wrap">
                      <textarea
                        className={`setup-wkt-input setup-input${wktInputLocked ? " setup-wkt-input-locked" : ""}`}
                        placeholder="e.g. POLYGON((...)) or SRID / CRS description"
                        value={mapOverlayLocalConfig.wkt ?? ""}
                        onChange={(e) =>
                          setMapOverlayLocalConfig((c) => ({
                            ...c,
                            wkt: e.target.value,
                          }))
                        }
                        readOnly={wktInputLocked}
                        rows={3}
                      />
                      <div className="setup-wkt-actions">
                        {wktInputLocked ? (
                          <button
                            type="button"
                            className="setup-wkt-btn setup-wkt-btn-edit"
                            onClick={() => setWktInputLocked(false)}
                          >
                            <i className="fas fa-pen" aria-hidden /> Edit WKT
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="setup-wkt-btn setup-wkt-btn-save"
                            onClick={handleSaveWktClick}
                          >
                            <i className="fas fa-floppy-disk" aria-hidden />{" "}
                            Save WKT
                          </button>
                        )}
                      </div>
                    </div>
                    {!isRsaModelProject && (
                      <div className="setup-existing-project-fields">
                        <label className="setup-existing-project-field">
                          <span className="setup-existing-project-field-label">
                            State
                          </span>
                          <input
                            type="text"
                            className="setup-input setup-existing-project-input"
                            value={mapOverlayLocalConfig.state ?? ""}
                            onChange={(e) =>
                              setMapOverlayLocalConfig((c) => ({
                                ...c,
                                state: e.target.value,
                              }))
                            }
                            placeholder="Enter state"
                          />
                        </label>
                      </div>
                    )}
                  </div>
                </section>

                {/* Save Setup overwrite confirmation modal */}
                {showSaveSetupOverwriteModal && (
                  <div
                    className="rsa-modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="rsa-save-setup-modal-title"
                  >
                    <div className="rsa-modal rsa-save-setup-modal">
                      <h3
                        id="rsa-save-setup-modal-title"
                        className="rsa-modal-title"
                      >
                        Overwrite project setup?
                      </h3>
                      <p className="rsa-modal-message">
                        This action will overwrite the current project setup in
                        the registry. Do you want to continue?
                      </p>
                      <div className="rsa-modal-actions">
                        <button
                          type="button"
                          className="setup-wkt-btn setup-wkt-btn-edit"
                          onClick={handleSaveSetupOverwriteCancel}
                        >
                          No
                        </button>
                        <button
                          type="button"
                          className="setup-wkt-btn setup-wkt-btn-save"
                          onClick={handleSaveSetupOverwriteConfirm}
                        >
                          Yes
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* Setup footer */}
              <div className="rsa-step-footer rsa-step-footer--setup">
                <div className="rsa-step-footer-group">
                  <button
                    type="button"
                    className="rsa-wizard-nav-btn rsa-wizard-nav-btn-ghost"
                    onClick={() => setActiveMainTab("upload")}
                  >
                    <i className="fas fa-arrow-left" aria-hidden /> Back: Upload
                    Files
                  </button>
                  <button
                    type="button"
                    className="rsa-wizard-nav-btn rsa-wizard-nav-btn-primary"
                    onClick={() =>
                      setActiveMainTab(isRsaModelProject ? "routes" : "paths")
                    }
                  >
                    {isRsaModelProject ? "Next: Tasks & Routes" : "Next: Paths"}{" "}
                    <i className="fas fa-arrow-right" aria-hidden />
                  </button>
                </div>
              </div>
            </div>

            {!isRsaModelProject && (
              <>
                {/* Paths Tab Panel - PathSelectorMap only mounts when tab is active to avoid Loader singleton conflicts */}
                <div
                  className={`main-content-tab-panel main-content-tab-panel-paths ${activeMainTab === "paths" ? "active" : ""}`}
                >
                  <div className="tab-content-RSA tab-content-RSA-paths">
                    <PathSelectorMap
                      pathsPanelMode={pathsPanelMode}
                      onPathsPanelModeChange={setPathsPanelMode}
                      waypoints={activeRsaWaypoints}
                      waypointColor={activeRsaColor}
                      fullPathStrokeColor={SEGMENT_COLORS[0]}
                      fullPathPathData={fullPathPathData}
                      segmentVisibility={rsaSegmentVisibility}
                      activeSegmentIndex={activeRsaLegacyIndex}
                      activeSegmentPathData={activeRsaPathData}
                      freezeActiveSegmentPath={isGeneratedLaneActive}
                      persistentPathOverlays={persistentLaneOverlays}
                      highlightPathData={selectedRsaHighlightPathData}
                      highlightColor={"#FFEE00"}
                      fitToPathData={fitToPathData}
                      onFitToPathComplete={() => setFitToPathData(null)}
                      fitToBounds={fitToBounds}
                      onFitToBoundsComplete={() => setFitToBounds(null)}
                      setWaypoints={pathSetWaypoints}
                      onPathChange={pathOnPathChange}
                      allowAddWaypointOnClick={
                        pathsPanelMode !== "intersection" &&
                        (!isGeneratedLaneActive || isManualLaneEditingActive) &&
                        !rsaPathsHook.laneEditMode &&
                        !rsaPathsHook.speedEditMode
                      }
                      manualSplineMode={isManualLaneEditingActive}
                      centerOnWaypointClick={isManualLaneEditingActive}
                      traceSeedPickActive={traceSeed.active}
                      onTraceSeedPicked={handleTraceSeedPicked}
                      initialCenter={[38.8283, -98.5795]}
                      initialZoom={5}
                      isVisible={true}
                      imageOverlays={imageOverlaysForMap}
                      lasBoundingBoxes={
                        lasBoundingBoxesVisible ? lasBoundingBoxes : []
                      }
                      selectedLasBoundingBoxIndex={selectedLasBoundingBoxIndex}
                      overlayOpacity={orthoTilesVisible ? overlayOpacity : 0}
                      overlayBrightness={overlayBrightness}
                      overlayContrast={overlayContrast}
                      onImageOverlaysLoadProgress={onImageOverlaysLoadProgress}
                      orthoFetchingMeta={loadingOrthoTiles}
                      orthoTilesLoaded={imageOverlaysLoadedCount}
                      orthoTilesTotal={imageOverlaysTotal}
                      intersectionDrawingMode={intersectionDrawingMode}
                      onIntersectionDrawingModeChange={
                        handleIntersectionDrawingModeChange
                      }
                      onIntersectionOverlayComplete={(payload) => {
                        setIntersectionOverlays((prev) => [...prev, payload]);
                      }}
                      onIntersectionOverlayUpdate={(id, payload) =>
                        setIntersectionOverlays((prev) =>
                          prev.map((o) =>
                            o.id === id ? { ...o, ...payload } : o,
                          ),
                        )
                      }
                      onIntersectionOverlayRemove={(id) =>
                        setIntersectionOverlays((prev) =>
                          prev.filter((o) => o.id !== id),
                        )
                      }
                      intersectionOverlays={intersectionOverlays}
                      intersections={rsaPathsHook.intersections}
                      activeIntersectionId={rsaPathsHook.activeIntersectionId}
                      pendingIsdDraft={pendingIsdDraft}
                      onAddIntersection={(payload) => {
                        const id = rsaPathsHook.actions.addIntersection(
                          payload.insertionPoint,
                        );
                        rsaPathsHook.actions.updateIntersection(id, {
                          azimuth: payload.azimuth,
                          arrowLengthFeet: payload.arrowLengthFeet,
                          // Merge form fields from the pending draft if present.
                          ...(pendingIsdDraft ?? {}),
                          // Bundle the F-case opposite block (if the user
                          // placed one before clicking OK).
                          ...(oppositeBlockDraft
                            ? { oppositePoint: oppositeBlockDraft }
                            : {}),
                        });
                        // New blocks start deselected; user clicks to activate.
                        rsaPathsHook.actions.setActiveIntersection(null);
                        // Clear the drafts — placement done.
                        setPendingIsdDraft(null);
                        setOppositeBlockDraft(null);
                      }}
                      onUpdateIntersection={(id, updates) => {
                        rsaPathsHook.actions.updateIntersection(id, updates);
                      }}
                      onSetActiveIntersection={(id) => {
                        rsaPathsHook.actions.setActiveIntersection(id);
                      }}
                      onCancelPendingIsdDraft={() => {
                        setPendingIsdDraft(null);
                        setOppositeBlockDraft(null);
                      }}
                      oppositePointPickActive={oppositePointPickActive}
                      onOppositeBlockPicked={handleOppositeBlockPicked}
                      onCancelOppositePointPick={cancelOppositePointPick}
                      oppositeBlockDraft={oppositeBlockDraft}
                      onUpdateOppositeBlockDraft={setOppositeBlockDraft}
                      curvePoints={curvePoints}
                      onCurvedLinePointsChange={setCurvePoints}
                      measurePoints={measurePoints}
                      onMeasurePointsChange={setMeasurePoints}
                      curvedLineEditPointCount={curvedLineEditPointCount}
                      laneEditMode={rsaPathsHook.laneEditMode}
                      laneEditWaypoints={
                        rsaPathsHook.laneEditMode && rsaPathsHook.activeLaneId
                          ? (rsaPathsHook.lanes.find(
                              (l) => l.id === rsaPathsHook.activeLaneId,
                            )?.waypoints ?? [])
                          : []
                      }
                      laneEditSelectedIndices={
                        rsaPathsHook.selectedLaneWaypointIndices
                      }
                      onLaneEditSelectionChange={
                        rsaPathsHook.actions.setSelectedLaneWaypoints
                      }
                      onLaneEditWaypointsMoved={(primaryIdx, dLat, dLng) => {
                        const lane = rsaPathsHook.lanes.find(
                          (l) => l.id === rsaPathsHook.activeLaneId,
                        );
                        if (lane?.waypoints)
                          laneEditHistory.pushState(lane.waypoints);
                        rsaPathsHook.actions.moveSelectedLaneWaypoints(
                          rsaPathsHook.activeLaneId,
                          dLat,
                          dLng,
                          primaryIdx,
                        );
                      }}
                      onLaneEditWaypointInserted={(insertIdx, lat, lng) => {
                        const lane = rsaPathsHook.lanes.find(
                          (l) => l.id === rsaPathsHook.activeLaneId,
                        );
                        if (lane?.waypoints)
                          laneEditHistory.pushState(lane.waypoints);
                        const extras = bridge.active
                          ? { bridged: true }
                          : undefined;
                        rsaPathsHook.actions.insertLaneWaypoint(
                          rsaPathsHook.activeLaneId,
                          insertIdx,
                          lat,
                          lng,
                          extras,
                        );
                      }}
                      onLaneEditWaypointRemoved={(waypointIdx) => {
                        const lane = rsaPathsHook.lanes.find(
                          (l) => l.id === rsaPathsHook.activeLaneId,
                        );
                        if (lane?.waypoints)
                          laneEditHistory.pushState(lane.waypoints);
                        rsaPathsHook.actions.removeLaneWaypoint(
                          rsaPathsHook.activeLaneId,
                          waypointIdx,
                        );
                      }}
                      laneEditCanUndo={laneEditHistory.canUndo}
                      laneEditCanRedo={laneEditHistory.canRedo}
                      onLaneEditUndo={() => {
                        const prev = laneEditHistory.undo();
                        if (prev && rsaPathsHook.activeLaneId) {
                          rsaPathsHook.actions.updateLane(
                            rsaPathsHook.activeLaneId,
                            { waypoints: prev },
                          );
                          rsaPathsHook.actions.setSelectedLaneWaypoints([]);
                        }
                      }}
                      onLaneEditRedo={() => {
                        const next = laneEditHistory.redo();
                        if (next && rsaPathsHook.activeLaneId) {
                          rsaPathsHook.actions.updateLane(
                            rsaPathsHook.activeLaneId,
                            { waypoints: next },
                          );
                          rsaPathsHook.actions.setSelectedLaneWaypoints([]);
                        }
                      }}
                      onLaneEditDeleteSelected={() => {
                        const selected =
                          rsaPathsHook.selectedLaneWaypointIndices;
                        if (!selected?.length || !rsaPathsHook.activeLaneId)
                          return;
                        const lane = rsaPathsHook.lanes.find(
                          (l) => l.id === rsaPathsHook.activeLaneId,
                        );
                        if (!lane?.waypoints) return;
                        laneEditHistory.pushState(lane.waypoints);
                        const toRemove = new Set(selected);
                        const next = lane.waypoints.filter(
                          (_, i) => !toRemove.has(i),
                        );
                        rsaPathsHook.actions.updateLane(
                          rsaPathsHook.activeLaneId,
                          { waypoints: next },
                        );
                        rsaPathsHook.actions.setSelectedLaneWaypoints([]);
                      }}
                      onLaneEditSelectAll={() => {
                        const lane = rsaPathsHook.lanes.find(
                          (l) => l.id === rsaPathsHook.activeLaneId,
                        );
                        if (lane?.waypoints?.length) {
                          rsaPathsHook.actions.setSelectedLaneWaypoints(
                            lane.waypoints.map((_, i) => i),
                          );
                        }
                      }}
                      onLaneEditClearSelection={() =>
                        rsaPathsHook.actions.setSelectedLaneWaypoints([])
                      }
                      speedEditMode={rsaPathsHook.speedEditMode}
                      speedEditWaypoints={(() => {
                        if (
                          !rsaPathsHook.speedEditMode ||
                          !rsaPathsHook.activeLaneId
                        )
                          return [];
                        const lane = rsaPathsHook.lanes.find(
                          (l) => l.id === rsaPathsHook.activeLaneId,
                        );
                        if (!lane) return [];
                        // Use dense pathData coordinates when available (matches the visible polyline)
                        const coords = lane.pathData?.geometry?.coordinates;
                        if (Array.isArray(coords) && coords.length >= 2) {
                          return coords.map((c) =>
                            Array.isArray(c) ? { lat: c[1], lng: c[0] } : c,
                          );
                        }
                        return lane.waypoints ?? [];
                      })()}
                      speedEditSegments={
                        rsaPathsHook.speedEditMode && rsaPathsHook.activeLaneId
                          ? (rsaPathsHook.lanes.find(
                              (l) => l.id === rsaPathsHook.activeLaneId,
                            )?.speedSegments ?? [])
                          : []
                      }
                      onSpeedBreakpointAdded={(mileMark) => {
                        if (rsaPathsHook.activeLaneId) {
                          rsaPathsHook.actions.addSpeedBreakpoint(
                            rsaPathsHook.activeLaneId,
                            mileMark,
                            0,
                          );
                        }
                      }}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Dashboard Tab Panel */}
            <div
              className={`main-content-tab-panel main-content-tab-panel-dashboard ${activeMainTab === "dashboard" ? "active" : ""}`}
            >
              <div className="tab-content-RSA">
                <AnalysisDashboard
                  selectedRun={dashboardSelectedRun}
                  setSelectedRun={setDashboardSelectedRun}
                  mockProjectsData={dashboardProjectsData}
                  displayRuns={displayRuns}
                  runProgressMap={dashboardRunProgress}
                  loading={dashboardLoading}
                  error={dashboardError}
                  onBrowseDeliverables={
                    typeof onGoToFiles === "function"
                      ? handleBrowseDeliverables
                      : null
                  }
                  setActiveMainTab={setActiveMainTab}
                />
              </div>
            </div>

            <div
              className={`main-content-tab-panel main-content-tab-panel-deliverables rsa-wizard-step-page ${activeMainTab === "deliverables" ? "active" : ""}`}
            >
              <div className="tab-content-RSA rsa-deliverables-tab-content">
                {/* <div className="rsa-deliverables-tab-header">
                <p className="pHeader rsa-deliverables-title">Deliverables</p>
                <p className="rsa-deliverables-subtitle">
                  Manage Deliverables files directly here. This tab is intended to replace the separate files panel workflow.
                </p>
                {deliverablesCurrentPath.length > 0 && (
                  <div className="rsa-deliverables-breadcrumbs">
                    {deliverablesCurrentPath.map((pathItem, index) => (
                      <React.Fragment key={`${pathItem.id || pathItem.name}-${index}`}>
                        <button
                          type="button"
                          className={`rsa-deliverables-breadcrumb-item${index === deliverablesCurrentPath.length - 1 ? ' active' : ''}`}
                          onClick={(e) => handleDeliverablesBreadcrumbClick(index, e)}
                          title={pathItem.name}
                        >
                          {pathItem.name}
                        </button>
                        {index < deliverablesCurrentPath.length - 1 && (
                          <span className="rsa-deliverables-breadcrumb-separator" aria-hidden>
                            <i className="fas fa-chevron-right" />
                          </span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div> */}
                <div className="rsa-deliverables-explorer-host">
                  {deliverablesFolderResolving ? (
                    <div className="empty-folder">
                      <i className="fas fa-spinner fa-spin" />
                      <span>Resolving Deliverables folder...</span>
                    </div>
                  ) : !deliverablesScopedRootFolder ? (
                    <div className="empty-folder">
                      <i className="fas fa-circle-exclamation" />
                      <span>
                        {deliverablesError ||
                          "Deliverables folder could not be resolved for this project."}
                      </span>
                    </div>
                  ) : (
                    <FileExplorer
                      ref={deliverablesExplorerRef}
                      project={{
                        Id: effectiveProjectId,
                        Name:
                          deliverablesScopedRootFolder.name || "Deliverables",
                      }}
                      footerExtra={
                        <button
                          type="button"
                          className="rsa-wizard-nav-btn rsa-wizard-nav-btn-ghost"
                          onClick={() => setActiveMainTab("dashboard")}
                        >
                          <i className="fas fa-arrow-left" aria-hidden /> Back:
                          Dashboard
                        </button>
                      }
                      onFileSelect={() => {}}
                      onFolderSelect={setDeliverablesSelectedFolder}
                      currentPath={deliverablesCurrentPath}
                      onBreadcrumbUpdate={setDeliverablesCurrentPath}
                      selectedFolder={deliverablesSelectedFolder}
                      onClearSelection={() => {}}
                      showUploadModal={showDeliverablesUploadModal}
                      onOpenUploadModal={() =>
                        setShowDeliverablesUploadModal(true)
                      }
                      onCloseUploadModal={() =>
                        setShowDeliverablesUploadModal(false)
                      }
                      currentFolderId={
                        deliverablesSelectedFolder?.id ||
                        deliverablesScopedRootFolder.id ||
                        effectiveProjectId
                      }
                      scopedRootFolder={deliverablesScopedRootFolder}
                      hideTreePanel
                      onSelectionStateChange={setDeliverablesSelectionState}
                      fileListFilterText={deliverablesFilterText}
                      onFileListFilterTextChange={setDeliverablesFilterText}
                      fileListTopContent={
                        deliverablesBreadcrumbItems.length > 0 ? (
                          <nav
                            className="rsa-deliverables-breadcrumbs rsa-deliverables-file-list-breadcrumbs"
                            aria-label="Deliverables folder path"
                          >
                            {deliverablesBreadcrumbItems.map(
                              (pathItem, index) => (
                                <React.Fragment
                                  key={`${pathItem.id || pathItem.name}-${index}`}
                                >
                                  <button
                                    type="button"
                                    className={`rsa-deliverables-breadcrumb-item${index === deliverablesBreadcrumbItems.length - 1 ? " active" : ""}`}
                                    onClick={(e) =>
                                      handleDeliverablesBreadcrumbClick(
                                        index,
                                        e,
                                      )
                                    }
                                    title={pathItem.displayName}
                                    aria-current={
                                      index ===
                                      deliverablesBreadcrumbItems.length - 1
                                        ? "page"
                                        : undefined
                                    }
                                  >
                                    {pathItem.displayName}
                                  </button>
                                  {index <
                                    deliverablesBreadcrumbItems.length - 1 && (
                                    <span
                                      className="rsa-deliverables-breadcrumb-separator"
                                      aria-hidden
                                    >
                                      <i className="fas fa-chevron-right" />
                                    </span>
                                  )}
                                </React.Fragment>
                              ),
                            )}
                          </nav>
                        ) : null
                      }
                      fileListToolbar={
                        <div className="rsa-deliverables-explorer-toolbar">
                          {previousDeliverablesBreadcrumbStep && (
                            <button
                              type="button"
                              className="rsa-deliverables-toolbar-btn"
                              onClick={handleDeliverablesBack}
                              title={previousDeliverablesBreadcrumbTooltip}
                              aria-label={previousDeliverablesBreadcrumbTooltip}
                            >
                              <i className="fas fa-arrow-left" aria-hidden />
                            </button>
                          )}
                          <button
                            type="button"
                            className="rsa-deliverables-toolbar-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeliverablesRefresh();
                            }}
                            title="Refresh Deliverables"
                          >
                            <i className="fas fa-sync-alt" aria-hidden />
                          </button>
                          {deliverablesSelectionState.canDeleteSelection && (
                            <button
                              type="button"
                              className="rsa-deliverables-toolbar-btn rsa-deliverables-toolbar-btn-danger"
                              onClick={handleDeliverablesDelete}
                              title="Delete selected items"
                            >
                              <i className="fas fa-trash" aria-hidden />
                              {/* <span></span> */}
                            </button>
                          )}
                          {deliverablesSelectionState.hasSelectedFiles && (
                            <button
                              type="button"
                              className="rsa-deliverables-toolbar-btn"
                              onClick={handleDeliverablesDownload}
                              title="Download selected items"
                            >
                              <i className="fas fa-download" aria-hidden />
                              {/* <span></span> */}
                            </button>
                          )}
                          <div className="rsa-deliverables-toolbar-spacer" />
                        </div>
                      }
                    />
                  )}
                </div>
              </div>
            </div>

            {!isRsaModelProject && (
              <>
                {/* Settings Tab Panel */}
                <div
                  className={`main-content-tab-panel main-content-tab-panel-settings ${activeMainTab === "settings" ? "active" : ""}`}
                >
                  <div className="tab-content-RSA">
                    {/* Cloud Storage Service Selector */}
                    <PermissionGate
                      group="Admin"
                      fallback={
                        <div
                          style={{
                            padding: "10px",
                            background: "#fee",
                            border: "1px solid #fcc",
                            margin: "10px 0",
                          }}
                        >
                          <p style={{ margin: 0, color: "#c00" }}>
                            Warning: Storage Service Selector is only available
                            to Admin users.
                            <br />
                            <small>
                              Check browser console for permission details.
                            </small>
                          </p>
                        </div>
                      }
                    >
                      <div className="storage-service-selector">
                        <p className="pHeader">
                          Select your Cloud Storage Service:
                        </p>
                        <div className="storage-providers-container">
                          {/* System Google Drive - Uses existing service account integration */}
                          <button
                            className={`toggle-btn-rsa task-button storage-provider-btn ${selectedStorageProvider === "system_google_drive" ? "selected" : ""}`}
                            type="button"
                            onClick={() => {
                              setSelectedStorageProvider("system_google_drive");
                              setUseSystemGoogleDrive(true);
                              setSelectedConnection(null);
                            }}
                            title="System Google Drive (Service Account)"
                          >
                            <GoogleDriveIcon
                              className="storage-provider-icon"
                              style={{ width: "20px", height: "20px" }}
                            />
                            <span>System Drive</span>
                          </button>

                          {/* User's Google Drive - Uses OAuth 2.0 */}
                          <button
                            className={`toggle-btn-rsa task-button storage-provider-btn ${selectedStorageProvider === "google_drive" ? "selected" : ""} ${!hasGoogleDriveConnection ? "needs-connection" : ""}`}
                            type="button"
                            onClick={() => {
                              const googleDriveConn = storageConnections.find(
                                (c) =>
                                  c.provider_type === "google_drive" &&
                                  c.is_active,
                              );
                              if (googleDriveConn) {
                                setSelectedStorageProvider("google_drive");
                                setSelectedConnection(googleDriveConn);
                                setUseSystemGoogleDrive(false);
                              } else {
                                setConnectionProvider("google_drive");
                                setShowConnectionModal(true);
                              }
                            }}
                            title={
                              !hasGoogleDriveConnection
                                ? "Click to connect your Google Drive via OAuth 2.0"
                                : "Your Google Drive (OAuth 2.0)"
                            }
                          >
                            <GoogleDriveIcon
                              className="storage-provider-icon"
                              style={{ width: "18px", height: "18px" }}
                            />
                            <span>Google Drive</span>
                            {!hasGoogleDriveConnection && (
                              <span className="connection-indicator">
                                <i className="fa-solid fa-plus"></i>
                              </span>
                            )}
                          </button>

                          {/* AWS S3 - Uses Access Keys (NOT OAuth) */}
                          <button
                            className={`toggle-btn-rsa task-button storage-provider-btn ${selectedStorageProvider === "s3" ? "selected" : ""} ${!hasS3Connection ? "needs-connection" : ""}`}
                            type="button"
                            onClick={() => {
                              const s3Conn = storageConnections.find(
                                (c) => c.provider_type === "s3" && c.is_active,
                              );
                              if (s3Conn) {
                                setSelectedStorageProvider("s3");
                                setSelectedConnection(s3Conn);
                                setUseSystemGoogleDrive(false);
                              } else {
                                setConnectionProvider("s3");
                                setShowConnectionModal(true);
                              }
                            }}
                            title={
                              !hasS3Connection
                                ? "Click to connect S3 with Access Keys"
                                : "AWS S3 (Access Keys)"
                            }
                          >
                            <AmazonS3Icon
                              className="storage-provider-icon"
                              style={{ width: "20px", height: "20px" }}
                            />
                            <span>AWS S3</span>
                            {!hasS3Connection && (
                              <span className="connection-indicator">
                                <i className="fa-solid fa-plus"></i>
                              </span>
                            )}
                          </button>

                          {/* Azure Blob Storage - Uses OAuth 2.0 */}
                          <button
                            className={`toggle-btn-rsa task-button storage-provider-btn ${selectedStorageProvider === "azure" ? "selected" : ""} ${!hasAzureConnection ? "needs-connection" : ""}`}
                            type="button"
                            onClick={() => {
                              const azureConn = storageConnections.find(
                                (c) =>
                                  c.provider_type === "azure" && c.is_active,
                              );
                              if (azureConn) {
                                setSelectedStorageProvider("azure");
                                setSelectedConnection(azureConn);
                                setUseSystemGoogleDrive(false);
                              } else {
                                setConnectionProvider("azure");
                                setShowConnectionModal(true);
                              }
                            }}
                            title={
                              !hasAzureConnection
                                ? "Click to connect Azure via OAuth 2.0"
                                : "Azure (OAuth 2.0)"
                            }
                          >
                            <AzureIcon
                              className="storage-provider-icon"
                              style={{ width: "20px", height: "20px" }}
                            />
                            <span>Microsoft Azure</span>
                            {!hasAzureConnection && (
                              <span className="connection-indicator">
                                <i className="fa-solid fa-plus"></i>
                              </span>
                            )}
                          </button>

                          {/* Google Cloud Storage - Uses OAuth 2.0 or Service Account */}
                          <button
                            className={`toggle-btn-rsa task-button storage-provider-btn ${selectedStorageProvider === "gcp" ? "selected" : ""} ${!hasGCPConnection ? "needs-connection" : ""}`}
                            type="button"
                            onClick={() => {
                              const gcpConn = storageConnections.find(
                                (c) => c.provider_type === "gcp" && c.is_active,
                              );
                              if (gcpConn) {
                                setSelectedStorageProvider("gcp");
                                setSelectedConnection(gcpConn);
                                setUseSystemGoogleDrive(false);
                              } else {
                                setConnectionProvider("gcp");
                                setShowConnectionModal(true);
                              }
                            }}
                            title={
                              !hasGCPConnection
                                ? "Click to connect GCP via OAuth 2.0 or Service Account"
                                : "GCP (OAuth 2.0)"
                            }
                          >
                            <GoogleCloudIcon
                              className="storage-provider-icon"
                              style={{ width: "22px", height: "22px" }}
                            />
                            <span>Google Cloud Storage</span>
                            {!hasGCPConnection && (
                              <span className="connection-indicator">
                                <i className="fa-solid fa-plus"></i>
                              </span>
                            )}
                          </button>
                        </div>
                      </div>
                    </PermissionGate>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div
          className={`right-panels${activeMainTab === "analysis" || activeMainTab === "routes" ? " right-panels-analysis" : ""}`}
        >
          {activeMainTab === "dashboard" ? (
            /* Dashboard Projects Panel */
            <DashboardProjectsPanel
              runs={displayRuns}
              selectedRun={dashboardSelectedRun}
              onSelectRun={setDashboardSelectedRun}
              onRequestDeleteRun={handleRequestDeleteDashboardRun}
              onCancelDeleteRun={handleCancelDeleteDashboardRun}
              onConfirmDeleteRun={handleDeleteDashboardRun}
              onDeleteAllRuns={handleDeleteAllDashboardRuns}
              confirmingRunId={dashboardDeleteCandidateRunId}
              deletingRunId={dashboardDeletingRunId}
              deletingAllRuns={dashboardDeletingAllRuns}
              runProgressMap={dashboardRunProgress}
            />
          ) : !isRsaModelProject && activeMainTab === "paths" ? (
            /* Paths Panel */
            <PathsPanel
              mode={pathsPanelMode}
              pendingIsdDraft={pendingIsdDraft}
              onSetPendingIsdDraft={setPendingIsdDraft}
              oppositePointPickActive={oppositePointPickActive}
              onStartOppositePointPick={startOppositePointPick}
              onCancelOppositePointPick={cancelOppositePointPick}
              oppositeBlockDraft={oppositeBlockDraft}
              onUpdateOppositeBlockDraft={setOppositeBlockDraft}
              selectedRsaTargetId={selectedRsaTargetId}
              onSetSelectedRsaTargetId={setSelectedRsaTargetId}
              lasBoundingBoxes={lasBoundingBoxes}
              orthoTiles={orthoTiles}
              lasVisible={lasBoundingBoxesVisible}
              onToggleLasVisibility={() =>
                setLasBoundingBoxesVisible((v) => !v)
              }
              onClearLas={() => {
                setLasBoundingBoxes([]);
                setSelectedLasBoundingBoxIndex(0);
              }}
              onSelectedLasExtentIndexChange={setSelectedLasBoundingBoxIndex}
              orthoVisible={orthoTilesVisible}
              onToggleOrthoVisibility={() => setOrthoTilesVisible((v) => !v)}
              onClearOrtho={() => {
                if (projectId) {
                  saveOrthoTilesToCache(projectId, []);
                }
                setOrthoTiles([]);
                setMaps2000ByPath({});
              }}
              onGoToLasBounds={(b) => setFitToBounds(b)}
              loadingLasBounds={loadingLasBounds}
              loadingOrthoTiles={loadingOrthoTiles}
              orthoImagesLoading={
                orthoTiles.length > 0 &&
                (imageOverlaysTotal === 0 ||
                  imageOverlaysLoadedCount < imageOverlaysTotal)
              }
              orthoLoadedCount={
                orthoTiles.length === 0
                  ? 0
                  : Math.min(
                      orthoTiles.length,
                      Math.max(0, imageOverlaysLoadedCount),
                    )
              }
              orthoTotal={orthoTiles.length}
              maps2000ByPath={maps2000ByPath}
              loadingMaps2000Check={loadingMaps2000Check}
              onRescanMaps2000={checkMaps2000ForPaths}
              onLoadLasToMap={loadLasToMap}
              hasFoldersSelected={mapOverlaySelectedFolders.length > 0}
              lasFoldersLoading={
                preloadRsaSetupLoading && mapOverlaySelectedFolders.length === 0
              }
              onLoadOrthoMaps={loadOrthoMaps}
              onCreateOffsetLane={createOffsetLaneForPath}
              onStartManualLane={startManualLaneCreation}
              onStopManualLane={stopManualLaneCreation}
              onResumeManualLane={resumeManualLane}
              manualLaneCreationActive={manualLaneCreationActive}
              activeManualLanePointCount={activeManualLanePointCount}
              wktAvailable={Boolean(
                normalizeWktInput(
                  String(mapOverlayLocalConfig?.wkt ?? "").trim(),
                ),
              )}
              overlayOpacity={overlayOpacity}
              onOverlayOpacityChange={setOverlayOpacity}
              overlayBrightness={overlayBrightness}
              onOverlayBrightnessChange={setOverlayBrightness}
              overlayContrast={overlayContrast}
              onOverlayContrastChange={setOverlayContrast}
              intersectionDrawingMode={intersectionDrawingMode}
              onIntersectionDrawingModeChange={
                handleIntersectionDrawingModeChange
              }
              intersectionOverlays={intersectionOverlays}
              onIntersectionOverlayUpdate={(id, payload) =>
                setIntersectionOverlays((prev) =>
                  prev.map((o) => (o.id === id ? { ...o, ...payload } : o)),
                )
              }
              onClearIntersectionOverlays={() => setIntersectionOverlays([])}
              onIntersectionOverlayRemove={(id) =>
                setIntersectionOverlays((prev) =>
                  prev.filter((o) => o.id !== id),
                )
              }
              curvePoints={curvePoints}
              measurePoints={measurePoints}
              onResetMeasureDistance={() => setMeasurePoints([])}
              curvedLineEditPointCount={curvedLineEditPointCount}
              onCurvedLineEditPointCountChange={setCurvedLineEditPointCount}
              onGoToSegment={(idxOrPathData) => {
                if (typeof idxOrPathData === "number") {
                  const pathData = rsaLegacySegments[idxOrPathData]?.pathData;
                  if (pathData?.geometry?.coordinates?.length)
                    setFitToPathData(pathData);
                } else if (idxOrPathData?.geometry?.coordinates?.length) {
                  setFitToPathData(idxOrPathData);
                }
              }}
              onSavePath={saveLasSeparationPathToDrive}
              isSavingPath={savingLasSeparationPath}
              onCreateOrthoMaps={createOrthoMaps}
              creatingOrthoMapsByPath={creatingOrthoMapsByPath}
              orthoProgressByPath={orthoProgressByPath}
              onStartAutoTrace={startAutoTrace}
              laneTraceProgress={laneTraceProgress}
              bridge={bridge}
              onResumeAuto={resumeAutoFromBridge}
              onEndBridge={endBridge}
              onEnterBridgeManually={enterBridgeManually}
              onDeletePath={handleDeletePathWithCleanup}
              onRenamePath={handleRenamePath}
              deletingPathIds={deletingPathIds}
            />
          ) : (
            <>
              <RSASummary
                activeMainTab={activeMainTab}
                mapOverlayLocalConfig={mapOverlayLocalConfig}
                wktValidation={wktValidation}
                mapOverlaySelectedFolders={mapOverlaySelectedFolders}
                setupFolderAvailable={Boolean(effectiveSetupFolderId)}
                lasBoundingBoxes={lasBoundingBoxes}
                orthoTiles={orthoTiles}
                existingProjectName={existingProjectName}
                selectedProjectId={effectiveProjectId}
                rsaGroupsMileages={rsaGroupsMileages}
                rsaProjectsPerGroup={rsaProjectsPerGroup}
                rsaGroupsIsdManeuvers={rsaGroupsIsdManeuvers}
                services={services}
                selectedFolders={selectedFolders}
                selectedDriveName={selectedDriveName}
                roadDetectionCategory={roadDetectionCategory}
                horizontalData={horizontalData}
                EmbankmentData={EmbankmentData}
                selectedStorageProvider={selectedStorageProvider}
                onSubmit={async (e) => {
                  if (routesXmlFiles.length > 0) {
                    const routesSyncResult = await saveRoutesMatrixSelection();
                    if (!routesSyncResult?.ok) {
                      return;
                    }
                  }
                  await handleSubmit(e);
                }}
                onClear={handleClearClick}
                onSaveSetup={handleSaveSetupClick}
                savingSetup={savingSetup}
                wktInputLocked={wktInputLocked}
                onOpenWktRequirementsHelp={() =>
                  setWktRequirementsHelpModalOpen(true)
                }
                isRsaModelProject={isRsaModelProject}
                rsaModelRouteInfoRef={rsaModelRouteInfoRef}
                rsaRunMetricsRef={rsaRunMetricsRef}
                rsaUsageLedgerState={rsaUsageLedgerState}
                rsaUsageLedgerLoading={rsaUsageLedgerLoading}
                routesSourceFiles={routesSourceFiles}
                routesXmlFiles={routesXmlFiles}
                modelFiles={modelFiles}
                modelFolderId={modelFolderId}
                existingSourceFolderId={existingSourceFolderId}
                existingSourceFiles={existingSourceFiles}
                routesMatrixSelection={routesMatrixSelection}
                onToggleRouteFile={toggleRoutesMatrixCell}
                routesMatrixSaving={routesMatrixSaving}
              />
            </>
          )}
        </div>
        <WktRequirementsHelpModal
          open={wktRequirementsHelpModalOpen}
          onClose={() => setWktRequirementsHelpModalOpen(false)}
        />
        {xmlOverwriteWarning && (
          <div className="xml-overwrite-modal-overlay">
            <div className="xml-overwrite-modal">
              <h3 className="xml-overwrite-modal-title">
                <i
                  className="fa-solid fa-triangle-exclamation xml-overwrite-modal-title-icon"
                  aria-hidden="true"
                />
                {xmlOverwriteWarning.filenames.length > 1
                  ? "XML Files Already Exist"
                  : "XML File Already Exists"}
              </h3>
              <p className="xml-overwrite-modal-body">
                {xmlOverwriteWarning.filenames.length > 1
                  ? "The following files already exist in Source/Routes. Overwriting will replace them with the new version. Cancel to keep the existing files."
                  : "The following file already exists in Source/Routes. Overwriting will replace it with the new version. Cancel to keep the existing file."}
              </p>
              <ul className="xml-overwrite-modal-list">
                {xmlOverwriteWarning.filenames.map((fn) => (
                  <li key={fn}>{fn}</li>
                ))}
              </ul>
              <div className="xml-overwrite-modal-actions">
                <button
                  className="xml-overwrite-btn-cancel"
                  onClick={() => xmlOverwriteWarning.resolve("cancel")}
                >
                  Cancel
                </button>
                <button
                  className="xml-overwrite-btn-confirm"
                  onClick={() => xmlOverwriteWarning.resolve("overwrite")}
                >
                  Overwrite &amp; Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </RsaPathsContext.Provider>
  );
}

export default PayloadForm;
